// ── Translation shape: same structure as EN_STRINGS but all leaves widened to string ──
type Stringify<T> =
  T extends string ? string :
  T extends readonly (infer U)[] ? readonly Stringify<U>[] :
  T extends object ? { -readonly [K in keyof T]: Stringify<T[K]> } :
  string;

// ── English strings (source of truth for shape) ───────────────────────────────
export const EN_STRINGS = {

  PAGE_TITLES: {
    DEFAULT: 'Contactless Sign In | Visitly',
    PRE_ARRIVAL: 'Pre Arrival | Visitly',
  },

  // /home
  HOME: {
    TITLE: 'Contactless Sign In',
    SUBTITLE: 'Point your camera at the QR code shown on the iPad or printed sign to continue',
  },

  // Shared across multiple pages
  COMMON: {
    BACK: 'Back',
    CONTINUE: 'Continue',
    AGREE_AND_CONTINUE: 'Agree & Continue',
    DECLINE: 'Decline',
    CLEAR_SIGNATURE: 'Clear Signature',
    LOADING: 'Loading...',
    SEARCHING: 'Searching...',
    NO: 'NO',
    YES: 'YES',
    ENABLE_CAMERA: 'Enable Camera',
    PAGE_NOT_FOUND: '404 - Page Not Found',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: 'Retake',
    CAPTURE: 'Capture',
    RETRY: 'Retry',
    POINT_OF_ENTRY: 'Point of Entry',
    PARKING_LOT: 'Parking Lot',
    DESTINATION: 'Building',
    ERROR_PROCESSING_REQUEST: 'Error processing request. Please try again.',
    SESSION_EXPIRED: 'Session expired. Please reopen the link from your email.',
  },

  // /full-name
  FULL_NAME: {
    TITLE: "What's your full name?",
    SUBTITLE: "Just so we know who's visiting today",
    FORM_LABEL: 'Full Name',
    PLACEHOLDER: 'e.g. Joseph Jose',
  },

  // /visitortypes
  VISITOR_TYPES: {
    SUBTITLE: 'Please choose your visit type.',
    FALLBACK_TITLE: 'What brings you in?',
  },

  // /select-host
  SELECT_HOST: {
    TITLE: 'Who are you visiting?',
    SUBTITLE: 'Search for your host to notify them of your arrival.',
    PLACEHOLDER: 'Select a host',
    SEARCH_PLACEHOLDER: 'Search for host...',
    SEARCH_PLACEHOLDER_ALT: 'Search host by name...',
    NO_HOSTS_FOUND: 'No hosts found',
    MIN_CHARS_MESSAGE: 'Type at least 3 characters to search',
  },

  // /visitorform
  VISITOR_FORM: {
    TITLE: 'Visit Details',
    SUBTITLE: 'Please fill in the required information below.',
    SELECT_FIELD_PLACEHOLDER: 'Select {field}',
  },

  // Validation messages
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Please enter your full name',
    EMAIL_REQUIRED: 'Please enter a valid email address',
    EMAIL_INVALID: 'Please enter a valid email address',
    HOST_REQUIRED: 'Please select your host name',
    COMPANY_NAME_REQUIRED: 'Please enter your company name',
    PHONE_NUMBER_REQUIRED: 'Please enter your phone number',
    CUSTOM_FIELD_REQUIRED: 'Please enter {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} is required',
    FULL_NAME_INVALID: 'Please enter a valid name.',
    TOAST_ERROR_PROCESSING: 'Error processing request. Please try again.',
  },

  // /signdocument
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Please review and sign the document below',
    ERROR_DOCUMENT_EXPIRY: 'Error checking document expiry',
    ERROR_LOADING_CONTENT: 'Error loading document content',
    ERROR_FETCHING_DOCUMENT: 'Error fetching document',
    ERROR_RESTORE_SESSION: 'Unable to restore the document signing session. Please restart from the home page.',
    BTN_COLLAPSE: 'Collapse',
    BTN_EXPAND: 'Expand',
  },

  // /privacy-policy
  PRIVACY_POLICY: {
    TITLE: 'Before you continue',
    SUBTITLE: 'Please review and agree before continuing',
    IMPLICIT_CONSENT: 'By clicking continue below, you consent to allow {orgName} to store and process the personal information submitted above to provide the content you requested.',
    EXPLICIT_CONSENT: 'I agree to allow {orgName} to store and process my personal data.',
  },

  // /capturepicture
  CAPTURE_PICTURE: {
    TITLE: 'Select ID',
    SUBTITLE: 'Please select an ID type to upload',
    ID_TYPES: [
      { label: 'Passport', value: 'PASSPORT' },
      { label: "Driver's License", value: 'DRIVING_LICENCE' },
      { label: 'State-Issued ID', value: 'STATE_ISSUED_ID' },
      { label: 'I have a different ID', value: 'OTHERS' },
    ],
  },

  // /visitorpicture
  VISITOR_PICTURE: {
    TITLE: 'Take Photo',
    SUBTITLE: 'Please center your face and click continue',
    INSTRUCTIONS: 'Use your front camera, look straight ahead, and center your face.',
    BTN_CAPTURE: 'Capture',
    BTN_RETAKE: 'Retake',
    BTN_ENABLE_CAMERA: 'Enable Camera',
    PERMISSION_PROMPT: 'Tap "Enable Camera" to grant camera access.',
    PERMISSION_DENIED: 'Camera access is blocked. Enable it in your browser settings, then tap Enable Camera again.',
    PERMISSION_UNSUPPORTED: 'No camera found on this device.',
    TOAST_CAMERA_ERROR: 'Camera access error. Check console!',
    BTN_RETRY_CAMERA: 'Retry Camera',
    CAMERA_NOT_AVAILABLE: "Camera couldn't start. Tap retry, or reload the page.",
  },

  // /frontbackpicture
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Enable Camera',
    BTN_START_CAMERA: 'Start Camera',
    BTN_TRY_NOW: 'Try Now',
    PERMISSION_PROMPT: 'Tap "Enable Camera" to grant camera access.',
    PERMISSION_DENIED: 'Camera access is blocked. Enable it in your browser settings, then tap Enable Camera again.',
    PERMISSION_UNSUPPORTED: 'No camera found on this device.',
    ID_DRIVERS_LICENSE: "Driver's License",
    ID_STATE_ISSUED: 'State-Issued ID',
    ID_PASSPORT: 'Passport',
    ID_OTHERS: 'Others',
    ERROR_CAMERA_IN_USE: 'Camera is in use. Close the other app and try again.',
    ERROR_CAMERA_INIT_FAILED: 'Camera initialization failed. Please check permissions.',
    ERROR_CAMERA_BUSY_RETRY: 'Camera is still in use. Close the other app and try again.',
    ERROR_PLEASE_SCAN_DOCS: 'Please scan document(s)',
    SUBTITLE: 'Scan the selected document below.',
    LABEL_FRONT_SIDE: 'Front Side',
    LABEL_BACK_SIDE: 'Back Side',
    INSTRUCTION_POSITION_FRONT: 'Position the front of your ID',
    INSTRUCTION_POSITION_BACK: 'Position the back of your ID',
    MRZ_BAND_HINT: 'Line up the two lines at the bottom of the photo page below the dashed line',
    MRZ_SCAN_FAILED: 'We couldn\'t read your passport. Hold it steady with the two lines at the bottom inside the band, and try again.',
    MRZ_SCAN_ATTEMPT: 'Scan {current} of {total}',
    GLARE_WARNING: 'Glare on the document. Tilt it slightly or move away from the light.',
    BARCODE_BAND_HINT: 'Hold the card steady, about 20 cm away, with the barcode inside the frame',
    BARCODE_SCAN_FAILED: 'We couldn\'t read the barcode. Hold the back of the card steady and level inside the frame, and try again.',
    TORCH_ON: 'Turn on light',
    TORCH_OFF: 'Turn off light',
    BARCODE_READING_PHOTO: 'Reading the barcode…',
    ID_CHECKING: 'Checking your ID…',
  },

  // /visitor-video
  VISITOR_VIDEO: {
    TITLE: 'Almost There!',
    SUBTITLE: 'Watch the video below to continue.',
    BTN_CHECK_IN: 'Check In',
    BTN_FINISH: 'Finish',
  },

  // /pre-reg-confirmation
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registration Found',
    SUBTITLE: 'Please verify your details to complete check-in.',
    BTN_NOT_YOU: 'Not you?',
    MEETING_WITH_LABEL: 'Meeting with {hostName}',
    ONSITE_TITLE: "You're already checked in",
    ONSITE_SUBTITLE: 'Select your session to sign out.',
    CHECKIN_AT_LABEL: 'Checked in at:',
  },

  // /confirmation (standard check-in)
  CONFIRMATION: {
    CHECKED_IN_TITLE: "You're Checked In!",
    WALLET_HINT: 'The pass will automatically open on your device.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Pre-Arrival Registration Completed',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Arrival Instructions',
    ARRIVAL_INSTRUCTIONS_TEXT: 'A QR code has been sent to your registered email. Use it for a faster check-in at the kiosk when you arrive.',
    QR_CAPTION: 'This QR code can be scanned at the lobby to pull up your registration.',
    BTN_SAVE_QR: 'Save QR Code',
    WALLET_LABEL: 'Wallet',
    ACTION_ADD_PASS: 'Add Pass',
    CALENDAR_LABEL: 'Calendar',
    ACTION_SAVE_EVENT: 'Save Event',
    DEFAULT_WELCOME_MESSAGE: 'Your host has been notified and will be with you shortly.',
    PREFILL_SCHEDULED_FOR: "You're all set for {date}.",
    PREFILL_HOST_NOTIFIED: 'Your host has been notified, and we look forward to welcoming you.',
    TOAST_UPDATE_VISIT_ERROR: 'Error encountered while updating visit. Please try again.',
    TOAST_GENERIC_ERROR: 'We have encountered an error. Please try again!',
  },

  // /prefill-details
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Visit Details',
    PAGE_SUBTITLE: 'Complete the steps below to speed up your check-in.',
    SECTION_HEADING: 'Complete Your Pre-Arrival Registration',
    STEPS_COMPLETED_SUFFIX: 'steps completed',
    BTN_START: "Let's Get Started!",
  },

  // /signout
  SIGN_OUT: {
    EYEBROW: 'Sign Out',
    TITLE_WITH_NAME: 'Goodbye,',
    TITLE_FALLBACK: 'Goodbye!',
    SUBTITLE_SESSION: 'Confirm your details and sign out.',
    STRIP_SUBTITLE: 'Currently Signed In',
    LABEL_ORG: 'ORGANISATION',
    LABEL_LOCATION: 'LOCATION',
    LABEL_HOST: 'HOST',
    LABEL_SIGNED_IN_AT: 'SIGNED IN',
    BTN_SIGN_OUT: 'Sign out',
    BTN_SIGNING_OUT: 'Signing out...',
    BTN_START_OVER: 'Start over',
    LABEL_NOT_YOU: 'Not you?',
    TOAST_SUCCESS: 'You have been successfully signed out!',
    TOAST_ERROR_FALLBACK: 'Error signing out. Please try again.',
    CUSTOM_FIELDS_EYEBROW: 'Almost Done',
    CUSTOM_FIELDS_TITLE: 'Complete sign-out',
    CUSTOM_FIELDS_SUBTITLE: 'Please fill in the required details.',
    BTN_CONFIRM_SIGN_OUT: 'Confirm sign-out',
    BTN_GO_BACK: 'Go back',
    SUCCESS_TITLE: "You're all signed out",
    SUCCESS_SUBTITLE: 'Thanks for visiting',
    BTN_DONE: 'Done',
    LABEL_VISIT_DURATION: 'Visit duration',
    GEOLOCATION_UNAVAILABLE: 'Location access is required to check out. Please use a mobile device.',
    LOCATION_PERMISSION_REQUIRED: 'Location permission is required to check out. Please allow location access and try again.',
    MUST_BE_ON_SITE: 'You must be on-site to check out.',
    GUEST_PLACEHOLDER: 'Guest',
    SUCCESS_CLOSING: 'Have a great day!',
    STATUS_CHECKED_IN: 'Checked In',
    STATUS_CHECKED_OUT: 'Checked Out',
    DETAIL_LABEL_HOST: 'Host',
  },

  // /prefill (preregister prefilled-info section)
  PREREGISTER: {
    ALL_SET_TITLE: "You're all set for your visit",
    ALL_SUB_TITLE: 'Your pre-arrival registration is complete. You can review your visit details below.',
    BTN_CHECK_DETAILS: 'Check my details',
    BTN_VIEW_CHECKIN: 'View contactless check-in',
    QR_TITLE: 'Contactless Sign In',
    QR_SUBTITLE: 'Point your camera at the QR code shown on the iPad or printed sign to continue',
    ERROR_TITLE: 'Something went wrong!',
    ERROR_SUBTITLE: "We're sorry, but an error occurred. Please try again later.",
    DETAIL_LABEL_VISITOR: 'Visitor',
    DETAIL_LABEL_EMAIL: 'Email',
    DETAIL_LABEL_HOST: 'Host',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },

  // Pre-registration link expired page
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Pre-Arrival Registration Link Expired',
    SUBTITLE: 'This link is no longer valid',
    REASONS_HEADING: 'Possible reasons:',
    REASON_CANCELLED: 'Invitation cancelled',
    REASON_CHECKED_IN: 'Already checked in',
    REASON_PAST: 'Scheduled time has passed',
    HELP_PROMPT: 'Need help?',
    HELP_TEXT: 'Contact your host or front desk',
  },

  // SmartPass check-in
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Invalid pass link.',
    PASS_REVOKED_OR_EXPIRED: 'This pass has been revoked or expired.',
    UNABLE_TO_LOAD: 'Unable to load check-in page.',
    PASS_EXPIRED_TITLE: 'Pass Expired',
    CONTACT_HOST_FOR_NEW_PASS: 'Please contact your host for a new SmartPass.',
    ERROR_TITLE: 'Something went wrong',
    BTN_TRY_AGAIN: 'Try Again',
    STATUS_NOT_ON_SITE: 'Not On-Site',
    STATUS_CHECKED_IN: 'Checked In',
    SELECT_LOCATION_LABEL: 'Select your location:',
    SELECT_LOCATION_PLACEHOLDER: '-- Select location --',
    BTN_PROCESSING: 'Processing...',
    BTN_CHECK_IN: 'Check In',
    BTN_CHECK_OUT: 'Check Out',
    BTN_CLOSE_HINT: 'You can close this page.',
    POWERED_BY: 'Powered by Visitly',
  },

  // SmartPass download
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Invalid pass link.',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet support coming soon. Please check back later.',
    OPEN_ON_MOBILE: 'Please open this link on your iPhone or Android phone to add the pass to your wallet.',
    PREPARING_PASS: 'Preparing your SmartPass...',
    TITLE: 'SmartPass',
  },

  // SmartPass redirect
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Invalid pass link.',
    LOCATION_REQUIRED: 'SmartPass check-in requires location access. Please open this link on your mobile device.',
    LOCATION_PERMISSION_REQUIRED: 'Location permission is required for SmartPass check-in. Please allow location access and try again.',
    LOCATION_UNAVAILABLE: 'Unable to determine your location. Please ensure location services are enabled and try again.',
    PASS_REVOKED_OR_EXPIRED: 'This pass has been revoked or expired.',
    OUTSIDE_GEOFENCE: 'You are outside the check-in area. Please move closer to the building and try again.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Retry',
    OPENING_CHECK_IN: 'Opening check-in...',
  },

  // Touchless / kiosk splash
  TOUCHLESS: {
    EVACUATION_BANNER: 'EVACUATION IS IN PROGRESS AT THIS LOCATION',
    BTN_TAP_TO_SIGNIN: 'Tap to Sign in',
    BTN_SCAN_QR_CODE: 'Scan QR code',
    TOAST_INVALID_QR: 'Invalid or Expired QR Code. Please try again!',
  },

  // Layout: Footer
  FOOTER: {
    POWERED_BY: 'POWERED BY ',
  },

  // Layout: Header
  HEADER: {
    STEP: 'STEP ',
    OF: ' OF ',
  },

  // Splash screen
  SPLASH_SCREEN: {
    TAP_TO_START: 'Tap to start',
  },

  // QR scanner
  QR_SCANNER: {
    INVALID_CODE_ERROR: 'Invalid QR Code. Please try again!',
  },

  // QR scanner card
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'We only use your camera to read the lobby QR and do not store any video',
    PERMISSION_PROMPT: 'Tap "Enable Camera" to grant camera access.',
    PERMISSION_DENIED: 'Camera access is blocked. Enable it in your browser settings, then tap Enable Camera again.',
    PERMISSION_UNSUPPORTED: 'No camera found on this device.',
    CAMERA_NOT_AVAILABLE: "Camera couldn't start. Tap retry, or reload the page.",
    ERROR_CAMERA_IN_USE: 'Camera is in use. Close the other app and try again.',
    ERROR_CAMERA_INIT_FAILED: 'Camera initialization failed. Please check permissions.',
    ERROR_CAMERA_BUSY_RETRY: 'Camera is still in use. Close the other app and try again.',
  },

  // Camera permission
  CAMERA_PERMISSION: {
    REQUIRED: 'Camera permission is required to scan QR codes.',
    ACCESS_DENIED: 'Camera access denied or not available',
  },

  // Confirmation modal
  CONFIRMATION_MODAL: {
    DEFAULT_MESSAGE: 'Are you sure, you want to cancel? You will lose all of your progress.',
  },

  // Progress bar step labels (used in useProgressStep)
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Select Host',       prefillTitle: 'Select Host',    prefillDesc: "Choose who you're visiting" },
    DETAILS:    { headerTitle: 'Your Details',      prefillTitle: 'Your Details',   prefillDesc: 'Provide your contact information' },
    DOCUMENT:   { headerTitle: 'Sign Documents',    prefillTitle: 'Sign Documents', prefillDesc: 'Review and sign required documents' },
    ID_CAPTURE: { headerTitle: 'Verify ID',         prefillTitle: 'Verify ID',      prefillDesc: 'Scan your ID' },
    PHOTO:      { headerTitle: 'Take a Selfie',     prefillTitle: 'Take a Selfie',  prefillDesc: 'Capture your photo' },
    VIDEO:      { headerTitle: 'Watch a Video',     prefillTitle: 'Watch a Video',  prefillDesc: 'View a safety or orientation video' },
  },

} as const;

export type StringsShape = Stringify<typeof EN_STRINGS>;

// ── French ─────────────────────────────────────────────────────────────────────
export const FR_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Connexion sans contact | Visitly',
    PRE_ARRIVAL: 'Pré-arrivée | Visitly',
  },
  HOME: {
    TITLE: 'Connexion sans contact',
    SUBTITLE: "Pointez votre caméra vers le code QR affiché sur l'iPad ou l'affiche imprimée pour continuer",
  },
  COMMON: {
    BACK: 'Retour',
    CONTINUE: 'Continuer',
    AGREE_AND_CONTINUE: 'Accepter et continuer',
    DECLINE: 'Refuser',
    CLEAR_SIGNATURE: 'Effacer la signature',
    LOADING: 'Chargement...',
    SEARCHING: 'Recherche...',
    NO: 'NON',
    YES: 'OUI',
    ENABLE_CAMERA: 'Activer la caméra',
    PAGE_NOT_FOUND: '404 - Page non trouvée',
    POWERED_BY: 'Propulsé par Visitly',
    RETAKE: 'Reprendre',
    CAPTURE: 'Capturer',
    RETRY: 'Réessayer',
    POINT_OF_ENTRY: "Point d'entrée",
    PARKING_LOT: 'Parking',
    DESTINATION: 'Bâtiment',
    ERROR_PROCESSING_REQUEST: 'Erreur lors du traitement de la demande. Veuillez réessayer.',
    SESSION_EXPIRED: 'Session expirée. Veuillez rouvrir le lien depuis votre e-mail.',
  },
  FULL_NAME: {
    TITLE: 'Quel est votre nom complet ?',
    SUBTITLE: "Juste pour savoir qui nous rend visite aujourd'hui",
    FORM_LABEL: 'Nom complet',
    PLACEHOLDER: 'ex. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Veuillez choisir votre type de visite.',
    FALLBACK_TITLE: 'Quelle est la raison de votre venue ?',
  },
  SELECT_HOST: {
    TITLE: 'Qui venez-vous visiter ?',
    SUBTITLE: 'Recherchez votre hôte pour le notifier de votre arrivée.',
    PLACEHOLDER: 'Sélectionner un hôte',
    SEARCH_PLACEHOLDER: 'Rechercher un hôte...',
    SEARCH_PLACEHOLDER_ALT: 'Rechercher un hôte par nom...',
    NO_HOSTS_FOUND: 'Aucun hôte trouvé',
    MIN_CHARS_MESSAGE: 'Tapez au moins 3 caractères pour rechercher',
  },
  VISITOR_FORM: {
    TITLE: 'Détails de la visite',
    SUBTITLE: 'Veuillez remplir les informations requises ci-dessous.',
    SELECT_FIELD_PLACEHOLDER: 'Sélectionner {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Veuillez entrer votre nom complet',
    EMAIL_REQUIRED: 'Veuillez entrer une adresse e-mail valide',
    EMAIL_INVALID: 'Veuillez entrer une adresse e-mail valide',
    HOST_REQUIRED: 'Veuillez sélectionner le nom de votre hôte',
    COMPANY_NAME_REQUIRED: 'Veuillez entrer le nom de votre entreprise',
    PHONE_NUMBER_REQUIRED: 'Veuillez entrer votre numéro de téléphone',
    CUSTOM_FIELD_REQUIRED: 'Veuillez entrer {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} est requis',
    FULL_NAME_INVALID: 'Veuillez saisir un nom valide.',
    TOAST_ERROR_PROCESSING: 'Erreur lors du traitement de la demande. Veuillez réessayer.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Veuillez lire et signer le document ci-dessous',
    ERROR_DOCUMENT_EXPIRY: "Erreur lors de la vérification de l'expiration du document",
    ERROR_LOADING_CONTENT: 'Erreur lors du chargement du contenu du document',
    ERROR_FETCHING_DOCUMENT: 'Erreur lors de la récupération du document',
    ERROR_RESTORE_SESSION: "Impossible de restaurer la session de signature. Veuillez recommencer depuis la page d'accueil.",
    BTN_COLLAPSE: 'Réduire',
    BTN_EXPAND: 'Développer',
  },
  PRIVACY_POLICY: {
    TITLE: 'Avant de continuer',
    SUBTITLE: 'Veuillez lire et accepter avant de continuer',
    IMPLICIT_CONSENT: 'En cliquant sur continuer ci-dessous, vous consentez à autoriser {orgName} à stocker et traiter les informations personnelles soumises ci-dessus afin de vous fournir le contenu demandé.',
    EXPLICIT_CONSENT: "J'accepte d'autoriser {orgName} à stocker et traiter mes données personnelles.",
  },
  CAPTURE_PICTURE: {
    TITLE: "Sélectionner une pièce d'identité",
    SUBTITLE: "Veuillez sélectionner un type de pièce d'identité à télécharger",
    ID_TYPES: [
      { label: 'Passeport', value: 'PASSPORT' },
      { label: 'Permis de conduire', value: 'DRIVING_LICENCE' },
      { label: "Carte d'identité nationale", value: 'STATE_ISSUED_ID' },
      { label: "J'ai une autre pièce d'identité", value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Prendre une photo',
    SUBTITLE: 'Centrez votre visage et appuyez sur continuer',
    INSTRUCTIONS: 'Utilisez votre caméra frontale, regardez droit devant vous et centrez votre visage.',
    BTN_CAPTURE: 'Capturer',
    BTN_RETAKE: 'Reprendre',
    BTN_ENABLE_CAMERA: 'Activer la caméra',
    PERMISSION_PROMPT: 'Appuyez sur "Activer la caméra" pour autoriser l\'accès à la caméra.',
    PERMISSION_DENIED: "L'accès à la caméra est bloqué. Activez-le dans les paramètres de votre navigateur, puis appuyez à nouveau sur Activer la caméra.",
    PERMISSION_UNSUPPORTED: 'Aucune caméra trouvée sur cet appareil.',
    TOAST_CAMERA_ERROR: "Erreur d'accès à la caméra. Vérifiez la console !",
    BTN_RETRY_CAMERA: 'Réessayer la caméra',
    CAMERA_NOT_AVAILABLE: "Impossible de démarrer la caméra. Appuyez sur Réessayer ou rechargez la page.",
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Activer la caméra',
    BTN_START_CAMERA: 'Démarrer la caméra',
    BTN_TRY_NOW: 'Essayer maintenant',
    PERMISSION_PROMPT: 'Appuyez sur "Activer la caméra" pour autoriser l\'accès à la caméra.',
    PERMISSION_DENIED: "L'accès à la caméra est bloqué. Activez-le dans les paramètres de votre navigateur, puis appuyez à nouveau sur Activer la caméra.",
    PERMISSION_UNSUPPORTED: 'Aucune caméra trouvée sur cet appareil.',
    ID_DRIVERS_LICENSE: 'Permis de conduire',
    ID_STATE_ISSUED: "Carte d'identité nationale",
    ID_PASSPORT: 'Passeport',
    ID_OTHERS: 'Autres',
    ERROR_CAMERA_IN_USE: "La caméra est utilisée. Fermez l'autre application et réessayez.",
    ERROR_CAMERA_INIT_FAILED: "Échec de l'initialisation de la caméra. Veuillez vérifier les autorisations.",
    ERROR_CAMERA_BUSY_RETRY: "La caméra est toujours utilisée. Fermez l'autre application et réessayez.",
    ERROR_PLEASE_SCAN_DOCS: 'Veuillez scanner le(s) document(s)',
    SUBTITLE: 'Scannez le document sélectionné ci-dessous.',
    LABEL_FRONT_SIDE: 'Face avant',
    LABEL_BACK_SIDE: 'Face arrière',
    INSTRUCTION_POSITION_FRONT: "Positionnez le recto de votre pièce d'identité",
    INSTRUCTION_POSITION_BACK: "Positionnez le verso de votre pièce d'identité",
    MRZ_BAND_HINT: 'Placez les deux lignes du bas de la page photo sous la ligne pointillée',
    MRZ_SCAN_FAILED: 'Nous n\'avons pas pu lire votre passeport. Tenez-le immobile, les deux lignes du bas dans la bande, puis réessayez.',
    MRZ_SCAN_ATTEMPT: 'Lecture {current} sur {total}',
    GLARE_WARNING: 'Reflet sur le document. Inclinez-le légèrement ou éloignez-vous de la lumière.',
    BARCODE_BAND_HINT: 'Tenez la carte immobile, à environ 20 cm, le code-barres dans le cadre',
    BARCODE_SCAN_FAILED: 'Nous n\'avons pas pu lire le code-barres. Tenez le dos de la carte immobile et droit dans le cadre, puis réessayez.',
    TORCH_ON: 'Allumer la lampe',
    TORCH_OFF: 'Éteindre la lampe',
    BARCODE_READING_PHOTO: 'Lecture du code-barres…',
    ID_CHECKING: 'Vérification de votre pièce d\'identité…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Presque terminé !',
    SUBTITLE: 'Regardez la vidéo ci-dessous pour continuer.',
    BTN_CHECK_IN: "S'enregistrer",
    BTN_FINISH: 'Terminer',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Inscription trouvée',
    SUBTITLE: "Veuillez vérifier vos informations pour finaliser l'enregistrement.",
    BTN_NOT_YOU: "Ce n'est pas vous ?",
    MEETING_WITH_LABEL: 'Réunion avec {hostName}',
    ONSITE_TITLE: 'Vous êtes déjà enregistré',
    ONSITE_SUBTITLE: 'Sélectionnez votre session pour vous déconnecter.',
    CHECKIN_AT_LABEL: 'Enregistré à :',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Vous êtes enregistré !',
    WALLET_HINT: "Le pass s'ouvrira automatiquement sur votre appareil.",
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Pré-inscription terminée',
    ARRIVAL_INSTRUCTIONS_TITLE: "Instructions d'arrivée",
    ARRIVAL_INSTRUCTIONS_TEXT: "Un code QR a été envoyé à votre adresse e-mail enregistrée. Utilisez-le pour un enregistrement plus rapide au kiosque à votre arrivée.",
    QR_CAPTION: 'Ce code QR peut être scanné dans le hall pour afficher votre inscription.',
    BTN_SAVE_QR: 'Enregistrer le code QR',
    WALLET_LABEL: 'Portefeuille',
    ACTION_ADD_PASS: 'Ajouter un pass',
    CALENDAR_LABEL: 'Calendrier',
    ACTION_SAVE_EVENT: "Enregistrer l'événement",
    DEFAULT_WELCOME_MESSAGE: 'Votre hôte a été informé et sera bientôt avec vous.',
    PREFILL_SCHEDULED_FOR: 'Tout est prêt pour le {date}.',
    PREFILL_HOST_NOTIFIED: 'Votre hôte a été informé et nous avons hâte de vous accueillir.',
    TOAST_UPDATE_VISIT_ERROR: 'Erreur lors de la mise à jour de la visite. Veuillez réessayer.',
    TOAST_GENERIC_ERROR: 'Une erreur est survenue. Veuillez réessayer !',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Détails de la visite',
    PAGE_SUBTITLE: 'Complétez les étapes ci-dessous pour accélérer votre enregistrement.',
    SECTION_HEADING: 'Complétez votre pré-inscription',
    STEPS_COMPLETED_SUFFIX: 'étapes terminées',
    BTN_START: "C'est parti !",
  },
  SIGN_OUT: {
    EYEBROW: 'Déconnexion',
    TITLE_WITH_NAME: 'Au revoir,',
    TITLE_FALLBACK: 'Au revoir !',
    SUBTITLE_SESSION: 'Confirmez vos informations et déconnectez-vous.',
    STRIP_SUBTITLE: 'Actuellement connecté',
    LABEL_ORG: 'ORGANISATION',
    LABEL_LOCATION: 'EMPLACEMENT',
    LABEL_HOST: 'HÔTE',
    LABEL_SIGNED_IN_AT: 'CONNECTÉ À',
    BTN_SIGN_OUT: 'Se déconnecter',
    BTN_SIGNING_OUT: 'Déconnexion...',
    BTN_START_OVER: 'Recommencer',
    LABEL_NOT_YOU: "Ce n'est pas vous ?",
    TOAST_SUCCESS: 'Vous avez été déconnecté avec succès !',
    TOAST_ERROR_FALLBACK: 'Erreur lors de la déconnexion. Veuillez réessayer.',
    CUSTOM_FIELDS_EYEBROW: 'Presque terminé',
    CUSTOM_FIELDS_TITLE: 'Finaliser la déconnexion',
    CUSTOM_FIELDS_SUBTITLE: 'Veuillez remplir les informations requises.',
    BTN_CONFIRM_SIGN_OUT: 'Confirmer la déconnexion',
    BTN_GO_BACK: 'Retour',
    SUCCESS_TITLE: 'Vous êtes déconnecté',
    SUCCESS_SUBTITLE: 'Merci pour votre visite',
    BTN_DONE: 'Terminé',
    LABEL_VISIT_DURATION: 'Durée de la visite',
    GEOLOCATION_UNAVAILABLE: "L'accès à la localisation est requis pour se déconnecter. Veuillez utiliser un appareil mobile.",
    LOCATION_PERMISSION_REQUIRED: "La permission de localisation est requise pour se déconnecter. Veuillez autoriser l'accès à la localisation et réessayer.",
    MUST_BE_ON_SITE: 'Vous devez être sur site pour vous déconnecter.',
    GUEST_PLACEHOLDER: 'Invité',
    SUCCESS_CLOSING: 'Bonne journée !',
    STATUS_CHECKED_IN: 'Enregistré',
    STATUS_CHECKED_OUT: 'Sorti',
    DETAIL_LABEL_HOST: 'Hôte',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Tout est prêt pour votre visite',
    ALL_SUB_TITLE: 'Votre pré-inscription est complète. Vous pouvez consulter les détails de votre visite ci-dessous.',
    BTN_CHECK_DETAILS: 'Vérifier mes informations',
    BTN_VIEW_CHECKIN: "Voir l'enregistrement sans contact",
    QR_TITLE: 'Connexion sans contact',
    QR_SUBTITLE: "Pointez votre caméra vers le code QR affiché sur l'iPad ou l'affiche imprimée pour continuer",
    ERROR_TITLE: "Quelque chose s'est mal passé !",
    ERROR_SUBTITLE: "Nous sommes désolés, mais une erreur s'est produite. Veuillez réessayer plus tard.",
    DETAIL_LABEL_VISITOR: 'Visiteur',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Hôte',
    DETAIL_HOST_PLACEHOLDER: 'À déterminer',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: "Lien d'inscription pré-arrivée expiré",
    SUBTITLE: "Ce lien n'est plus valide",
    REASONS_HEADING: 'Raisons possibles :',
    REASON_CANCELLED: 'Invitation annulée',
    REASON_CHECKED_IN: 'Déjà enregistré',
    REASON_PAST: "L'heure prévue est passée",
    HELP_PROMPT: "Besoin d'aide ?",
    HELP_TEXT: 'Contactez votre hôte ou la réception',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Lien de pass invalide.',
    PASS_REVOKED_OR_EXPIRED: 'Ce pass a été révoqué ou est expiré.',
    UNABLE_TO_LOAD: "Impossible de charger la page d'enregistrement.",
    PASS_EXPIRED_TITLE: 'Pass expiré',
    CONTACT_HOST_FOR_NEW_PASS: 'Veuillez contacter votre hôte pour un nouveau SmartPass.',
    ERROR_TITLE: "Une erreur s'est produite",
    BTN_TRY_AGAIN: 'Réessayer',
    STATUS_NOT_ON_SITE: 'Pas sur site',
    STATUS_CHECKED_IN: 'Enregistré',
    SELECT_LOCATION_LABEL: 'Sélectionnez votre emplacement :',
    SELECT_LOCATION_PLACEHOLDER: '-- Sélectionner un emplacement --',
    BTN_PROCESSING: 'Traitement...',
    BTN_CHECK_IN: "S'enregistrer",
    BTN_CHECK_OUT: 'Se déconnecter',
    BTN_CLOSE_HINT: 'Vous pouvez fermer cette page.',
    POWERED_BY: 'Propulsé par Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Lien de pass invalide.',
    GOOGLE_WALLET_COMING_SOON: 'La prise en charge de Google Wallet arrive bientôt. Veuillez revenir plus tard.',
    OPEN_ON_MOBILE: 'Veuillez ouvrir ce lien sur votre iPhone ou téléphone Android pour ajouter le pass à votre portefeuille.',
    PREPARING_PASS: 'Préparation de votre SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Lien de pass invalide.',
    LOCATION_REQUIRED: "L'enregistrement SmartPass nécessite l'accès à la localisation. Veuillez ouvrir ce lien sur votre appareil mobile.",
    LOCATION_PERMISSION_REQUIRED: "La permission de localisation est requise pour l'enregistrement SmartPass. Veuillez autoriser l'accès à la localisation et réessayer.",
    LOCATION_UNAVAILABLE: "Impossible de déterminer votre emplacement. Veuillez vous assurer que les services de localisation sont activés et réessayer.",
    PASS_REVOKED_OR_EXPIRED: 'Ce pass a été révoqué ou est expiré.',
    OUTSIDE_GEOFENCE: "Vous êtes en dehors de la zone d'enregistrement. Veuillez vous rapprocher du bâtiment et réessayer.",
    TITLE: 'SmartPass',
    BTN_RETRY: 'Réessayer',
    OPENING_CHECK_IN: "Ouverture de l'enregistrement...",
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'UNE ÉVACUATION EST EN COURS DANS CET EMPLACEMENT',
    BTN_TAP_TO_SIGNIN: 'Appuyez pour vous connecter',
    BTN_SCAN_QR_CODE: 'Scanner le code QR',
    TOAST_INVALID_QR: 'Code QR invalide ou expiré. Veuillez réessayer !',
  },
  FOOTER: { POWERED_BY: 'PROPULSÉ PAR ' },
  HEADER: { STEP: 'ÉTAPE ', OF: ' SUR ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Appuyez pour commencer' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Code QR invalide. Veuillez réessayer !' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Nous utilisons uniquement votre caméra pour lire le code QR du hall et ne stockons aucune vidéo',
    PERMISSION_PROMPT: 'Appuyez sur "Activer la caméra" pour autoriser l\'accès à la caméra.',
    PERMISSION_DENIED: "L'accès à la caméra est bloqué. Activez-le dans les paramètres de votre navigateur, puis appuyez à nouveau sur Activer la caméra.",
    PERMISSION_UNSUPPORTED: 'Aucune caméra trouvée sur cet appareil.',
    CAMERA_NOT_AVAILABLE: "Impossible de démarrer la caméra. Appuyez sur Réessayer ou rechargez la page.",
    ERROR_CAMERA_IN_USE: "La caméra est utilisée. Fermez l'autre application et réessayez.",
    ERROR_CAMERA_INIT_FAILED: "Échec de l'initialisation de la caméra. Veuillez vérifier les autorisations.",
    ERROR_CAMERA_BUSY_RETRY: "La caméra est toujours utilisée. Fermez l'autre application et réessayez.",
  },
  CAMERA_PERMISSION: {
    REQUIRED: "La permission d'accès à la caméra est requise pour scanner les codes QR.",
    ACCESS_DENIED: 'Accès à la caméra refusé ou non disponible',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Êtes-vous sûr de vouloir annuler ? Vous perdrez toute votre progression.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: "Sélectionner l'hôte",  prefillTitle: "Sélectionner l'hôte",  prefillDesc: 'Choisissez qui vous rendez visite' },
    DETAILS:    { headerTitle: 'Vos informations',      prefillTitle: 'Vos informations',      prefillDesc: 'Fournissez vos coordonnées' },
    DOCUMENT:   { headerTitle: 'Signer les documents', prefillTitle: 'Signer les documents', prefillDesc: 'Lire et signer les documents requis' },
    ID_CAPTURE: { headerTitle: "Vérifier l'identité",  prefillTitle: "Vérifier l'identité",  prefillDesc: "Scannez votre pièce d'identité" },
    PHOTO:      { headerTitle: 'Prendre un selfie',    prefillTitle: 'Prendre un selfie',    prefillDesc: 'Capturez votre photo' },
    VIDEO:      { headerTitle: 'Regarder une vidéo',   prefillTitle: 'Regarder une vidéo',   prefillDesc: "Voir une vidéo de sécurité ou d'orientation" },
  },
};

// ── Canadian French ───────────────────────────────────────────────────────────
export const FR_CA_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Connexion sans contact | Visitly',
    PRE_ARRIVAL: 'Pré-arrivée | Visitly',
  },
  HOME: {
    TITLE: 'Connexion sans contact',
    SUBTITLE: "Pointez votre caméra vers le code QR affiché sur l'iPad ou l'affiche imprimée pour continuer",
  },
  COMMON: {
    BACK: 'Retour',
    CONTINUE: 'Continuer',
    AGREE_AND_CONTINUE: 'Accepter et continuer',
    DECLINE: 'Refuser',
    CLEAR_SIGNATURE: 'Effacer la signature',
    LOADING: 'Chargement...',
    SEARCHING: 'Recherche...',
    NO: 'NON',
    YES: 'OUI',
    ENABLE_CAMERA: 'Activer la caméra',
    PAGE_NOT_FOUND: '404 - Page non trouvée',
    POWERED_BY: 'Propulsé par Visitly',
    RETAKE: 'Reprendre',
    CAPTURE: 'Capturer',
    RETRY: 'Réessayer',
    POINT_OF_ENTRY: "Point d'entrée",
    PARKING_LOT: 'Parking',
    DESTINATION: 'Bâtiment',
    ERROR_PROCESSING_REQUEST: 'Erreur lors du traitement de la demande. Veuillez réessayer.',
    SESSION_EXPIRED: 'Session expirée. Veuillez rouvrir le lien depuis votre e-mail.',
  },
  FULL_NAME: {
    TITLE: 'Quel est votre nom complet ?',
    SUBTITLE: "Juste pour savoir qui nous rend visite aujourd'hui",
    FORM_LABEL: 'Nom complet',
    PLACEHOLDER: 'ex. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Veuillez choisir votre type de visite.',
    FALLBACK_TITLE: 'Quelle est la raison de votre venue ?',
  },
  SELECT_HOST: {
    TITLE: 'Qui venez-vous visiter ?',
    SUBTITLE: 'Recherchez votre hôte pour le notifier de votre arrivée.',
    PLACEHOLDER: 'Sélectionner un hôte',
    SEARCH_PLACEHOLDER: 'Rechercher un hôte...',
    SEARCH_PLACEHOLDER_ALT: 'Rechercher un hôte par nom...',
    NO_HOSTS_FOUND: 'Aucun hôte trouvé',
    MIN_CHARS_MESSAGE: 'Tapez au moins 3 caractères pour rechercher',
  },
  VISITOR_FORM: {
    TITLE: 'Détails de la visite',
    SUBTITLE: 'Veuillez remplir les informations requises ci-dessous.',
    SELECT_FIELD_PLACEHOLDER: 'Sélectionner {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Veuillez entrer votre nom complet',
    EMAIL_REQUIRED: 'Veuillez entrer une adresse e-mail valide',
    EMAIL_INVALID: 'Veuillez entrer une adresse e-mail valide',
    HOST_REQUIRED: 'Veuillez sélectionner le nom de votre hôte',
    COMPANY_NAME_REQUIRED: 'Veuillez entrer le nom de votre entreprise',
    PHONE_NUMBER_REQUIRED: 'Veuillez entrer votre numéro de téléphone',
    CUSTOM_FIELD_REQUIRED: 'Veuillez entrer {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} est requis',
    FULL_NAME_INVALID: 'Veuillez saisir un nom valide.',
    TOAST_ERROR_PROCESSING: 'Erreur lors du traitement de la demande. Veuillez réessayer.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Veuillez lire et signer le document ci-dessous',
    ERROR_DOCUMENT_EXPIRY: "Erreur lors de la vérification de l'expiration du document",
    ERROR_LOADING_CONTENT: 'Erreur lors du chargement du contenu du document',
    ERROR_FETCHING_DOCUMENT: 'Erreur lors de la récupération du document',
    ERROR_RESTORE_SESSION: "Impossible de restaurer la session de signature. Veuillez recommencer depuis la page d'accueil.",
    BTN_COLLAPSE: 'Réduire',
    BTN_EXPAND: 'Développer',
  },
  PRIVACY_POLICY: {
    TITLE: 'Avant de continuer',
    SUBTITLE: 'Veuillez lire et accepter avant de continuer',
    IMPLICIT_CONSENT: 'En cliquant sur continuer ci-dessous, vous consentez à autoriser {orgName} à stocker et traiter les informations personnelles soumises ci-dessus afin de vous fournir le contenu demandé.',
    EXPLICIT_CONSENT: "J'accepte d'autoriser {orgName} à stocker et traiter mes données personnelles.",
  },
  CAPTURE_PICTURE: {
    TITLE: "Sélectionner une pièce d'identité",
    SUBTITLE: "Veuillez sélectionner un type de pièce d'identité à télécharger",
    ID_TYPES: [
      { label: 'Passeport', value: 'PASSPORT' },
      { label: 'Permis de conduire', value: 'DRIVING_LICENCE' },
      { label: "Carte d'identité nationale", value: 'STATE_ISSUED_ID' },
      { label: "J'ai une autre pièce d'identité", value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Prendre une photo',
    SUBTITLE: 'Centrez votre visage et appuyez sur continuer',
    INSTRUCTIONS: 'Utilisez votre caméra frontale, regardez droit devant vous et centrez votre visage.',
    BTN_CAPTURE: 'Capturer',
    BTN_RETAKE: 'Reprendre',
    BTN_ENABLE_CAMERA: 'Activer la caméra',
    PERMISSION_PROMPT: 'Appuyez sur "Activer la caméra" pour autoriser l\'accès à la caméra.',
    PERMISSION_DENIED: "L'accès à la caméra est bloqué. Activez-le dans les paramètres de votre navigateur, puis appuyez à nouveau sur Activer la caméra.",
    PERMISSION_UNSUPPORTED: 'Aucune caméra trouvée sur cet appareil.',
    TOAST_CAMERA_ERROR: "Erreur d'accès à la caméra. Vérifiez la console !",
    BTN_RETRY_CAMERA: 'Réessayer la caméra',
    CAMERA_NOT_AVAILABLE: "Impossible de démarrer la caméra. Appuyez sur Réessayer ou rechargez la page.",
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Activer la caméra',
    BTN_START_CAMERA: 'Démarrer la caméra',
    BTN_TRY_NOW: 'Essayer maintenant',
    PERMISSION_PROMPT: 'Appuyez sur "Activer la caméra" pour autoriser l\'accès à la caméra.',
    PERMISSION_DENIED: "L'accès à la caméra est bloqué. Activez-le dans les paramètres de votre navigateur, puis appuyez à nouveau sur Activer la caméra.",
    PERMISSION_UNSUPPORTED: 'Aucune caméra trouvée sur cet appareil.',
    ID_DRIVERS_LICENSE: 'Permis de conduire',
    ID_STATE_ISSUED: "Carte d'identité nationale",
    ID_PASSPORT: 'Passeport',
    ID_OTHERS: 'Autres',
    ERROR_CAMERA_IN_USE: "La caméra est utilisée. Fermez l'autre application et réessayez.",
    ERROR_CAMERA_INIT_FAILED: "Échec de l'initialisation de la caméra. Veuillez vérifier les autorisations.",
    ERROR_CAMERA_BUSY_RETRY: "La caméra est toujours utilisée. Fermez l'autre application et réessayez.",
    ERROR_PLEASE_SCAN_DOCS: 'Veuillez scanner le(s) document(s)',
    SUBTITLE: 'Scannez le document sélectionné ci-dessous.',
    LABEL_FRONT_SIDE: 'Face avant',
    LABEL_BACK_SIDE: 'Face arrière',
    INSTRUCTION_POSITION_FRONT: "Positionnez le recto de votre pièce d'identité",
    INSTRUCTION_POSITION_BACK: "Positionnez le verso de votre pièce d'identité",
    MRZ_BAND_HINT: 'Placez les deux lignes du bas de la page photo sous la ligne pointillée',
    MRZ_SCAN_FAILED: 'Nous n\'avons pas pu lire votre passeport. Tenez-le immobile, les deux lignes du bas dans la bande, puis réessayez.',
    MRZ_SCAN_ATTEMPT: 'Lecture {current} sur {total}',
    GLARE_WARNING: 'Reflet sur le document. Inclinez-le légèrement ou éloignez-vous de la lumière.',
    BARCODE_BAND_HINT: 'Tenez la carte immobile, à environ 20 cm, le code-barres dans le cadre',
    BARCODE_SCAN_FAILED: 'Nous n\'avons pas pu lire le code-barres. Tenez le dos de la carte immobile et droit dans le cadre, puis réessayez.',
    TORCH_ON: 'Allumer la lampe',
    TORCH_OFF: 'Éteindre la lampe',
    BARCODE_READING_PHOTO: 'Lecture du code-barres…',
    ID_CHECKING: 'Vérification de votre pièce d\'identité…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Presque terminé !',
    SUBTITLE: 'Regardez la vidéo ci-dessous pour continuer.',
    BTN_CHECK_IN: "S'enregistrer",
    BTN_FINISH: 'Terminer',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Inscription trouvée',
    SUBTITLE: "Veuillez vérifier vos informations pour finaliser l'enregistrement.",
    BTN_NOT_YOU: "Ce n'est pas vous ?",
    MEETING_WITH_LABEL: 'Réunion avec {hostName}',
    ONSITE_TITLE: 'Vous êtes déjà enregistré',
    ONSITE_SUBTITLE: 'Sélectionnez votre session pour vous déconnecter.',
    CHECKIN_AT_LABEL: 'Enregistré à :',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Vous êtes enregistré !',
    WALLET_HINT: "Le pass s'ouvrira automatiquement sur votre appareil.",
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Pré-inscription terminée',
    ARRIVAL_INSTRUCTIONS_TITLE: "Instructions d'arrivée",
    ARRIVAL_INSTRUCTIONS_TEXT: "Un code QR a été envoyé à votre adresse e-mail enregistrée. Utilisez-le pour un enregistrement plus rapide au kiosque à votre arrivée.",
    QR_CAPTION: 'Ce code QR peut être scanné dans le hall pour afficher votre inscription.',
    BTN_SAVE_QR: 'Enregistrer le code QR',
    WALLET_LABEL: 'Portefeuille',
    ACTION_ADD_PASS: 'Ajouter un pass',
    CALENDAR_LABEL: 'Calendrier',
    ACTION_SAVE_EVENT: "Enregistrer l'événement",
    DEFAULT_WELCOME_MESSAGE: 'Votre hôte a été informé et sera bientôt avec vous.',
    PREFILL_SCHEDULED_FOR: 'Tout est prêt pour le {date}.',
    PREFILL_HOST_NOTIFIED: 'Votre hôte a été informé et nous avons hâte de vous accueillir.',
    TOAST_UPDATE_VISIT_ERROR: 'Erreur lors de la mise à jour de la visite. Veuillez réessayer.',
    TOAST_GENERIC_ERROR: 'Une erreur est survenue. Veuillez réessayer !',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Détails de la visite',
    PAGE_SUBTITLE: 'Complétez les étapes ci-dessous pour accélérer votre enregistrement.',
    SECTION_HEADING: 'Complétez votre pré-inscription',
    STEPS_COMPLETED_SUFFIX: 'étapes terminées',
    BTN_START: "C'est parti !",
  },
  SIGN_OUT: {
    EYEBROW: 'Déconnexion',
    TITLE_WITH_NAME: 'Au revoir,',
    TITLE_FALLBACK: 'Au revoir !',
    SUBTITLE_SESSION: 'Confirmez vos informations et déconnectez-vous.',
    STRIP_SUBTITLE: 'Actuellement connecté',
    LABEL_ORG: 'ORGANISATION',
    LABEL_LOCATION: 'EMPLACEMENT',
    LABEL_HOST: 'HÔTE',
    LABEL_SIGNED_IN_AT: 'CONNECTÉ À',
    BTN_SIGN_OUT: 'Se déconnecter',
    BTN_SIGNING_OUT: 'Déconnexion...',
    BTN_START_OVER: 'Recommencer',
    LABEL_NOT_YOU: "Ce n'est pas vous ?",
    TOAST_SUCCESS: 'Vous avez été déconnecté avec succès !',
    TOAST_ERROR_FALLBACK: 'Erreur lors de la déconnexion. Veuillez réessayer.',
    CUSTOM_FIELDS_EYEBROW: 'Presque terminé',
    CUSTOM_FIELDS_TITLE: 'Finaliser la déconnexion',
    CUSTOM_FIELDS_SUBTITLE: 'Veuillez remplir les informations requises.',
    BTN_CONFIRM_SIGN_OUT: 'Confirmer la déconnexion',
    BTN_GO_BACK: 'Retour',
    SUCCESS_TITLE: 'Vous êtes déconnecté',
    SUCCESS_SUBTITLE: 'Merci pour votre visite',
    BTN_DONE: 'Terminé',
    LABEL_VISIT_DURATION: 'Durée de la visite',
    GEOLOCATION_UNAVAILABLE: "L'accès à la localisation est requis pour se déconnecter. Veuillez utiliser un appareil mobile.",
    LOCATION_PERMISSION_REQUIRED: "La permission de localisation est requise pour se déconnecter. Veuillez autoriser l'accès à la localisation et réessayer.",
    MUST_BE_ON_SITE: 'Vous devez être sur site pour vous déconnecter.',
    GUEST_PLACEHOLDER: 'Invité',
    SUCCESS_CLOSING: 'Bonne journée !',
    STATUS_CHECKED_IN: 'Enregistré',
    STATUS_CHECKED_OUT: 'Sorti',
    DETAIL_LABEL_HOST: 'Hôte',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Tout est prêt pour votre visite',
    ALL_SUB_TITLE: 'Votre pré-inscription est complète. Vous pouvez consulter les détails de votre visite ci-dessous.',
    BTN_CHECK_DETAILS: 'Vérifier mes informations',
    BTN_VIEW_CHECKIN: "Voir l'enregistrement sans contact",
    QR_TITLE: 'Connexion sans contact',
    QR_SUBTITLE: "Pointez votre caméra vers le code QR affiché sur l'iPad ou l'affiche imprimée pour continuer",
    ERROR_TITLE: "Quelque chose s'est mal passé !",
    ERROR_SUBTITLE: "Nous sommes désolés, mais une erreur s'est produite. Veuillez réessayer plus tard.",
    DETAIL_LABEL_VISITOR: 'Visiteur',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Hôte',
    DETAIL_HOST_PLACEHOLDER: 'À déterminer',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: "Lien d'inscription pré-arrivée expiré",
    SUBTITLE: "Ce lien n'est plus valide",
    REASONS_HEADING: 'Raisons possibles :',
    REASON_CANCELLED: 'Invitation annulée',
    REASON_CHECKED_IN: 'Déjà enregistré',
    REASON_PAST: "L'heure prévue est passée",
    HELP_PROMPT: "Besoin d'aide ?",
    HELP_TEXT: 'Contactez votre hôte ou la réception',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Lien de pass invalide.',
    PASS_REVOKED_OR_EXPIRED: 'Ce pass a été révoqué ou est expiré.',
    UNABLE_TO_LOAD: "Impossible de charger la page d'enregistrement.",
    PASS_EXPIRED_TITLE: 'Pass expiré',
    CONTACT_HOST_FOR_NEW_PASS: 'Veuillez contacter votre hôte pour un nouveau SmartPass.',
    ERROR_TITLE: "Une erreur s'est produite",
    BTN_TRY_AGAIN: 'Réessayer',
    STATUS_NOT_ON_SITE: 'Pas sur site',
    STATUS_CHECKED_IN: 'Enregistré',
    SELECT_LOCATION_LABEL: 'Sélectionnez votre emplacement :',
    SELECT_LOCATION_PLACEHOLDER: '-- Sélectionner un emplacement --',
    BTN_PROCESSING: 'Traitement...',
    BTN_CHECK_IN: "S'enregistrer",
    BTN_CHECK_OUT: 'Se déconnecter',
    BTN_CLOSE_HINT: 'Vous pouvez fermer cette page.',
    POWERED_BY: 'Propulsé par Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Lien de pass invalide.',
    GOOGLE_WALLET_COMING_SOON: 'La prise en charge de Google Wallet arrive bientôt. Veuillez revenir plus tard.',
    OPEN_ON_MOBILE: 'Veuillez ouvrir ce lien sur votre iPhone ou téléphone Android pour ajouter le pass à votre portefeuille.',
    PREPARING_PASS: 'Préparation de votre SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Lien de pass invalide.',
    LOCATION_REQUIRED: "L'enregistrement SmartPass nécessite l'accès à la localisation. Veuillez ouvrir ce lien sur votre appareil mobile.",
    LOCATION_PERMISSION_REQUIRED: "La permission de localisation est requise pour l'enregistrement SmartPass. Veuillez autoriser l'accès à la localisation et réessayer.",
    LOCATION_UNAVAILABLE: "Impossible de déterminer votre emplacement. Veuillez vous assurer que les services de localisation sont activés et réessayer.",
    PASS_REVOKED_OR_EXPIRED: 'Ce pass a été révoqué ou est expiré.',
    OUTSIDE_GEOFENCE: "Vous êtes en dehors de la zone d'enregistrement. Veuillez vous rapprocher du bâtiment et réessayer.",
    TITLE: 'SmartPass',
    BTN_RETRY: 'Réessayer',
    OPENING_CHECK_IN: "Ouverture de l'enregistrement...",
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'UNE ÉVACUATION EST EN COURS DANS CET EMPLACEMENT',
    BTN_TAP_TO_SIGNIN: 'Appuyez pour vous connecter',
    BTN_SCAN_QR_CODE: 'Scanner le code QR',
    TOAST_INVALID_QR: 'Code QR invalide ou expiré. Veuillez réessayer !',
  },
  FOOTER: { POWERED_BY: 'PROPULSÉ PAR ' },
  HEADER: { STEP: 'ÉTAPE ', OF: ' SUR ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Appuyez pour commencer' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Code QR invalide. Veuillez réessayer !' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Nous utilisons uniquement votre caméra pour lire le code QR du hall et ne stockons aucune vidéo',
    PERMISSION_PROMPT: 'Appuyez sur "Activer la caméra" pour autoriser l\'accès à la caméra.',
    PERMISSION_DENIED: "L'accès à la caméra est bloqué. Activez-le dans les paramètres de votre navigateur, puis appuyez à nouveau sur Activer la caméra.",
    PERMISSION_UNSUPPORTED: 'Aucune caméra trouvée sur cet appareil.',
    CAMERA_NOT_AVAILABLE: "Impossible de démarrer la caméra. Appuyez sur Réessayer ou rechargez la page.",
    ERROR_CAMERA_IN_USE: "La caméra est utilisée. Fermez l'autre application et réessayez.",
    ERROR_CAMERA_INIT_FAILED: "Échec de l'initialisation de la caméra. Veuillez vérifier les autorisations.",
    ERROR_CAMERA_BUSY_RETRY: "La caméra est toujours utilisée. Fermez l'autre application et réessayez.",
  },
  CAMERA_PERMISSION: {
    REQUIRED: "La permission d'accès à la caméra est requise pour scanner les codes QR.",
    ACCESS_DENIED: 'Accès à la caméra refusé ou non disponible',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Êtes-vous sûr de vouloir annuler ? Vous perdrez toute votre progression.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: "Sélectionner l'hôte",  prefillTitle: "Sélectionner l'hôte",  prefillDesc: 'Choisissez qui vous rendez visite' },
    DETAILS:    { headerTitle: 'Vos informations',      prefillTitle: 'Vos informations',      prefillDesc: 'Fournissez vos coordonnées' },
    DOCUMENT:   { headerTitle: 'Signer les documents', prefillTitle: 'Signer les documents', prefillDesc: 'Lire et signer les documents requis' },
    ID_CAPTURE: { headerTitle: "Vérifier l'identité",  prefillTitle: "Vérifier l'identité",  prefillDesc: "Scannez votre pièce d'identité" },
    PHOTO:      { headerTitle: 'Prendre un selfie',    prefillTitle: 'Prendre un selfie',    prefillDesc: 'Capturez votre photo' },
    VIDEO:      { headerTitle: 'Regarder une vidéo',   prefillTitle: 'Regarder une vidéo',   prefillDesc: "Voir une vidéo de sécurité ou d'orientation" },
  },
};

// ── Arabic ─────────────────────────────────────────────────────────────────────
export const AR_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'تسجيل الدخول بدون تلامس | Visitly',
    PRE_ARRIVAL: 'ما قبل الوصول | Visitly',
  },
  HOME: {
    TITLE: 'تسجيل الدخول بدون تلامس',
    SUBTITLE: 'وجّه الكاميرا نحو رمز QR المعروض على الجهاز اللوحي أو الإشارة المطبوعة للمتابعة',
  },
  COMMON: {
    BACK: 'رجوع',
    CONTINUE: 'متابعة',
    AGREE_AND_CONTINUE: 'موافق ومتابعة',
    DECLINE: 'رفض',
    CLEAR_SIGNATURE: 'مسح التوقيع',
    LOADING: 'جاري التحميل...',
    SEARCHING: 'جاري البحث...',
    NO: 'لا',
    YES: 'نعم',
    ENABLE_CAMERA: 'تفعيل الكاميرا',
    PAGE_NOT_FOUND: '404 - الصفحة غير موجودة',
    POWERED_BY: 'مدعوم من Visitly',
    RETAKE: 'إعادة الالتقاط',
    CAPTURE: 'التقاط',
    RETRY: 'إعادة المحاولة',
    POINT_OF_ENTRY: 'نقطة الدخول',
    PARKING_LOT: 'موقف السيارات',
    DESTINATION: 'المبنى',
    ERROR_PROCESSING_REQUEST: 'خطأ في معالجة الطلب. يرجى المحاولة مرة أخرى.',
    SESSION_EXPIRED: 'انتهت الجلسة. يرجى إعادة فتح الرابط من بريدك الإلكتروني.',
  },
  FULL_NAME: {
    TITLE: 'ما اسمك الكامل؟',
    SUBTITLE: 'فقط لنعرف من يزورنا اليوم',
    FORM_LABEL: 'الاسم الكامل',
    PLACEHOLDER: 'مثال: يوسف جوزيه',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'يرجى اختيار نوع زيارتك.',
    FALLBACK_TITLE: 'ما الغرض من زيارتك؟',
  },
  SELECT_HOST: {
    TITLE: 'من تزور؟',
    SUBTITLE: 'ابحث عن مضيفك لإعلامه بوصولك.',
    PLACEHOLDER: 'اختر مضيفًا',
    SEARCH_PLACEHOLDER: 'ابحث عن مضيف...',
    SEARCH_PLACEHOLDER_ALT: 'ابحث عن مضيف بالاسم...',
    NO_HOSTS_FOUND: 'لم يتم العثور على مضيفين',
    MIN_CHARS_MESSAGE: 'اكتب على الأقل 3 أحرف للبحث',
  },
  VISITOR_FORM: {
    TITLE: 'تفاصيل الزيارة',
    SUBTITLE: 'يرجى ملء المعلومات المطلوبة أدناه.',
    SELECT_FIELD_PLACEHOLDER: 'اختر {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'يرجى إدخال اسمك الكامل',
    EMAIL_REQUIRED: 'يرجى إدخال عنوان بريد إلكتروني صحيح',
    EMAIL_INVALID: 'يرجى إدخال عنوان بريد إلكتروني صحيح',
    HOST_REQUIRED: 'يرجى اختيار اسم المضيف',
    COMPANY_NAME_REQUIRED: 'يرجى إدخال اسم شركتك',
    PHONE_NUMBER_REQUIRED: 'يرجى إدخال رقم هاتفك',
    CUSTOM_FIELD_REQUIRED: 'يرجى إدخال {field}',
    FIELD_REQUIRED_TEMPLATE: 'الحقل {field} مطلوب',
    FULL_NAME_INVALID: 'يرجى إدخال اسم صالح.',
    TOAST_ERROR_PROCESSING: 'حدث خطأ أثناء معالجة الطلب. يرجى المحاولة مرة أخرى.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'يرجى مراجعة الوثيقة أدناه والتوقيع عليها',
    ERROR_DOCUMENT_EXPIRY: 'خطأ في التحقق من انتهاء صلاحية الوثيقة',
    ERROR_LOADING_CONTENT: 'خطأ في تحميل محتوى الوثيقة',
    ERROR_FETCHING_DOCUMENT: 'خطأ في جلب الوثيقة',
    ERROR_RESTORE_SESSION: 'تعذّر استعادة جلسة توقيع الوثيقة. يرجى إعادة التشغيل من الصفحة الرئيسية.',
    BTN_COLLAPSE: 'طي',
    BTN_EXPAND: 'توسيع',
  },
  PRIVACY_POLICY: {
    TITLE: 'قبل المتابعة',
    SUBTITLE: 'يرجى المراجعة والموافقة قبل المتابعة',
    IMPLICIT_CONSENT: 'بالنقر على متابعة أدناه، فإنك توافق على السماح لـ {orgName} بتخزين ومعالجة المعلومات الشخصية المقدمة أعلاه لتزويدك بالمحتوى المطلوب.',
    EXPLICIT_CONSENT: 'أوافق على السماح لـ {orgName} بتخزين ومعالجة بياناتي الشخصية.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'اختيار هوية',
    SUBTITLE: 'يرجى اختيار نوع الهوية للتحميل',
    ID_TYPES: [
      { label: 'جواز السفر', value: 'PASSPORT' },
      { label: 'رخصة القيادة', value: 'DRIVING_LICENCE' },
      { label: 'بطاقة هوية حكومية', value: 'STATE_ISSUED_ID' },
      { label: 'لديّ هوية مختلفة', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'التقاط صورة',
    SUBTITLE: 'يرجى توسيط وجهك والضغط على متابعة',
    INSTRUCTIONS: 'استخدم الكاميرا الأمامية، انظر مباشرة للأمام، وضع وجهك في المنتصف.',
    BTN_CAPTURE: 'التقاط',
    BTN_RETAKE: 'إعادة الالتقاط',
    BTN_ENABLE_CAMERA: 'تفعيل الكاميرا',
    PERMISSION_PROMPT: 'اضغط على "تفعيل الكاميرا" للسماح بالوصول إليها.',
    PERMISSION_DENIED: 'الوصول إلى الكاميرا محظور. فعّله من إعدادات المتصفح ثم اضغط تفعيل الكاميرا مرة أخرى.',
    PERMISSION_UNSUPPORTED: 'لم يتم العثور على كاميرا في هذا الجهاز.',
    TOAST_CAMERA_ERROR: 'خطأ في الوصول إلى الكاميرا. تحقق من وحدة التحكم!',
    BTN_RETRY_CAMERA: 'إعادة تشغيل الكاميرا',
    CAMERA_NOT_AVAILABLE: 'تعذر تشغيل الكاميرا. اضغط على إعادة المحاولة أو أعد تحميل الصفحة.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'تفعيل الكاميرا',
    BTN_START_CAMERA: 'تشغيل الكاميرا',
    BTN_TRY_NOW: 'جرّب الآن',
    PERMISSION_PROMPT: 'اضغط على "تفعيل الكاميرا" للسماح بالوصول إليها.',
    PERMISSION_DENIED: 'الوصول إلى الكاميرا محظور. فعّله من إعدادات المتصفح ثم اضغط تفعيل الكاميرا مرة أخرى.',
    PERMISSION_UNSUPPORTED: 'لم يتم العثور على كاميرا في هذا الجهاز.',
    ID_DRIVERS_LICENSE: 'رخصة القيادة',
    ID_STATE_ISSUED: 'بطاقة هوية حكومية',
    ID_PASSPORT: 'جواز السفر',
    ID_OTHERS: 'أخرى',
    ERROR_CAMERA_IN_USE: 'الكاميرا قيد الاستخدام. أغلق التطبيق الآخر وحاول مرة أخرى.',
    ERROR_CAMERA_INIT_FAILED: 'فشل تهيئة الكاميرا. يرجى التحقق من الأذونات.',
    ERROR_CAMERA_BUSY_RETRY: 'الكاميرا لا تزال قيد الاستخدام. أغلق التطبيق الآخر وحاول مرة أخرى.',
    ERROR_PLEASE_SCAN_DOCS: 'يرجى مسح الوثيقة/الوثائق',
    SUBTITLE: 'امسح الوثيقة المحددة أدناه.',
    LABEL_FRONT_SIDE: 'الوجه الأمامي',
    LABEL_BACK_SIDE: 'الوجه الخلفي',
    INSTRUCTION_POSITION_FRONT: 'ضع الجهة الأمامية من هويتك',
    INSTRUCTION_POSITION_BACK: 'ضع الجهة الخلفية من هويتك',
    MRZ_BAND_HINT: 'ضع السطرين السفليين من صفحة الصورة أسفل الخط المتقطع',
    MRZ_SCAN_FAILED: 'تعذّرت قراءة جواز سفرك. أمسكه بثبات مع وضع السطرين السفليين داخل الشريط، ثم حاول مرة أخرى.',
    MRZ_SCAN_ATTEMPT: 'المسح {current} من {total}',
    GLARE_WARNING: 'يوجد وهج على المستند. أمِله قليلاً أو ابتعد عن الضوء.',
    BARCODE_BAND_HINT: 'أمسك البطاقة بثبات على بُعد 20 سم تقريبًا مع وضع الرمز الشريطي داخل الإطار',
    BARCODE_SCAN_FAILED: 'تعذّرت قراءة الرمز الشريطي. أمسك ظهر البطاقة بثبات وبشكل مستوٍ داخل الإطار، ثم حاول مرة أخرى.',
    TORCH_ON: 'تشغيل الضوء',
    TORCH_OFF: 'إيقاف الضوء',
    BARCODE_READING_PHOTO: 'جارٍ قراءة الرمز الشريطي…',
    ID_CHECKING: 'جارٍ التحقق من هويتك…',
  },
  VISITOR_VIDEO: {
    TITLE: 'اقتربت من الانتهاء!',
    SUBTITLE: 'شاهد الفيديو أدناه للمتابعة.',
    BTN_CHECK_IN: 'تسجيل الدخول',
    BTN_FINISH: 'إنهاء',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'تم العثور على التسجيل',
    SUBTITLE: 'يرجى التحقق من بياناتك لإتمام تسجيل الدخول.',
    BTN_NOT_YOU: 'لست أنت؟',
    MEETING_WITH_LABEL: 'اجتماع مع {hostName}',
    ONSITE_TITLE: 'لقد سجّلت الدخول بالفعل',
    ONSITE_SUBTITLE: 'اختر جلستك لتسجيل الخروج.',
    CHECKIN_AT_LABEL: 'تم تسجيل الدخول في:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'تم تسجيل دخولك!',
    WALLET_HINT: 'سيتم فتح التصريح تلقائيًا على جهازك.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'اكتمل التسجيل المسبق',
    ARRIVAL_INSTRUCTIONS_TITLE: 'تعليمات الوصول',
    ARRIVAL_INSTRUCTIONS_TEXT: 'تم إرسال رمز QR إلى بريدك الإلكتروني المسجل. استخدمه لتسجيل دخول أسرع عند الكشك عند وصولك.',
    QR_CAPTION: 'يمكن مسح رمز QR هذا في اللوبي لاسترداد تسجيلك.',
    BTN_SAVE_QR: 'حفظ رمز QR',
    WALLET_LABEL: 'المحفظة',
    ACTION_ADD_PASS: 'إضافة تصريح',
    CALENDAR_LABEL: 'التقويم',
    ACTION_SAVE_EVENT: 'حفظ الحدث',
    DEFAULT_WELCOME_MESSAGE: 'تم إخطار مضيفك وسيكون معك قريبًا.',
    PREFILL_SCHEDULED_FOR: 'كل شيء جاهز ليوم {date}.',
    PREFILL_HOST_NOTIFIED: 'تم إخطار مضيفك ونتطلع إلى الترحيب بك.',
    TOAST_UPDATE_VISIT_ERROR: 'حدث خطأ أثناء تحديث الزيارة. يرجى المحاولة مرة أخرى.',
    TOAST_GENERIC_ERROR: 'لقد واجهنا خطأ. يرجى المحاولة مرة أخرى!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'تفاصيل الزيارة',
    PAGE_SUBTITLE: 'أكمل الخطوات أدناه لتسريع تسجيل دخولك.',
    SECTION_HEADING: 'أكمل تسجيلك المسبق',
    STEPS_COMPLETED_SUFFIX: 'خطوات مكتملة',
    BTN_START: 'هيا نبدأ!',
  },
  SIGN_OUT: {
    EYEBROW: 'تسجيل الخروج',
    TITLE_WITH_NAME: 'وداعًا،',
    TITLE_FALLBACK: 'وداعًا!',
    SUBTITLE_SESSION: 'أكّد بياناتك وسجّل خروجك.',
    STRIP_SUBTITLE: 'مسجّل الدخول حاليًا',
    LABEL_ORG: 'المنظمة',
    LABEL_LOCATION: 'الموقع',
    LABEL_HOST: 'المضيف',
    LABEL_SIGNED_IN_AT: 'وقت الدخول',
    BTN_SIGN_OUT: 'تسجيل الخروج',
    BTN_SIGNING_OUT: 'جاري تسجيل الخروج...',
    BTN_START_OVER: 'البدء من جديد',
    LABEL_NOT_YOU: 'لست أنت؟',
    TOAST_SUCCESS: 'تم تسجيل خروجك بنجاح!',
    TOAST_ERROR_FALLBACK: 'خطأ في تسجيل الخروج. يرجى المحاولة مرة أخرى.',
    CUSTOM_FIELDS_EYEBROW: 'اقتربت من الانتهاء',
    CUSTOM_FIELDS_TITLE: 'إتمام تسجيل الخروج',
    CUSTOM_FIELDS_SUBTITLE: 'يرجى ملء التفاصيل المطلوبة.',
    BTN_CONFIRM_SIGN_OUT: 'تأكيد تسجيل الخروج',
    BTN_GO_BACK: 'رجوع',
    SUCCESS_TITLE: 'لقد سجّلت خروجك',
    SUCCESS_SUBTITLE: 'شكرًا لزيارتك',
    BTN_DONE: 'تم',
    LABEL_VISIT_DURATION: 'مدة الزيارة',
    GEOLOCATION_UNAVAILABLE: 'مطلوب الوصول إلى الموقع لتسجيل الخروج. يرجى استخدام جهاز محمول.',
    LOCATION_PERMISSION_REQUIRED: 'إذن الموقع مطلوب لتسجيل الخروج. يرجى السماح بالوصول إلى الموقع والمحاولة مرة أخرى.',
    MUST_BE_ON_SITE: 'يجب أن تكون في الموقع لتسجيل الخروج.',
    GUEST_PLACEHOLDER: 'زائر',
    SUCCESS_CLOSING: 'أتمنى لك يومًا رائعًا!',
    STATUS_CHECKED_IN: 'تم الدخول',
    STATUS_CHECKED_OUT: 'تم الخروج',
    DETAIL_LABEL_HOST: 'المضيف',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'أنت مستعد لزيارتك',
    ALL_SUB_TITLE: 'تسجيلك المسبق مكتمل. يمكنك مراجعة تفاصيل زيارتك أدناه.',
    BTN_CHECK_DETAILS: 'تحقق من بياناتي',
    BTN_VIEW_CHECKIN: 'عرض تسجيل الدخول بدون تلامس',
    QR_TITLE: 'تسجيل الدخول بدون تلامس',
    QR_SUBTITLE: 'وجّه الكاميرا نحو رمز QR المعروض على الجهاز اللوحي أو الإشارة المطبوعة للمتابعة',
    ERROR_TITLE: 'حدث خطأ ما!',
    ERROR_SUBTITLE: 'نعتذر، لكن حدث خطأ. يرجى المحاولة مرة أخرى لاحقًا.',
    DETAIL_LABEL_VISITOR: 'الزائر',
    DETAIL_LABEL_EMAIL: 'البريد الإلكتروني',
    DETAIL_LABEL_HOST: 'المضيف',
    DETAIL_HOST_PLACEHOLDER: 'سيتم التحديد',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'انتهت صلاحية رابط التسجيل المسبق',
    SUBTITLE: 'هذا الرابط لم يعد صالحًا',
    REASONS_HEADING: 'الأسباب المحتملة:',
    REASON_CANCELLED: 'تم إلغاء الدعوة',
    REASON_CHECKED_IN: 'تم تسجيل الدخول مسبقًا',
    REASON_PAST: 'مرّ الموعد المحدد',
    HELP_PROMPT: 'تحتاج إلى مساعدة؟',
    HELP_TEXT: 'تواصل مع مضيفك أو الاستقبال',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'رابط التصريح غير صالح.',
    PASS_REVOKED_OR_EXPIRED: 'تم إلغاء هذا التصريح أو انتهت صلاحيته.',
    UNABLE_TO_LOAD: 'تعذّر تحميل صفحة تسجيل الدخول.',
    PASS_EXPIRED_TITLE: 'انتهت صلاحية التصريح',
    CONTACT_HOST_FOR_NEW_PASS: 'يرجى التواصل مع مضيفك للحصول على SmartPass جديد.',
    ERROR_TITLE: 'حدث خطأ',
    BTN_TRY_AGAIN: 'حاول مرة أخرى',
    STATUS_NOT_ON_SITE: 'غير متواجد في الموقع',
    STATUS_CHECKED_IN: 'مسجّل الدخول',
    SELECT_LOCATION_LABEL: 'اختر موقعك:',
    SELECT_LOCATION_PLACEHOLDER: '-- اختر موقعًا --',
    BTN_PROCESSING: 'جاري المعالجة...',
    BTN_CHECK_IN: 'تسجيل الدخول',
    BTN_CHECK_OUT: 'تسجيل الخروج',
    BTN_CLOSE_HINT: 'يمكنك إغلاق هذه الصفحة.',
    POWERED_BY: 'مدعوم من Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'رابط التصريح غير صالح.',
    GOOGLE_WALLET_COMING_SOON: 'دعم Google Wallet قادم قريبًا. يرجى المراجعة لاحقًا.',
    OPEN_ON_MOBILE: 'يرجى فتح هذا الرابط على هاتفك iPhone أو Android لإضافة التصريح إلى محفظتك.',
    PREPARING_PASS: 'جاري تحضير SmartPass الخاص بك...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'رابط التصريح غير صالح.',
    LOCATION_REQUIRED: 'يتطلب تسجيل دخول SmartPass الوصول إلى الموقع. يرجى فتح هذا الرابط على جهازك المحمول.',
    LOCATION_PERMISSION_REQUIRED: 'إذن الموقع مطلوب لتسجيل دخول SmartPass. يرجى السماح بالوصول إلى الموقع والمحاولة مرة أخرى.',
    LOCATION_UNAVAILABLE: 'تعذّر تحديد موقعك. يرجى التأكد من تفعيل خدمات الموقع والمحاولة مرة أخرى.',
    PASS_REVOKED_OR_EXPIRED: 'تم إلغاء هذا التصريح أو انتهت صلاحيته.',
    OUTSIDE_GEOFENCE: 'أنت خارج منطقة تسجيل الدخول. يرجى الاقتراب من المبنى والمحاولة مرة أخرى.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'إعادة المحاولة',
    OPENING_CHECK_IN: 'جاري فتح تسجيل الدخول...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'الإخلاء جارٍ في هذا الموقع',
    BTN_TAP_TO_SIGNIN: 'اضغط لتسجيل الدخول',
    BTN_SCAN_QR_CODE: 'مسح رمز QR',
    TOAST_INVALID_QR: 'رمز QR غير صالح أو منتهي الصلاحية. يرجى المحاولة مرة أخرى!',
  },
  FOOTER: { POWERED_BY: 'مدعوم من ' },
  HEADER: { STEP: 'الخطوة ', OF: ' من ' },
  SPLASH_SCREEN: { TAP_TO_START: 'اضغط للبدء' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'رمز QR غير صالح. يرجى المحاولة مرة أخرى!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'نستخدم الكاميرا فقط لقراءة رمز QR في اللوبي ولا نحتفظ بأي مقاطع فيديو',
    PERMISSION_PROMPT: 'اضغط على "تفعيل الكاميرا" للسماح بالوصول إليها.',
    PERMISSION_DENIED: 'الوصول إلى الكاميرا محظور. فعّله من إعدادات المتصفح ثم اضغط تفعيل الكاميرا مرة أخرى.',
    PERMISSION_UNSUPPORTED: 'لم يتم العثور على كاميرا في هذا الجهاز.',
    CAMERA_NOT_AVAILABLE: 'تعذر تشغيل الكاميرا. اضغط على إعادة المحاولة أو أعد تحميل الصفحة.',
    ERROR_CAMERA_IN_USE: 'الكاميرا قيد الاستخدام. أغلق التطبيق الآخر وحاول مرة أخرى.',
    ERROR_CAMERA_INIT_FAILED: 'فشل تهيئة الكاميرا. يرجى التحقق من الأذونات.',
    ERROR_CAMERA_BUSY_RETRY: 'الكاميرا لا تزال قيد الاستخدام. أغلق التطبيق الآخر وحاول مرة أخرى.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'إذن الكاميرا مطلوب لمسح رموز QR.',
    ACCESS_DENIED: 'تم رفض الوصول إلى الكاميرا أو غير متاحة',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'هل أنت متأكد أنك تريد الإلغاء؟ ستفقد كل تقدمك.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'اختيار المضيف',      prefillTitle: 'اختيار المضيف',      prefillDesc: 'اختر من تزور' },
    DETAILS:    { headerTitle: 'بياناتك',             prefillTitle: 'بياناتك',             prefillDesc: 'قدّم معلومات الاتصال الخاصة بك' },
    DOCUMENT:   { headerTitle: 'توقيع الوثائق',      prefillTitle: 'توقيع الوثائق',      prefillDesc: 'راجع الوثائق المطلوبة ووقّعها' },
    ID_CAPTURE: { headerTitle: 'التحقق من الهوية',   prefillTitle: 'التحقق من الهوية',   prefillDesc: 'امسح هويتك' },
    PHOTO:      { headerTitle: 'التقاط صورة شخصية',  prefillTitle: 'التقاط صورة شخصية',  prefillDesc: 'التقط صورتك' },
    VIDEO:      { headerTitle: 'مشاهدة فيديو',       prefillTitle: 'مشاهدة فيديو',       prefillDesc: 'شاهد فيديو أمان أو توجيه' },
  },
};

// ── Spanish ────────────────────────────────────────────────────────────────────
export const ES_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Registro sin contacto | Visitly',
    PRE_ARRIVAL: 'Pre-llegada | Visitly',
  },
  HOME: {
    TITLE: 'Registro sin contacto',
    SUBTITLE: 'Apunta tu cámara al código QR que se muestra en el iPad o en el cartel impreso para continuar',
  },
  COMMON: {
    BACK: 'Atrás',
    CONTINUE: 'Continuar',
    AGREE_AND_CONTINUE: 'Aceptar y continuar',
    DECLINE: 'Rechazar',
    CLEAR_SIGNATURE: 'Borrar firma',
    LOADING: 'Cargando...',
    SEARCHING: 'Buscando...',
    NO: 'NO',
    YES: 'SÍ',
    ENABLE_CAMERA: 'Activar cámara',
    PAGE_NOT_FOUND: '404 - Página no encontrada',
    POWERED_BY: 'Desarrollado por Visitly',
    RETAKE: 'Volver a tomar',
    CAPTURE: 'Capturar',
    RETRY: 'Reintentar',
    POINT_OF_ENTRY: 'Punto de entrada',
    PARKING_LOT: 'Estacionamiento',
    DESTINATION: 'Edificio',
    ERROR_PROCESSING_REQUEST: 'Error al procesar la solicitud. Por favor, inténtalo de nuevo.',
    SESSION_EXPIRED: 'Sesión expirada. Por favor, vuelve a abrir el enlace desde tu correo electrónico.',
  },
  FULL_NAME: {
    TITLE: 'Cuál es tu nombre completo?',
    SUBTITLE: 'Para saber quién nos visita hoy',
    FORM_LABEL: 'Nombre completo',
    PLACEHOLDER: 'ej. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Por favor, elige tu tipo de visita.',
    FALLBACK_TITLE: '¿Cuál es el motivo de tu visita?',
  },
  SELECT_HOST: {
    TITLE: '¿A quién vas a visitar?',
    SUBTITLE: 'Busca a tu anfitrión para notificarle de tu llegada.',
    PLACEHOLDER: 'Seleccionar anfitrión',
    SEARCH_PLACEHOLDER: 'Buscar anfitrión...',
    SEARCH_PLACEHOLDER_ALT: 'Buscar anfitrión por nombre...',
    NO_HOSTS_FOUND: 'No se encontraron anfitriones',
    MIN_CHARS_MESSAGE: 'Escribe al menos 3 caracteres para buscar',
  },
  VISITOR_FORM: {
    TITLE: 'Detalles de la visita',
    SUBTITLE: 'Por favor, completa la información requerida a continuación.',
    SELECT_FIELD_PLACEHOLDER: 'Seleccionar {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Por favor, ingresa tu nombre completo',
    EMAIL_REQUIRED: 'Por favor, ingresa una dirección de correo electrónico válida',
    EMAIL_INVALID: 'Por favor, ingresa una dirección de correo electrónico válida',
    HOST_REQUIRED: 'Por favor, selecciona el nombre de tu anfitrión',
    COMPANY_NAME_REQUIRED: 'Por favor, ingresa el nombre de tu empresa',
    PHONE_NUMBER_REQUIRED: 'Por favor, ingresa tu número de teléfono',
    CUSTOM_FIELD_REQUIRED: 'Por favor, ingresa {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} es obligatorio',
    FULL_NAME_INVALID: 'Por favor, ingresa un nombre válido.',
    TOAST_ERROR_PROCESSING: 'Error al procesar la solicitud. Por favor, inténtelo de nuevo.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Por favor, revisa y firma el documento a continuación',
    ERROR_DOCUMENT_EXPIRY: 'Error al verificar la expiración del documento',
    ERROR_LOADING_CONTENT: 'Error al cargar el contenido del documento',
    ERROR_FETCHING_DOCUMENT: 'Error al obtener el documento',
    ERROR_RESTORE_SESSION: 'No se pudo restaurar la sesión de firma del documento. Por favor, reinicia desde la página de inicio.',
    BTN_COLLAPSE: 'Contraer',
    BTN_EXPAND: 'Expandir',
  },
  PRIVACY_POLICY: {
    TITLE: 'Antes de continuar',
    SUBTITLE: 'Por favor, revisa y acepta antes de continuar',
    IMPLICIT_CONSENT: 'Al hacer clic en continuar a continuación, consientes que {orgName} almacene y procese la información personal enviada anteriormente para proporcionarte el contenido solicitado.',
    EXPLICIT_CONSENT: 'Acepto permitir que {orgName} almacene y procese mis datos personales.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Seleccionar identificación',
    SUBTITLE: 'Por favor, selecciona un tipo de identificación para cargar',
    ID_TYPES: [
      { label: 'Pasaporte', value: 'PASSPORT' },
      { label: 'Licencia de conducir', value: 'DRIVING_LICENCE' },
      { label: 'Identificación estatal', value: 'STATE_ISSUED_ID' },
      { label: 'Tengo una identificación diferente', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Tomar foto',
    SUBTITLE: 'Por favor, centra tu cara y presiona continuar',
    INSTRUCTIONS: 'Usa tu cámara frontal, mira directamente al frente y centra tu cara.',
    BTN_CAPTURE: 'Capturar',
    BTN_RETAKE: 'Volver a tomar',
    BTN_ENABLE_CAMERA: 'Activar cámara',
    PERMISSION_PROMPT: 'Toca "Activar cámara" para conceder acceso a la cámara.',
    PERMISSION_DENIED: 'El acceso a la cámara está bloqueado. Actívalo en la configuración de tu navegador y vuelve a tocar Activar cámara.',
    PERMISSION_UNSUPPORTED: 'No se encontró ninguna cámara en este dispositivo.',
    TOAST_CAMERA_ERROR: 'Error de acceso a la cámara. ¡Revisa la consola!',
    BTN_RETRY_CAMERA: 'Reintentar cámara',
    CAMERA_NOT_AVAILABLE: 'La cámara no pudo iniciarse. Toca Reintentar o recarga la página.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Activar cámara',
    BTN_START_CAMERA: 'Iniciar cámara',
    BTN_TRY_NOW: 'Intentar ahora',
    PERMISSION_PROMPT: 'Toca "Activar cámara" para conceder acceso a la cámara.',
    PERMISSION_DENIED: 'El acceso a la cámara está bloqueado. Actívalo en la configuración de tu navegador y vuelve a tocar Activar cámara.',
    PERMISSION_UNSUPPORTED: 'No se encontró ninguna cámara en este dispositivo.',
    ID_DRIVERS_LICENSE: 'Licencia de conducir',
    ID_STATE_ISSUED: 'Identificación estatal',
    ID_PASSPORT: 'Pasaporte',
    ID_OTHERS: 'Otros',
    ERROR_CAMERA_IN_USE: 'La cámara está en uso. Cierra la otra aplicación e inténtalo de nuevo.',
    ERROR_CAMERA_INIT_FAILED: 'Error al inicializar la cámara. Por favor, verifica los permisos.',
    ERROR_CAMERA_BUSY_RETRY: 'La cámara sigue en uso. Cierra la otra aplicación e inténtalo de nuevo.',
    ERROR_PLEASE_SCAN_DOCS: 'Por favor, escanea el/los documento(s)',
    SUBTITLE: 'Escanea el documento seleccionado a continuación.',
    LABEL_FRONT_SIDE: 'Lado frontal',
    LABEL_BACK_SIDE: 'Lado trasero',
    INSTRUCTION_POSITION_FRONT: 'Coloca el frente de tu identificación',
    INSTRUCTION_POSITION_BACK: 'Coloca el reverso de tu identificación',
    MRZ_BAND_HINT: 'Coloca las dos líneas inferiores de la página de la foto debajo de la línea discontinua',
    MRZ_SCAN_FAILED: 'No pudimos leer tu pasaporte. Mantenlo quieto con las dos líneas inferiores dentro de la franja e inténtalo de nuevo.',
    MRZ_SCAN_ATTEMPT: 'Escaneo {current} de {total}',
    GLARE_WARNING: 'Hay reflejos en el documento. Inclínalo un poco o aléjate de la luz.',
    BARCODE_BAND_HINT: 'Mantén la tarjeta quieta, a unos 20 cm, con el código de barras dentro del marco',
    BARCODE_SCAN_FAILED: 'No pudimos leer el código de barras. Mantén el reverso de la tarjeta quieto y recto dentro del marco e inténtalo de nuevo.',
    TORCH_ON: 'Encender la luz',
    TORCH_OFF: 'Apagar la luz',
    BARCODE_READING_PHOTO: 'Leyendo el código de barras…',
    ID_CHECKING: 'Verificando tu identificación…',
  },
  VISITOR_VIDEO: {
    TITLE: '¡Casi listo!',
    SUBTITLE: 'Mira el video a continuación para continuar.',
    BTN_CHECK_IN: 'Registrarse',
    BTN_FINISH: 'Finalizar',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registro encontrado',
    SUBTITLE: 'Por favor, verifica tus datos para completar el registro.',
    BTN_NOT_YOU: '¿No eres tú?',
    MEETING_WITH_LABEL: 'Reunión con {hostName}',
    ONSITE_TITLE: 'Ya estás registrado',
    ONSITE_SUBTITLE: 'Selecciona tu sesión para cerrar sesión.',
    CHECKIN_AT_LABEL: 'Registrado a las:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: '¡Estás registrado!',
    WALLET_HINT: 'El pase se abrirá automáticamente en tu dispositivo.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Registro previo completado',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Instrucciones de llegada',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Se ha enviado un código QR a tu correo electrónico registrado. Úsalo para un registro más rápido en el quiosco cuando llegues.',
    QR_CAPTION: 'Este código QR puede escanearse en el vestíbulo para obtener tu registro.',
    BTN_SAVE_QR: 'Guardar código QR',
    WALLET_LABEL: 'Cartera',
    ACTION_ADD_PASS: 'Agregar pase',
    CALENDAR_LABEL: 'Calendario',
    ACTION_SAVE_EVENT: 'Guardar evento',
    DEFAULT_WELCOME_MESSAGE: 'Su anfitrión ha sido notificado y estará con usted en breve.',
    PREFILL_SCHEDULED_FOR: 'Todo está listo para el {date}.',
    PREFILL_HOST_NOTIFIED: 'Su anfitrión ha sido notificado y esperamos darle la bienvenida.',
    TOAST_UPDATE_VISIT_ERROR: 'Error al actualizar la visita. Por favor, inténtelo de nuevo.',
    TOAST_GENERIC_ERROR: 'Hemos encontrado un error. ¡Por favor, inténtelo de nuevo!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Detalles de la visita',
    PAGE_SUBTITLE: 'Completa los pasos a continuación para agilizar tu registro.',
    SECTION_HEADING: 'Completa tu registro previo',
    STEPS_COMPLETED_SUFFIX: 'pasos completados',
    BTN_START: '¡Comencemos!',
  },
  SIGN_OUT: {
    EYEBROW: 'Cerrar sesión',
    TITLE_WITH_NAME: 'Adiós,',
    TITLE_FALLBACK: '¡Adiós!',
    SUBTITLE_SESSION: 'Confirma tus datos y cierra sesión.',
    STRIP_SUBTITLE: 'Actualmente con sesión iniciada',
    LABEL_ORG: 'ORGANIZACIÓN',
    LABEL_LOCATION: 'UBICACIÓN',
    LABEL_HOST: 'ANFITRIÓN',
    LABEL_SIGNED_IN_AT: 'INGRESADO',
    BTN_SIGN_OUT: 'Cerrar sesión',
    BTN_SIGNING_OUT: 'Cerrando sesión...',
    BTN_START_OVER: 'Empezar de nuevo',
    LABEL_NOT_YOU: '¿No eres tú?',
    TOAST_SUCCESS: '¡Has cerrado sesión correctamente!',
    TOAST_ERROR_FALLBACK: 'Error al cerrar sesión. Por favor, inténtalo de nuevo.',
    CUSTOM_FIELDS_EYEBROW: 'Casi listo',
    CUSTOM_FIELDS_TITLE: 'Completar cierre de sesión',
    CUSTOM_FIELDS_SUBTITLE: 'Por favor, completa los detalles requeridos.',
    BTN_CONFIRM_SIGN_OUT: 'Confirmar cierre de sesión',
    BTN_GO_BACK: 'Regresar',
    SUCCESS_TITLE: 'Has cerrado sesión',
    SUCCESS_SUBTITLE: 'Gracias por tu visita',
    BTN_DONE: 'Listo',
    LABEL_VISIT_DURATION: 'Duración de la visita',
    GEOLOCATION_UNAVAILABLE: 'Se requiere acceso a la ubicación para cerrar sesión. Por favor, usa un dispositivo móvil.',
    LOCATION_PERMISSION_REQUIRED: 'Se requiere permiso de ubicación para cerrar sesión. Por favor, permite el acceso a la ubicación e inténtalo de nuevo.',
    MUST_BE_ON_SITE: 'Debes estar en el sitio para cerrar sesión.',
    GUEST_PLACEHOLDER: 'Invitado',
    SUCCESS_CLOSING: '¡Que tengas un buen día!',
    STATUS_CHECKED_IN: 'Registrado',
    STATUS_CHECKED_OUT: 'Salida registrada',
    DETAIL_LABEL_HOST: 'Anfitrión',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Todo listo para tu visita',
    ALL_SUB_TITLE: 'Tu registro previo está completo. Puedes revisar los detalles de tu visita a continuación.',
    BTN_CHECK_DETAILS: 'Verificar mis datos',
    BTN_VIEW_CHECKIN: 'Ver registro sin contacto',
    QR_TITLE: 'Registro sin contacto',
    QR_SUBTITLE: 'Apunta tu cámara al código QR que se muestra en el iPad o en el cartel impreso para continuar',
    ERROR_TITLE: '¡Algo salió mal!',
    ERROR_SUBTITLE: 'Lo sentimos, ocurrió un error. Por favor, inténtalo de nuevo más tarde.',
    DETAIL_LABEL_VISITOR: 'Visitante',
    DETAIL_LABEL_EMAIL: 'Correo electrónico',
    DETAIL_LABEL_HOST: 'Anfitrión',
    DETAIL_HOST_PLACEHOLDER: 'Por definir',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Enlace de registro previo expirado',
    SUBTITLE: 'Este enlace ya no es válido',
    REASONS_HEADING: 'Posibles razones:',
    REASON_CANCELLED: 'Invitación cancelada',
    REASON_CHECKED_IN: 'Ya registrado',
    REASON_PAST: 'El horario programado ha pasado',
    HELP_PROMPT: '¿Necesitas ayuda?',
    HELP_TEXT: 'Contacta a tu anfitrión o a la recepción',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Enlace de pase inválido.',
    PASS_REVOKED_OR_EXPIRED: 'Este pase ha sido revocado o ha expirado.',
    UNABLE_TO_LOAD: 'No se puede cargar la página de registro.',
    PASS_EXPIRED_TITLE: 'Pase expirado',
    CONTACT_HOST_FOR_NEW_PASS: 'Por favor, contacta a tu anfitrión para obtener un nuevo SmartPass.',
    ERROR_TITLE: 'Algo salió mal',
    BTN_TRY_AGAIN: 'Intentar de nuevo',
    STATUS_NOT_ON_SITE: 'No en el sitio',
    STATUS_CHECKED_IN: 'Registrado',
    SELECT_LOCATION_LABEL: 'Selecciona tu ubicación:',
    SELECT_LOCATION_PLACEHOLDER: '-- Seleccionar ubicación --',
    BTN_PROCESSING: 'Procesando...',
    BTN_CHECK_IN: 'Registrarse',
    BTN_CHECK_OUT: 'Cerrar sesión',
    BTN_CLOSE_HINT: 'Puedes cerrar esta página.',
    POWERED_BY: 'Desarrollado por Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Enlace de pase inválido.',
    GOOGLE_WALLET_COMING_SOON: 'La compatibilidad con Google Wallet llegará pronto. Por favor, vuelve más tarde.',
    OPEN_ON_MOBILE: 'Por favor, abre este enlace en tu iPhone o teléfono Android para agregar el pase a tu cartera.',
    PREPARING_PASS: 'Preparando tu SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Enlace de pase inválido.',
    LOCATION_REQUIRED: 'El registro con SmartPass requiere acceso a la ubicación. Por favor, abre este enlace en tu dispositivo móvil.',
    LOCATION_PERMISSION_REQUIRED: 'Se requiere permiso de ubicación para el registro con SmartPass. Por favor, permite el acceso a la ubicación e inténtalo de nuevo.',
    LOCATION_UNAVAILABLE: 'No se puede determinar tu ubicación. Por favor, asegúrate de que los servicios de ubicación estén activados e inténtalo de nuevo.',
    PASS_REVOKED_OR_EXPIRED: 'Este pase ha sido revocado o ha expirado.',
    OUTSIDE_GEOFENCE: 'Estás fuera del área de registro. Por favor, acércate al edificio e inténtalo de nuevo.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Reintentar',
    OPENING_CHECK_IN: 'Abriendo registro...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'HAY UNA EVACUACIÓN EN PROGRESO EN ESTA UBICACIÓN',
    BTN_TAP_TO_SIGNIN: 'Toca para registrarte',
    BTN_SCAN_QR_CODE: 'Escanear código QR',
    TOAST_INVALID_QR: 'Código QR inválido o caducado. ¡Por favor, inténtelo de nuevo!',
  },
  FOOTER: { POWERED_BY: 'DESARROLLADO POR ' },
  HEADER: { STEP: 'PASO ', OF: ' DE ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Toca para comenzar' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Código QR inválido. ¡Por favor, inténtalo de nuevo!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Solo usamos tu cámara para leer el código QR del vestíbulo y no almacenamos ningún video',
    PERMISSION_PROMPT: 'Toca "Activar cámara" para conceder acceso a la cámara.',
    PERMISSION_DENIED: 'El acceso a la cámara está bloqueado. Actívalo en la configuración de tu navegador y vuelve a tocar Activar cámara.',
    PERMISSION_UNSUPPORTED: 'No se encontró ninguna cámara en este dispositivo.',
    CAMERA_NOT_AVAILABLE: 'La cámara no pudo iniciarse. Toca Reintentar o recarga la página.',
    ERROR_CAMERA_IN_USE: 'La cámara está en uso. Cierra la otra aplicación e inténtalo de nuevo.',
    ERROR_CAMERA_INIT_FAILED: 'Error al inicializar la cámara. Por favor, verifica los permisos.',
    ERROR_CAMERA_BUSY_RETRY: 'La cámara sigue en uso. Cierra la otra aplicación e inténtalo de nuevo.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Se requiere permiso de cámara para escanear códigos QR.',
    ACCESS_DENIED: 'Acceso a la cámara denegado o no disponible',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: '¿Estás seguro de que deseas cancelar? Perderás todo tu progreso.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Seleccionar anfitrión', prefillTitle: 'Seleccionar anfitrión', prefillDesc: 'Elige a quién vas a visitar' },
    DETAILS:    { headerTitle: 'Tus datos',             prefillTitle: 'Tus datos',             prefillDesc: 'Proporciona tu información de contacto' },
    DOCUMENT:   { headerTitle: 'Firmar documentos',    prefillTitle: 'Firmar documentos',    prefillDesc: 'Revisa y firma los documentos requeridos' },
    ID_CAPTURE: { headerTitle: 'Verificar ID',         prefillTitle: 'Verificar ID',         prefillDesc: 'Escanea tu identificación' },
    PHOTO:      { headerTitle: 'Tomar selfie',         prefillTitle: 'Tomar selfie',         prefillDesc: 'Captura tu foto' },
    VIDEO:      { headerTitle: 'Ver video',            prefillTitle: 'Ver video',            prefillDesc: 'Mira un video de seguridad o bienvenida' },
  },
};

// ── German ─────────────────────────────────────────────────────────────────────
export const DE_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Kontaktloser Check-in | Visitly',
    PRE_ARRIVAL: 'Voranmeldung | Visitly',
  },
  HOME: {
    TITLE: 'Kontaktloser Check-in',
    SUBTITLE: 'Richten Sie Ihre Kamera auf den QR-Code auf dem iPad oder dem ausgedruckten Schild, um fortzufahren',
  },
  COMMON: {
    BACK: 'Zurück',
    CONTINUE: 'Weiter',
    AGREE_AND_CONTINUE: 'Zustimmen und weiter',
    DECLINE: 'Ablehnen',
    CLEAR_SIGNATURE: 'Unterschrift löschen',
    LOADING: 'Wird geladen...',
    SEARCHING: 'Suche läuft...',
    NO: 'NEIN',
    YES: 'JA',
    ENABLE_CAMERA: 'Kamera aktivieren',
    PAGE_NOT_FOUND: '404 - Seite nicht gefunden',
    POWERED_BY: 'Bereitgestellt von Visitly',
    RETAKE: 'Neu aufnehmen',
    CAPTURE: 'Aufnehmen',
    RETRY: 'Erneut versuchen',
    POINT_OF_ENTRY: 'Eintrittspunkt',
    PARKING_LOT: 'Parkplatz',
    DESTINATION: 'Gebäude',
    ERROR_PROCESSING_REQUEST: 'Fehler bei der Verarbeitung der Anfrage. Bitte versuchen Sie es erneut.',
    SESSION_EXPIRED: 'Sitzung abgelaufen. Bitte öffnen Sie den Link aus Ihrer E-Mail erneut.',
  },
  FULL_NAME: {
    TITLE: 'Wie heißen Sie?',
    SUBTITLE: 'Damit wir wissen, wer uns heute besucht',
    FORM_LABEL: 'Vollständiger Name',
    PLACEHOLDER: 'z.B. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Bitte wählen Sie Ihre Besuchsart.',
    FALLBACK_TITLE: 'Was ist der Grund Ihres Besuchs?',
  },
  SELECT_HOST: {
    TITLE: 'Wen besuchen Sie?',
    SUBTITLE: 'Suchen Sie Ihren Gastgeber, um ihn über Ihre Ankunft zu informieren.',
    PLACEHOLDER: 'Gastgeber auswählen',
    SEARCH_PLACEHOLDER: 'Gastgeber suchen...',
    SEARCH_PLACEHOLDER_ALT: 'Gastgeber nach Name suchen...',
    NO_HOSTS_FOUND: 'Keine Gastgeber gefunden',
    MIN_CHARS_MESSAGE: 'Geben Sie mindestens 3 Zeichen ein, um zu suchen',
  },
  VISITOR_FORM: {
    TITLE: 'Besuchsdetails',
    SUBTITLE: 'Bitte füllen Sie die erforderlichen Informationen unten aus.',
    SELECT_FIELD_PLACEHOLDER: '{field} auswählen',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Bitte geben Sie Ihren vollständigen Namen ein',
    EMAIL_REQUIRED: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    EMAIL_INVALID: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    HOST_REQUIRED: 'Bitte wählen Sie Ihren Gastgeber aus',
    COMPANY_NAME_REQUIRED: 'Bitte geben Sie Ihren Firmennamen ein',
    PHONE_NUMBER_REQUIRED: 'Bitte geben Sie Ihre Telefonnummer ein',
    CUSTOM_FIELD_REQUIRED: 'Bitte geben Sie {field} ein',
    FIELD_REQUIRED_TEMPLATE: '{field} ist erforderlich',
    FULL_NAME_INVALID: 'Bitte geben Sie einen gültigen Namen ein.',
    TOAST_ERROR_PROCESSING: 'Fehler bei der Verarbeitung der Anfrage. Bitte erneut versuchen.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Bitte überprüfen und unterschreiben Sie das Dokument unten',
    ERROR_DOCUMENT_EXPIRY: 'Fehler beim Überprüfen des Dokumentablaufs',
    ERROR_LOADING_CONTENT: 'Fehler beim Laden des Dokumentinhalts',
    ERROR_FETCHING_DOCUMENT: 'Fehler beim Abrufen des Dokuments',
    ERROR_RESTORE_SESSION: 'Die Dokumentunterzeichnungssitzung konnte nicht wiederhergestellt werden. Bitte starten Sie von der Startseite neu.',
    BTN_COLLAPSE: 'Einklappen',
    BTN_EXPAND: 'Ausklappen',
  },
  PRIVACY_POLICY: {
    TITLE: 'Bevor Sie fortfahren',
    SUBTITLE: 'Bitte überprüfen und stimmen Sie zu, bevor Sie fortfahren',
    IMPLICIT_CONSENT: 'Indem Sie unten auf „Weiter" klicken, stimmen Sie zu, dass {orgName} die oben angegebenen persönlichen Daten speichert und verarbeitet, um Ihnen den angeforderten Inhalt bereitzustellen.',
    EXPLICIT_CONSENT: 'Ich stimme zu, dass {orgName} meine persönlichen Daten speichert und verarbeitet.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Ausweis auswählen',
    SUBTITLE: 'Bitte wählen Sie einen Ausweistyp zum Hochladen',
    ID_TYPES: [
      { label: 'Reisepass', value: 'PASSPORT' },
      { label: 'Führerschein', value: 'DRIVING_LICENCE' },
      { label: 'Staatlicher Ausweis', value: 'STATE_ISSUED_ID' },
      { label: 'Ich habe einen anderen Ausweis', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Foto aufnehmen',
    SUBTITLE: 'Bitte zentrieren Sie Ihr Gesicht und klicken Sie auf Weiter',
    INSTRUCTIONS: 'Verwenden Sie Ihre Frontkamera, schauen Sie geradeaus und zentrieren Sie Ihr Gesicht.',
    BTN_CAPTURE: 'Aufnehmen',
    BTN_RETAKE: 'Neu aufnehmen',
    BTN_ENABLE_CAMERA: 'Kamera aktivieren',
    PERMISSION_PROMPT: 'Tippen Sie auf "Kamera aktivieren", um den Kamerazugriff zu gewähren.',
    PERMISSION_DENIED: 'Kamerazugriff ist gesperrt. Aktivieren Sie ihn in den Browsereinstellungen und tippen Sie dann erneut auf Kamera aktivieren.',
    PERMISSION_UNSUPPORTED: 'Keine Kamera auf diesem Gerät gefunden.',
    TOAST_CAMERA_ERROR: 'Fehler beim Kamerazugriff. Konsole prüfen!',
    BTN_RETRY_CAMERA: 'Kamera erneut versuchen',
    CAMERA_NOT_AVAILABLE: 'Kamera konnte nicht gestartet werden. Tippen Sie auf Erneut versuchen oder laden Sie die Seite neu.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Kamera aktivieren',
    BTN_START_CAMERA: 'Kamera starten',
    BTN_TRY_NOW: 'Jetzt versuchen',
    PERMISSION_PROMPT: 'Tippen Sie auf "Kamera aktivieren", um den Kamerazugriff zu gewähren.',
    PERMISSION_DENIED: 'Kamerazugriff ist gesperrt. Aktivieren Sie ihn in den Browsereinstellungen und tippen Sie dann erneut auf Kamera aktivieren.',
    PERMISSION_UNSUPPORTED: 'Keine Kamera auf diesem Gerät gefunden.',
    ID_DRIVERS_LICENSE: 'Führerschein',
    ID_STATE_ISSUED: 'Staatlicher Ausweis',
    ID_PASSPORT: 'Reisepass',
    ID_OTHERS: 'Andere',
    ERROR_CAMERA_IN_USE: 'Kamera wird verwendet. Schließen Sie die andere App und versuchen Sie es erneut.',
    ERROR_CAMERA_INIT_FAILED: 'Kamerainitialisierung fehlgeschlagen. Bitte überprüfen Sie die Berechtigungen.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera wird noch verwendet. Schließen Sie die andere App und versuchen Sie es erneut.',
    ERROR_PLEASE_SCAN_DOCS: 'Bitte scannen Sie das/die Dokument(e)',
    SUBTITLE: 'Scannen Sie das ausgewählte Dokument unten.',
    LABEL_FRONT_SIDE: 'Vorderseite',
    LABEL_BACK_SIDE: 'Rückseite',
    INSTRUCTION_POSITION_FRONT: 'Positionieren Sie die Vorderseite Ihres Ausweises',
    INSTRUCTION_POSITION_BACK: 'Positionieren Sie die Rückseite Ihres Ausweises',
    MRZ_BAND_HINT: 'Richten Sie die zwei unteren Zeilen der Bildseite unter der gestrichelten Linie aus',
    MRZ_SCAN_FAILED: 'Ihr Reisepass konnte nicht gelesen werden. Halten Sie ihn ruhig, mit den zwei unteren Zeilen im Streifen, und versuchen Sie es erneut.',
    MRZ_SCAN_ATTEMPT: 'Scan {current} von {total}',
    GLARE_WARNING: 'Spiegelung auf dem Dokument. Neigen Sie es leicht oder gehen Sie aus dem Licht.',
    BARCODE_BAND_HINT: 'Halten Sie die Karte ruhig, etwa 20 cm entfernt, mit dem Barcode im Rahmen',
    BARCODE_SCAN_FAILED: 'Der Barcode konnte nicht gelesen werden. Halten Sie die Rückseite der Karte ruhig und gerade im Rahmen und versuchen Sie es erneut.',
    TORCH_ON: 'Licht einschalten',
    TORCH_OFF: 'Licht ausschalten',
    BARCODE_READING_PHOTO: 'Barcode wird gelesen…',
    ID_CHECKING: 'Ihr Ausweis wird geprüft…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Fast geschafft!',
    SUBTITLE: 'Schauen Sie sich das Video unten an, um fortzufahren.',
    BTN_CHECK_IN: 'Einchecken',
    BTN_FINISH: 'Fertigstellen',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registrierung gefunden',
    SUBTITLE: 'Bitte überprüfen Sie Ihre Daten, um den Check-in abzuschließen.',
    BTN_NOT_YOU: 'Nicht Sie?',
    MEETING_WITH_LABEL: 'Meeting mit {hostName}',
    ONSITE_TITLE: 'Sie sind bereits eingecheckt',
    ONSITE_SUBTITLE: 'Wählen Sie Ihre Sitzung aus, um sich abzumelden.',
    CHECKIN_AT_LABEL: 'Eingecheckt um:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Sie sind eingecheckt!',
    WALLET_HINT: 'Der Pass wird automatisch auf Ihrem Gerät geöffnet.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Voranmeldung abgeschlossen',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Ankunftsanweisungen',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Ein QR-Code wurde an Ihre registrierte E-Mail gesendet. Verwenden Sie ihn für einen schnelleren Check-in am Kiosk bei Ihrer Ankunft.',
    QR_CAPTION: 'Dieser QR-Code kann in der Lobby gescannt werden, um Ihre Registrierung aufzurufen.',
    BTN_SAVE_QR: 'QR-Code speichern',
    WALLET_LABEL: 'Wallet',
    ACTION_ADD_PASS: 'Pass hinzufügen',
    CALENDAR_LABEL: 'Kalender',
    ACTION_SAVE_EVENT: 'Ereignis speichern',
    DEFAULT_WELCOME_MESSAGE: 'Ihr Gastgeber wurde benachrichtigt und wird in Kürze bei Ihnen sein.',
    PREFILL_SCHEDULED_FOR: 'Alles bereit für den {date}.',
    PREFILL_HOST_NOTIFIED: 'Ihr Gastgeber wurde benachrichtigt und wir freuen uns auf Sie.',
    TOAST_UPDATE_VISIT_ERROR: 'Fehler beim Aktualisieren des Besuchs. Bitte erneut versuchen.',
    TOAST_GENERIC_ERROR: 'Es ist ein Fehler aufgetreten. Bitte erneut versuchen!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Besuchsdetails',
    PAGE_SUBTITLE: 'Schließen Sie die folgenden Schritte ab, um Ihren Check-in zu beschleunigen.',
    SECTION_HEADING: 'Schließen Sie Ihre Voranmeldung ab',
    STEPS_COMPLETED_SUFFIX: 'Schritte abgeschlossen',
    BTN_START: 'Los geht\'s!',
  },
  SIGN_OUT: {
    EYEBROW: 'Abmelden',
    TITLE_WITH_NAME: 'Auf Wiedersehen,',
    TITLE_FALLBACK: 'Auf Wiedersehen!',
    SUBTITLE_SESSION: 'Bestätigen Sie Ihre Daten und melden Sie sich ab.',
    STRIP_SUBTITLE: 'Aktuell angemeldet',
    LABEL_ORG: 'ORGANISATION',
    LABEL_LOCATION: 'STANDORT',
    LABEL_HOST: 'GASTGEBER',
    LABEL_SIGNED_IN_AT: 'ANGEMELDET',
    BTN_SIGN_OUT: 'Abmelden',
    BTN_SIGNING_OUT: 'Abmeldung läuft...',
    BTN_START_OVER: 'Von vorne beginnen',
    LABEL_NOT_YOU: 'Nicht Sie?',
    TOAST_SUCCESS: 'Sie wurden erfolgreich abgemeldet!',
    TOAST_ERROR_FALLBACK: 'Fehler beim Abmelden. Bitte versuchen Sie es erneut.',
    CUSTOM_FIELDS_EYEBROW: 'Fast fertig',
    CUSTOM_FIELDS_TITLE: 'Abmeldung abschließen',
    CUSTOM_FIELDS_SUBTITLE: 'Bitte füllen Sie die erforderlichen Details aus.',
    BTN_CONFIRM_SIGN_OUT: 'Abmeldung bestätigen',
    BTN_GO_BACK: 'Zurück',
    SUCCESS_TITLE: 'Sie sind abgemeldet',
    SUCCESS_SUBTITLE: 'Danke für Ihren Besuch',
    BTN_DONE: 'Fertig',
    LABEL_VISIT_DURATION: 'Besuchsdauer',
    GEOLOCATION_UNAVAILABLE: 'Standortzugriff ist für das Auschecken erforderlich. Bitte verwenden Sie ein mobiles Gerät.',
    LOCATION_PERMISSION_REQUIRED: 'Standortberechtigung ist für das Auschecken erforderlich. Bitte erlauben Sie den Standortzugriff und versuchen Sie es erneut.',
    MUST_BE_ON_SITE: 'Sie müssen sich vor Ort befinden, um auszuchecken.',
    GUEST_PLACEHOLDER: 'Gast',
    SUCCESS_CLOSING: 'Haben Sie einen schönen Tag!',
    STATUS_CHECKED_IN: 'Eingecheckt',
    STATUS_CHECKED_OUT: 'Ausgecheckt',
    DETAIL_LABEL_HOST: 'Gastgeber',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Alles bereit für Ihren Besuch',
    ALL_SUB_TITLE: 'Ihre Voranmeldung ist abgeschlossen. Sie können Ihre Besuchsdetails unten überprüfen.',
    BTN_CHECK_DETAILS: 'Meine Daten prüfen',
    BTN_VIEW_CHECKIN: 'Kontaktlosen Check-in ansehen',
    QR_TITLE: 'Kontaktloser Check-in',
    QR_SUBTITLE: 'Richten Sie Ihre Kamera auf den QR-Code auf dem iPad oder dem ausgedruckten Schild, um fortzufahren',
    ERROR_TITLE: 'Etwas ist schiefgelaufen!',
    ERROR_SUBTITLE: 'Es tut uns leid, aber ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    DETAIL_LABEL_VISITOR: 'Besucher',
    DETAIL_LABEL_EMAIL: 'E-Mail',
    DETAIL_LABEL_HOST: 'Gastgeber',
    DETAIL_HOST_PLACEHOLDER: 'Noch festzulegen',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Voranmeldungslink abgelaufen',
    SUBTITLE: 'Dieser Link ist nicht mehr gültig',
    REASONS_HEADING: 'Mögliche Gründe:',
    REASON_CANCELLED: 'Einladung abgesagt',
    REASON_CHECKED_IN: 'Bereits eingecheckt',
    REASON_PAST: 'Geplante Zeit ist vergangen',
    HELP_PROMPT: 'Brauchen Sie Hilfe?',
    HELP_TEXT: 'Kontaktieren Sie Ihren Gastgeber oder den Empfang',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Ungültiger Pass-Link.',
    PASS_REVOKED_OR_EXPIRED: 'Dieser Pass wurde widerrufen oder ist abgelaufen.',
    UNABLE_TO_LOAD: 'Check-in-Seite konnte nicht geladen werden.',
    PASS_EXPIRED_TITLE: 'Pass abgelaufen',
    CONTACT_HOST_FOR_NEW_PASS: 'Bitte kontaktieren Sie Ihren Gastgeber für einen neuen SmartPass.',
    ERROR_TITLE: 'Etwas ist schiefgelaufen',
    BTN_TRY_AGAIN: 'Erneut versuchen',
    STATUS_NOT_ON_SITE: 'Nicht vor Ort',
    STATUS_CHECKED_IN: 'Eingecheckt',
    SELECT_LOCATION_LABEL: 'Wählen Sie Ihren Standort:',
    SELECT_LOCATION_PLACEHOLDER: '-- Standort auswählen --',
    BTN_PROCESSING: 'Verarbeitung...',
    BTN_CHECK_IN: 'Einchecken',
    BTN_CHECK_OUT: 'Auschecken',
    BTN_CLOSE_HINT: 'Sie können diese Seite schließen.',
    POWERED_BY: 'Bereitgestellt von Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Ungültiger Pass-Link.',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet-Unterstützung kommt bald. Bitte schauen Sie später noch einmal vorbei.',
    OPEN_ON_MOBILE: 'Bitte öffnen Sie diesen Link auf Ihrem iPhone oder Android-Telefon, um den Pass zu Ihrer Wallet hinzuzufügen.',
    PREPARING_PASS: 'Ihr SmartPass wird vorbereitet...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Ungültiger Pass-Link.',
    LOCATION_REQUIRED: 'SmartPass-Check-in erfordert Standortzugriff. Bitte öffnen Sie diesen Link auf Ihrem mobilen Gerät.',
    LOCATION_PERMISSION_REQUIRED: 'Standortberechtigung ist für den SmartPass-Check-in erforderlich. Bitte erlauben Sie den Standortzugriff und versuchen Sie es erneut.',
    LOCATION_UNAVAILABLE: 'Ihr Standort konnte nicht ermittelt werden. Bitte stellen Sie sicher, dass die Standortdienste aktiviert sind, und versuchen Sie es erneut.',
    PASS_REVOKED_OR_EXPIRED: 'Dieser Pass wurde widerrufen oder ist abgelaufen.',
    OUTSIDE_GEOFENCE: 'Sie befinden sich außerhalb des Check-in-Bereichs. Bitte nähern Sie sich dem Gebäude und versuchen Sie es erneut.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Erneut versuchen',
    OPENING_CHECK_IN: 'Check-in wird geöffnet...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'AN DIESEM STANDORT FINDET GERADE EINE EVAKUIERUNG STATT',
    BTN_TAP_TO_SIGNIN: 'Tippen zum Anmelden',
    BTN_SCAN_QR_CODE: 'QR-Code scannen',
    TOAST_INVALID_QR: 'Ungültiger oder abgelaufener QR-Code. Bitte erneut versuchen!',
  },
  FOOTER: { POWERED_BY: 'BEREITGESTELLT VON ' },
  HEADER: { STEP: 'SCHRITT ', OF: ' VON ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Tippen zum Starten' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Ungültiger QR-Code. Bitte versuchen Sie es erneut!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Wir verwenden Ihre Kamera nur zum Lesen des Lobby-QR-Codes und speichern kein Video',
    PERMISSION_PROMPT: 'Tippen Sie auf "Kamera aktivieren", um den Kamerazugriff zu gewähren.',
    PERMISSION_DENIED: 'Kamerazugriff ist gesperrt. Aktivieren Sie ihn in den Browsereinstellungen und tippen Sie dann erneut auf Kamera aktivieren.',
    PERMISSION_UNSUPPORTED: 'Keine Kamera auf diesem Gerät gefunden.',
    CAMERA_NOT_AVAILABLE: 'Kamera konnte nicht gestartet werden. Tippen Sie auf Erneut versuchen oder laden Sie die Seite neu.',
    ERROR_CAMERA_IN_USE: 'Kamera wird verwendet. Schließen Sie die andere App und versuchen Sie es erneut.',
    ERROR_CAMERA_INIT_FAILED: 'Kamerainitialisierung fehlgeschlagen. Bitte überprüfen Sie die Berechtigungen.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera wird noch verwendet. Schließen Sie die andere App und versuchen Sie es erneut.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Kameraberechtigung ist zum Scannen von QR-Codes erforderlich.',
    ACCESS_DENIED: 'Kamerazugriff verweigert oder nicht verfügbar',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Sind Sie sicher, dass Sie abbrechen möchten? Sie verlieren Ihren gesamten Fortschritt.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Gastgeber wählen',        prefillTitle: 'Gastgeber wählen',        prefillDesc: 'Wählen Sie, wen Sie besuchen' },
    DETAILS:    { headerTitle: 'Ihre Daten',              prefillTitle: 'Ihre Daten',              prefillDesc: 'Geben Sie Ihre Kontaktinformationen an' },
    DOCUMENT:   { headerTitle: 'Dokumente unterzeichnen', prefillTitle: 'Dokumente unterzeichnen', prefillDesc: 'Erforderliche Dokumente prüfen und unterzeichnen' },
    ID_CAPTURE: { headerTitle: 'ID verifizieren',        prefillTitle: 'ID verifizieren',        prefillDesc: 'Scannen Sie Ihren Ausweis' },
    PHOTO:      { headerTitle: 'Selfie aufnehmen',       prefillTitle: 'Selfie aufnehmen',       prefillDesc: 'Machen Sie Ihr Foto' },
    VIDEO:      { headerTitle: 'Video ansehen',          prefillTitle: 'Video ansehen',          prefillDesc: 'Sehen Sie sich ein Sicherheits- oder Einführungsvideo an' },
  },
};

// ── Czech ──────────────────────────────────────────────────────────────────────
export const CS_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Bezkontaktní přihlášení | Visitly',
    PRE_ARRIVAL: 'Předregistrace | Visitly',
  },
  HOME: {
    TITLE: 'Bezkontaktní přihlášení',
    SUBTITLE: 'Namiřte fotoaparát na QR kód zobrazený na iPadu nebo vytištěném oznámení a pokračujte',
  },
  COMMON: {
    BACK: 'Zpět',
    CONTINUE: 'Pokračovat',
    AGREE_AND_CONTINUE: 'Souhlasím a pokračuji',
    DECLINE: 'Odmítnout',
    CLEAR_SIGNATURE: 'Vymazat podpis',
    LOADING: 'Načítání...',
    SEARCHING: 'Vyhledávání...',
    NO: 'NE',
    YES: 'ANO',
    ENABLE_CAMERA: 'Povolit fotoaparát',
    PAGE_NOT_FOUND: '404 – Stránka nenalezena',
    POWERED_BY: 'Provozováno službou Visitly',
    RETAKE: 'Znovu vyfotit',
    CAPTURE: 'Vyfotit',
    RETRY: 'Zkusit znovu',
    POINT_OF_ENTRY: 'Vstupní místo',
    PARKING_LOT: 'Parkoviště',
    DESTINATION: 'Budova',
    ERROR_PROCESSING_REQUEST: 'Chyba při zpracování požadavku. Zkuste to prosím znovu.',
    SESSION_EXPIRED: 'Relace vypršela. Otevřete prosím odkaz z e-mailu znovu.',
  },
  FULL_NAME: {
    TITLE: 'Jaké je vaše celé jméno?',
    SUBTITLE: 'Abychom věděli, kdo nás dnes navštívil',
    FORM_LABEL: 'Celé jméno',
    PLACEHOLDER: 'např. Josef Novák',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Vyberte prosím typ návštěvy.',
    FALLBACK_TITLE: 'Co vás sem přivádí?',
  },
  SELECT_HOST: {
    TITLE: 'Koho navštěvujete?',
    SUBTITLE: 'Vyhledejte svého hostitele a upozorněte ho na váš příchod.',
    PLACEHOLDER: 'Vyberte hostitele',
    SEARCH_PLACEHOLDER: 'Hledat hostitele...',
    SEARCH_PLACEHOLDER_ALT: 'Hledat hostitele podle jména...',
    NO_HOSTS_FOUND: 'Žádní hostitelé nenalezeni',
    MIN_CHARS_MESSAGE: 'Pro vyhledávání zadejte alespoň 3 znaky',
  },
  VISITOR_FORM: {
    TITLE: 'Podrobnosti návštěvy',
    SUBTITLE: 'Vyplňte prosím níže požadované informace.',
    SELECT_FIELD_PLACEHOLDER: 'Vyberte {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Zadejte prosím celé jméno',
    EMAIL_REQUIRED: 'Zadejte prosím platnou e-mailovou adresu',
    EMAIL_INVALID: 'Zadejte prosím platnou e-mailovou adresu',
    HOST_REQUIRED: 'Vyberte prosím jméno hostitele',
    COMPANY_NAME_REQUIRED: 'Zadejte prosím název společnosti',
    PHONE_NUMBER_REQUIRED: 'Zadejte prosím telefonní číslo',
    CUSTOM_FIELD_REQUIRED: 'Zadejte prosím {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} je povinné',
    FULL_NAME_INVALID: 'Zadejte prosím platné jméno.',
    TOAST_ERROR_PROCESSING: 'Při zpracování požadavku došlo k chybě. Zkuste to prosím znovu.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Přečtěte si prosím dokument níže a podepište ho',
    ERROR_DOCUMENT_EXPIRY: 'Chyba při ověřování platnosti dokumentu',
    ERROR_LOADING_CONTENT: 'Chyba při načítání obsahu dokumentu',
    ERROR_FETCHING_DOCUMENT: 'Chyba při načítání dokumentu',
    ERROR_RESTORE_SESSION: 'Nelze obnovit relaci pro podepisování dokumentu. Restartujte prosím od úvodní stránky.',
    BTN_COLLAPSE: 'Sbalit',
    BTN_EXPAND: 'Rozbalit',
  },
  PRIVACY_POLICY: {
    TITLE: 'Než budete pokračovat',
    SUBTITLE: 'Přečtěte si a odsouhlaste prosím před pokračováním',
    IMPLICIT_CONSENT: 'Kliknutím na tlačítko Pokračovat níže souhlasíte s tím, aby {orgName} ukládal a zpracovával výše uvedené osobní údaje za účelem poskytnutí požadovaného obsahu.',
    EXPLICIT_CONSENT: 'Souhlasím s tím, aby {orgName} ukládal a zpracovával moje osobní údaje.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Vyberte doklad totožnosti',
    SUBTITLE: 'Vyberte prosím typ dokladu totožnosti k nahrání',
    ID_TYPES: [
      { label: 'Cestovní pas', value: 'PASSPORT' },
      { label: 'Řidičský průkaz', value: 'DRIVING_LICENCE' },
      { label: 'Státem vydaný průkaz totožnosti', value: 'STATE_ISSUED_ID' },
      { label: 'Mám jiný doklad totožnosti', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Vyfotit fotografii',
    SUBTITLE: 'Vycentrujte prosím obličej a klikněte na Pokračovat',
    INSTRUCTIONS: 'Použijte přední fotoaparát, dívejte se přímo dopředu a vycentrujte obličej.',
    BTN_CAPTURE: 'Vyfotit',
    BTN_RETAKE: 'Znovu vyfotit',
    BTN_ENABLE_CAMERA: 'Povolit fotoaparát',
    PERMISSION_PROMPT: 'Klepnutím na „Povolit fotoaparát" udělte přístup ke kameře.',
    PERMISSION_DENIED: 'Přístup ke kameře je zablokován. Povolte ho v nastavení prohlížeče a klepněte znovu na Povolit fotoaparát.',
    PERMISSION_UNSUPPORTED: 'Na tomto zařízení nebyla nalezena žádná kamera.',
    TOAST_CAMERA_ERROR: 'Chyba přístupu ke kameře. Zkontrolujte konzoli!',
    BTN_RETRY_CAMERA: 'Zkusit kameru znovu',
    CAMERA_NOT_AVAILABLE: 'Kameru se nepodařilo spustit. Klepněte na Zkusit znovu nebo znovu načtěte stránku.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Povolit fotoaparát',
    BTN_START_CAMERA: 'Spustit kameru',
    BTN_TRY_NOW: 'Vyzkoušet nyní',
    PERMISSION_PROMPT: 'Klepnutím na „Povolit fotoaparát" udělte přístup ke kameře.',
    PERMISSION_DENIED: 'Přístup ke kameře je zablokován. Povolte ho v nastavení prohlížeče a klepněte znovu na Povolit fotoaparát.',
    PERMISSION_UNSUPPORTED: 'Na tomto zařízení nebyla nalezena žádná kamera.',
    ID_DRIVERS_LICENSE: 'Řidičský průkaz',
    ID_STATE_ISSUED: 'Státem vydaný průkaz totožnosti',
    ID_PASSPORT: 'Cestovní pas',
    ID_OTHERS: 'Jiné',
    ERROR_CAMERA_IN_USE: 'Kamera je používána. Zavřete jinou aplikaci a zkuste to znovu.',
    ERROR_CAMERA_INIT_FAILED: 'Inicializace kamery se nezdařila. Zkontrolujte prosím oprávnění.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera je stále používána. Zavřete jinou aplikaci a zkuste to znovu.',
    ERROR_PLEASE_SCAN_DOCS: 'Naskenujte prosím doklad(y)',
    SUBTITLE: 'Naskenujte vybraný doklad níže.',
    LABEL_FRONT_SIDE: 'Přední strana',
    LABEL_BACK_SIDE: 'Zadní strana',
    INSTRUCTION_POSITION_FRONT: 'Umístěte přední stranu průkazu totožnosti',
    INSTRUCTION_POSITION_BACK: 'Umístěte zadní stranu průkazu totožnosti',
    MRZ_BAND_HINT: 'Umístěte dva spodní řádky stránky s fotografií pod přerušovanou čáru',
    MRZ_SCAN_FAILED: 'Váš pas se nepodařilo přečíst. Držte jej v klidu se dvěma spodními řádky uvnitř pruhu a zkuste to znovu.',
    MRZ_SCAN_ATTEMPT: 'Skenování {current} z {total}',
    GLARE_WARNING: 'Na dokladu je odlesk. Mírně jej nakloňte nebo se vzdalte od světla.',
    BARCODE_BAND_HINT: 'Držte kartu v klidu, asi 20 cm od fotoaparátu, s čárovým kódem v rámečku',
    BARCODE_SCAN_FAILED: 'Čárový kód se nepodařilo přečíst. Držte zadní stranu karty v klidu a rovně uvnitř rámečku a zkuste to znovu.',
    TORCH_ON: 'Zapnout světlo',
    TORCH_OFF: 'Vypnout světlo',
    BARCODE_READING_PHOTO: 'Čtení čárového kódu…',
    ID_CHECKING: 'Kontrola vašeho dokladu…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Téměř hotovo!',
    SUBTITLE: 'Pro pokračování se podívejte na video níže.',
    BTN_CHECK_IN: 'Přihlásit se',
    BTN_FINISH: 'Dokončit',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registrace nalezena',
    SUBTITLE: 'Ověřte prosím své údaje pro dokončení přihlášení.',
    BTN_NOT_YOU: 'To nejste vy?',
    MEETING_WITH_LABEL: 'Schůzka s {hostName}',
    ONSITE_TITLE: 'Již jste přihlášeni',
    ONSITE_SUBTITLE: 'Vyberte svou relaci pro odhlášení.',
    CHECKIN_AT_LABEL: 'Přihlášen/a v:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Jste přihlášeni!',
    WALLET_HINT: 'Průkaz se automaticky otevře na vašem zařízení.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Předregistrace dokončena',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Pokyny pro příchod',
    ARRIVAL_INSTRUCTIONS_TEXT: 'QR kód byl odeslán na vaši registrovanou e-mailovou adresu. Použijte ho pro rychlejší přihlášení na kiosku po příchodu.',
    QR_CAPTION: 'Tento QR kód lze naskenovat v hale pro zobrazení vaší registrace.',
    BTN_SAVE_QR: 'Uložit QR kód',
    WALLET_LABEL: 'Peněženka',
    ACTION_ADD_PASS: 'Přidat průkaz',
    CALENDAR_LABEL: 'Kalendář',
    ACTION_SAVE_EVENT: 'Uložit událost',
    DEFAULT_WELCOME_MESSAGE: 'Váš hostitel byl upozorněn a brzy bude s vámi.',
    PREFILL_SCHEDULED_FOR: 'Vše je připraveno na {date}.',
    PREFILL_HOST_NOTIFIED: 'Váš hostitel byl upozorněn a těšíme se na vás.',
    TOAST_UPDATE_VISIT_ERROR: 'Při aktualizaci návštěvy došlo k chybě. Zkuste to prosím znovu.',
    TOAST_GENERIC_ERROR: 'Došlo k chybě. Zkuste to prosím znovu!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Podrobnosti návštěvy',
    PAGE_SUBTITLE: 'Dokončete níže uvedené kroky a urychlíte tak přihlášení.',
    SECTION_HEADING: 'Dokončete předregistraci',
    STEPS_COMPLETED_SUFFIX: 'kroků dokončeno',
    BTN_START: 'Začínáme!',
  },
  SIGN_OUT: {
    EYEBROW: 'Odhlásit se',
    TITLE_WITH_NAME: 'Na shledanou,',
    TITLE_FALLBACK: 'Na shledanou!',
    SUBTITLE_SESSION: 'Potvrďte své údaje a odhlaste se.',
    STRIP_SUBTITLE: 'Aktuálně přihlášeni',
    LABEL_ORG: 'ORGANIZACE',
    LABEL_LOCATION: 'MÍSTO',
    LABEL_HOST: 'HOSTITEL',
    LABEL_SIGNED_IN_AT: 'PŘIHLÁŠENÍ',
    BTN_SIGN_OUT: 'Odhlásit se',
    BTN_SIGNING_OUT: 'Odhlašování...',
    BTN_START_OVER: 'Začít znovu',
    LABEL_NOT_YOU: 'To nejste vy?',
    TOAST_SUCCESS: 'Byli jste úspěšně odhlášeni!',
    TOAST_ERROR_FALLBACK: 'Chyba při odhlašování. Zkuste to prosím znovu.',
    CUSTOM_FIELDS_EYEBROW: 'Téměř hotovo',
    CUSTOM_FIELDS_TITLE: 'Dokončit odhlášení',
    CUSTOM_FIELDS_SUBTITLE: 'Vyplňte prosím požadované údaje.',
    BTN_CONFIRM_SIGN_OUT: 'Potvrdit odhlášení',
    BTN_GO_BACK: 'Zpět',
    SUCCESS_TITLE: 'Jste odhlášeni',
    SUCCESS_SUBTITLE: 'Děkujeme za návštěvu',
    BTN_DONE: 'Hotovo',
    LABEL_VISIT_DURATION: 'Délka návštěvy',
    GEOLOCATION_UNAVAILABLE: 'Pro odhlášení je vyžadován přístup k poloze. Použijte prosím mobilní zařízení.',
    LOCATION_PERMISSION_REQUIRED: 'Pro odhlášení je vyžadováno oprávnění k poloze. Povolte prosím přístup k poloze a zkuste to znovu.',
    MUST_BE_ON_SITE: 'Pro odhlášení musíte být na místě.',
    GUEST_PLACEHOLDER: 'Host',
    SUCCESS_CLOSING: 'Hezký den!',
    STATUS_CHECKED_IN: 'Přihlášen',
    STATUS_CHECKED_OUT: 'Odhlášen',
    DETAIL_LABEL_HOST: 'Hostitel',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Vše je připraveno pro vaši návštěvu',
    ALL_SUB_TITLE: 'Vaše předregistrace je dokončena. Níže si můžete prohlédnout podrobnosti návštěvy.',
    BTN_CHECK_DETAILS: 'Zkontrolovat moje údaje',
    BTN_VIEW_CHECKIN: 'Zobrazit bezkontaktní přihlášení',
    QR_TITLE: 'Bezkontaktní přihlášení',
    QR_SUBTITLE: 'Namiřte fotoaparát na QR kód zobrazený na iPadu nebo vytištěném oznámení a pokračujte',
    ERROR_TITLE: 'Něco se pokazilo!',
    ERROR_SUBTITLE: 'Omlouváme se, ale došlo k chybě. Zkuste to prosím znovu později.',
    DETAIL_LABEL_VISITOR: 'Návštěvník',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Hostitel',
    DETAIL_HOST_PLACEHOLDER: 'Bude upřesněno',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Odkaz pro předregistraci vypršel',
    SUBTITLE: 'Tento odkaz již není platný',
    REASONS_HEADING: 'Možné důvody:',
    REASON_CANCELLED: 'Pozvánka zrušena',
    REASON_CHECKED_IN: 'Již přihlášen',
    REASON_PAST: 'Plánovaný čas již uplynul',
    HELP_PROMPT: 'Potřebujete pomoc?',
    HELP_TEXT: 'Kontaktujte svého hostitele nebo recepci',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Neplatný odkaz průkazu.',
    PASS_REVOKED_OR_EXPIRED: 'Tento průkaz byl odvolán nebo vypršela jeho platnost.',
    UNABLE_TO_LOAD: 'Nelze načíst stránku pro přihlášení.',
    PASS_EXPIRED_TITLE: 'Platnost průkazu vypršela',
    CONTACT_HOST_FOR_NEW_PASS: 'Kontaktujte prosím svého hostitele pro vydání nového SmartPasu.',
    ERROR_TITLE: 'Něco se pokazilo',
    BTN_TRY_AGAIN: 'Zkusit znovu',
    STATUS_NOT_ON_SITE: 'Mimo areál',
    STATUS_CHECKED_IN: 'Přihlášen',
    SELECT_LOCATION_LABEL: 'Vyberte své místo:',
    SELECT_LOCATION_PLACEHOLDER: '-- Vyberte místo --',
    BTN_PROCESSING: 'Zpracování...',
    BTN_CHECK_IN: 'Přihlásit se',
    BTN_CHECK_OUT: 'Odhlásit se',
    BTN_CLOSE_HINT: 'Tuto stránku můžete zavřít.',
    POWERED_BY: 'Provozováno službou Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Neplatný odkaz průkazu.',
    GOOGLE_WALLET_COMING_SOON: 'Podpora Google Wallet bude brzy k dispozici. Zkuste to prosím později.',
    OPEN_ON_MOBILE: 'Otevřete prosím tento odkaz na svém iPhonu nebo telefonu Android a přidejte průkaz do peněženky.',
    PREPARING_PASS: 'Připravuje se váš SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Neplatný odkaz průkazu.',
    LOCATION_REQUIRED: 'Přihlášení přes SmartPass vyžaduje přístup k poloze. Otevřete prosím tento odkaz na mobilním zařízení.',
    LOCATION_PERMISSION_REQUIRED: 'Pro přihlášení přes SmartPass je vyžadováno oprávnění k poloze. Povolte prosím přístup k poloze a zkuste to znovu.',
    LOCATION_UNAVAILABLE: 'Nelze určit vaši polohu. Ujistěte se, že jsou povoleny služby určování polohy, a zkuste to znovu.',
    PASS_REVOKED_OR_EXPIRED: 'Tento průkaz byl odvolán nebo vypršela jeho platnost.',
    OUTSIDE_GEOFENCE: 'Nacházíte se mimo oblast pro přihlášení. Přiblížte se prosím k budově a zkuste to znovu.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Zkusit znovu',
    OPENING_CHECK_IN: 'Otevírání přihlášení...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'NA TOMTO MÍSTĚ PROBÍHÁ EVAKUACE',
    BTN_TAP_TO_SIGNIN: 'Klepněte pro přihlášení',
    BTN_SCAN_QR_CODE: 'Naskenovat QR kód',
    TOAST_INVALID_QR: 'Neplatný nebo prošlý QR kód. Zkuste to prosím znovu!',
  },
  FOOTER: { POWERED_BY: 'PROVOZOVÁNO SLUŽBOU ' },
  HEADER: { STEP: 'KROK ', OF: ' Z ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Klepněte pro zahájení' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Neplatný QR kód. Zkuste to prosím znovu!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Kameru používáme pouze ke čtení QR kódu v hale a žádné video neukládáme',
    PERMISSION_PROMPT: 'Klepnutím na „Povolit fotoaparát" udělte přístup ke kameře.',
    PERMISSION_DENIED: 'Přístup ke kameře je zablokován. Povolte ho v nastavení prohlížeče a klepněte znovu na Povolit fotoaparát.',
    PERMISSION_UNSUPPORTED: 'Na tomto zařízení nebyla nalezena žádná kamera.',
    CAMERA_NOT_AVAILABLE: 'Kameru se nepodařilo spustit. Klepněte na Zkusit znovu nebo znovu načtěte stránku.',
    ERROR_CAMERA_IN_USE: 'Kamera je používána. Zavřete jinou aplikaci a zkuste to znovu.',
    ERROR_CAMERA_INIT_FAILED: 'Inicializace kamery se nezdařila. Zkontrolujte prosím oprávnění.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera je stále používána. Zavřete jinou aplikaci a zkuste to znovu.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Pro skenování QR kódů je vyžadováno oprávnění ke kameře.',
    ACCESS_DENIED: 'Přístup ke kameře byl odepřen nebo není dostupný',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Opravdu chcete zrušit? Ztratíte veškerý dosavadní postup.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Vybrat hostitele',        prefillTitle: 'Vybrat hostitele',        prefillDesc: 'Vyberte, koho navštěvujete' },
    DETAILS:    { headerTitle: 'Vaše údaje',              prefillTitle: 'Vaše údaje',              prefillDesc: 'Zadejte kontaktní informace' },
    DOCUMENT:   { headerTitle: 'Podepsat dokumenty',      prefillTitle: 'Podepsat dokumenty',      prefillDesc: 'Přečíst a podepsat požadované dokumenty' },
    ID_CAPTURE: { headerTitle: 'Ověřit totožnost',        prefillTitle: 'Ověřit totožnost',        prefillDesc: 'Naskenujte průkaz totožnosti' },
    PHOTO:      { headerTitle: 'Vyfotit selfie',          prefillTitle: 'Vyfotit selfie',          prefillDesc: 'Pořiďte svou fotografii' },
    VIDEO:      { headerTitle: 'Zhlédnout video',         prefillTitle: 'Zhlédnout video',         prefillDesc: 'Přehrajte si bezpečnostní nebo úvodní video' },
  },
};

// ── Finnish ────────────────────────────────────────────────────────────────────
export const FI_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Kosketusvapaa kirjautuminen | Visitly',
    PRE_ARRIVAL: 'Ennakkoilmoittautuminen | Visitly',
  },
  HOME: {
    TITLE: 'Kosketusvapaa kirjautuminen',
    SUBTITLE: 'Kohdista kamera iPadissa tai tulostetussa kyltissä olevaan QR-koodiin jatkaaksesi',
  },
  COMMON: {
    BACK: 'Takaisin',
    CONTINUE: 'Jatka',
    AGREE_AND_CONTINUE: 'Hyväksy ja jatka',
    DECLINE: 'Hylkää',
    CLEAR_SIGNATURE: 'Tyhjennä allekirjoitus',
    LOADING: 'Ladataan...',
    SEARCHING: 'Etsitään...',
    NO: 'EI',
    YES: 'KYLLÄ',
    ENABLE_CAMERA: 'Ota kamera käyttöön',
    PAGE_NOT_FOUND: '404 – Sivua ei löydy',
    POWERED_BY: 'Toimittaja Visitly',
    RETAKE: 'Ota uudelleen',
    CAPTURE: 'Kuvaa',
    RETRY: 'Yritä uudelleen',
    POINT_OF_ENTRY: 'Sisäänkäynti',
    PARKING_LOT: 'Pysäköintialue',
    DESTINATION: 'Rakennus',
    ERROR_PROCESSING_REQUEST: 'Pyyntöä käsiteltäessä tapahtui virhe. Yritä uudelleen.',
    SESSION_EXPIRED: 'Istunto on vanhentunut. Avaa linkki uudelleen sähköpostistasi.',
  },
  FULL_NAME: {
    TITLE: 'Mikä on koko nimesi?',
    SUBTITLE: 'Jotta tiedämme, kuka meitä tänään vierailee',
    FORM_LABEL: 'Koko nimi',
    PLACEHOLDER: 'esim. Matti Meikäläinen',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Valitse vierailun tyyppi.',
    FALLBACK_TITLE: 'Mikä tuo sinut tänne?',
  },
  SELECT_HOST: {
    TITLE: 'Ketä olet tapaamassa?',
    SUBTITLE: 'Etsi isäntäsi ilmoittaaksesi saapumisestasi.',
    PLACEHOLDER: 'Valitse isäntä',
    SEARCH_PLACEHOLDER: 'Etsi isäntää...',
    SEARCH_PLACEHOLDER_ALT: 'Etsi isäntää nimellä...',
    NO_HOSTS_FOUND: 'Isäntiä ei löydy',
    MIN_CHARS_MESSAGE: 'Kirjoita vähintään 3 merkkiä hakua varten',
  },
  VISITOR_FORM: {
    TITLE: 'Vierailun tiedot',
    SUBTITLE: 'Täytä alla olevat vaaditut tiedot.',
    SELECT_FIELD_PLACEHOLDER: 'Valitse {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Syötä koko nimesi',
    EMAIL_REQUIRED: 'Syötä voimassa oleva sähköpostiosoite',
    EMAIL_INVALID: 'Syötä voimassa oleva sähköpostiosoite',
    HOST_REQUIRED: 'Valitse isäntäsi nimi',
    COMPANY_NAME_REQUIRED: 'Syötä yrityksesi nimi',
    PHONE_NUMBER_REQUIRED: 'Syötä puhelinnumerosi',
    CUSTOM_FIELD_REQUIRED: 'Syötä {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} on pakollinen',
    FULL_NAME_INVALID: 'Syötä kelvollinen nimi.',
    TOAST_ERROR_PROCESSING: 'Pyynnön käsittelyssä tapahtui virhe. Yritä uudelleen.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Lue alla oleva asiakirja ja allekirjoita se',
    ERROR_DOCUMENT_EXPIRY: 'Virhe asiakirjan voimassaoloajan tarkistuksessa',
    ERROR_LOADING_CONTENT: 'Virhe asiakirjan sisällön lataamisessa',
    ERROR_FETCHING_DOCUMENT: 'Virhe asiakirjan hakemisessa',
    ERROR_RESTORE_SESSION: 'Asiakirjan allekirjoitusistuntoa ei voida palauttaa. Aloita uudelleen etusivulta.',
    BTN_COLLAPSE: 'Pienennä',
    BTN_EXPAND: 'Laajenna',
  },
  PRIVACY_POLICY: {
    TITLE: 'Ennen kuin jatkat',
    SUBTITLE: 'Lue ja hyväksy ennen jatkamista',
    IMPLICIT_CONSENT: 'Napsauttamalla alla olevaa Jatka-painiketta hyväksyt, että {orgName} saa tallentaa ja käsitellä yllä antamiasi henkilötietoja pyydetyn sisällön toimittamiseksi.',
    EXPLICIT_CONSENT: 'Hyväksyn, että {orgName} saa tallentaa ja käsitellä henkilötietojani.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Valitse henkilöllisyystodistus',
    SUBTITLE: 'Valitse ladattavan henkilöllisyystodistuksen tyyppi',
    ID_TYPES: [
      { label: 'Passi', value: 'PASSPORT' },
      { label: 'Ajokortti', value: 'DRIVING_LICENCE' },
      { label: 'Viranomaisen myöntämä henkilökortti', value: 'STATE_ISSUED_ID' },
      { label: 'Minulla on muu henkilöllisyystodistus', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Ota kuva',
    SUBTITLE: 'Keskitä kasvosi ja napsauta Jatka',
    INSTRUCTIONS: 'Käytä etukameraa, katso suoraan eteenpäin ja keskitä kasvosi.',
    BTN_CAPTURE: 'Kuvaa',
    BTN_RETAKE: 'Ota uudelleen',
    BTN_ENABLE_CAMERA: 'Ota kamera käyttöön',
    PERMISSION_PROMPT: 'Myönnä kameran käyttöoikeus napauttamalla "Ota kamera käyttöön".',
    PERMISSION_DENIED: 'Kameran käyttö on estetty. Ota se käyttöön selaimesi asetuksissa ja napauta sitten uudelleen Ota kamera käyttöön.',
    PERMISSION_UNSUPPORTED: 'Tästä laitteesta ei löydy kameraa.',
    TOAST_CAMERA_ERROR: 'Kameran käyttövirhe. Tarkista konsoli!',
    BTN_RETRY_CAMERA: 'Yritä kameraa uudelleen',
    CAMERA_NOT_AVAILABLE: 'Kameraa ei voitu käynnistää. Napauta Yritä uudelleen tai päivitä sivu.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Ota kamera käyttöön',
    BTN_START_CAMERA: 'Käynnistä kamera',
    BTN_TRY_NOW: 'Kokeile nyt',
    PERMISSION_PROMPT: 'Myönnä kameran käyttöoikeus napauttamalla "Ota kamera käyttöön".',
    PERMISSION_DENIED: 'Kameran käyttö on estetty. Ota se käyttöön selaimesi asetuksissa ja napauta sitten uudelleen Ota kamera käyttöön.',
    PERMISSION_UNSUPPORTED: 'Tästä laitteesta ei löydy kameraa.',
    ID_DRIVERS_LICENSE: 'Ajokortti',
    ID_STATE_ISSUED: 'Viranomaisen myöntämä henkilökortti',
    ID_PASSPORT: 'Passi',
    ID_OTHERS: 'Muu',
    ERROR_CAMERA_IN_USE: 'Kamera on käytössä. Sulje toinen sovellus ja yritä uudelleen.',
    ERROR_CAMERA_INIT_FAILED: 'Kameran alustus epäonnistui. Tarkista käyttöoikeudet.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera on yhä käytössä. Sulje toinen sovellus ja yritä uudelleen.',
    ERROR_PLEASE_SCAN_DOCS: 'Skannaa asiakirja(t)',
    SUBTITLE: 'Skannaa valittu asiakirja alla.',
    LABEL_FRONT_SIDE: 'Etupuoli',
    LABEL_BACK_SIDE: 'Takapuoli',
    INSTRUCTION_POSITION_FRONT: 'Aseta henkilöllisyystodistuksesi etupuoli kuvaan',
    INSTRUCTION_POSITION_BACK: 'Aseta henkilöllisyystodistuksesi takapuoli kuvaan',
    MRZ_BAND_HINT: 'Aseta kuvasivun kaksi alinta riviä katkoviivan alapuolelle',
    MRZ_SCAN_FAILED: 'Passia ei voitu lukea. Pidä se paikallaan niin, että alareunan kaksi riviä ovat raidan sisällä, ja yritä uudelleen.',
    MRZ_SCAN_ATTEMPT: 'Luku {current}/{total}',
    GLARE_WARNING: 'Asiakirjassa on heijastus. Kallista sitä hieman tai siirry pois valosta.',
    BARCODE_BAND_HINT: 'Pidä kortti paikallaan noin 20 cm:n päässä, viivakoodi kehyksen sisällä',
    BARCODE_SCAN_FAILED: 'Viivakoodia ei voitu lukea. Pidä kortin takapuoli paikallaan ja suorassa kehyksen sisällä ja yritä uudelleen.',
    TORCH_ON: 'Sytytä valo',
    TORCH_OFF: 'Sammuta valo',
    BARCODE_READING_PHOTO: 'Luetaan viivakoodia…',
    ID_CHECKING: 'Tarkistetaan henkilöllisyystodistustasi…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Melkein valmis!',
    SUBTITLE: 'Katso alla oleva video jatkaaksesi.',
    BTN_CHECK_IN: 'Kirjaudu sisään',
    BTN_FINISH: 'Valmis',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Rekisteröinti löytyi',
    SUBTITLE: 'Vahvista tietosi kirjautumisen viimeistelemiseksi.',
    BTN_NOT_YOU: 'Etkö ole sinä?',
    MEETING_WITH_LABEL: 'Tapaaminen: {hostName}',
    ONSITE_TITLE: 'Olet jo kirjautunut sisään',
    ONSITE_SUBTITLE: 'Valitse istuntosi kirjautuaksesi ulos.',
    CHECKIN_AT_LABEL: 'Kirjautunut klo:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Olet kirjautunut sisään!',
    WALLET_HINT: 'Kortti avautuu automaattisesti laitteellesi.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Ennakkoilmoittautuminen valmis',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Saapumisohjeet',
    ARRIVAL_INSTRUCTIONS_TEXT: 'QR-koodi on lähetetty rekisteröityyn sähköpostiosoitteeseesi. Käytä sitä nopeampaan kirjautumiseen kioskilla saapumisesi yhteydessä.',
    QR_CAPTION: 'Tämä QR-koodi voidaan skannata aulassa rekisteröintisi hakemiseksi.',
    BTN_SAVE_QR: 'Tallenna QR-koodi',
    WALLET_LABEL: 'Lompakko',
    ACTION_ADD_PASS: 'Lisää kortti',
    CALENDAR_LABEL: 'Kalenteri',
    ACTION_SAVE_EVENT: 'Tallenna tapahtuma',
    DEFAULT_WELCOME_MESSAGE: 'Isäntääsi on ilmoitettu, ja hän on pian luonasi.',
    PREFILL_SCHEDULED_FOR: 'Kaikki on valmista {date} varten.',
    PREFILL_HOST_NOTIFIED: 'Isäntääsi on ilmoitettu, ja odotamme sinua mielellämme.',
    TOAST_UPDATE_VISIT_ERROR: 'Vierailun päivittäminen epäonnistui. Yritä uudelleen.',
    TOAST_GENERIC_ERROR: 'Tapahtui virhe. Yritä uudelleen!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Vierailun tiedot',
    PAGE_SUBTITLE: 'Suorita alla olevat vaiheet nopeuttaaksesi kirjautumistasi.',
    SECTION_HEADING: 'Viimeistele ennakkoilmoittautuminen',
    STEPS_COMPLETED_SUFFIX: 'vaihetta suoritettu',
    BTN_START: 'Aloitetaan!',
  },
  SIGN_OUT: {
    EYEBROW: 'Kirjaudu ulos',
    TITLE_WITH_NAME: 'Näkemiin,',
    TITLE_FALLBACK: 'Näkemiin!',
    SUBTITLE_SESSION: 'Vahvista tietosi ja kirjaudu ulos.',
    STRIP_SUBTITLE: 'Tällä hetkellä kirjautunut sisään',
    LABEL_ORG: 'ORGANISAATIO',
    LABEL_LOCATION: 'SIJAINTI',
    LABEL_HOST: 'ISÄNTÄ',
    LABEL_SIGNED_IN_AT: 'KIRJAUTUNUT',
    BTN_SIGN_OUT: 'Kirjaudu ulos',
    BTN_SIGNING_OUT: 'Kirjaudutaan ulos...',
    BTN_START_OVER: 'Aloita alusta',
    LABEL_NOT_YOU: 'Etkö ole sinä?',
    TOAST_SUCCESS: 'Sinut on kirjattu ulos onnistuneesti!',
    TOAST_ERROR_FALLBACK: 'Uloskirjautumisessa tapahtui virhe. Yritä uudelleen.',
    CUSTOM_FIELDS_EYEBROW: 'Melkein valmis',
    CUSTOM_FIELDS_TITLE: 'Viimeistele uloskirjautuminen',
    CUSTOM_FIELDS_SUBTITLE: 'Täytä vaaditut tiedot.',
    BTN_CONFIRM_SIGN_OUT: 'Vahvista uloskirjautuminen',
    BTN_GO_BACK: 'Takaisin',
    SUCCESS_TITLE: 'Olet kirjautunut ulos',
    SUCCESS_SUBTITLE: 'Kiitos vierailustasi',
    BTN_DONE: 'Valmis',
    LABEL_VISIT_DURATION: 'Vierailun kesto',
    GEOLOCATION_UNAVAILABLE: 'Uloskirjautuminen edellyttää sijaintitietojen käyttöä. Käytä mobiililaitetta.',
    LOCATION_PERMISSION_REQUIRED: 'Uloskirjautuminen edellyttää sijaintilupaa. Salli sijaintitietojen käyttö ja yritä uudelleen.',
    MUST_BE_ON_SITE: 'Uloskirjautuminen edellyttää, että olet paikan päällä.',
    GUEST_PLACEHOLDER: 'Vieras',
    SUCCESS_CLOSING: 'Hyvää päivänjatkoa!',
    STATUS_CHECKED_IN: 'Kirjautunut sisään',
    STATUS_CHECKED_OUT: 'Kirjautunut ulos',
    DETAIL_LABEL_HOST: 'Isäntä',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Kaikki on valmista vierailuasi varten',
    ALL_SUB_TITLE: 'Ennakkoilmoittautumisesi on valmis. Voit tarkastella vierailusi tietoja alla.',
    BTN_CHECK_DETAILS: 'Tarkasta tietoni',
    BTN_VIEW_CHECKIN: 'Näytä kosketusvapaa kirjautuminen',
    QR_TITLE: 'Kosketusvapaa kirjautuminen',
    QR_SUBTITLE: 'Kohdista kamera iPadissa tai tulostetussa kyltissä olevaan QR-koodiin jatkaaksesi',
    ERROR_TITLE: 'Jotain meni pieleen!',
    ERROR_SUBTITLE: 'Pahoittelemme, mutta tapahtui virhe. Yritä myöhemmin uudelleen.',
    DETAIL_LABEL_VISITOR: 'Vierailija',
    DETAIL_LABEL_EMAIL: 'Sähköposti',
    DETAIL_LABEL_HOST: 'Isäntä',
    DETAIL_HOST_PLACEHOLDER: 'Vahvistetaan myöhemmin',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Ennakkoilmoittautumisen linkki on vanhentunut',
    SUBTITLE: 'Tämä linkki ei ole enää voimassa',
    REASONS_HEADING: 'Mahdolliset syyt:',
    REASON_CANCELLED: 'Kutsu peruutettu',
    REASON_CHECKED_IN: 'Jo kirjautunut',
    REASON_PAST: 'Sovittu aika on ohitettu',
    HELP_PROMPT: 'Tarvitsetko apua?',
    HELP_TEXT: 'Ota yhteyttä isäntääsi tai vastaanottoon',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Virheellinen korttilinki.',
    PASS_REVOKED_OR_EXPIRED: 'Tämä kortti on peruutettu tai vanhentunut.',
    UNABLE_TO_LOAD: 'Kirjautumissivua ei voi ladata.',
    PASS_EXPIRED_TITLE: 'Kortti vanhentunut',
    CONTACT_HOST_FOR_NEW_PASS: 'Pyydä isännältäsi uusi SmartPass.',
    ERROR_TITLE: 'Jotain meni pieleen',
    BTN_TRY_AGAIN: 'Yritä uudelleen',
    STATUS_NOT_ON_SITE: 'Ei paikan päällä',
    STATUS_CHECKED_IN: 'Kirjautunut sisään',
    SELECT_LOCATION_LABEL: 'Valitse sijaintisi:',
    SELECT_LOCATION_PLACEHOLDER: '-- Valitse sijainti --',
    BTN_PROCESSING: 'Käsitellään...',
    BTN_CHECK_IN: 'Kirjaudu sisään',
    BTN_CHECK_OUT: 'Kirjaudu ulos',
    BTN_CLOSE_HINT: 'Voit sulkea tämän sivun.',
    POWERED_BY: 'Toimittaja Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Virheellinen korttilinki.',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet -tuki tulossa pian. Tarkista myöhemmin uudelleen.',
    OPEN_ON_MOBILE: 'Avaa tämä linkki iPhonellasi tai Android-puhelimellasi lisätäksesi kortin lompakkoosi.',
    PREPARING_PASS: 'Valmistellaan SmartPassiasi...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Virheellinen korttilinki.',
    LOCATION_REQUIRED: 'SmartPass-kirjautuminen edellyttää sijaintitietojen käyttöä. Avaa tämä linkki mobiililaitteellasi.',
    LOCATION_PERMISSION_REQUIRED: 'SmartPass-kirjautuminen edellyttää sijaintilupaa. Salli sijaintitietojen käyttö ja yritä uudelleen.',
    LOCATION_UNAVAILABLE: 'Sijaintiasi ei voida määrittää. Varmista, että sijaintipalvelut ovat käytössä, ja yritä uudelleen.',
    PASS_REVOKED_OR_EXPIRED: 'Tämä kortti on peruutettu tai vanhentunut.',
    OUTSIDE_GEOFENCE: 'Olet kirjautumisalueen ulkopuolella. Siirry lähemmäs rakennusta ja yritä uudelleen.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Yritä uudelleen',
    OPENING_CHECK_IN: 'Avataan kirjautumista...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'EVAKUOINTI ON KÄYNNISSÄ TÄSSÄ KOHTEESSA',
    BTN_TAP_TO_SIGNIN: 'Napauta kirjautuaksesi',
    BTN_SCAN_QR_CODE: 'Skannaa QR-koodi',
    TOAST_INVALID_QR: 'Virheellinen tai vanhentunut QR-koodi. Yritä uudelleen!',
  },
  FOOTER: { POWERED_BY: 'TOIMITTAJA ' },
  HEADER: { STEP: 'VAIHE ', OF: ' / ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Napauta aloittaaksesi' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Virheellinen QR-koodi. Yritä uudelleen!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Käytämme kameraasi vain aulan QR-koodin lukemiseen emmekä tallenna videota',
    PERMISSION_PROMPT: 'Myönnä kameran käyttöoikeus napauttamalla "Ota kamera käyttöön".',
    PERMISSION_DENIED: 'Kameran käyttö on estetty. Ota se käyttöön selaimesi asetuksissa ja napauta sitten uudelleen Ota kamera käyttöön.',
    PERMISSION_UNSUPPORTED: 'Tästä laitteesta ei löydy kameraa.',
    CAMERA_NOT_AVAILABLE: 'Kameraa ei voitu käynnistää. Napauta Yritä uudelleen tai päivitä sivu.',
    ERROR_CAMERA_IN_USE: 'Kamera on käytössä. Sulje toinen sovellus ja yritä uudelleen.',
    ERROR_CAMERA_INIT_FAILED: 'Kameran alustus epäonnistui. Tarkista käyttöoikeudet.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera on yhä käytössä. Sulje toinen sovellus ja yritä uudelleen.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'QR-koodien skannaamiseen tarvitaan kameran käyttöoikeus.',
    ACCESS_DENIED: 'Kameran käyttö estetty tai ei saatavilla',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Haluatko varmasti peruuttaa? Menetät kaiken edistymisesi.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Valitse isäntä',              prefillTitle: 'Valitse isäntä',              prefillDesc: 'Valitse kenet tapaat' },
    DETAILS:    { headerTitle: 'Omat tietosi',                prefillTitle: 'Omat tietosi',                prefillDesc: 'Anna yhteystietosi' },
    DOCUMENT:   { headerTitle: 'Allekirjoita asiakirjat',     prefillTitle: 'Allekirjoita asiakirjat',     prefillDesc: 'Lue ja allekirjoita vaaditut asiakirjat' },
    ID_CAPTURE: { headerTitle: 'Vahvista henkilöllisyys',     prefillTitle: 'Vahvista henkilöllisyys',     prefillDesc: 'Skannaa henkilöllisyystodistuksesi' },
    PHOTO:      { headerTitle: 'Ota selfie',                  prefillTitle: 'Ota selfie',                  prefillDesc: 'Ota kasvokuvasi' },
    VIDEO:      { headerTitle: 'Katso video',                 prefillTitle: 'Katso video',                 prefillDesc: 'Katso turvallisuus- tai perehdytysvideo' },
  },
};

// ── Hebrew ─────────────────────────────────────────────────────────────────────
export const HE_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'כניסה ללא מגע | Visitly',
    PRE_ARRIVAL: 'רישום מקדים | Visitly',
  },
  HOME: {
    TITLE: 'כניסה ללא מגע',
    SUBTITLE: 'כוון את המצלמה לקוד ה-QR המוצג על ה-iPad או על השלט המודפס כדי להמשיך',
  },
  COMMON: {
    BACK: 'חזרה',
    CONTINUE: 'המשך',
    AGREE_AND_CONTINUE: 'מסכים וממשיך',
    DECLINE: 'דחייה',
    CLEAR_SIGNATURE: 'מחק חתימה',
    LOADING: 'טוען...',
    SEARCHING: 'מחפש...',
    NO: 'לא',
    YES: 'כן',
    ENABLE_CAMERA: 'הפעל מצלמה',
    PAGE_NOT_FOUND: '404 – הדף לא נמצא',
    POWERED_BY: 'מופעל על ידי Visitly',
    RETAKE: 'צלם מחדש',
    CAPTURE: 'צלם',
    RETRY: 'נסה שוב',
    POINT_OF_ENTRY: 'נקודת כניסה',
    PARKING_LOT: 'חניון',
    DESTINATION: 'בניין',
    ERROR_PROCESSING_REQUEST: 'שגיאה בעיבוד הבקשה. נסה שוב.',
    SESSION_EXPIRED: 'פג תוקף ההפעלה. פתח מחדש את הקישור מהדואר האלקטרוני שלך.',
  },
  FULL_NAME: {
    TITLE: 'מה שמך המלא?',
    SUBTITLE: 'כדי שנדע מי מבקר אצלנו היום',
    FORM_LABEL: 'שם מלא',
    PLACEHOLDER: 'לדוגמה: ישראל ישראלי',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'בחר את סוג הביקור שלך.',
    FALLBACK_TITLE: 'מה מביא אותך לכאן?',
  },
  SELECT_HOST: {
    TITLE: 'את מי אתה מבקר?',
    SUBTITLE: 'חפש את המארח שלך כדי להודיע לו על הגעתך.',
    PLACEHOLDER: 'בחר מארח',
    SEARCH_PLACEHOLDER: 'חפש מארח...',
    SEARCH_PLACEHOLDER_ALT: 'חפש מארח לפי שם...',
    NO_HOSTS_FOUND: 'לא נמצאו מארחים',
    MIN_CHARS_MESSAGE: 'הקלד לפחות 3 תווים לחיפוש',
  },
  VISITOR_FORM: {
    TITLE: 'פרטי ביקור',
    SUBTITLE: 'מלא את המידע הנדרש להלן.',
    SELECT_FIELD_PLACEHOLDER: 'בחר {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'הזן את שמך המלא',
    EMAIL_REQUIRED: 'הזן כתובת דואר אלקטרוני תקינה',
    EMAIL_INVALID: 'הזן כתובת דואר אלקטרוני תקינה',
    HOST_REQUIRED: 'בחר את שם המארח שלך',
    COMPANY_NAME_REQUIRED: 'הזן את שם החברה שלך',
    PHONE_NUMBER_REQUIRED: 'הזן את מספר הטלפון שלך',
    CUSTOM_FIELD_REQUIRED: 'הזן {field}',
    FIELD_REQUIRED_TEMPLATE: 'השדה {field} נדרש',
    FULL_NAME_INVALID: 'אנא הזן שם תקין.',
    TOAST_ERROR_PROCESSING: 'אירעה שגיאה בעיבוד הבקשה. אנא נסה שוב.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'עיין במסמך להלן וחתום עליו',
    ERROR_DOCUMENT_EXPIRY: 'שגיאה בבדיקת תפוגת המסמך',
    ERROR_LOADING_CONTENT: 'שגיאה בטעינת תוכן המסמך',
    ERROR_FETCHING_DOCUMENT: 'שגיאה באחזור המסמך',
    ERROR_RESTORE_SESSION: 'לא ניתן לשחזר את הפעלת חתימת המסמך. הפעל מחדש מדף הבית.',
    BTN_COLLAPSE: 'כווץ',
    BTN_EXPAND: 'הרחב',
  },
  PRIVACY_POLICY: {
    TITLE: 'לפני שתמשיך',
    SUBTITLE: 'עיין ואשר לפני המשך',
    IMPLICIT_CONSENT: 'בלחיצה על המשך להלן, אתה מסכים לאפשר ל-{orgName} לאחסן ולעבד את המידע האישי שהוגש לעיל כדי לספק את התוכן שביקשת.',
    EXPLICIT_CONSENT: 'אני מסכים לאפשר ל-{orgName} לאחסן ולעבד את הנתונים האישיים שלי.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'בחר תעודת זהות',
    SUBTITLE: 'בחר את סוג תעודת הזהות להעלאה',
    ID_TYPES: [
      { label: 'דרכון', value: 'PASSPORT' },
      { label: 'רישיון נהיגה', value: 'DRIVING_LICENCE' },
      { label: 'תעודת זהות ממשלתית', value: 'STATE_ISSUED_ID' },
      { label: 'יש לי תעודת זהות אחרת', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'צלם תמונה',
    SUBTITLE: 'מרכז את פניך ולחץ על המשך',
    INSTRUCTIONS: 'השתמש במצלמה הקדמית, הסתכל ישר קדימה ומרכז את פניך.',
    BTN_CAPTURE: 'צלם',
    BTN_RETAKE: 'צלם מחדש',
    BTN_ENABLE_CAMERA: 'הפעל מצלמה',
    PERMISSION_PROMPT: 'הקש על "הפעל מצלמה" להענקת גישה למצלמה.',
    PERMISSION_DENIED: 'הגישה למצלמה חסומה. הפעל אותה בהגדרות הדפדפן ולאחר מכן הקש שוב על הפעל מצלמה.',
    PERMISSION_UNSUPPORTED: 'לא נמצאה מצלמה במכשיר זה.',
    TOAST_CAMERA_ERROR: 'שגיאה בגישה למצלמה. בדוק את המסוף!',
    BTN_RETRY_CAMERA: 'נסה מצלמה שוב',
    CAMERA_NOT_AVAILABLE: 'לא ניתן היה להפעיל את המצלמה. הקש על נסה שוב או טען מחדש את הדף.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'הפעל מצלמה',
    BTN_START_CAMERA: 'הפעל מצלמה',
    BTN_TRY_NOW: 'נסה עכשיו',
    PERMISSION_PROMPT: 'הקש על "הפעל מצלמה" להענקת גישה למצלמה.',
    PERMISSION_DENIED: 'הגישה למצלמה חסומה. הפעל אותה בהגדרות הדפדפן ולאחר מכן הקש שוב על הפעל מצלמה.',
    PERMISSION_UNSUPPORTED: 'לא נמצאה מצלמה במכשיר זה.',
    ID_DRIVERS_LICENSE: 'רישיון נהיגה',
    ID_STATE_ISSUED: 'תעודת זהות ממשלתית',
    ID_PASSPORT: 'דרכון',
    ID_OTHERS: 'אחר',
    ERROR_CAMERA_IN_USE: 'המצלמה בשימוש. סגור את האפליקציה האחרת ונסה שוב.',
    ERROR_CAMERA_INIT_FAILED: 'אתחול המצלמה נכשל. בדוק את ההרשאות.',
    ERROR_CAMERA_BUSY_RETRY: 'המצלמה עדיין בשימוש. סגור את האפליקציה האחרת ונסה שוב.',
    ERROR_PLEASE_SCAN_DOCS: 'סרוק את המסמך/ים',
    SUBTITLE: 'סרוק את המסמך שנבחר להלן.',
    LABEL_FRONT_SIDE: 'צד קדמי',
    LABEL_BACK_SIDE: 'צד אחורי',
    INSTRUCTION_POSITION_FRONT: 'מקם את הצד הקדמי של תעודת הזהות',
    INSTRUCTION_POSITION_BACK: 'מקם את הצד האחורי של תעודת הזהות',
    MRZ_BAND_HINT: 'יש למקם את שתי השורות התחתונות של עמוד התמונה מתחת לקו המקווקו',
    MRZ_SCAN_FAILED: 'לא הצלחנו לקרוא את הדרכון. יש להחזיק אותו יציב כששתי השורות התחתונות בתוך הפס, ולנסות שוב.',
    MRZ_SCAN_ATTEMPT: 'סריקה {current} מתוך {total}',
    GLARE_WARNING: 'יש סנוור על המסמך. יש להטות אותו מעט או להתרחק מהאור.',
    BARCODE_BAND_HINT: 'יש להחזיק את הכרטיס יציב, במרחק של כ-20 ס״מ, כשהברקוד בתוך המסגרת',
    BARCODE_SCAN_FAILED: 'לא הצלחנו לקרוא את הברקוד. יש להחזיק את גב הכרטיס יציב וישר בתוך המסגרת, ולנסות שוב.',
    TORCH_ON: 'הדלקת תאורה',
    TORCH_OFF: 'כיבוי תאורה',
    BARCODE_READING_PHOTO: 'קורא את הברקוד…',
    ID_CHECKING: 'בודק את תעודת הזהות שלך…',
  },
  VISITOR_VIDEO: {
    TITLE: 'כמעט סיימנו!',
    SUBTITLE: 'צפה בסרטון להלן כדי להמשיך.',
    BTN_CHECK_IN: 'כניסה',
    BTN_FINISH: 'סיים',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'הרישום נמצא',
    SUBTITLE: 'אמת את פרטיך להשלמת הכניסה.',
    BTN_NOT_YOU: 'זה לא אתה?',
    MEETING_WITH_LABEL: 'פגישה עם {hostName}',
    ONSITE_TITLE: 'כבר נרשמת',
    ONSITE_SUBTITLE: 'בחר את הפגישה שלך לצורך יציאה.',
    CHECKIN_AT_LABEL: 'נרשם בשעה:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'נרשמת בהצלחה!',
    WALLET_HINT: 'הכרטיס ייפתח אוטומטית במכשיר שלך.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'הרישום המקדים הושלם',
    ARRIVAL_INSTRUCTIONS_TITLE: 'הוראות הגעה',
    ARRIVAL_INSTRUCTIONS_TEXT: 'קוד QR נשלח לכתובת הדואר האלקטרוני הרשומה שלך. השתמש בו לכניסה מהירה יותר בקיוסק עם הגעתך.',
    QR_CAPTION: 'ניתן לסרוק קוד QR זה בלובי כדי להציג את הרישום שלך.',
    BTN_SAVE_QR: 'שמור קוד QR',
    WALLET_LABEL: 'ארנק',
    ACTION_ADD_PASS: 'הוסף כרטיס',
    CALENDAR_LABEL: 'לוח שנה',
    ACTION_SAVE_EVENT: 'שמור אירוע',
    DEFAULT_WELCOME_MESSAGE: 'המארח שלך עודכן ויהיה איתך בקרוב.',
    PREFILL_SCHEDULED_FOR: 'הכל מוכן לתאריך {date}.',
    PREFILL_HOST_NOTIFIED: 'המארח שלך עודכן ואנו מצפים לקבל את פניך.',
    TOAST_UPDATE_VISIT_ERROR: 'אירעה שגיאה בעדכון הביקור. אנא נסה שוב.',
    TOAST_GENERIC_ERROR: 'נתקלנו בשגיאה. אנא נסה שוב!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'פרטי ביקור',
    PAGE_SUBTITLE: 'השלם את השלבים להלן כדי לזרז את הכניסה.',
    SECTION_HEADING: 'השלם את הרישום המקדים',
    STEPS_COMPLETED_SUFFIX: 'שלבים הושלמו',
    BTN_START: 'בואו נתחיל!',
  },
  SIGN_OUT: {
    EYEBROW: 'יציאה',
    TITLE_WITH_NAME: 'להתראות,',
    TITLE_FALLBACK: 'להתראות!',
    SUBTITLE_SESSION: 'אשר את פרטיך וצא.',
    STRIP_SUBTITLE: 'כרגע מחובר',
    LABEL_ORG: 'ארגון',
    LABEL_LOCATION: 'מיקום',
    LABEL_HOST: 'מארח',
    LABEL_SIGNED_IN_AT: 'נכנס ב-',
    BTN_SIGN_OUT: 'יציאה',
    BTN_SIGNING_OUT: 'יוצא...',
    BTN_START_OVER: 'התחל מחדש',
    LABEL_NOT_YOU: 'זה לא אתה?',
    TOAST_SUCCESS: 'יצאת בהצלחה!',
    TOAST_ERROR_FALLBACK: 'שגיאה ביציאה. נסה שוב.',
    CUSTOM_FIELDS_EYEBROW: 'כמעט סיימנו',
    CUSTOM_FIELDS_TITLE: 'השלם יציאה',
    CUSTOM_FIELDS_SUBTITLE: 'מלא את הפרטים הנדרשים.',
    BTN_CONFIRM_SIGN_OUT: 'אשר יציאה',
    BTN_GO_BACK: 'חזרה',
    SUCCESS_TITLE: 'יצאת בהצלחה',
    SUCCESS_SUBTITLE: 'תודה על ביקורך',
    BTN_DONE: 'סיום',
    LABEL_VISIT_DURATION: 'משך הביקור',
    GEOLOCATION_UNAVAILABLE: 'נדרשת גישה למיקום ליציאה. השתמש במכשיר נייד.',
    LOCATION_PERMISSION_REQUIRED: 'נדרשת הרשאת מיקום ליציאה. אפשר גישה למיקום ונסה שוב.',
    MUST_BE_ON_SITE: 'עליך להיות במקום כדי לצאת.',
    GUEST_PLACEHOLDER: 'אורח',
    SUCCESS_CLOSING: 'יום טוב!',
    STATUS_CHECKED_IN: 'נכנס',
    STATUS_CHECKED_OUT: 'יצא',
    DETAIL_LABEL_HOST: 'מארח',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'הכל מוכן לביקורך',
    ALL_SUB_TITLE: 'הרישום המקדים שלך הושלם. תוכל לסקור את פרטי הביקור להלן.',
    BTN_CHECK_DETAILS: 'בדוק את הפרטים שלי',
    BTN_VIEW_CHECKIN: 'הצג כניסה ללא מגע',
    QR_TITLE: 'כניסה ללא מגע',
    QR_SUBTITLE: 'כוון את המצלמה לקוד ה-QR המוצג על ה-iPad או על השלט המודפס כדי להמשיך',
    ERROR_TITLE: 'משהו השתבש!',
    ERROR_SUBTITLE: 'אנו מתנצלים, אך אירעה שגיאה. נסה שוב מאוחר יותר.',
    DETAIL_LABEL_VISITOR: 'מבקר',
    DETAIL_LABEL_EMAIL: 'דואר אלקטרוני',
    DETAIL_LABEL_HOST: 'מארח',
    DETAIL_HOST_PLACEHOLDER: 'יאושר בהמשך',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'קישור הרישום המקדים פג תוקפו',
    SUBTITLE: 'קישור זה אינו תקף עוד',
    REASONS_HEADING: 'סיבות אפשריות:',
    REASON_CANCELLED: 'ההזמנה בוטלה',
    REASON_CHECKED_IN: 'כבר נרשם',
    REASON_PAST: 'המועד שנקבע עבר',
    HELP_PROMPT: 'זקוק לעזרה?',
    HELP_TEXT: 'פנה למארח שלך או לדלפק הקבלה',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'קישור כרטיס לא חוקי.',
    PASS_REVOKED_OR_EXPIRED: 'כרטיס זה בוטל או פג תוקפו.',
    UNABLE_TO_LOAD: 'לא ניתן לטעון את דף הכניסה.',
    PASS_EXPIRED_TITLE: 'תוקף הכרטיס פג',
    CONTACT_HOST_FOR_NEW_PASS: 'פנה למארח שלך לקבלת SmartPass חדש.',
    ERROR_TITLE: 'משהו השתבש',
    BTN_TRY_AGAIN: 'נסה שוב',
    STATUS_NOT_ON_SITE: 'לא באתר',
    STATUS_CHECKED_IN: 'נכנס',
    SELECT_LOCATION_LABEL: 'בחר את המיקום שלך:',
    SELECT_LOCATION_PLACEHOLDER: '-- בחר מיקום --',
    BTN_PROCESSING: 'מעבד...',
    BTN_CHECK_IN: 'כניסה',
    BTN_CHECK_OUT: 'יציאה',
    BTN_CLOSE_HINT: 'ניתן לסגור דף זה.',
    POWERED_BY: 'מופעל על ידי Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'קישור כרטיס לא חוקי.',
    GOOGLE_WALLET_COMING_SOON: 'תמיכה ב-Google Wallet בקרוב. חזור מאוחר יותר.',
    OPEN_ON_MOBILE: 'פתח קישור זה ב-iPhone או בטלפון Android שלך כדי להוסיף את הכרטיס לארנק שלך.',
    PREPARING_PASS: 'מכין את ה-SmartPass שלך...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'קישור כרטיס לא חוקי.',
    LOCATION_REQUIRED: 'כניסה עם SmartPass דורשת גישה למיקום. פתח קישור זה במכשיר הנייד שלך.',
    LOCATION_PERMISSION_REQUIRED: 'נדרשת הרשאת מיקום לכניסה עם SmartPass. אפשר גישה למיקום ונסה שוב.',
    LOCATION_UNAVAILABLE: 'לא ניתן לקבוע את מיקומך. וודא שירותי המיקום מופעלים ונסה שוב.',
    PASS_REVOKED_OR_EXPIRED: 'כרטיס זה בוטל או פג תוקפו.',
    OUTSIDE_GEOFENCE: 'אתה מחוץ לאזור הכניסה. התקרב לבניין ונסה שוב.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'נסה שוב',
    OPENING_CHECK_IN: 'פותח כניסה...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'פינוי מתבצע כעת במיקום זה',
    BTN_TAP_TO_SIGNIN: 'הקש לכניסה',
    BTN_SCAN_QR_CODE: 'סרוק קוד QR',
    TOAST_INVALID_QR: 'קוד QR לא חוקי או שתוקפו פג. אנא נסה שוב!',
  },
  FOOTER: { POWERED_BY: 'מופעל על ידי ' },
  HEADER: { STEP: 'שלב ', OF: ' מתוך ' },
  SPLASH_SCREEN: { TAP_TO_START: 'הקש להתחלה' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'קוד QR לא חוקי. נסה שוב!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'אנו משתמשים במצלמה שלך רק לקריאת קוד ה-QR של הלובי ואיננו שומרים שום וידאו',
    PERMISSION_PROMPT: 'הקש על "הפעל מצלמה" להענקת גישה למצלמה.',
    PERMISSION_DENIED: 'הגישה למצלמה חסומה. הפעל אותה בהגדרות הדפדפן ולאחר מכן הקש שוב על הפעל מצלמה.',
    PERMISSION_UNSUPPORTED: 'לא נמצאה מצלמה במכשיר זה.',
    CAMERA_NOT_AVAILABLE: 'לא ניתן היה להפעיל את המצלמה. הקש על נסה שוב או טען מחדש את הדף.',
    ERROR_CAMERA_IN_USE: 'המצלמה בשימוש. סגור את האפליקציה האחרת ונסה שוב.',
    ERROR_CAMERA_INIT_FAILED: 'אתחול המצלמה נכשל. בדוק את ההרשאות.',
    ERROR_CAMERA_BUSY_RETRY: 'המצלמה עדיין בשימוש. סגור את האפליקציה האחרת ונסה שוב.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'נדרשת הרשאת מצלמה לסריקת קודי QR.',
    ACCESS_DENIED: 'גישה למצלמה נדחתה או אינה זמינה',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'האם אתה בטוח שברצונך לבטל? תאבד את כל ההתקדמות שלך.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'בחר מארח',              prefillTitle: 'בחר מארח',              prefillDesc: 'בחר את מי אתה מבקר' },
    DETAILS:    { headerTitle: 'הפרטים שלך',             prefillTitle: 'הפרטים שלך',             prefillDesc: 'ספק את פרטי הקשר שלך' },
    DOCUMENT:   { headerTitle: 'חתום על מסמכים',        prefillTitle: 'חתום על מסמכים',        prefillDesc: 'עיין וחתום על המסמכים הנדרשים' },
    ID_CAPTURE: { headerTitle: 'אמת זהות',              prefillTitle: 'אמת זהות',              prefillDesc: 'סרוק את תעודת הזהות שלך' },
    PHOTO:      { headerTitle: 'צלם סלפי',              prefillTitle: 'צלם סלפי',              prefillDesc: 'צלם את התמונה שלך' },
    VIDEO:      { headerTitle: 'צפה בסרטון',            prefillTitle: 'צפה בסרטון',            prefillDesc: 'צפה בסרטון בטיחות או הכוונה' },
  },
};

// ── Italian ────────────────────────────────────────────────────────────────────
export const IT_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Accesso senza contatto | Visitly',
    PRE_ARRIVAL: 'Pre-arrivo | Visitly',
  },
  HOME: {
    TITLE: 'Accesso senza contatto',
    SUBTITLE: "Punta la fotocamera sul codice QR mostrato sull'iPad o sul cartello stampato per continuare",
  },
  COMMON: {
    BACK: 'Indietro',
    CONTINUE: 'Continua',
    AGREE_AND_CONTINUE: 'Accetta e continua',
    DECLINE: 'Rifiuta',
    CLEAR_SIGNATURE: 'Cancella firma',
    LOADING: 'Caricamento...',
    SEARCHING: 'Ricerca in corso...',
    NO: 'NO',
    YES: 'SÌ',
    ENABLE_CAMERA: 'Attiva fotocamera',
    PAGE_NOT_FOUND: '404 – Pagina non trovata',
    POWERED_BY: 'Sviluppato da Visitly',
    RETAKE: 'Riprendi',
    CAPTURE: 'Acquisisci',
    RETRY: 'Riprova',
    POINT_OF_ENTRY: 'Punto di ingresso',
    PARKING_LOT: 'Parcheggio',
    DESTINATION: 'Edificio',
    ERROR_PROCESSING_REQUEST: "Errore durante l'elaborazione della richiesta. Riprova.",
    SESSION_EXPIRED: "Sessione scaduta. Riapri il collegamento dall'e-mail.",
  },
  FULL_NAME: {
    TITLE: 'Qual è il tuo nome completo?',
    SUBTITLE: 'Per sapere chi ci visita oggi',
    FORM_LABEL: 'Nome completo',
    PLACEHOLDER: 'es. Mario Rossi',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Scegli il tipo di visita.',
    FALLBACK_TITLE: 'Cosa ti porta qui?',
  },
  SELECT_HOST: {
    TITLE: 'Chi stai visitando?',
    SUBTITLE: 'Cerca il tuo referente per notificargli il tuo arrivo.',
    PLACEHOLDER: 'Seleziona un referente',
    SEARCH_PLACEHOLDER: 'Cerca referente...',
    SEARCH_PLACEHOLDER_ALT: 'Cerca referente per nome...',
    NO_HOSTS_FOUND: 'Nessun referente trovato',
    MIN_CHARS_MESSAGE: 'Digita almeno 3 caratteri per cercare',
  },
  VISITOR_FORM: {
    TITLE: 'Dettagli della visita',
    SUBTITLE: 'Compila le informazioni richieste di seguito.',
    SELECT_FIELD_PLACEHOLDER: 'Seleziona {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Inserisci il tuo nome completo',
    EMAIL_REQUIRED: 'Inserisci un indirizzo e-mail valido',
    EMAIL_INVALID: 'Inserisci un indirizzo e-mail valido',
    HOST_REQUIRED: 'Seleziona il nome del tuo referente',
    COMPANY_NAME_REQUIRED: 'Inserisci il nome della tua azienda',
    PHONE_NUMBER_REQUIRED: 'Inserisci il tuo numero di telefono',
    CUSTOM_FIELD_REQUIRED: 'Inserisci {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} è obbligatorio',
    FULL_NAME_INVALID: 'Inserisci un nome valido.',
    TOAST_ERROR_PROCESSING: "Errore durante l'elaborazione della richiesta. Riprova.",
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Leggi e firma il documento di seguito',
    ERROR_DOCUMENT_EXPIRY: 'Errore durante la verifica della scadenza del documento',
    ERROR_LOADING_CONTENT: 'Errore durante il caricamento del contenuto del documento',
    ERROR_FETCHING_DOCUMENT: 'Errore durante il recupero del documento',
    ERROR_RESTORE_SESSION: 'Impossibile ripristinare la sessione di firma del documento. Riavvia dalla pagina iniziale.',
    BTN_COLLAPSE: 'Comprimi',
    BTN_EXPAND: 'Espandi',
  },
  PRIVACY_POLICY: {
    TITLE: 'Prima di continuare',
    SUBTITLE: 'Leggi e accetta prima di continuare',
    IMPLICIT_CONSENT: 'Facendo clic su Continua di seguito, acconsenti a consentire a {orgName} di archiviare ed elaborare le informazioni personali inviate sopra per fornire il contenuto richiesto.',
    EXPLICIT_CONSENT: 'Acconsento a consentire a {orgName} di archiviare ed elaborare i miei dati personali.',
  },
  CAPTURE_PICTURE: {
    TITLE: "Seleziona documento d'identità",
    SUBTITLE: "Seleziona il tipo di documento d'identità da caricare",
    ID_TYPES: [
      { label: 'Passaporto', value: 'PASSPORT' },
      { label: 'Patente di guida', value: 'DRIVING_LICENCE' },
      { label: "Carta d'identità", value: 'STATE_ISSUED_ID' },
      { label: 'Ho un documento diverso', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Scatta una foto',
    SUBTITLE: 'Centra il tuo viso e fai clic su Continua',
    INSTRUCTIONS: 'Usa la fotocamera frontale, guarda dritto davanti a te e centra il tuo viso.',
    BTN_CAPTURE: 'Acquisisci',
    BTN_RETAKE: 'Riprendi',
    BTN_ENABLE_CAMERA: 'Attiva fotocamera',
    PERMISSION_PROMPT: "Tocca \"Attiva fotocamera\" per consentire l'accesso alla fotocamera.",
    PERMISSION_DENIED: "L'accesso alla fotocamera è bloccato. Abilitalo nelle impostazioni del browser, poi tocca nuovamente Attiva fotocamera.",
    PERMISSION_UNSUPPORTED: 'Nessuna fotocamera trovata su questo dispositivo.',
    TOAST_CAMERA_ERROR: 'Errore di accesso alla fotocamera. Controlla la console!',
    BTN_RETRY_CAMERA: 'Riprova fotocamera',
    CAMERA_NOT_AVAILABLE: "La fotocamera non si è avviata. Tocca Riprova o ricarica la pagina.",
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Attiva fotocamera',
    BTN_START_CAMERA: 'Avvia fotocamera',
    BTN_TRY_NOW: 'Prova adesso',
    PERMISSION_PROMPT: "Tocca \"Attiva fotocamera\" per consentire l'accesso alla fotocamera.",
    PERMISSION_DENIED: "L'accesso alla fotocamera è bloccato. Abilitalo nelle impostazioni del browser, poi tocca nuovamente Attiva fotocamera.",
    PERMISSION_UNSUPPORTED: 'Nessuna fotocamera trovata su questo dispositivo.',
    ID_DRIVERS_LICENSE: 'Patente di guida',
    ID_STATE_ISSUED: "Carta d'identità",
    ID_PASSPORT: 'Passaporto',
    ID_OTHERS: 'Altro',
    ERROR_CAMERA_IN_USE: "La fotocamera è in uso. Chiudi l'altra applicazione e riprova.",
    ERROR_CAMERA_INIT_FAILED: 'Inizializzazione della fotocamera non riuscita. Controlla le autorizzazioni.',
    ERROR_CAMERA_BUSY_RETRY: "La fotocamera è ancora in uso. Chiudi l'altra applicazione e riprova.",
    ERROR_PLEASE_SCAN_DOCS: 'Scansiona il/i documento/i',
    SUBTITLE: 'Scansiona il documento selezionato di seguito.',
    LABEL_FRONT_SIDE: 'Fronte',
    LABEL_BACK_SIDE: 'Retro',
    INSTRUCTION_POSITION_FRONT: "Posiziona il fronte del documento d'identità",
    INSTRUCTION_POSITION_BACK: "Posiziona il retro del documento d'identità",
    MRZ_BAND_HINT: 'Allinea le due righe in basso della pagina con la foto sotto la linea tratteggiata',
    MRZ_SCAN_FAILED: 'Non è stato possibile leggere il passaporto. Tienilo fermo con le due righe in basso all\'interno della fascia e riprova.',
    MRZ_SCAN_ATTEMPT: 'Scansione {current} di {total}',
    GLARE_WARNING: 'Riflesso sul documento. Inclinalo leggermente o allontanati dalla luce.',
    BARCODE_BAND_HINT: 'Tieni la carta ferma, a circa 20 cm, con il codice a barre nella cornice',
    BARCODE_SCAN_FAILED: 'Non è stato possibile leggere il codice a barre. Tieni il retro della carta fermo e dritto all\'interno della cornice e riprova.',
    TORCH_ON: 'Accendi la luce',
    TORCH_OFF: 'Spegni la luce',
    BARCODE_READING_PHOTO: 'Lettura del codice a barre…',
    ID_CHECKING: 'Verifica del documento in corso…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Quasi fatto!',
    SUBTITLE: 'Guarda il video di seguito per continuare.',
    BTN_CHECK_IN: 'Accedi',
    BTN_FINISH: 'Fine',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registrazione trovata',
    SUBTITLE: "Verifica i tuoi dati per completare l'accesso.",
    BTN_NOT_YOU: 'Non sei tu?',
    MEETING_WITH_LABEL: 'Incontro con {hostName}',
    ONSITE_TITLE: 'Hai già effettuato il check-in',
    ONSITE_SUBTITLE: 'Seleziona la tua sessione per effettuare il check-out.',
    CHECKIN_AT_LABEL: 'Check-in effettuato alle:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: "Hai effettuato l'accesso!",
    WALLET_HINT: 'Il pass si aprirà automaticamente sul tuo dispositivo.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Pre-registrazione completata',
    ARRIVAL_INSTRUCTIONS_TITLE: "Istruzioni per l'arrivo",
    ARRIVAL_INSTRUCTIONS_TEXT: "Un codice QR è stato inviato all'e-mail registrata. Usalo per un accesso più rapido al chiosco al tuo arrivo.",
    QR_CAPTION: "Questo codice QR può essere scansionato nell'atrio per richiamare la tua registrazione.",
    BTN_SAVE_QR: 'Salva codice QR',
    WALLET_LABEL: 'Portafoglio',
    ACTION_ADD_PASS: 'Aggiungi pass',
    CALENDAR_LABEL: 'Calendario',
    ACTION_SAVE_EVENT: 'Salva evento',
    DEFAULT_WELCOME_MESSAGE: 'Il tuo ospite è stato avvisato e sarà presto con te.',
    PREFILL_SCHEDULED_FOR: 'È tutto pronto per il {date}.',
    PREFILL_HOST_NOTIFIED: "Il tuo ospite è stato avvisato e non vediamo l'ora di accoglierti.",
    TOAST_UPDATE_VISIT_ERROR: "Errore durante l'aggiornamento della visita. Riprova.",
    TOAST_GENERIC_ERROR: 'Si è verificato un errore. Riprova!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Dettagli della visita',
    PAGE_SUBTITLE: "Completa i passaggi di seguito per velocizzare l'accesso.",
    SECTION_HEADING: 'Completa la pre-registrazione',
    STEPS_COMPLETED_SUFFIX: 'passaggi completati',
    BTN_START: 'Iniziamo!',
  },
  SIGN_OUT: {
    EYEBROW: 'Esci',
    TITLE_WITH_NAME: 'Arrivederci,',
    TITLE_FALLBACK: 'Arrivederci!',
    SUBTITLE_SESSION: 'Conferma i tuoi dati ed esci.',
    STRIP_SUBTITLE: 'Attualmente connesso',
    LABEL_ORG: 'ORGANIZZAZIONE',
    LABEL_LOCATION: 'SEDE',
    LABEL_HOST: 'REFERENTE',
    LABEL_SIGNED_IN_AT: 'ACCESSO EFFETTUATO',
    BTN_SIGN_OUT: 'Esci',
    BTN_SIGNING_OUT: 'Uscita in corso...',
    BTN_START_OVER: 'Ricomincia',
    LABEL_NOT_YOU: 'Non sei tu?',
    TOAST_SUCCESS: 'Hai effettuato la disconnessione con successo!',
    TOAST_ERROR_FALLBACK: 'Errore durante la disconnessione. Riprova.',
    CUSTOM_FIELDS_EYEBROW: 'Quasi fatto',
    CUSTOM_FIELDS_TITLE: 'Completa la disconnessione',
    CUSTOM_FIELDS_SUBTITLE: 'Compila i dettagli richiesti.',
    BTN_CONFIRM_SIGN_OUT: 'Conferma disconnessione',
    BTN_GO_BACK: 'Torna indietro',
    SUCCESS_TITLE: 'Hai effettuato la disconnessione',
    SUCCESS_SUBTITLE: 'Grazie per la visita',
    BTN_DONE: 'Fatto',
    LABEL_VISIT_DURATION: 'Durata della visita',
    GEOLOCATION_UNAVAILABLE: "L'accesso alla posizione è necessario per effettuare l'uscita. Usa un dispositivo mobile.",
    LOCATION_PERMISSION_REQUIRED: "L'autorizzazione alla posizione è necessaria per effettuare l'uscita. Consenti l'accesso alla posizione e riprova.",
    MUST_BE_ON_SITE: "Devi essere in sede per effettuare l'uscita.",
    GUEST_PLACEHOLDER: 'Ospite',
    SUCCESS_CLOSING: 'Buona giornata!',
    STATUS_CHECKED_IN: 'Arrivato',
    STATUS_CHECKED_OUT: 'Uscito',
    DETAIL_LABEL_HOST: 'Referente',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Tutto pronto per la tua visita',
    ALL_SUB_TITLE: 'La tua pre-registrazione è completa. Puoi consultare i dettagli della visita di seguito.',
    BTN_CHECK_DETAILS: 'Controlla i miei dettagli',
    BTN_VIEW_CHECKIN: 'Visualizza accesso senza contatto',
    QR_TITLE: 'Accesso senza contatto',
    QR_SUBTITLE: "Punta la fotocamera sul codice QR mostrato sull'iPad o sul cartello stampato per continuare",
    ERROR_TITLE: 'Qualcosa è andato storto!',
    ERROR_SUBTITLE: 'Ci dispiace, si è verificato un errore. Riprova più tardi.',
    DETAIL_LABEL_VISITOR: 'Visitatore',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Referente',
    DETAIL_HOST_PLACEHOLDER: 'Da definire',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Collegamento per la pre-registrazione scaduto',
    SUBTITLE: 'Questo collegamento non è più valido',
    REASONS_HEADING: 'Possibili motivi:',
    REASON_CANCELLED: 'Invito annullato',
    REASON_CHECKED_IN: 'Già registrato',
    REASON_PAST: "L'orario programmato è passato",
    HELP_PROMPT: 'Hai bisogno di aiuto?',
    HELP_TEXT: 'Contatta il tuo referente o la reception',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Collegamento pass non valido.',
    PASS_REVOKED_OR_EXPIRED: 'Questo pass è stato revocato o è scaduto.',
    UNABLE_TO_LOAD: 'Impossibile caricare la pagina di accesso.',
    PASS_EXPIRED_TITLE: 'Pass scaduto',
    CONTACT_HOST_FOR_NEW_PASS: 'Contatta il tuo referente per un nuovo SmartPass.',
    ERROR_TITLE: 'Qualcosa è andato storto',
    BTN_TRY_AGAIN: 'Riprova',
    STATUS_NOT_ON_SITE: 'Non in sede',
    STATUS_CHECKED_IN: 'Accesso effettuato',
    SELECT_LOCATION_LABEL: 'Seleziona la tua sede:',
    SELECT_LOCATION_PLACEHOLDER: '-- Seleziona sede --',
    BTN_PROCESSING: 'Elaborazione...',
    BTN_CHECK_IN: 'Accedi',
    BTN_CHECK_OUT: 'Esci',
    BTN_CLOSE_HINT: 'Puoi chiudere questa pagina.',
    POWERED_BY: 'Sviluppato da Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Collegamento pass non valido.',
    GOOGLE_WALLET_COMING_SOON: 'Il supporto per Google Wallet arriverà presto. Ricontrolla più tardi.',
    OPEN_ON_MOBILE: 'Apri questo collegamento sul tuo iPhone o telefono Android per aggiungere il pass al tuo portafoglio.',
    PREPARING_PASS: 'Preparazione del tuo SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Collegamento pass non valido.',
    LOCATION_REQUIRED: "L'accesso con SmartPass richiede la posizione. Apri questo collegamento sul tuo dispositivo mobile.",
    LOCATION_PERMISSION_REQUIRED: "L'autorizzazione alla posizione è necessaria per l'accesso con SmartPass. Consenti l'accesso alla posizione e riprova.",
    LOCATION_UNAVAILABLE: 'Impossibile determinare la tua posizione. Assicurati che i servizi di localizzazione siano abilitati e riprova.',
    PASS_REVOKED_OR_EXPIRED: 'Questo pass è stato revocato o è scaduto.',
    OUTSIDE_GEOFENCE: "Sei fuori dall'area di accesso. Avvicinati all'edificio e riprova.",
    TITLE: 'SmartPass',
    BTN_RETRY: 'Riprova',
    OPENING_CHECK_IN: 'Apertura accesso in corso...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'EVACUAZIONE IN CORSO IN QUESTA SEDE',
    BTN_TAP_TO_SIGNIN: 'Tocca per accedere',
    BTN_SCAN_QR_CODE: 'Scansiona codice QR',
    TOAST_INVALID_QR: 'Codice QR non valido o scaduto. Riprova!',
  },
  FOOTER: { POWERED_BY: 'SVILUPPATO DA ' },
  HEADER: { STEP: 'PASSO ', OF: ' DI ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Tocca per iniziare' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Codice QR non valido. Riprova!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Utilizziamo la fotocamera solo per leggere il codice QR della hall e non memorizziamo alcun video',
    PERMISSION_PROMPT: "Tocca \"Attiva fotocamera\" per consentire l'accesso alla fotocamera.",
    PERMISSION_DENIED: "L'accesso alla fotocamera è bloccato. Abilitalo nelle impostazioni del browser, poi tocca nuovamente Attiva fotocamera.",
    PERMISSION_UNSUPPORTED: 'Nessuna fotocamera trovata su questo dispositivo.',
    CAMERA_NOT_AVAILABLE: "La fotocamera non si è avviata. Tocca Riprova o ricarica la pagina.",
    ERROR_CAMERA_IN_USE: "La fotocamera è in uso. Chiudi l'altra applicazione e riprova.",
    ERROR_CAMERA_INIT_FAILED: 'Inizializzazione della fotocamera non riuscita. Controlla le autorizzazioni.',
    ERROR_CAMERA_BUSY_RETRY: "La fotocamera è ancora in uso. Chiudi l'altra applicazione e riprova.",
  },
  CAMERA_PERMISSION: {
    REQUIRED: "L'autorizzazione alla fotocamera è necessaria per scansionare i codici QR.",
    ACCESS_DENIED: 'Accesso alla fotocamera negato o non disponibile',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Sei sicuro di voler annullare? Perderai tutti i tuoi progressi.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Seleziona referente',        prefillTitle: 'Seleziona referente',        prefillDesc: 'Scegli chi stai visitando' },
    DETAILS:    { headerTitle: 'I tuoi dettagli',            prefillTitle: 'I tuoi dettagli',            prefillDesc: 'Fornisci le tue informazioni di contatto' },
    DOCUMENT:   { headerTitle: 'Firma documenti',            prefillTitle: 'Firma documenti',            prefillDesc: 'Esamina e firma i documenti richiesti' },
    ID_CAPTURE: { headerTitle: 'Verifica identità',          prefillTitle: 'Verifica identità',          prefillDesc: "Scansiona il tuo documento d'identità" },
    PHOTO:      { headerTitle: 'Scatta un selfie',           prefillTitle: 'Scatta un selfie',           prefillDesc: 'Acquisisci la tua foto' },
    VIDEO:      { headerTitle: 'Guarda un video',            prefillTitle: 'Guarda un video',            prefillDesc: 'Visualizza un video di sicurezza o orientamento' },
  },
};

// ── Japanese ───────────────────────────────────────────────────────────────────
export const JA_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: '非接触サインイン | Visitly',
    PRE_ARRIVAL: '事前到着登録 | Visitly',
  },
  HOME: {
    TITLE: '非接触サインイン',
    SUBTITLE: 'iPadまたは印刷されたサインに表示されているQRコードにカメラを向けて続行してください',
  },
  COMMON: {
    BACK: '戻る',
    CONTINUE: '続行',
    AGREE_AND_CONTINUE: '同意して続行',
    DECLINE: '拒否',
    CLEAR_SIGNATURE: '署名をクリア',
    LOADING: '読み込み中...',
    SEARCHING: '検索中...',
    NO: 'いいえ',
    YES: 'はい',
    ENABLE_CAMERA: 'カメラを有効にする',
    PAGE_NOT_FOUND: '404 - ページが見つかりません',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: '撮り直す',
    CAPTURE: '撮影',
    RETRY: '再試行',
    POINT_OF_ENTRY: '入口',
    PARKING_LOT: '駐車場',
    DESTINATION: 'ビル',
    ERROR_PROCESSING_REQUEST: 'リクエストの処理中にエラーが発生しました。もう一度お試しください。',
    SESSION_EXPIRED: 'セッションが期限切れです。メールのリンクを再度開いてください。',
  },
  FULL_NAME: {
    TITLE: 'フルネームを入力してください',
    SUBTITLE: '本日ご来訪の方のお名前を確認するためにお聞きしています',
    FORM_LABEL: 'フルネーム',
    PLACEHOLDER: '例：山田 太郎',
  },
  VISITOR_TYPES: {
    SUBTITLE: '訪問の種類を選択してください。',
    FALLBACK_TITLE: '本日のご用件は何ですか？',
  },
  SELECT_HOST: {
    TITLE: '誰を訪問しますか？',
    SUBTITLE: 'ホストを検索して到着を通知してください。',
    PLACEHOLDER: 'ホストを選択',
    SEARCH_PLACEHOLDER: 'ホストを検索...',
    SEARCH_PLACEHOLDER_ALT: '名前でホストを検索...',
    NO_HOSTS_FOUND: 'ホストが見つかりません',
    MIN_CHARS_MESSAGE: '検索するには3文字以上入力してください',
  },
  VISITOR_FORM: {
    TITLE: '訪問詳細',
    SUBTITLE: '以下の必要事項を入力してください。',
    SELECT_FIELD_PLACEHOLDER: '{field}を選択',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'フルネームを入力してください',
    EMAIL_REQUIRED: '有効なメールアドレスを入力してください',
    EMAIL_INVALID: '有効なメールアドレスを入力してください',
    HOST_REQUIRED: 'ホスト名を選択してください',
    COMPANY_NAME_REQUIRED: '会社名を入力してください',
    PHONE_NUMBER_REQUIRED: '電話番号を入力してください',
    CUSTOM_FIELD_REQUIRED: '{field}を入力してください',
    FIELD_REQUIRED_TEMPLATE: '{field}は必須です',
    FULL_NAME_INVALID: '有効な名前を入力してください。',
    TOAST_ERROR_PROCESSING: 'リクエストの処理中にエラーが発生しました。もう一度お試しください。',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: '以下の書類を確認して署名してください',
    ERROR_DOCUMENT_EXPIRY: '書類の有効期限の確認中にエラーが発生しました',
    ERROR_LOADING_CONTENT: '書類の内容の読み込み中にエラーが発生しました',
    ERROR_FETCHING_DOCUMENT: '書類の取得中にエラーが発生しました',
    ERROR_RESTORE_SESSION: '書類署名セッションを復元できませんでした。ホームページから再度お試しください。',
    BTN_COLLAPSE: '折りたたむ',
    BTN_EXPAND: '展開する',
  },
  PRIVACY_POLICY: {
    TITLE: '続行する前に',
    SUBTITLE: '続行する前に確認して同意してください',
    IMPLICIT_CONSENT: '以下の続行ボタンをクリックすることにより、{orgName}がリクエストしたコンテンツを提供するために、上記で送信された個人情報を保存および処理することに同意します。',
    EXPLICIT_CONSENT: '{orgName}が私の個人データを保存および処理することに同意します。',
  },
  CAPTURE_PICTURE: {
    TITLE: 'IDを選択',
    SUBTITLE: 'アップロードするIDの種類を選択してください',
    ID_TYPES: [
      { label: 'パスポート', value: 'PASSPORT' },
      { label: '運転免許証', value: 'DRIVING_LICENCE' },
      { label: '公的身分証明書', value: 'STATE_ISSUED_ID' },
      { label: '別のIDを持っています', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: '写真を撮る',
    SUBTITLE: '顔を中央に合わせて続行ボタンをクリックしてください',
    INSTRUCTIONS: 'フロントカメラを使用し、正面を向いて顔を中央に合わせてください。',
    BTN_CAPTURE: '撮影',
    BTN_RETAKE: '撮り直す',
    BTN_ENABLE_CAMERA: 'カメラを有効にする',
    PERMISSION_PROMPT: '「カメラを有効にする」をタップしてカメラアクセスを許可してください。',
    PERMISSION_DENIED: 'カメラアクセスがブロックされています。ブラウザの設定で有効にしてから、「カメラを有効にする」をタップしてください。',
    PERMISSION_UNSUPPORTED: 'このデバイスにカメラが見つかりません。',
    TOAST_CAMERA_ERROR: 'カメラへのアクセスエラーです。コンソールを確認してください！',
    BTN_RETRY_CAMERA: 'カメラを再試行',
    CAMERA_NOT_AVAILABLE: 'カメラを開始できませんでした。再試行をタップするか、ページを再読み込みしてください。',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'カメラを有効にする',
    BTN_START_CAMERA: 'カメラを起動',
    BTN_TRY_NOW: '今すぐ試す',
    PERMISSION_PROMPT: '「カメラを有効にする」をタップしてカメラアクセスを許可してください。',
    PERMISSION_DENIED: 'カメラアクセスがブロックされています。ブラウザの設定で有効にしてから、「カメラを有効にする」をタップしてください。',
    PERMISSION_UNSUPPORTED: 'このデバイスにカメラが見つかりません。',
    ID_DRIVERS_LICENSE: '運転免許証',
    ID_STATE_ISSUED: '公的身分証明書',
    ID_PASSPORT: 'パスポート',
    ID_OTHERS: 'その他',
    ERROR_CAMERA_IN_USE: 'カメラが使用中です。他のアプリを閉じてもう一度お試しください。',
    ERROR_CAMERA_INIT_FAILED: 'カメラの初期化に失敗しました。権限を確認してください。',
    ERROR_CAMERA_BUSY_RETRY: 'カメラはまだ使用中です。他のアプリを閉じてもう一度お試しください。',
    ERROR_PLEASE_SCAN_DOCS: '書類をスキャンしてください',
    SUBTITLE: '選択した書類をスキャンしてください。',
    LABEL_FRONT_SIDE: '表面',
    LABEL_BACK_SIDE: '裏面',
    INSTRUCTION_POSITION_FRONT: 'IDの表面を配置してください',
    INSTRUCTION_POSITION_BACK: 'IDの裏面を配置してください',
    MRZ_BAND_HINT: '写真ページ下部の2行を点線の下に合わせてください',
    MRZ_SCAN_FAILED: 'パスポートを読み取れませんでした。下部の2行が帯の中に入るようにしっかり持ち、もう一度お試しください。',
    MRZ_SCAN_ATTEMPT: 'スキャン {current}/{total}',
    GLARE_WARNING: '書類に光が反射しています。少し傾けるか、光から離れてください。',
    BARCODE_BAND_HINT: 'カードを約20cm離して動かさずに持ち、バーコードを枠内に収めてください',
    BARCODE_SCAN_FAILED: 'バーコードを読み取れませんでした。カードの裏面を枠内でまっすぐ、しっかり持ち、もう一度お試しください。',
    TORCH_ON: 'ライトをつける',
    TORCH_OFF: 'ライトを消す',
    BARCODE_READING_PHOTO: 'バーコードを読み取っています…',
    ID_CHECKING: '身分証明書を確認しています…',
  },
  VISITOR_VIDEO: {
    TITLE: 'もう少しです！',
    SUBTITLE: '続行するには以下の動画をご覧ください。',
    BTN_CHECK_IN: 'チェックイン',
    BTN_FINISH: '完了',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: '登録が見つかりました',
    SUBTITLE: 'チェックインを完了するために詳細を確認してください。',
    BTN_NOT_YOU: '別の方ですか？',
    MEETING_WITH_LABEL: '{hostName}との面会',
    ONSITE_TITLE: 'すでにチェックインしています',
    ONSITE_SUBTITLE: 'サインアウトするセッションを選択してください。',
    CHECKIN_AT_LABEL: 'チェックイン時刻:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'チェックインが完了しました！',
    WALLET_HINT: 'パスがデバイスに自動的に開きます。',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: '事前到着登録が完了しました',
    ARRIVAL_INSTRUCTIONS_TITLE: '到着時のご案内',
    ARRIVAL_INSTRUCTIONS_TEXT: '登録済みのメールアドレスにQRコードが送信されました。到着時にキオスクでのチェックインを素早く行うためにご利用ください。',
    QR_CAPTION: 'このQRコードをロビーでスキャンすることで登録情報を確認できます。',
    BTN_SAVE_QR: 'QRコードを保存',
    WALLET_LABEL: 'ウォレット',
    ACTION_ADD_PASS: 'パスを追加',
    CALENDAR_LABEL: 'カレンダー',
    ACTION_SAVE_EVENT: 'イベントを保存',
    DEFAULT_WELCOME_MESSAGE: 'ホストに通知されました。まもなくお越しします。',
    PREFILL_SCHEDULED_FOR: '{date} のご準備が整いました。',
    PREFILL_HOST_NOTIFIED: 'ホストに通知されました。お会いできることを楽しみにしています。',
    TOAST_UPDATE_VISIT_ERROR: '訪問の更新中にエラーが発生しました。もう一度お試しください。',
    TOAST_GENERIC_ERROR: 'エラーが発生しました。もう一度お試しください！',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: '訪問詳細',
    PAGE_SUBTITLE: '以下の手順を完了してチェックインを迅速化してください。',
    SECTION_HEADING: '事前到着登録を完了してください',
    STEPS_COMPLETED_SUFFIX: 'ステップ完了',
    BTN_START: 'さあ始めましょう！',
  },
  SIGN_OUT: {
    EYEBROW: 'サインアウト',
    TITLE_WITH_NAME: 'さようなら、',
    TITLE_FALLBACK: 'さようなら！',
    SUBTITLE_SESSION: '詳細を確認してサインアウトしてください。',
    STRIP_SUBTITLE: '現在サインイン中',
    LABEL_ORG: '組織',
    LABEL_LOCATION: '場所',
    LABEL_HOST: 'ホスト',
    LABEL_SIGNED_IN_AT: 'サインイン時刻',
    BTN_SIGN_OUT: 'サインアウト',
    BTN_SIGNING_OUT: 'サインアウト中...',
    BTN_START_OVER: '最初からやり直す',
    LABEL_NOT_YOU: '別の方ですか？',
    TOAST_SUCCESS: '正常にサインアウトしました！',
    TOAST_ERROR_FALLBACK: 'サインアウト中にエラーが発生しました。もう一度お試しください。',
    CUSTOM_FIELDS_EYEBROW: 'もう少しで完了',
    CUSTOM_FIELDS_TITLE: 'サインアウトを完了',
    CUSTOM_FIELDS_SUBTITLE: '必要な詳細を入力してください。',
    BTN_CONFIRM_SIGN_OUT: 'サインアウトを確認',
    BTN_GO_BACK: '戻る',
    SUCCESS_TITLE: 'サインアウトが完了しました',
    SUCCESS_SUBTITLE: 'ご来訪ありがとうございました',
    BTN_DONE: '完了',
    LABEL_VISIT_DURATION: '訪問時間',
    GEOLOCATION_UNAVAILABLE: 'チェックアウトには位置情報へのアクセスが必要です。モバイルデバイスをご使用ください。',
    LOCATION_PERMISSION_REQUIRED: 'チェックアウトには位置情報の許可が必要です。位置情報アクセスを許可してもう一度お試しください。',
    MUST_BE_ON_SITE: 'チェックアウトするにはオンサイトにいる必要があります。',
    GUEST_PLACEHOLDER: 'ゲスト',
    SUCCESS_CLOSING: '良い一日をお過ごしください！',
    STATUS_CHECKED_IN: 'チェックイン済み',
    STATUS_CHECKED_OUT: 'チェックアウト済み',
    DETAIL_LABEL_HOST: 'ホスト',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'ご訪問の準備が整いました',
    ALL_SUB_TITLE: '事前到着登録が完了しました。以下で訪問詳細を確認できます。',
    BTN_CHECK_DETAILS: '詳細を確認する',
    BTN_VIEW_CHECKIN: '非接触チェックインを表示',
    QR_TITLE: '非接触サインイン',
    QR_SUBTITLE: 'iPadまたは印刷されたサインに表示されているQRコードにカメラを向けて続行してください',
    ERROR_TITLE: '問題が発生しました！',
    ERROR_SUBTITLE: '申し訳ありません。エラーが発生しました。後でもう一度お試しください。',
    DETAIL_LABEL_VISITOR: '訪問者',
    DETAIL_LABEL_EMAIL: 'メール',
    DETAIL_LABEL_HOST: 'ホスト',
    DETAIL_HOST_PLACEHOLDER: '未定',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: '事前到着登録リンクの有効期限が切れました',
    SUBTITLE: 'このリンクは無効です',
    REASONS_HEADING: '考えられる理由：',
    REASON_CANCELLED: '招待がキャンセルされました',
    REASON_CHECKED_IN: 'すでにチェックイン済みです',
    REASON_PAST: '予定時刻が過ぎました',
    HELP_PROMPT: 'ヘルプが必要ですか？',
    HELP_TEXT: 'ホストまたは受付にお問い合わせください',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: '無効なパスリンクです。',
    PASS_REVOKED_OR_EXPIRED: 'このパスは失効または期限切れです。',
    UNABLE_TO_LOAD: 'チェックインページを読み込めません。',
    PASS_EXPIRED_TITLE: 'パスの有効期限切れ',
    CONTACT_HOST_FOR_NEW_PASS: '新しいSmartPassについてはホストにお問い合わせください。',
    ERROR_TITLE: '問題が発生しました',
    BTN_TRY_AGAIN: '再試行',
    STATUS_NOT_ON_SITE: 'オフサイト',
    STATUS_CHECKED_IN: 'チェックイン済み',
    SELECT_LOCATION_LABEL: '場所を選択してください：',
    SELECT_LOCATION_PLACEHOLDER: '-- 場所を選択 --',
    BTN_PROCESSING: '処理中...',
    BTN_CHECK_IN: 'チェックイン',
    BTN_CHECK_OUT: 'チェックアウト',
    BTN_CLOSE_HINT: 'このページを閉じることができます。',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: '無効なパスリンクです。',
    GOOGLE_WALLET_COMING_SOON: 'Google Walletのサポートは近日公開予定です。後でご確認ください。',
    OPEN_ON_MOBILE: 'パスをウォレットに追加するには、iPhoneまたはAndroidでこのリンクを開いてください。',
    PREPARING_PASS: 'SmartPassを準備しています...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: '無効なパスリンクです。',
    LOCATION_REQUIRED: 'SmartPassチェックインには位置情報アクセスが必要です。モバイルデバイスでこのリンクを開いてください。',
    LOCATION_PERMISSION_REQUIRED: 'SmartPassチェックインには位置情報の許可が必要です。位置情報アクセスを許可してもう一度お試しください。',
    LOCATION_UNAVAILABLE: '現在地を確認できません。位置情報サービスが有効になっていることを確認してもう一度お試しください。',
    PASS_REVOKED_OR_EXPIRED: 'このパスは失効または期限切れです。',
    OUTSIDE_GEOFENCE: 'チェックインエリア外にいます。建物に近づいてもう一度お試しください。',
    TITLE: 'SmartPass',
    BTN_RETRY: '再試行',
    OPENING_CHECK_IN: 'チェックインを開いています...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'この場所で避難が進行中です',
    BTN_TAP_TO_SIGNIN: 'タップしてサインイン',
    BTN_SCAN_QR_CODE: 'QRコードをスキャン',
    TOAST_INVALID_QR: '無効または期限切れのQRコードです。もう一度お試しください！',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: 'ステップ ', OF: ' / ' },
  SPLASH_SCREEN: { TAP_TO_START: 'タップして開始' },
  QR_SCANNER: { INVALID_CODE_ERROR: '無効なQRコードです。もう一度お試しください！' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'カメラはロビーのQRコードの読み取りのみに使用し、動画は保存しません',
    PERMISSION_PROMPT: '「カメラを有効にする」をタップしてカメラアクセスを許可してください。',
    PERMISSION_DENIED: 'カメラアクセスがブロックされています。ブラウザの設定で有効にしてから、「カメラを有効にする」をタップしてください。',
    PERMISSION_UNSUPPORTED: 'このデバイスにカメラが見つかりません。',
    CAMERA_NOT_AVAILABLE: 'カメラを開始できませんでした。再試行をタップするか、ページを再読み込みしてください。',
    ERROR_CAMERA_IN_USE: 'カメラが使用中です。他のアプリを閉じてもう一度お試しください。',
    ERROR_CAMERA_INIT_FAILED: 'カメラの初期化に失敗しました。権限を確認してください。',
    ERROR_CAMERA_BUSY_RETRY: 'カメラはまだ使用中です。他のアプリを閉じてもう一度お試しください。',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'QRコードをスキャンするにはカメラの許可が必要です。',
    ACCESS_DENIED: 'カメラアクセスが拒否されたか、利用できません',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'キャンセルしてもよろしいですか？すべての進捗が失われます。' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'ホストを選択',         prefillTitle: 'ホストを選択',         prefillDesc: '訪問先を選択してください' },
    DETAILS:    { headerTitle: '個人情報',             prefillTitle: '個人情報',             prefillDesc: '連絡先情報を入力してください' },
    DOCUMENT:   { headerTitle: '書類に署名',           prefillTitle: '書類に署名',           prefillDesc: '必要な書類を確認して署名してください' },
    ID_CAPTURE: { headerTitle: 'IDを確認',             prefillTitle: 'IDを確認',             prefillDesc: 'IDをスキャンしてください' },
    PHOTO:      { headerTitle: '自撮りを撮る',         prefillTitle: '自撮りを撮る',         prefillDesc: '写真を撮影してください' },
    VIDEO:      { headerTitle: '動画を視聴',           prefillTitle: '動画を視聴',           prefillDesc: '安全またはオリエンテーション動画をご覧ください' },
  },
};

// ── Korean ─────────────────────────────────────────────────────────────────────
export const KO_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: '비접촉 로그인 | Visitly',
    PRE_ARRIVAL: '사전 도착 등록 | Visitly',
  },
  HOME: {
    TITLE: '비접촉 로그인',
    SUBTITLE: 'iPad 또는 인쇄된 안내판에 표시된 QR 코드에 카메라를 향하여 계속 진행하세요',
  },
  COMMON: {
    BACK: '뒤로',
    CONTINUE: '계속',
    AGREE_AND_CONTINUE: '동의하고 계속',
    DECLINE: '거부',
    CLEAR_SIGNATURE: '서명 지우기',
    LOADING: '로딩 중...',
    SEARCHING: '검색 중...',
    NO: '아니오',
    YES: '예',
    ENABLE_CAMERA: '카메라 활성화',
    PAGE_NOT_FOUND: '404 - 페이지를 찾을 수 없습니다',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: '다시 찍기',
    CAPTURE: '촬영',
    RETRY: '다시 시도',
    POINT_OF_ENTRY: '입구',
    PARKING_LOT: '주차장',
    DESTINATION: '건물',
    ERROR_PROCESSING_REQUEST: '요청 처리 중 오류가 발생했습니다. 다시 시도해 주세요.',
    SESSION_EXPIRED: '세션이 만료되었습니다. 이메일의 링크를 다시 열어 주세요.',
  },
  FULL_NAME: {
    TITLE: '성함을 입력해 주세요',
    SUBTITLE: '오늘 방문하시는 분을 확인하기 위해 여쭤보고 있습니다',
    FORM_LABEL: '성명',
    PLACEHOLDER: '예: 홍길동',
  },
  VISITOR_TYPES: {
    SUBTITLE: '방문 유형을 선택해 주세요.',
    FALLBACK_TITLE: '방문 목적이 무엇인가요?',
  },
  SELECT_HOST: {
    TITLE: '누구를 방문하시나요?',
    SUBTITLE: '호스트를 검색하여 도착을 알려주세요.',
    PLACEHOLDER: '호스트 선택',
    SEARCH_PLACEHOLDER: '호스트 검색...',
    SEARCH_PLACEHOLDER_ALT: '이름으로 호스트 검색...',
    NO_HOSTS_FOUND: '호스트를 찾을 수 없습니다',
    MIN_CHARS_MESSAGE: '검색하려면 최소 3자를 입력해 주세요',
  },
  VISITOR_FORM: {
    TITLE: '방문 세부 정보',
    SUBTITLE: '아래에 필요한 정보를 입력해 주세요.',
    SELECT_FIELD_PLACEHOLDER: '{field} 선택',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: '성명을 입력해 주세요',
    EMAIL_REQUIRED: '유효한 이메일 주소를 입력해 주세요',
    EMAIL_INVALID: '유효한 이메일 주소를 입력해 주세요',
    HOST_REQUIRED: '호스트 이름을 선택해 주세요',
    COMPANY_NAME_REQUIRED: '회사명을 입력해 주세요',
    PHONE_NUMBER_REQUIRED: '전화번호를 입력해 주세요',
    CUSTOM_FIELD_REQUIRED: '{field}을(를) 입력해 주세요',
    FIELD_REQUIRED_TEMPLATE: '{field}은(는) 필수입니다',
    FULL_NAME_INVALID: '유효한 이름을 입력해 주세요.',
    TOAST_ERROR_PROCESSING: '요청 처리 중 오류가 발생했습니다. 다시 시도해 주세요.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: '아래 문서를 검토하고 서명해 주세요',
    ERROR_DOCUMENT_EXPIRY: '문서 만료 확인 중 오류가 발생했습니다',
    ERROR_LOADING_CONTENT: '문서 내용 로딩 중 오류가 발생했습니다',
    ERROR_FETCHING_DOCUMENT: '문서 가져오기 중 오류가 발생했습니다',
    ERROR_RESTORE_SESSION: '문서 서명 세션을 복원할 수 없습니다. 홈 페이지에서 다시 시작해 주세요.',
    BTN_COLLAPSE: '접기',
    BTN_EXPAND: '펼치기',
  },
  PRIVACY_POLICY: {
    TITLE: '계속하기 전에',
    SUBTITLE: '계속하기 전에 검토하고 동의해 주세요',
    IMPLICIT_CONSENT: '아래 계속 버튼을 클릭하면, {orgName}이(가) 요청하신 콘텐츠를 제공하기 위해 위에 제출된 개인 정보를 저장하고 처리하는 것에 동의하게 됩니다.',
    EXPLICIT_CONSENT: '{orgName}이(가) 제 개인 정보를 저장하고 처리하는 것에 동의합니다.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'ID 선택',
    SUBTITLE: '업로드할 ID 유형을 선택해 주세요',
    ID_TYPES: [
      { label: '여권', value: 'PASSPORT' },
      { label: '운전면허증', value: 'DRIVING_LICENCE' },
      { label: '정부 발급 신분증', value: 'STATE_ISSUED_ID' },
      { label: '다른 ID가 있습니다', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: '사진 촬영',
    SUBTITLE: '얼굴을 중앙에 맞추고 계속 버튼을 클릭해 주세요',
    INSTRUCTIONS: '전면 카메라를 사용하고 정면을 바라보며 얼굴을 중앙에 맞춰 주세요.',
    BTN_CAPTURE: '촬영',
    BTN_RETAKE: '다시 찍기',
    BTN_ENABLE_CAMERA: '카메라 활성화',
    PERMISSION_PROMPT: '"카메라 활성화"를 탭하여 카메라 접근을 허용해 주세요.',
    PERMISSION_DENIED: '카메라 접근이 차단되었습니다. 브라우저 설정에서 활성화한 후 다시 "카메라 활성화"를 탭해 주세요.',
    PERMISSION_UNSUPPORTED: '이 기기에서 카메라를 찾을 수 없습니다.',
    TOAST_CAMERA_ERROR: '카메라 접근 오류입니다. 콘솔을 확인해 주세요!',
    BTN_RETRY_CAMERA: '카메라 재시도',
    CAMERA_NOT_AVAILABLE: '카메라를 시작할 수 없습니다. 재시도를 탭하거나 페이지를 새로 고침하세요.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: '카메라 활성화',
    BTN_START_CAMERA: '카메라 시작',
    BTN_TRY_NOW: '지금 시도',
    PERMISSION_PROMPT: '"카메라 활성화"를 탭하여 카메라 접근을 허용해 주세요.',
    PERMISSION_DENIED: '카메라 접근이 차단되었습니다. 브라우저 설정에서 활성화한 후 다시 "카메라 활성화"를 탭해 주세요.',
    PERMISSION_UNSUPPORTED: '이 기기에서 카메라를 찾을 수 없습니다.',
    ID_DRIVERS_LICENSE: '운전면허증',
    ID_STATE_ISSUED: '정부 발급 신분증',
    ID_PASSPORT: '여권',
    ID_OTHERS: '기타',
    ERROR_CAMERA_IN_USE: '카메라가 사용 중입니다. 다른 앱을 닫고 다시 시도해 주세요.',
    ERROR_CAMERA_INIT_FAILED: '카메라 초기화에 실패했습니다. 권한을 확인해 주세요.',
    ERROR_CAMERA_BUSY_RETRY: '카메라가 아직 사용 중입니다. 다른 앱을 닫고 다시 시도해 주세요.',
    ERROR_PLEASE_SCAN_DOCS: '문서를 스캔해 주세요',
    SUBTITLE: '선택한 문서를 아래에서 스캔해 주세요.',
    LABEL_FRONT_SIDE: '앞면',
    LABEL_BACK_SIDE: '뒷면',
    INSTRUCTION_POSITION_FRONT: 'ID 앞면을 위치시켜 주세요',
    INSTRUCTION_POSITION_BACK: 'ID 뒷면을 위치시켜 주세요',
    MRZ_BAND_HINT: '사진 페이지 하단의 두 줄을 점선 아래에 맞춰 주세요',
    MRZ_SCAN_FAILED: '여권을 읽을 수 없습니다. 하단의 두 줄이 띠 안에 들어오도록 흔들리지 않게 잡고 다시 시도하세요.',
    MRZ_SCAN_ATTEMPT: '스캔 {current}/{total}',
    GLARE_WARNING: '문서에 빛 반사가 있습니다. 살짝 기울이거나 빛에서 멀어지세요.',
    BARCODE_BAND_HINT: '카드를 약 20cm 떨어뜨려 흔들리지 않게 잡고 바코드를 프레임 안에 맞춰 주세요',
    BARCODE_SCAN_FAILED: '바코드를 읽을 수 없습니다. 카드 뒷면을 프레임 안에서 수평으로 흔들리지 않게 잡고 다시 시도하세요.',
    TORCH_ON: '조명 켜기',
    TORCH_OFF: '조명 끄기',
    BARCODE_READING_PHOTO: '바코드를 읽는 중…',
    ID_CHECKING: '신분증을 확인하는 중…',
  },
  VISITOR_VIDEO: {
    TITLE: '거의 다 왔어요!',
    SUBTITLE: '계속하려면 아래 동영상을 시청해 주세요.',
    BTN_CHECK_IN: '체크인',
    BTN_FINISH: '완료',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: '등록이 확인되었습니다',
    SUBTITLE: '체크인을 완료하려면 세부 정보를 확인해 주세요.',
    BTN_NOT_YOU: '본인이 아닌가요?',
    MEETING_WITH_LABEL: '{hostName}와(과) 미팅',
    ONSITE_TITLE: '이미 체크인되어 있습니다',
    ONSITE_SUBTITLE: '로그아웃할 세션을 선택하세요.',
    CHECKIN_AT_LABEL: '체크인 시간:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: '체크인이 완료되었습니다!',
    WALLET_HINT: '패스가 기기에서 자동으로 열립니다.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: '사전 도착 등록이 완료되었습니다',
    ARRIVAL_INSTRUCTIONS_TITLE: '도착 안내',
    ARRIVAL_INSTRUCTIONS_TEXT: '등록된 이메일로 QR 코드가 전송되었습니다. 도착 시 키오스크에서 빠른 체크인을 위해 사용해 주세요.',
    QR_CAPTION: '이 QR 코드를 로비에서 스캔하면 등록 정보를 확인할 수 있습니다.',
    BTN_SAVE_QR: 'QR 코드 저장',
    WALLET_LABEL: '지갑',
    ACTION_ADD_PASS: '패스 추가',
    CALENDAR_LABEL: '캘린더',
    ACTION_SAVE_EVENT: '일정 저장',
    DEFAULT_WELCOME_MESSAGE: '호스트에게 알림이 전송되었으며 곧 도착합니다.',
    PREFILL_SCHEDULED_FOR: '{date} 방문 준비가 완료되었습니다.',
    PREFILL_HOST_NOTIFIED: '호스트에게 알림이 전송되었으며 방문을 기다리고 있습니다.',
    TOAST_UPDATE_VISIT_ERROR: '방문 업데이트 중 오류가 발생했습니다. 다시 시도해 주세요.',
    TOAST_GENERIC_ERROR: '오류가 발생했습니다. 다시 시도해 주세요!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: '방문 세부 정보',
    PAGE_SUBTITLE: '아래 단계를 완료하여 체크인을 신속히 진행하세요.',
    SECTION_HEADING: '사전 도착 등록을 완료해 주세요',
    STEPS_COMPLETED_SUFFIX: '단계 완료',
    BTN_START: '시작해 봅시다!',
  },
  SIGN_OUT: {
    EYEBROW: '로그아웃',
    TITLE_WITH_NAME: '안녕히 가세요,',
    TITLE_FALLBACK: '안녕히 가세요!',
    SUBTITLE_SESSION: '세부 정보를 확인하고 로그아웃하세요.',
    STRIP_SUBTITLE: '현재 로그인 중',
    LABEL_ORG: '조직',
    LABEL_LOCATION: '위치',
    LABEL_HOST: '호스트',
    LABEL_SIGNED_IN_AT: '로그인 시간',
    BTN_SIGN_OUT: '로그아웃',
    BTN_SIGNING_OUT: '로그아웃 중...',
    BTN_START_OVER: '처음부터 다시',
    LABEL_NOT_YOU: '본인이 아닌가요?',
    TOAST_SUCCESS: '성공적으로 로그아웃되었습니다!',
    TOAST_ERROR_FALLBACK: '로그아웃 중 오류가 발생했습니다. 다시 시도해 주세요.',
    CUSTOM_FIELDS_EYEBROW: '거의 완료',
    CUSTOM_FIELDS_TITLE: '로그아웃 완료',
    CUSTOM_FIELDS_SUBTITLE: '필요한 세부 정보를 입력해 주세요.',
    BTN_CONFIRM_SIGN_OUT: '로그아웃 확인',
    BTN_GO_BACK: '뒤로',
    SUCCESS_TITLE: '로그아웃이 완료되었습니다',
    SUCCESS_SUBTITLE: '방문해 주셔서 감사합니다',
    BTN_DONE: '완료',
    LABEL_VISIT_DURATION: '방문 시간',
    GEOLOCATION_UNAVAILABLE: '체크아웃하려면 위치 접근이 필요합니다. 모바일 기기를 사용해 주세요.',
    LOCATION_PERMISSION_REQUIRED: '체크아웃하려면 위치 권한이 필요합니다. 위치 접근을 허용하고 다시 시도해 주세요.',
    MUST_BE_ON_SITE: '체크아웃하려면 현장에 있어야 합니다.',
    GUEST_PLACEHOLDER: '방문객',
    SUCCESS_CLOSING: '좋은 하루 되세요!',
    STATUS_CHECKED_IN: '체크인됨',
    STATUS_CHECKED_OUT: '체크아웃됨',
    DETAIL_LABEL_HOST: '호스트',
  },
  PREREGISTER: {
    ALL_SET_TITLE: '방문 준비가 완료되었습니다',
    ALL_SUB_TITLE: '사전 도착 등록이 완료되었습니다. 아래에서 방문 세부 정보를 확인할 수 있습니다.',
    BTN_CHECK_DETAILS: '내 세부 정보 확인',
    BTN_VIEW_CHECKIN: '비접촉 체크인 보기',
    QR_TITLE: '비접촉 로그인',
    QR_SUBTITLE: 'iPad 또는 인쇄된 안내판에 표시된 QR 코드에 카메라를 향하여 계속 진행하세요',
    ERROR_TITLE: '문제가 발생했습니다!',
    ERROR_SUBTITLE: '죄송합니다. 오류가 발생했습니다. 나중에 다시 시도해 주세요.',
    DETAIL_LABEL_VISITOR: '방문객',
    DETAIL_LABEL_EMAIL: '이메일',
    DETAIL_LABEL_HOST: '호스트',
    DETAIL_HOST_PLACEHOLDER: '미정',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: '사전 도착 등록 링크가 만료되었습니다',
    SUBTITLE: '이 링크는 더 이상 유효하지 않습니다',
    REASONS_HEADING: '가능한 이유:',
    REASON_CANCELLED: '초대가 취소되었습니다',
    REASON_CHECKED_IN: '이미 체크인했습니다',
    REASON_PAST: '예정 시간이 지났습니다',
    HELP_PROMPT: '도움이 필요하신가요?',
    HELP_TEXT: '호스트 또는 안내 데스크에 문의해 주세요',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: '유효하지 않은 패스 링크입니다.',
    PASS_REVOKED_OR_EXPIRED: '이 패스는 취소되었거나 만료되었습니다.',
    UNABLE_TO_LOAD: '체크인 페이지를 로드할 수 없습니다.',
    PASS_EXPIRED_TITLE: '패스 만료',
    CONTACT_HOST_FOR_NEW_PASS: '새로운 SmartPass를 위해 호스트에게 문의해 주세요.',
    ERROR_TITLE: '문제가 발생했습니다',
    BTN_TRY_AGAIN: '다시 시도',
    STATUS_NOT_ON_SITE: '오프사이트',
    STATUS_CHECKED_IN: '체크인 완료',
    SELECT_LOCATION_LABEL: '위치를 선택해 주세요:',
    SELECT_LOCATION_PLACEHOLDER: '-- 위치 선택 --',
    BTN_PROCESSING: '처리 중...',
    BTN_CHECK_IN: '체크인',
    BTN_CHECK_OUT: '체크아웃',
    BTN_CLOSE_HINT: '이 페이지를 닫아도 됩니다.',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: '유효하지 않은 패스 링크입니다.',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet 지원이 곧 제공될 예정입니다. 나중에 다시 확인해 주세요.',
    OPEN_ON_MOBILE: '패스를 지갑에 추가하려면 iPhone 또는 Android 휴대폰에서 이 링크를 열어 주세요.',
    PREPARING_PASS: 'SmartPass를 준비하고 있습니다...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: '유효하지 않은 패스 링크입니다.',
    LOCATION_REQUIRED: 'SmartPass 체크인에는 위치 접근이 필요합니다. 모바일 기기에서 이 링크를 열어 주세요.',
    LOCATION_PERMISSION_REQUIRED: 'SmartPass 체크인에는 위치 권한이 필요합니다. 위치 접근을 허용하고 다시 시도해 주세요.',
    LOCATION_UNAVAILABLE: '현재 위치를 확인할 수 없습니다. 위치 서비스가 활성화되어 있는지 확인하고 다시 시도해 주세요.',
    PASS_REVOKED_OR_EXPIRED: '이 패스는 취소되었거나 만료되었습니다.',
    OUTSIDE_GEOFENCE: '체크인 구역 밖에 있습니다. 건물에 가까이 이동한 후 다시 시도해 주세요.',
    TITLE: 'SmartPass',
    BTN_RETRY: '다시 시도',
    OPENING_CHECK_IN: '체크인을 열고 있습니다...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: '이 위치에서 대피가 진행 중입니다',
    BTN_TAP_TO_SIGNIN: '탭하여 로그인',
    BTN_SCAN_QR_CODE: 'QR 코드 스캔',
    TOAST_INVALID_QR: '잘못되었거나 만료된 QR 코드입니다. 다시 시도해 주세요!',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: '단계 ', OF: ' / ' },
  SPLASH_SCREEN: { TAP_TO_START: '탭하여 시작' },
  QR_SCANNER: { INVALID_CODE_ERROR: '유효하지 않은 QR 코드입니다. 다시 시도해 주세요!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: '카메라는 로비 QR 코드 읽기에만 사용되며 동영상은 저장되지 않습니다',
    PERMISSION_PROMPT: '"카메라 활성화"를 탭하여 카메라 접근을 허용해 주세요.',
    PERMISSION_DENIED: '카메라 접근이 차단되었습니다. 브라우저 설정에서 활성화한 후 다시 "카메라 활성화"를 탭해 주세요.',
    PERMISSION_UNSUPPORTED: '이 기기에서 카메라를 찾을 수 없습니다.',
    CAMERA_NOT_AVAILABLE: '카메라를 시작할 수 없습니다. 재시도를 탭하거나 페이지를 새로 고침하세요.',
    ERROR_CAMERA_IN_USE: '카메라가 사용 중입니다. 다른 앱을 닫고 다시 시도해 주세요.',
    ERROR_CAMERA_INIT_FAILED: '카메라 초기화에 실패했습니다. 권한을 확인해 주세요.',
    ERROR_CAMERA_BUSY_RETRY: '카메라가 아직 사용 중입니다. 다른 앱을 닫고 다시 시도해 주세요.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'QR 코드를 스캔하려면 카메라 권한이 필요합니다.',
    ACCESS_DENIED: '카메라 접근이 거부되었거나 사용할 수 없습니다',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: '취소하시겠습니까? 모든 진행 상황이 사라집니다.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: '호스트 선택',         prefillTitle: '호스트 선택',         prefillDesc: '방문할 분을 선택하세요' },
    DETAILS:    { headerTitle: '내 정보',              prefillTitle: '내 정보',              prefillDesc: '연락처 정보를 입력하세요' },
    DOCUMENT:   { headerTitle: '문서 서명',            prefillTitle: '문서 서명',            prefillDesc: '필수 문서를 검토하고 서명하세요' },
    ID_CAPTURE: { headerTitle: 'ID 확인',              prefillTitle: 'ID 확인',              prefillDesc: 'ID를 스캔하세요' },
    PHOTO:      { headerTitle: '셀피 촬영',            prefillTitle: '셀피 촬영',            prefillDesc: '사진을 촬영하세요' },
    VIDEO:      { headerTitle: '동영상 시청',          prefillTitle: '동영상 시청',          prefillDesc: '안전 또는 오리엔테이션 동영상을 시청하세요' },
  },
};

// ── Norwegian ──────────────────────────────────────────────────────────────────
export const NO_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Kontaktfri innlogging | Visitly',
    PRE_ARRIVAL: 'Forhåndsregistrering | Visitly',
  },
  HOME: {
    TITLE: 'Kontaktfri innlogging',
    SUBTITLE: 'Pek kameraet mot QR-koden som vises på iPaden eller det trykte skiltet for å fortsette',
  },
  COMMON: {
    BACK: 'Tilbake',
    CONTINUE: 'Fortsett',
    AGREE_AND_CONTINUE: 'Godta og fortsett',
    DECLINE: 'Avslå',
    CLEAR_SIGNATURE: 'Fjern signatur',
    LOADING: 'Laster...',
    SEARCHING: 'Søker...',
    NO: 'NEI',
    YES: 'JA',
    ENABLE_CAMERA: 'Aktiver kamera',
    PAGE_NOT_FOUND: '404 - Side ikke funnet',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: 'Ta på nytt',
    CAPTURE: 'Ta bilde',
    RETRY: 'Prøv igjen',
    POINT_OF_ENTRY: 'Inngang',
    PARKING_LOT: 'Parkeringsplass',
    DESTINATION: 'Bygg',
    ERROR_PROCESSING_REQUEST: 'Feil ved behandling av forespørselen. Vennligst prøv igjen.',
    SESSION_EXPIRED: 'Sesjonen er utløpt. Vennligst åpne lenken fra e-posten din på nytt.',
  },
  FULL_NAME: {
    TITLE: 'Hva er ditt fulle navn?',
    SUBTITLE: 'Bare så vi vet hvem som besøker oss i dag',
    FORM_LABEL: 'Fullt navn',
    PLACEHOLDER: 'f.eks. Ola Nordmann',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Velg type besøk.',
    FALLBACK_TITLE: 'Hva er hensikten med besøket?',
  },
  SELECT_HOST: {
    TITLE: 'Hvem besøker du?',
    SUBTITLE: 'Søk etter din vert for å varsle dem om ankomsten din.',
    PLACEHOLDER: 'Velg en vert',
    SEARCH_PLACEHOLDER: 'Søk etter vert...',
    SEARCH_PLACEHOLDER_ALT: 'Søk etter vert ved navn...',
    NO_HOSTS_FOUND: 'Ingen verter funnet',
    MIN_CHARS_MESSAGE: 'Skriv minst 3 tegn for å søke',
  },
  VISITOR_FORM: {
    TITLE: 'Besøksdetaljer',
    SUBTITLE: 'Fyll inn den nødvendige informasjonen nedenfor.',
    SELECT_FIELD_PLACEHOLDER: 'Velg {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Vennligst oppgi ditt fulle navn',
    EMAIL_REQUIRED: 'Vennligst oppgi en gyldig e-postadresse',
    EMAIL_INVALID: 'Vennligst oppgi en gyldig e-postadresse',
    HOST_REQUIRED: 'Vennligst velg vertens navn',
    COMPANY_NAME_REQUIRED: 'Vennligst oppgi firmanavnet',
    PHONE_NUMBER_REQUIRED: 'Vennligst oppgi telefonnummeret ditt',
    CUSTOM_FIELD_REQUIRED: 'Vennligst oppgi {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} er påkrevd',
    FULL_NAME_INVALID: 'Vennligst skriv inn et gyldig navn.',
    TOAST_ERROR_PROCESSING: 'Feil ved behandling av forespørsel. Prøv igjen.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Vennligst se gjennom og signer dokumentet nedenfor',
    ERROR_DOCUMENT_EXPIRY: 'Feil ved kontroll av dokumentets utløpsdato',
    ERROR_LOADING_CONTENT: 'Feil ved lasting av dokumentinnhold',
    ERROR_FETCHING_DOCUMENT: 'Feil ved henting av dokument',
    ERROR_RESTORE_SESSION: 'Kunne ikke gjenopprette dokumentsigneringsøkten. Start på nytt fra startsiden.',
    BTN_COLLAPSE: 'Skjul',
    BTN_EXPAND: 'Vis',
  },
  PRIVACY_POLICY: {
    TITLE: 'Før du fortsetter',
    SUBTITLE: 'Vennligst les gjennom og godta før du fortsetter',
    IMPLICIT_CONSENT: 'Ved å klikke på fortsett nedenfor, samtykker du til at {orgName} lagrer og behandler personopplysningene du har oppgitt ovenfor for å levere innholdet du har bedt om.',
    EXPLICIT_CONSENT: 'Jeg samtykker til at {orgName} lagrer og behandler mine personopplysninger.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Velg ID',
    SUBTITLE: 'Velg en ID-type å laste opp',
    ID_TYPES: [
      { label: 'Pass', value: 'PASSPORT' },
      { label: 'Førerkort', value: 'DRIVING_LICENCE' },
      { label: 'Offentlig utstedt ID', value: 'STATE_ISSUED_ID' },
      { label: 'Jeg har en annen ID', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Ta bilde',
    SUBTITLE: 'Plasser ansiktet ditt i midten og klikk på fortsett',
    INSTRUCTIONS: 'Bruk frontkameraet, se rett frem og plasser ansiktet i midten.',
    BTN_CAPTURE: 'Ta bilde',
    BTN_RETAKE: 'Ta på nytt',
    BTN_ENABLE_CAMERA: 'Aktiver kamera',
    PERMISSION_PROMPT: 'Trykk på "Aktiver kamera" for å gi kameratilgang.',
    PERMISSION_DENIED: 'Kameratilgang er blokkert. Aktiver det i nettleserinnstillingene og trykk på Aktiver kamera igjen.',
    PERMISSION_UNSUPPORTED: 'Ingen kamera funnet på denne enheten.',
    TOAST_CAMERA_ERROR: 'Feil ved kameratilgang. Sjekk konsollen!',
    BTN_RETRY_CAMERA: 'Prøv kameraet på nytt',
    CAMERA_NOT_AVAILABLE: 'Kameraet kunne ikke startes. Trykk på Prøv igjen eller last inn siden på nytt.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Aktiver kamera',
    BTN_START_CAMERA: 'Start kamera',
    BTN_TRY_NOW: 'Prøv nå',
    PERMISSION_PROMPT: 'Trykk på "Aktiver kamera" for å gi kameratilgang.',
    PERMISSION_DENIED: 'Kameratilgang er blokkert. Aktiver det i nettleserinnstillingene og trykk på Aktiver kamera igjen.',
    PERMISSION_UNSUPPORTED: 'Ingen kamera funnet på denne enheten.',
    ID_DRIVERS_LICENSE: 'Førerkort',
    ID_STATE_ISSUED: 'Offentlig utstedt ID',
    ID_PASSPORT: 'Pass',
    ID_OTHERS: 'Andre',
    ERROR_CAMERA_IN_USE: 'Kameraet er i bruk. Lukk den andre appen og prøv igjen.',
    ERROR_CAMERA_INIT_FAILED: 'Kamerainitialisering mislyktes. Vennligst kontroller tillatelser.',
    ERROR_CAMERA_BUSY_RETRY: 'Kameraet er fortsatt i bruk. Lukk den andre appen og prøv igjen.',
    ERROR_PLEASE_SCAN_DOCS: 'Vennligst skann dokument(er)',
    SUBTITLE: 'Skann det valgte dokumentet nedenfor.',
    LABEL_FRONT_SIDE: 'Forside',
    LABEL_BACK_SIDE: 'Bakside',
    INSTRUCTION_POSITION_FRONT: 'Plasser forsiden av ID-en din',
    INSTRUCTION_POSITION_BACK: 'Plasser baksiden av ID-en din',
    MRZ_BAND_HINT: 'Plasser de to nederste linjene på bildesiden under den stiplede linjen',
    MRZ_SCAN_FAILED: 'Vi kunne ikke lese passet ditt. Hold det stødig med de to nederste linjene innenfor feltet, og prøv igjen.',
    MRZ_SCAN_ATTEMPT: 'Skanning {current} av {total}',
    GLARE_WARNING: 'Gjenskinn på dokumentet. Vipp det litt eller flytt deg bort fra lyset.',
    BARCODE_BAND_HINT: 'Hold kortet stødig, ca. 20 cm unna, med strekkoden innenfor rammen',
    BARCODE_SCAN_FAILED: 'Vi kunne ikke lese strekkoden. Hold baksiden av kortet stødig og rett innenfor rammen, og prøv igjen.',
    TORCH_ON: 'Slå på lys',
    TORCH_OFF: 'Slå av lys',
    BARCODE_READING_PHOTO: 'Leser strekkoden…',
    ID_CHECKING: 'Kontrollerer legitimasjonen din…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Nesten fremme!',
    SUBTITLE: 'Se videoen nedenfor for å fortsette.',
    BTN_CHECK_IN: 'Sjekk inn',
    BTN_FINISH: 'Fullfør',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registrering funnet',
    SUBTITLE: 'Vennligst bekreft opplysningene dine for å fullføre innsjekking.',
    BTN_NOT_YOU: 'Ikke deg?',
    MEETING_WITH_LABEL: 'Møte med {hostName}',
    ONSITE_TITLE: 'Du er allerede sjekket inn',
    ONSITE_SUBTITLE: 'Velg din økt for å logge ut.',
    CHECKIN_AT_LABEL: 'Sjekket inn kl.:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Du er sjekket inn!',
    WALLET_HINT: 'Passet åpnes automatisk på enheten din.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Forhåndsregistrering fullført',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Ankomstinformasjon',
    ARRIVAL_INSTRUCTIONS_TEXT: 'En QR-kode er sendt til din registrerte e-post. Bruk den for raskere innsjekking ved kiosken når du ankommer.',
    QR_CAPTION: 'Denne QR-koden kan skannes i lobbyen for å hente opp registreringen din.',
    BTN_SAVE_QR: 'Lagre QR-kode',
    WALLET_LABEL: 'Lommebok',
    ACTION_ADD_PASS: 'Legg til pass',
    CALENDAR_LABEL: 'Kalender',
    ACTION_SAVE_EVENT: 'Lagre hendelse',
    DEFAULT_WELCOME_MESSAGE: 'Verten din er varslet og kommer snart.',
    PREFILL_SCHEDULED_FOR: 'Alt er klart for {date}.',
    PREFILL_HOST_NOTIFIED: 'Verten din er varslet og vi gleder oss til å ta imot deg.',
    TOAST_UPDATE_VISIT_ERROR: 'Feil ved oppdatering av besøket. Prøv igjen.',
    TOAST_GENERIC_ERROR: 'Det oppstod en feil. Prøv igjen!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Besøksdetaljer',
    PAGE_SUBTITLE: 'Fullfør trinnene nedenfor for å fremskynde innsjekkingen.',
    SECTION_HEADING: 'Fullfør forhåndsregistreringen din',
    STEPS_COMPLETED_SUFFIX: 'trinn fullført',
    BTN_START: 'La oss komme i gang!',
  },
  SIGN_OUT: {
    EYEBROW: 'Logg ut',
    TITLE_WITH_NAME: 'Ha det bra,',
    TITLE_FALLBACK: 'Ha det bra!',
    SUBTITLE_SESSION: 'Bekreft opplysningene dine og logg ut.',
    STRIP_SUBTITLE: 'Pålogget',
    LABEL_ORG: 'ORGANISASJON',
    LABEL_LOCATION: 'STED',
    LABEL_HOST: 'VERT',
    LABEL_SIGNED_IN_AT: 'LOGGET INN',
    BTN_SIGN_OUT: 'Logg ut',
    BTN_SIGNING_OUT: 'Logger ut...',
    BTN_START_OVER: 'Start på nytt',
    LABEL_NOT_YOU: 'Ikke deg?',
    TOAST_SUCCESS: 'Du er nå logget ut!',
    TOAST_ERROR_FALLBACK: 'Feil ved utlogging. Vennligst prøv igjen.',
    CUSTOM_FIELDS_EYEBROW: 'Nesten ferdig',
    CUSTOM_FIELDS_TITLE: 'Fullfør utlogging',
    CUSTOM_FIELDS_SUBTITLE: 'Vennligst fyll inn de nødvendige opplysningene.',
    BTN_CONFIRM_SIGN_OUT: 'Bekreft utlogging',
    BTN_GO_BACK: 'Gå tilbake',
    SUCCESS_TITLE: 'Du er logget ut',
    SUCCESS_SUBTITLE: 'Takk for besøket',
    BTN_DONE: 'Ferdig',
    LABEL_VISIT_DURATION: 'Besøksvarighet',
    GEOLOCATION_UNAVAILABLE: 'Stedstilgang er nødvendig for å sjekke ut. Bruk en mobilenhet.',
    LOCATION_PERMISSION_REQUIRED: 'Stedstillatelse er nødvendig for å sjekke ut. Tillat stedstilgang og prøv igjen.',
    MUST_BE_ON_SITE: 'Du må være på stedet for å sjekke ut.',
    GUEST_PLACEHOLDER: 'Gjest',
    SUCCESS_CLOSING: 'Ha en fin dag!',
    STATUS_CHECKED_IN: 'Sjekket inn',
    STATUS_CHECKED_OUT: 'Sjekket ut',
    DETAIL_LABEL_HOST: 'Vert',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Alt er klart for besøket ditt',
    ALL_SUB_TITLE: 'Forhåndsregistreringen er fullført. Du kan se besøksdetaljene dine nedenfor.',
    BTN_CHECK_DETAILS: 'Sjekk detaljene mine',
    BTN_VIEW_CHECKIN: 'Vis kontaktfri innsjekking',
    QR_TITLE: 'Kontaktfri innlogging',
    QR_SUBTITLE: 'Pek kameraet mot QR-koden som vises på iPaden eller det trykte skiltet for å fortsette',
    ERROR_TITLE: 'Noe gikk galt!',
    ERROR_SUBTITLE: 'Vi beklager, men det oppstod en feil. Vennligst prøv igjen senere.',
    DETAIL_LABEL_VISITOR: 'Besøkende',
    DETAIL_LABEL_EMAIL: 'E-post',
    DETAIL_LABEL_HOST: 'Vert',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Lenken for forhåndsregistrering er utløpt',
    SUBTITLE: 'Denne lenken er ikke lenger gyldig',
    REASONS_HEADING: 'Mulige årsaker:',
    REASON_CANCELLED: 'Invitasjon kansellert',
    REASON_CHECKED_IN: 'Allerede sjekket inn',
    REASON_PAST: 'Planlagt tidspunkt har passert',
    HELP_PROMPT: 'Trenger du hjelp?',
    HELP_TEXT: 'Kontakt verten eller resepsjonen',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Ugyldig passlenke.',
    PASS_REVOKED_OR_EXPIRED: 'Dette passet er tilbakekalt eller utløpt.',
    UNABLE_TO_LOAD: 'Kan ikke laste innsjekkingssiden.',
    PASS_EXPIRED_TITLE: 'Pass utløpt',
    CONTACT_HOST_FOR_NEW_PASS: 'Kontakt verten din for å få et nytt SmartPass.',
    ERROR_TITLE: 'Noe gikk galt',
    BTN_TRY_AGAIN: 'Prøv igjen',
    STATUS_NOT_ON_SITE: 'Ikke på stedet',
    STATUS_CHECKED_IN: 'Sjekket inn',
    SELECT_LOCATION_LABEL: 'Velg sted:',
    SELECT_LOCATION_PLACEHOLDER: '-- Velg sted --',
    BTN_PROCESSING: 'Behandler...',
    BTN_CHECK_IN: 'Sjekk inn',
    BTN_CHECK_OUT: 'Sjekk ut',
    BTN_CLOSE_HINT: 'Du kan lukke denne siden.',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Ugyldig passlenke.',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet-støtte kommer snart. Sjekk tilbake senere.',
    OPEN_ON_MOBILE: 'Åpne denne lenken på iPhone eller Android-telefonen din for å legge til passet i lommeboken.',
    PREPARING_PASS: 'Forbereder SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Ugyldig passlenke.',
    LOCATION_REQUIRED: 'SmartPass-innsjekking krever stedstilgang. Åpne denne lenken på mobilenheten din.',
    LOCATION_PERMISSION_REQUIRED: 'Stedstillatelse er nødvendig for SmartPass-innsjekking. Tillat stedstilgang og prøv igjen.',
    LOCATION_UNAVAILABLE: 'Kan ikke bestemme stedet ditt. Sørg for at stedsjenestene er aktivert og prøv igjen.',
    PASS_REVOKED_OR_EXPIRED: 'Dette passet er tilbakekalt eller utløpt.',
    OUTSIDE_GEOFENCE: 'Du er utenfor innsjekkingsområdet. Flytt deg nærmere bygningen og prøv igjen.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Prøv igjen',
    OPENING_CHECK_IN: 'Åpner innsjekking...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'EVAKUERING PÅGÅR PÅ DETTE STEDET',
    BTN_TAP_TO_SIGNIN: 'Trykk for å logge inn',
    BTN_SCAN_QR_CODE: 'Skann QR-kode',
    TOAST_INVALID_QR: 'Ugyldig eller utløpt QR-kode. Prøv igjen!',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: 'TRINN ', OF: ' AV ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Trykk for å starte' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Ugyldig QR-kode. Vennligst prøv igjen!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Vi bruker kun kameraet til å lese lobby-QR-koden og lagrer ingen video',
    PERMISSION_PROMPT: 'Trykk på "Aktiver kamera" for å gi kameratilgang.',
    PERMISSION_DENIED: 'Kameratilgang er blokkert. Aktiver det i nettleserinnstillingene og trykk på Aktiver kamera igjen.',
    PERMISSION_UNSUPPORTED: 'Ingen kamera funnet på denne enheten.',
    CAMERA_NOT_AVAILABLE: 'Kameraet kunne ikke startes. Trykk på Prøv igjen eller last inn siden på nytt.',
    ERROR_CAMERA_IN_USE: 'Kameraet er i bruk. Lukk den andre appen og prøv igjen.',
    ERROR_CAMERA_INIT_FAILED: 'Kamerainitialisering mislyktes. Vennligst kontroller tillatelser.',
    ERROR_CAMERA_BUSY_RETRY: 'Kameraet er fortsatt i bruk. Lukk den andre appen og prøv igjen.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Kameratillatelse er nødvendig for å skanne QR-koder.',
    ACCESS_DENIED: 'Kameratilgang avvist eller ikke tilgjengelig',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Er du sikker på at du vil avbryte? Du vil miste all fremgangen din.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Velg vert',              prefillTitle: 'Velg vert',              prefillDesc: 'Velg hvem du besøker' },
    DETAILS:    { headerTitle: 'Dine opplysninger',      prefillTitle: 'Dine opplysninger',      prefillDesc: 'Oppgi kontaktinformasjonen din' },
    DOCUMENT:   { headerTitle: 'Signer dokumenter',      prefillTitle: 'Signer dokumenter',      prefillDesc: 'Se gjennom og signer nødvendige dokumenter' },
    ID_CAPTURE: { headerTitle: 'Bekreft ID',             prefillTitle: 'Bekreft ID',             prefillDesc: 'Skann ID-en din' },
    PHOTO:      { headerTitle: 'Ta en selfie',           prefillTitle: 'Ta en selfie',           prefillDesc: 'Ta bildet ditt' },
    VIDEO:      { headerTitle: 'Se en video',            prefillTitle: 'Se en video',            prefillDesc: 'Se en sikkerhets- eller orienteringsvideo' },
  },
};

// ── Polish ─────────────────────────────────────────────────────────────────────
export const PL_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Bezdotykowe logowanie | Visitly',
    PRE_ARRIVAL: 'Rejestracja przed przyjazdem | Visitly',
  },
  HOME: {
    TITLE: 'Bezdotykowe logowanie',
    SUBTITLE: 'Skieruj aparat na kod QR wyświetlany na iPadzie lub wydrukowanym znaku, aby kontynuować',
  },
  COMMON: {
    BACK: 'Wstecz',
    CONTINUE: 'Kontynuuj',
    AGREE_AND_CONTINUE: 'Zgadzam się i kontynuuję',
    DECLINE: 'Odrzuć',
    CLEAR_SIGNATURE: 'Wyczyść podpis',
    LOADING: 'Ładowanie...',
    SEARCHING: 'Wyszukiwanie...',
    NO: 'NIE',
    YES: 'TAK',
    ENABLE_CAMERA: 'Włącz kamerę',
    PAGE_NOT_FOUND: '404 - Nie znaleziono strony',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: 'Zrób ponownie',
    CAPTURE: 'Zrób zdjęcie',
    RETRY: 'Spróbuj ponownie',
    POINT_OF_ENTRY: 'Punkt wejścia',
    PARKING_LOT: 'Parking',
    DESTINATION: 'Budynek',
    ERROR_PROCESSING_REQUEST: 'Błąd podczas przetwarzania żądania. Proszę spróbować ponownie.',
    SESSION_EXPIRED: 'Sesja wygasła. Proszę ponownie otworzyć link z e-maila.',
  },
  FULL_NAME: {
    TITLE: 'Jak masz na imię i nazwisko?',
    SUBTITLE: 'Potrzebujemy wiedzieć, kto odwiedza nas dzisiaj',
    FORM_LABEL: 'Imię i nazwisko',
    PLACEHOLDER: 'np. Jan Kowalski',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Proszę wybrać typ wizyty.',
    FALLBACK_TITLE: 'Jaki jest cel Twojej wizyty?',
  },
  SELECT_HOST: {
    TITLE: 'Kogo odwiedzasz?',
    SUBTITLE: 'Wyszukaj swojego gospodarza, aby powiadomić go o przybyciu.',
    PLACEHOLDER: 'Wybierz gospodarza',
    SEARCH_PLACEHOLDER: 'Wyszukaj gospodarza...',
    SEARCH_PLACEHOLDER_ALT: 'Wyszukaj gospodarza po nazwisku...',
    NO_HOSTS_FOUND: 'Nie znaleziono żadnych gospodarzy',
    MIN_CHARS_MESSAGE: 'Wpisz co najmniej 3 znaki, aby wyszukać',
  },
  VISITOR_FORM: {
    TITLE: 'Szczegóły wizyty',
    SUBTITLE: 'Proszę wypełnić wymagane informacje poniżej.',
    SELECT_FIELD_PLACEHOLDER: 'Wybierz {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Proszę podać imię i nazwisko',
    EMAIL_REQUIRED: 'Proszę podać prawidłowy adres e-mail',
    EMAIL_INVALID: 'Proszę podać prawidłowy adres e-mail',
    HOST_REQUIRED: 'Proszę wybrać imię i nazwisko gospodarza',
    COMPANY_NAME_REQUIRED: 'Proszę podać nazwę firmy',
    PHONE_NUMBER_REQUIRED: 'Proszę podać numer telefonu',
    CUSTOM_FIELD_REQUIRED: 'Proszę podać {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} jest wymagane',
    FULL_NAME_INVALID: 'Proszę podać prawidłowe imię.',
    TOAST_ERROR_PROCESSING: 'Błąd podczas przetwarzania żądania. Spróbuj ponownie.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Proszę przejrzeć i podpisać dokument poniżej',
    ERROR_DOCUMENT_EXPIRY: 'Błąd podczas sprawdzania daty ważności dokumentu',
    ERROR_LOADING_CONTENT: 'Błąd podczas ładowania treści dokumentu',
    ERROR_FETCHING_DOCUMENT: 'Błąd podczas pobierania dokumentu',
    ERROR_RESTORE_SESSION: 'Nie można przywrócić sesji podpisywania dokumentu. Uruchom ponownie ze strony głównej.',
    BTN_COLLAPSE: 'Zwiń',
    BTN_EXPAND: 'Rozwiń',
  },
  PRIVACY_POLICY: {
    TITLE: 'Zanim przejdziesz dalej',
    SUBTITLE: 'Proszę zapoznać się i wyrazić zgodę przed kontynuowaniem',
    IMPLICIT_CONSENT: 'Klikając przycisk Kontynuuj poniżej, wyrażasz zgodę na to, aby {orgName} przechowywał i przetwarzał dane osobowe przesłane powyżej w celu dostarczenia żądanej treści.',
    EXPLICIT_CONSENT: 'Wyrażam zgodę na przechowywanie i przetwarzanie moich danych osobowych przez {orgName}.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Wybierz dokument tożsamości',
    SUBTITLE: 'Wybierz typ dokumentu tożsamości do przesłania',
    ID_TYPES: [
      { label: 'Paszport', value: 'PASSPORT' },
      { label: 'Prawo jazdy', value: 'DRIVING_LICENCE' },
      { label: 'Dowód osobisty', value: 'STATE_ISSUED_ID' },
      { label: 'Mam inny dokument', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Zrób zdjęcie',
    SUBTITLE: 'Wyśrodkuj twarz i kliknij przycisk Kontynuuj',
    INSTRUCTIONS: 'Użyj przedniego aparatu, patrz prosto przed siebie i wyśrodkuj twarz.',
    BTN_CAPTURE: 'Zrób zdjęcie',
    BTN_RETAKE: 'Zrób ponownie',
    BTN_ENABLE_CAMERA: 'Włącz kamerę',
    PERMISSION_PROMPT: 'Stuknij „Włącz kamerę", aby udzielić dostępu do kamery.',
    PERMISSION_DENIED: 'Dostęp do kamery jest zablokowany. Włącz go w ustawieniach przeglądarki, a następnie ponownie stuknij Włącz kamerę.',
    PERMISSION_UNSUPPORTED: 'Na tym urządzeniu nie znaleziono kamery.',
    TOAST_CAMERA_ERROR: 'Błąd dostępu do kamery. Sprawdź konsolę!',
    BTN_RETRY_CAMERA: 'Ponów próbę kamery',
    CAMERA_NOT_AVAILABLE: 'Nie udało się uruchomić kamery. Stuknij Ponów próbę lub odśwież stronę.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Włącz kamerę',
    BTN_START_CAMERA: 'Uruchom kamerę',
    BTN_TRY_NOW: 'Spróbuj teraz',
    PERMISSION_PROMPT: 'Stuknij „Włącz kamerę", aby udzielić dostępu do kamery.',
    PERMISSION_DENIED: 'Dostęp do kamery jest zablokowany. Włącz go w ustawieniach przeglądarki, a następnie ponownie stuknij Włącz kamerę.',
    PERMISSION_UNSUPPORTED: 'Na tym urządzeniu nie znaleziono kamery.',
    ID_DRIVERS_LICENSE: 'Prawo jazdy',
    ID_STATE_ISSUED: 'Dowód osobisty',
    ID_PASSPORT: 'Paszport',
    ID_OTHERS: 'Inne',
    ERROR_CAMERA_IN_USE: 'Kamera jest w użyciu. Zamknij inną aplikację i spróbuj ponownie.',
    ERROR_CAMERA_INIT_FAILED: 'Inicjalizacja kamery nie powiodła się. Sprawdź uprawnienia.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera jest nadal w użyciu. Zamknij inną aplikację i spróbuj ponownie.',
    ERROR_PLEASE_SCAN_DOCS: 'Proszę zeskanować dokument(y)',
    SUBTITLE: 'Zeskanuj wybrany dokument poniżej.',
    LABEL_FRONT_SIDE: 'Strona przednia',
    LABEL_BACK_SIDE: 'Strona tylna',
    INSTRUCTION_POSITION_FRONT: 'Umieść przednią stronę dokumentu tożsamości',
    INSTRUCTION_POSITION_BACK: 'Umieść tylną stronę dokumentu tożsamości',
    MRZ_BAND_HINT: 'Umieść dwa dolne wiersze strony ze zdjęciem pod linią przerywaną',
    MRZ_SCAN_FAILED: 'Nie udało się odczytać paszportu. Trzymaj go nieruchomo, z dwoma dolnymi wierszami w pasku, i spróbuj ponownie.',
    MRZ_SCAN_ATTEMPT: 'Skan {current} z {total}',
    GLARE_WARNING: 'Na dokumencie widać odblask. Lekko go przechyl lub odsuń się od światła.',
    BARCODE_BAND_HINT: 'Trzymaj kartę nieruchomo, ok. 20 cm od aparatu, z kodem kreskowym w ramce',
    BARCODE_SCAN_FAILED: 'Nie udało się odczytać kodu kreskowego. Trzymaj tył karty nieruchomo i prosto w ramce, i spróbuj ponownie.',
    TORCH_ON: 'Włącz światło',
    TORCH_OFF: 'Wyłącz światło',
    BARCODE_READING_PHOTO: 'Odczytywanie kodu kreskowego…',
    ID_CHECKING: 'Sprawdzanie dokumentu…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Prawie gotowe!',
    SUBTITLE: 'Obejrzyj poniższy film, aby kontynuować.',
    BTN_CHECK_IN: 'Zamelduj się',
    BTN_FINISH: 'Zakończ',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Znaleziono rejestrację',
    SUBTITLE: 'Zweryfikuj swoje dane, aby zakończyć odprawę.',
    BTN_NOT_YOU: 'To nie Ty?',
    MEETING_WITH_LABEL: 'Spotkanie z {hostName}',
    ONSITE_TITLE: 'Jesteś już zameldowany',
    ONSITE_SUBTITLE: 'Wybierz swoją sesję, aby się wylogować.',
    CHECKIN_AT_LABEL: 'Zameldowany o:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Jesteś zameldowany!',
    WALLET_HINT: 'Przepustka otworzy się automatycznie na Twoim urządzeniu.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Rejestracja przed przyjazdem zakończona',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Instrukcje dotyczące przybycia',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Na Twój zarejestrowany adres e-mail został wysłany kod QR. Użyj go, aby przyspieszyć odprawę w kiosku po przybyciu.',
    QR_CAPTION: 'Ten kod QR można zeskanować w holu, aby wyświetlić Twoją rejestrację.',
    BTN_SAVE_QR: 'Zapisz kod QR',
    WALLET_LABEL: 'Portfel',
    ACTION_ADD_PASS: 'Dodaj przepustkę',
    CALENDAR_LABEL: 'Kalendarz',
    ACTION_SAVE_EVENT: 'Zapisz wydarzenie',
    DEFAULT_WELCOME_MESSAGE: 'Twój gospodarz został powiadomiony i wkrótce się z Tobą skontaktuje.',
    PREFILL_SCHEDULED_FOR: 'Wszystko gotowe na {date}.',
    PREFILL_HOST_NOTIFIED: 'Twój gospodarz został powiadomiony i z radością Cię przywitamy.',
    TOAST_UPDATE_VISIT_ERROR: 'Wystąpił błąd podczas aktualizowania wizyty. Spróbuj ponownie.',
    TOAST_GENERIC_ERROR: 'Wystąpił błąd. Spróbuj ponownie!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Szczegóły wizyty',
    PAGE_SUBTITLE: 'Wykonaj poniższe kroki, aby przyspieszyć odprawę.',
    SECTION_HEADING: 'Uzupełnij rejestrację przed przyjazdem',
    STEPS_COMPLETED_SUFFIX: 'kroki ukończone',
    BTN_START: 'Zaczynajmy!',
  },
  SIGN_OUT: {
    EYEBROW: 'Wyloguj się',
    TITLE_WITH_NAME: 'Do widzenia,',
    TITLE_FALLBACK: 'Do widzenia!',
    SUBTITLE_SESSION: 'Potwierdź swoje dane i wyloguj się.',
    STRIP_SUBTITLE: 'Aktualnie zalogowany',
    LABEL_ORG: 'ORGANIZACJA',
    LABEL_LOCATION: 'LOKALIZACJA',
    LABEL_HOST: 'GOSPODARZ',
    LABEL_SIGNED_IN_AT: 'ZALOGOWANO',
    BTN_SIGN_OUT: 'Wyloguj się',
    BTN_SIGNING_OUT: 'Wylogowywanie...',
    BTN_START_OVER: 'Zacznij od nowa',
    LABEL_NOT_YOU: 'To nie Ty?',
    TOAST_SUCCESS: 'Zostałeś pomyślnie wylogowany!',
    TOAST_ERROR_FALLBACK: 'Błąd podczas wylogowywania. Proszę spróbować ponownie.',
    CUSTOM_FIELDS_EYEBROW: 'Prawie gotowe',
    CUSTOM_FIELDS_TITLE: 'Zakończ wylogowywanie',
    CUSTOM_FIELDS_SUBTITLE: 'Proszę wypełnić wymagane dane.',
    BTN_CONFIRM_SIGN_OUT: 'Potwierdź wylogowanie',
    BTN_GO_BACK: 'Wróć',
    SUCCESS_TITLE: 'Zostałeś wylogowany',
    SUCCESS_SUBTITLE: 'Dziękujemy za wizytę',
    BTN_DONE: 'Gotowe',
    LABEL_VISIT_DURATION: 'Czas wizyty',
    GEOLOCATION_UNAVAILABLE: 'Dostęp do lokalizacji jest wymagany do wymeldowania. Proszę użyć urządzenia mobilnego.',
    LOCATION_PERMISSION_REQUIRED: 'Uprawnienie do lokalizacji jest wymagane do wymeldowania. Proszę zezwolić na dostęp do lokalizacji i spróbować ponownie.',
    MUST_BE_ON_SITE: 'Musisz być na miejscu, aby się wymeldować.',
    GUEST_PLACEHOLDER: 'Gość',
    SUCCESS_CLOSING: 'Miłego dnia!',
    STATUS_CHECKED_IN: 'Zameldowany',
    STATUS_CHECKED_OUT: 'Wymeldowany',
    DETAIL_LABEL_HOST: 'Gospodarz',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Wszystko gotowe na Twoją wizytę',
    ALL_SUB_TITLE: 'Rejestracja przed przyjazdem jest zakończona. Możesz przejrzeć szczegóły wizyty poniżej.',
    BTN_CHECK_DETAILS: 'Sprawdź moje dane',
    BTN_VIEW_CHECKIN: 'Wyświetl bezdotykowe logowanie',
    QR_TITLE: 'Bezdotykowe logowanie',
    QR_SUBTITLE: 'Skieruj aparat na kod QR wyświetlany na iPadzie lub wydrukowanym znaku, aby kontynuować',
    ERROR_TITLE: 'Coś poszło nie tak!',
    ERROR_SUBTITLE: 'Przepraszamy, wystąpił błąd. Proszę spróbować ponownie później.',
    DETAIL_LABEL_VISITOR: 'Gość',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Gospodarz',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Link do rejestracji przed przyjazdem wygasł',
    SUBTITLE: 'Ten link nie jest już ważny',
    REASONS_HEADING: 'Możliwe przyczyny:',
    REASON_CANCELLED: 'Zaproszenie anulowane',
    REASON_CHECKED_IN: 'Już zameldowany',
    REASON_PAST: 'Zaplanowany czas minął',
    HELP_PROMPT: 'Potrzebujesz pomocy?',
    HELP_TEXT: 'Skontaktuj się z gospodarzem lub recepcją',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Nieprawidłowy link do przepustki.',
    PASS_REVOKED_OR_EXPIRED: 'Ta przepustka została unieważniona lub wygasła.',
    UNABLE_TO_LOAD: 'Nie można załadować strony odprawy.',
    PASS_EXPIRED_TITLE: 'Przepustka wygasła',
    CONTACT_HOST_FOR_NEW_PASS: 'Skontaktuj się z gospodarzem, aby uzyskać nowy SmartPass.',
    ERROR_TITLE: 'Coś poszło nie tak',
    BTN_TRY_AGAIN: 'Spróbuj ponownie',
    STATUS_NOT_ON_SITE: 'Poza siedzibą',
    STATUS_CHECKED_IN: 'Zameldowany',
    SELECT_LOCATION_LABEL: 'Wybierz swoją lokalizację:',
    SELECT_LOCATION_PLACEHOLDER: '-- Wybierz lokalizację --',
    BTN_PROCESSING: 'Przetwarzanie...',
    BTN_CHECK_IN: 'Zamelduj się',
    BTN_CHECK_OUT: 'Wymelduj się',
    BTN_CLOSE_HINT: 'Możesz zamknąć tę stronę.',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Nieprawidłowy link do przepustki.',
    GOOGLE_WALLET_COMING_SOON: 'Obsługa Google Wallet wkrótce dostępna. Sprawdź ponownie później.',
    OPEN_ON_MOBILE: 'Otwórz ten link na iPhonie lub telefonie z systemem Android, aby dodać przepustkę do portfela.',
    PREPARING_PASS: 'Przygotowywanie SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Nieprawidłowy link do przepustki.',
    LOCATION_REQUIRED: 'Zameldowanie SmartPass wymaga dostępu do lokalizacji. Otwórz ten link na urządzeniu mobilnym.',
    LOCATION_PERMISSION_REQUIRED: 'Uprawnienie do lokalizacji jest wymagane do zameldowania SmartPass. Zezwól na dostęp do lokalizacji i spróbuj ponownie.',
    LOCATION_UNAVAILABLE: 'Nie można ustalić Twojej lokalizacji. Upewnij się, że usługi lokalizacyjne są włączone i spróbuj ponownie.',
    PASS_REVOKED_OR_EXPIRED: 'Ta przepustka została unieważniona lub wygasła.',
    OUTSIDE_GEOFENCE: 'Jesteś poza strefą zameldowania. Zbliż się do budynku i spróbuj ponownie.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Spróbuj ponownie',
    OPENING_CHECK_IN: 'Otwieranie strony zameldowania...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'TRWA EWAKUACJA W TEJ LOKALIZACJI',
    BTN_TAP_TO_SIGNIN: 'Dotknij, aby się zalogować',
    BTN_SCAN_QR_CODE: 'Zeskanuj kod QR',
    TOAST_INVALID_QR: 'Nieprawidłowy lub wygasły kod QR. Spróbuj ponownie!',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: 'KROK ', OF: ' Z ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Dotknij, aby rozpocząć' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Nieprawidłowy kod QR. Proszę spróbować ponownie!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Używamy aparatu wyłącznie do odczytu kodu QR w holu i nie przechowujemy żadnego wideo',
    PERMISSION_PROMPT: 'Stuknij „Włącz kamerę", aby udzielić dostępu do kamery.',
    PERMISSION_DENIED: 'Dostęp do kamery jest zablokowany. Włącz go w ustawieniach przeglądarki, a następnie ponownie stuknij Włącz kamerę.',
    PERMISSION_UNSUPPORTED: 'Na tym urządzeniu nie znaleziono kamery.',
    CAMERA_NOT_AVAILABLE: 'Nie udało się uruchomić kamery. Stuknij Ponów próbę lub odśwież stronę.',
    ERROR_CAMERA_IN_USE: 'Kamera jest w użyciu. Zamknij inną aplikację i spróbuj ponownie.',
    ERROR_CAMERA_INIT_FAILED: 'Inicjalizacja kamery nie powiodła się. Sprawdź uprawnienia.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera jest nadal w użyciu. Zamknij inną aplikację i spróbuj ponownie.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Uprawnienie do kamery jest wymagane do skanowania kodów QR.',
    ACCESS_DENIED: 'Dostęp do kamery odrzucony lub niedostępny',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Czy na pewno chcesz anulować? Stracisz wszystkie postępy.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Wybierz gospodarza',      prefillTitle: 'Wybierz gospodarza',      prefillDesc: 'Wybierz osobę, którą odwiedzasz' },
    DETAILS:    { headerTitle: 'Twoje dane',              prefillTitle: 'Twoje dane',              prefillDesc: 'Podaj swoje dane kontaktowe' },
    DOCUMENT:   { headerTitle: 'Podpisz dokumenty',       prefillTitle: 'Podpisz dokumenty',       prefillDesc: 'Przejrzyj i podpisz wymagane dokumenty' },
    ID_CAPTURE: { headerTitle: 'Zweryfikuj dokument',     prefillTitle: 'Zweryfikuj dokument',     prefillDesc: 'Zeskanuj swój dokument tożsamości' },
    PHOTO:      { headerTitle: 'Zrób selfie',             prefillTitle: 'Zrób selfie',             prefillDesc: 'Zrób swoje zdjęcie' },
    VIDEO:      { headerTitle: 'Obejrzyj film',           prefillTitle: 'Obejrzyj film',           prefillDesc: 'Obejrzyj film instruktażowy lub BHP' },
  },
};

// ── Portuguese (Brazil) ────────────────────────────────────────────────────────
export const PT_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Entrada sem Contato | Visitly',
    PRE_ARRIVAL: 'Pré-chegada | Visitly',
  },
  HOME: {
    TITLE: 'Entrada sem Contato',
    SUBTITLE: 'Aponte sua câmera para o código QR exibido no iPad ou na placa impressa para continuar',
  },
  COMMON: {
    BACK: 'Voltar',
    CONTINUE: 'Continuar',
    AGREE_AND_CONTINUE: 'Concordar e Continuar',
    DECLINE: 'Recusar',
    CLEAR_SIGNATURE: 'Limpar Assinatura',
    LOADING: 'Carregando...',
    SEARCHING: 'Pesquisando...',
    NO: 'NÃO',
    YES: 'SIM',
    ENABLE_CAMERA: 'Ativar Câmera',
    PAGE_NOT_FOUND: '404 - Página Não Encontrada',
    POWERED_BY: 'Desenvolvido por Visitly',
    RETAKE: 'Tirar Novamente',
    CAPTURE: 'Capturar',
    RETRY: 'Tentar Novamente',
    POINT_OF_ENTRY: 'Ponto de Entrada',
    PARKING_LOT: 'Estacionamento',
    DESTINATION: 'Edifício',
    ERROR_PROCESSING_REQUEST: 'Erro ao processar a solicitação. Por favor, tente novamente.',
    SESSION_EXPIRED: 'Sessão expirada. Por favor, reabra o link do seu e-mail.',
  },
  FULL_NAME: {
    TITLE: 'Qual é o seu nome completo?',
    SUBTITLE: 'Para sabermos quem está nos visitando hoje',
    FORM_LABEL: 'Nome Completo',
    PLACEHOLDER: 'ex. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Por favor, escolha o tipo de visita.',
    FALLBACK_TITLE: 'O que traz você aqui?',
  },
  SELECT_HOST: {
    TITLE: 'Quem você está visitando?',
    SUBTITLE: 'Pesquise seu anfitrião para notificá-lo da sua chegada.',
    PLACEHOLDER: 'Selecionar anfitrião',
    SEARCH_PLACEHOLDER: 'Pesquisar anfitrião...',
    SEARCH_PLACEHOLDER_ALT: 'Pesquisar anfitrião pelo nome...',
    NO_HOSTS_FOUND: 'Nenhum anfitrião encontrado',
    MIN_CHARS_MESSAGE: 'Digite pelo menos 3 caracteres para pesquisar',
  },
  VISITOR_FORM: {
    TITLE: 'Detalhes da Visita',
    SUBTITLE: 'Por favor, preencha as informações necessárias abaixo.',
    SELECT_FIELD_PLACEHOLDER: 'Selecionar {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Por favor, insira seu nome completo',
    EMAIL_REQUIRED: 'Por favor, insira um endereço de e-mail válido',
    EMAIL_INVALID: 'Por favor, insira um endereço de e-mail válido',
    HOST_REQUIRED: 'Por favor, selecione o nome do seu anfitrião',
    COMPANY_NAME_REQUIRED: 'Por favor, insira o nome da sua empresa',
    PHONE_NUMBER_REQUIRED: 'Por favor, insira seu número de telefone',
    CUSTOM_FIELD_REQUIRED: 'Por favor, insira {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} é obrigatório',
    FULL_NAME_INVALID: 'Por favor, insira um nome válido.',
    TOAST_ERROR_PROCESSING: 'Erro ao processar a solicitação. Por favor, tente novamente.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Por favor, revise e assine o documento abaixo',
    ERROR_DOCUMENT_EXPIRY: 'Erro ao verificar a validade do documento',
    ERROR_LOADING_CONTENT: 'Erro ao carregar o conteúdo do documento',
    ERROR_FETCHING_DOCUMENT: 'Erro ao buscar o documento',
    ERROR_RESTORE_SESSION: 'Não foi possível restaurar a sessão de assinatura do documento. Por favor, reinicie a partir da página inicial.',
    BTN_COLLAPSE: 'Recolher',
    BTN_EXPAND: 'Expandir',
  },
  PRIVACY_POLICY: {
    TITLE: 'Antes de continuar',
    SUBTITLE: 'Por favor, revise e concorde antes de continuar',
    IMPLICIT_CONSENT: 'Ao clicar em continuar abaixo, você consente que {orgName} armazene e processe as informações pessoais enviadas acima para fornecer o conteúdo solicitado.',
    EXPLICIT_CONSENT: 'Concordo em permitir que {orgName} armazene e processe meus dados pessoais.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Selecionar Documento',
    SUBTITLE: 'Por favor, selecione um tipo de documento para enviar',
    ID_TYPES: [
      { label: 'Passaporte', value: 'PASSPORT' },
      { label: 'Carteira de Motorista', value: 'DRIVING_LICENCE' },
      { label: 'Documento de Identidade', value: 'STATE_ISSUED_ID' },
      { label: 'Tenho um documento diferente', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Tirar Foto',
    SUBTITLE: 'Por favor, centralize seu rosto e clique em continuar',
    INSTRUCTIONS: 'Use sua câmera frontal, olhe diretamente para frente e centralize seu rosto.',
    BTN_CAPTURE: 'Capturar',
    BTN_RETAKE: 'Tirar Novamente',
    BTN_ENABLE_CAMERA: 'Ativar Câmera',
    PERMISSION_PROMPT: 'Toque em "Ativar Câmera" para conceder acesso à câmera.',
    PERMISSION_DENIED: 'O acesso à câmera está bloqueado. Ative-o nas configurações do seu navegador e toque em Ativar Câmera novamente.',
    PERMISSION_UNSUPPORTED: 'Nenhuma câmera encontrada neste dispositivo.',
    TOAST_CAMERA_ERROR: 'Erro de acesso à câmera. Verifique o console!',
    BTN_RETRY_CAMERA: 'Tentar Câmera Novamente',
    CAMERA_NOT_AVAILABLE: 'A câmera não pôde iniciar. Toque em Tentar novamente ou recarregue a página.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Ativar Câmera',
    BTN_START_CAMERA: 'Iniciar Câmera',
    BTN_TRY_NOW: 'Tentar Agora',
    PERMISSION_PROMPT: 'Toque em "Ativar Câmera" para conceder acesso à câmera.',
    PERMISSION_DENIED: 'O acesso à câmera está bloqueado. Ative-o nas configurações do seu navegador e toque em Ativar Câmera novamente.',
    PERMISSION_UNSUPPORTED: 'Nenhuma câmera encontrada neste dispositivo.',
    ID_DRIVERS_LICENSE: 'Carteira de Motorista',
    ID_STATE_ISSUED: 'Documento de Identidade',
    ID_PASSPORT: 'Passaporte',
    ID_OTHERS: 'Outros',
    ERROR_CAMERA_IN_USE: 'A câmera está em uso. Feche o outro aplicativo e tente novamente.',
    ERROR_CAMERA_INIT_FAILED: 'Falha na inicialização da câmera. Por favor, verifique as permissões.',
    ERROR_CAMERA_BUSY_RETRY: 'A câmera ainda está em uso. Feche o outro aplicativo e tente novamente.',
    ERROR_PLEASE_SCAN_DOCS: 'Por favor, escaneie o(s) documento(s)',
    SUBTITLE: 'Escaneie o documento selecionado abaixo.',
    LABEL_FRONT_SIDE: 'Frente',
    LABEL_BACK_SIDE: 'Verso',
    INSTRUCTION_POSITION_FRONT: 'Posicione a frente do seu documento',
    INSTRUCTION_POSITION_BACK: 'Posicione o verso do seu documento',
    MRZ_BAND_HINT: 'Alinhe as duas linhas inferiores da página da foto abaixo da linha tracejada',
    MRZ_SCAN_FAILED: 'Não foi possível ler seu passaporte. Segure-o firme com as duas linhas inferiores dentro da faixa e tente novamente.',
    MRZ_SCAN_ATTEMPT: 'Leitura {current} de {total}',
    GLARE_WARNING: 'Reflexo no documento. Incline-o levemente ou afaste-se da luz.',
    BARCODE_BAND_HINT: 'Segure o cartão firme, a cerca de 20 cm, com o código de barras dentro da moldura',
    BARCODE_SCAN_FAILED: 'Não foi possível ler o código de barras. Segure o verso do cartão firme e reto dentro da moldura e tente novamente.',
    TORCH_ON: 'Ligar a luz',
    TORCH_OFF: 'Desligar a luz',
    BARCODE_READING_PHOTO: 'Lendo o código de barras…',
    ID_CHECKING: 'Verificando seu documento…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Quase lá!',
    SUBTITLE: 'Assista ao vídeo abaixo para continuar.',
    BTN_CHECK_IN: 'Fazer Check-in',
    BTN_FINISH: 'Concluir',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Cadastro Encontrado',
    SUBTITLE: 'Por favor, verifique seus dados para concluir o check-in.',
    BTN_NOT_YOU: 'Não é você?',
    MEETING_WITH_LABEL: 'Reunião com {hostName}',
    ONSITE_TITLE: 'Você já está registrado',
    ONSITE_SUBTITLE: 'Selecione sua sessão para sair.',
    CHECKIN_AT_LABEL: 'Check-in realizado às:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Check-in Realizado!',
    WALLET_HINT: 'O passe será aberto automaticamente no seu dispositivo.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Pré-cadastro Concluído',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Instruções de Chegada',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Um código QR foi enviado para o seu e-mail cadastrado. Use-o para um check-in mais rápido no quiosque ao chegar.',
    QR_CAPTION: 'Este código QR pode ser escaneado no lobby para acessar seu cadastro.',
    BTN_SAVE_QR: 'Salvar Código QR',
    WALLET_LABEL: 'Carteira',
    ACTION_ADD_PASS: 'Adicionar Passe',
    CALENDAR_LABEL: 'Calendário',
    ACTION_SAVE_EVENT: 'Salvar Evento',
    DEFAULT_WELCOME_MESSAGE: 'Seu anfitrião foi notificado e estará com você em breve.',
    PREFILL_SCHEDULED_FOR: 'Tudo pronto para {date}.',
    PREFILL_HOST_NOTIFIED: 'Seu anfitrião foi notificado e esperamos recebê-lo em breve.',
    TOAST_UPDATE_VISIT_ERROR: 'Erro ao atualizar a visita. Por favor, tente novamente.',
    TOAST_GENERIC_ERROR: 'Encontramos um erro. Por favor, tente novamente!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Detalhes da Visita',
    PAGE_SUBTITLE: 'Conclua as etapas abaixo para agilizar seu check-in.',
    SECTION_HEADING: 'Conclua seu Pré-cadastro',
    STEPS_COMPLETED_SUFFIX: 'etapas concluídas',
    BTN_START: 'Vamos Começar!',
  },
  SIGN_OUT: {
    EYEBROW: 'Saída',
    TITLE_WITH_NAME: 'Até logo,',
    TITLE_FALLBACK: 'Até logo!',
    SUBTITLE_SESSION: 'Confirme seus dados e faça check-out.',
    STRIP_SUBTITLE: 'Atualmente com check-in ativo',
    LABEL_ORG: 'ORGANIZAÇÃO',
    LABEL_LOCATION: 'LOCAL',
    LABEL_HOST: 'ANFITRIÃO',
    LABEL_SIGNED_IN_AT: 'ENTRADA',
    BTN_SIGN_OUT: 'Fazer check-out',
    BTN_SIGNING_OUT: 'Realizando check-out...',
    BTN_START_OVER: 'Recomeçar',
    LABEL_NOT_YOU: 'Não é você?',
    TOAST_SUCCESS: 'Seu check-out foi realizado com sucesso!',
    TOAST_ERROR_FALLBACK: 'Erro ao fazer check-out. Por favor, tente novamente.',
    CUSTOM_FIELDS_EYEBROW: 'Quase Pronto',
    CUSTOM_FIELDS_TITLE: 'Concluir check-out',
    CUSTOM_FIELDS_SUBTITLE: 'Por favor, preencha os detalhes necessários.',
    BTN_CONFIRM_SIGN_OUT: 'Confirmar check-out',
    BTN_GO_BACK: 'Voltar',
    SUCCESS_TITLE: 'Check-out Realizado',
    SUCCESS_SUBTITLE: 'Obrigado pela visita',
    BTN_DONE: 'Concluir',
    LABEL_VISIT_DURATION: 'Duração da visita',
    GEOLOCATION_UNAVAILABLE: 'O acesso à localização é necessário para o check-out. Por favor, use um dispositivo móvel.',
    LOCATION_PERMISSION_REQUIRED: 'A permissão de localização é necessária para o check-out. Por favor, permita o acesso à localização e tente novamente.',
    MUST_BE_ON_SITE: 'Você precisa estar no local para fazer check-out.',
    GUEST_PLACEHOLDER: 'Visitante',
    SUCCESS_CLOSING: 'Tenha um ótimo dia!',
    STATUS_CHECKED_IN: 'Check-in realizado',
    STATUS_CHECKED_OUT: 'Check-out realizado',
    DETAIL_LABEL_HOST: 'Anfitrião',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Tudo pronto para sua visita',
    ALL_SUB_TITLE: 'Seu pré-cadastro está completo. Você pode revisar os detalhes da visita abaixo.',
    BTN_CHECK_DETAILS: 'Verificar meus dados',
    BTN_VIEW_CHECKIN: 'Ver check-in sem contato',
    QR_TITLE: 'Entrada sem Contato',
    QR_SUBTITLE: 'Aponte sua câmera para o código QR exibido no iPad ou na placa impressa para continuar',
    ERROR_TITLE: 'Algo deu errado!',
    ERROR_SUBTITLE: 'Lamentamos, mas ocorreu um erro. Por favor, tente novamente mais tarde.',
    DETAIL_LABEL_VISITOR: 'Visitante',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Anfitrião',
    DETAIL_HOST_PLACEHOLDER: 'A definir',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Link de Pré-cadastro Expirado',
    SUBTITLE: 'Este link não é mais válido',
    REASONS_HEADING: 'Possíveis motivos:',
    REASON_CANCELLED: 'Convite cancelado',
    REASON_CHECKED_IN: 'Check-in já realizado',
    REASON_PAST: 'O horário agendado já passou',
    HELP_PROMPT: 'Precisa de ajuda?',
    HELP_TEXT: 'Entre em contato com seu anfitrião ou a recepção',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Link do passe inválido.',
    PASS_REVOKED_OR_EXPIRED: 'Este passe foi revogado ou expirou.',
    UNABLE_TO_LOAD: 'Não foi possível carregar a página de check-in.',
    PASS_EXPIRED_TITLE: 'Passe Expirado',
    CONTACT_HOST_FOR_NEW_PASS: 'Por favor, entre em contato com seu anfitrião para obter um novo SmartPass.',
    ERROR_TITLE: 'Algo deu errado',
    BTN_TRY_AGAIN: 'Tentar Novamente',
    STATUS_NOT_ON_SITE: 'Fora do Local',
    STATUS_CHECKED_IN: 'Check-in Realizado',
    SELECT_LOCATION_LABEL: 'Selecione sua localização:',
    SELECT_LOCATION_PLACEHOLDER: '-- Selecionar localização --',
    BTN_PROCESSING: 'Processando...',
    BTN_CHECK_IN: 'Fazer Check-in',
    BTN_CHECK_OUT: 'Fazer Check-out',
    BTN_CLOSE_HINT: 'Você pode fechar esta página.',
    POWERED_BY: 'Desenvolvido por Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Link do passe inválido.',
    GOOGLE_WALLET_COMING_SOON: 'Suporte ao Google Wallet em breve. Por favor, volte mais tarde.',
    OPEN_ON_MOBILE: 'Por favor, abra este link no seu iPhone ou telefone Android para adicionar o passe à sua carteira.',
    PREPARING_PASS: 'Preparando seu SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Link do passe inválido.',
    LOCATION_REQUIRED: 'O check-in com SmartPass requer acesso à localização. Por favor, abra este link no seu dispositivo móvel.',
    LOCATION_PERMISSION_REQUIRED: 'A permissão de localização é necessária para o check-in com SmartPass. Por favor, permita o acesso à localização e tente novamente.',
    LOCATION_UNAVAILABLE: 'Não foi possível determinar sua localização. Por favor, certifique-se de que os serviços de localização estão ativados e tente novamente.',
    PASS_REVOKED_OR_EXPIRED: 'Este passe foi revogado ou expirou.',
    OUTSIDE_GEOFENCE: 'Você está fora da área de check-in. Por favor, aproxime-se do edifício e tente novamente.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Tentar Novamente',
    OPENING_CHECK_IN: 'Abrindo check-in...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'UMA EVACUAÇÃO ESTÁ EM ANDAMENTO NESTE LOCAL',
    BTN_TAP_TO_SIGNIN: 'Toque para entrar',
    BTN_SCAN_QR_CODE: 'Escanear código QR',
    TOAST_INVALID_QR: 'Código QR inválido ou expirado. Por favor, tente novamente!',
  },
  FOOTER: { POWERED_BY: 'DESENVOLVIDO POR ' },
  HEADER: { STEP: 'ETAPA ', OF: ' DE ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Toque para começar' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Código QR inválido. Por favor, tente novamente!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Usamos sua câmera apenas para ler o QR code do lobby e não armazenamos nenhum vídeo',
    PERMISSION_PROMPT: 'Toque em "Ativar Câmera" para conceder acesso à câmera.',
    PERMISSION_DENIED: 'O acesso à câmera está bloqueado. Ative-o nas configurações do seu navegador e toque em Ativar Câmera novamente.',
    PERMISSION_UNSUPPORTED: 'Nenhuma câmera encontrada neste dispositivo.',
    CAMERA_NOT_AVAILABLE: 'A câmera não pôde iniciar. Toque em Tentar novamente ou recarregue a página.',
    ERROR_CAMERA_IN_USE: 'A câmera está em uso. Feche o outro aplicativo e tente novamente.',
    ERROR_CAMERA_INIT_FAILED: 'Falha na inicialização da câmera. Por favor, verifique as permissões.',
    ERROR_CAMERA_BUSY_RETRY: 'A câmera ainda está em uso. Feche o outro aplicativo e tente novamente.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'A permissão de câmera é necessária para escanear códigos QR.',
    ACCESS_DENIED: 'Acesso à câmera negado ou indisponível',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Tem certeza de que deseja cancelar? Você perderá todo o seu progresso.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Selecionar Anfitrião',    prefillTitle: 'Selecionar Anfitrião',    prefillDesc: 'Escolha quem você está visitando' },
    DETAILS:    { headerTitle: 'Seus Dados',              prefillTitle: 'Seus Dados',              prefillDesc: 'Forneça suas informações de contato' },
    DOCUMENT:   { headerTitle: 'Assinar Documentos',     prefillTitle: 'Assinar Documentos',     prefillDesc: 'Revise e assine os documentos necessários' },
    ID_CAPTURE: { headerTitle: 'Verificar Documento',    prefillTitle: 'Verificar Documento',    prefillDesc: 'Escaneie seu documento' },
    PHOTO:      { headerTitle: 'Tirar Selfie',           prefillTitle: 'Tirar Selfie',           prefillDesc: 'Capture sua foto' },
    VIDEO:      { headerTitle: 'Assistir Vídeo',         prefillTitle: 'Assistir Vídeo',         prefillDesc: 'Assista a um vídeo de segurança ou orientação' },
  },
};

// ── Slovak ─────────────────────────────────────────────────────────────────────
export const SK_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Bezkontaktné prihlásenie | Visitly',
    PRE_ARRIVAL: 'Pred príchodom | Visitly',
  },
  HOME: {
    TITLE: 'Bezkontaktné prihlásenie',
    SUBTITLE: 'Nasmerujte kameru na QR kód zobrazený na iPade alebo tlačenom oznámení a pokračujte',
  },
  COMMON: {
    BACK: 'Späť',
    CONTINUE: 'Pokračovať',
    AGREE_AND_CONTINUE: 'Súhlasiť a pokračovať',
    DECLINE: 'Odmietnuť',
    CLEAR_SIGNATURE: 'Vymazať podpis',
    LOADING: 'Načítavanie...',
    SEARCHING: 'Vyhľadávanie...',
    NO: 'NIE',
    YES: 'ÁNO',
    ENABLE_CAMERA: 'Povoliť kameru',
    PAGE_NOT_FOUND: '404 - Stránka sa nenašla',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: 'Opakovať',
    CAPTURE: 'Zachytiť',
    RETRY: 'Skúsiť znova',
    POINT_OF_ENTRY: 'Vstupný bod',
    PARKING_LOT: 'Parkovisko',
    DESTINATION: 'Budova',
    ERROR_PROCESSING_REQUEST: 'Chyba pri spracovaní požiadavky. Skúste to prosím znova.',
    SESSION_EXPIRED: 'Relácia vypršala. Prosím, znovu otvorte odkaz z vášho e-mailu.',
  },
  FULL_NAME: {
    TITLE: 'Aké je vaše celé meno?',
    SUBTITLE: 'Aby sme vedeli, kto nás dnes navštevuje',
    FORM_LABEL: 'Celé meno',
    PLACEHOLDER: 'napr. Jozef Novák',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Vyberte si prosím typ návštevy.',
    FALLBACK_TITLE: 'Čo vás sem privádza?',
  },
  SELECT_HOST: {
    TITLE: 'Koho navštevujete?',
    SUBTITLE: 'Vyhľadajte svojho hostiteľa, aby sme ho informovali o vašom príchode.',
    PLACEHOLDER: 'Vybrať hostiteľa',
    SEARCH_PLACEHOLDER: 'Hľadať hostiteľa...',
    SEARCH_PLACEHOLDER_ALT: 'Hľadať hostiteľa podľa mena...',
    NO_HOSTS_FOUND: 'Žiadni hostiteľia sa nenašli',
    MIN_CHARS_MESSAGE: 'Zadajte aspoň 3 znaky na vyhľadávanie',
  },
  VISITOR_FORM: {
    TITLE: 'Podrobnosti návštevy',
    SUBTITLE: 'Prosím, vyplňte požadované informácie nižšie.',
    SELECT_FIELD_PLACEHOLDER: 'Vybrať {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Prosím, zadajte vaše celé meno',
    EMAIL_REQUIRED: 'Prosím, zadajte platnú e-mailovú adresu',
    EMAIL_INVALID: 'Prosím, zadajte platnú e-mailovú adresu',
    HOST_REQUIRED: 'Prosím, vyberte meno vášho hostiteľa',
    COMPANY_NAME_REQUIRED: 'Prosím, zadajte názov vašej spoločnosti',
    PHONE_NUMBER_REQUIRED: 'Prosím, zadajte vaše telefónne číslo',
    CUSTOM_FIELD_REQUIRED: 'Prosím, zadajte {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} je povinné',
    FULL_NAME_INVALID: 'Zadajte prosím platné meno.',
    TOAST_ERROR_PROCESSING: 'Pri spracovaní požiadavky došlo k chybe. Skúste znova.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Prosím, skontrolujte a podpíšte dokument nižšie',
    ERROR_DOCUMENT_EXPIRY: 'Chyba pri kontrole platnosti dokumentu',
    ERROR_LOADING_CONTENT: 'Chyba pri načítaní obsahu dokumentu',
    ERROR_FETCHING_DOCUMENT: 'Chyba pri získavaní dokumentu',
    ERROR_RESTORE_SESSION: 'Reláciu podpisovania dokumentu sa nepodarilo obnoviť. Prosím, reštartujte z domovskej stránky.',
    BTN_COLLAPSE: 'Zbaliť',
    BTN_EXPAND: 'Rozbaliť',
  },
  PRIVACY_POLICY: {
    TITLE: 'Skôr ako pokračujete',
    SUBTITLE: 'Prosím, prečítajte si a odsúhlaste pred pokračovaním',
    IMPLICIT_CONSENT: 'Kliknutím na tlačidlo pokračovať nižšie súhlasíte s tým, aby {orgName} ukladal a spracovával osobné informácie odoslané vyššie na poskytnutie požadovaného obsahu.',
    EXPLICIT_CONSENT: 'Súhlasím s tým, aby {orgName} ukladal a spracovával moje osobné údaje.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Vybrať doklad',
    SUBTITLE: 'Prosím, vyberte typ dokladu na nahratie',
    ID_TYPES: [
      { label: 'Cestovný pas', value: 'PASSPORT' },
      { label: 'Vodičský preukaz', value: 'DRIVING_LICENCE' },
      { label: 'Občiansky preukaz', value: 'STATE_ISSUED_ID' },
      { label: 'Mám iný doklad', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Odfotiť sa',
    SUBTITLE: 'Prosím, vystredzte tvár a kliknite na pokračovať',
    INSTRUCTIONS: 'Použite prednú kameru, pozerajte priamo dopredu a vystredzte svoju tvár.',
    BTN_CAPTURE: 'Zachytiť',
    BTN_RETAKE: 'Opakovať',
    BTN_ENABLE_CAMERA: 'Povoliť kameru',
    PERMISSION_PROMPT: 'Ťuknite na „Povoliť kameru" na udelenie prístupu k fotoaparátu.',
    PERMISSION_DENIED: 'Prístup ku kamere je zablokovaný. Povoľte ho v nastaveniach prehliadača a znovu ťuknite na Povoliť kameru.',
    PERMISSION_UNSUPPORTED: 'Na tomto zariadení sa nenašla žiadna kamera.',
    TOAST_CAMERA_ERROR: 'Chyba prístupu ku kamere. Skontrolujte konzolu!',
    BTN_RETRY_CAMERA: 'Skúsiť kameru znova',
    CAMERA_NOT_AVAILABLE: 'Kameru sa nepodarilo spustiť. Ťuknite na Skúsiť znova alebo obnovte stránku.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Povoliť kameru',
    BTN_START_CAMERA: 'Spustiť kameru',
    BTN_TRY_NOW: 'Skúsiť teraz',
    PERMISSION_PROMPT: 'Ťuknite na „Povoliť kameru" na udelenie prístupu k fotoaparátu.',
    PERMISSION_DENIED: 'Prístup ku kamere je zablokovaný. Povoľte ho v nastaveniach prehliadača a znovu ťuknite na Povoliť kameru.',
    PERMISSION_UNSUPPORTED: 'Na tomto zariadení sa nenašla žiadna kamera.',
    ID_DRIVERS_LICENSE: 'Vodičský preukaz',
    ID_STATE_ISSUED: 'Občiansky preukaz',
    ID_PASSPORT: 'Cestovný pas',
    ID_OTHERS: 'Iné',
    ERROR_CAMERA_IN_USE: 'Kamera sa používa. Zatvorte druhú aplikáciu a skúste to znova.',
    ERROR_CAMERA_INIT_FAILED: 'Inicializácia kamery zlyhala. Skontrolujte prosím oprávnenia.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera sa stále používa. Zatvorte druhú aplikáciu a skúste to znova.',
    ERROR_PLEASE_SCAN_DOCS: 'Prosím, naskenujte doklad(y)',
    SUBTITLE: 'Naskenujte vybraný doklad nižšie.',
    LABEL_FRONT_SIDE: 'Predná strana',
    LABEL_BACK_SIDE: 'Zadná strana',
    INSTRUCTION_POSITION_FRONT: 'Priložte prednú stranu vášho dokladu',
    INSTRUCTION_POSITION_BACK: 'Priložte zadnú stranu vášho dokladu',
    MRZ_BAND_HINT: 'Umiestnite dva spodné riadky strany s fotografiou pod prerušovanú čiaru',
    MRZ_SCAN_FAILED: 'Váš pas sa nepodarilo prečítať. Držte ho pokojne s dvoma spodnými riadkami vnútri pásu a skúste to znova.',
    MRZ_SCAN_ATTEMPT: 'Skenovanie {current} z {total}',
    GLARE_WARNING: 'Na doklade je odlesk. Mierne ho nakloňte alebo sa vzdiaľte od svetla.',
    BARCODE_BAND_HINT: 'Držte kartu pokojne, asi 20 cm od fotoaparátu, s čiarovým kódom v rámčeku',
    BARCODE_SCAN_FAILED: 'Čiarový kód sa nepodarilo prečítať. Držte zadnú stranu karty pokojne a rovno vnútri rámčeka a skúste to znova.',
    TORCH_ON: 'Zapnúť svetlo',
    TORCH_OFF: 'Vypnúť svetlo',
    BARCODE_READING_PHOTO: 'Čítanie čiarového kódu…',
    ID_CHECKING: 'Kontrola vášho dokladu…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Takmer hotovo!',
    SUBTITLE: 'Pozrite si video nižšie a pokračujte.',
    BTN_CHECK_IN: 'Prihlásiť sa',
    BTN_FINISH: 'Dokončiť',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registrácia nájdená',
    SUBTITLE: 'Prosím, overte svoje údaje na dokončenie prihlásenia.',
    BTN_NOT_YOU: 'Nie ste to vy?',
    MEETING_WITH_LABEL: 'Stretnutie s {hostName}',
    ONSITE_TITLE: 'Už ste prihlásený',
    ONSITE_SUBTITLE: 'Vyberte svoju reláciu na odhlásenie.',
    CHECKIN_AT_LABEL: 'Prihlásený o:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Ste prihlásení!',
    WALLET_HINT: 'Priepustka sa automaticky otvorí na vašom zariadení.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Predbežná registrácia dokončená',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Pokyny pri príchode',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Na váš registrovaný e-mail bol odoslaný QR kód. Použite ho na rýchlejšie prihlásenie na kiosku po príchode.',
    QR_CAPTION: 'Tento QR kód je možné naskenovať v lobby na zobrazenie vašej registrácie.',
    BTN_SAVE_QR: 'Uložiť QR kód',
    WALLET_LABEL: 'Peňaženka',
    ACTION_ADD_PASS: 'Pridať priepustku',
    CALENDAR_LABEL: 'Kalendár',
    ACTION_SAVE_EVENT: 'Uložiť udalosť',
    DEFAULT_WELCOME_MESSAGE: 'Váš hostiteľ bol upozornený a čoskoro bude s vami.',
    PREFILL_SCHEDULED_FOR: 'Všetko je pripravené na {date}.',
    PREFILL_HOST_NOTIFIED: 'Váš hostiteľ bol upozornený a tešíme sa na vás.',
    TOAST_UPDATE_VISIT_ERROR: 'Pri aktualizácii návštevy došlo k chybe. Skúste znova.',
    TOAST_GENERIC_ERROR: 'Vyskytla sa chyba. Skúste znova!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Podrobnosti návštevy',
    PAGE_SUBTITLE: 'Dokončite kroky nižšie, aby ste urýchlili svoje prihlásenie.',
    SECTION_HEADING: 'Dokončite predbežnú registráciu',
    STEPS_COMPLETED_SUFFIX: 'kroky dokončené',
    BTN_START: 'Začíname!',
  },
  SIGN_OUT: {
    EYEBROW: 'Odhlásenie',
    TITLE_WITH_NAME: 'Dovidenia,',
    TITLE_FALLBACK: 'Dovidenia!',
    SUBTITLE_SESSION: 'Potvrďte svoje údaje a odhláste sa.',
    STRIP_SUBTITLE: 'Momentálne prihlásený',
    LABEL_ORG: 'ORGANIZÁCIA',
    LABEL_LOCATION: 'MIESTO',
    LABEL_HOST: 'HOSTITEĽ',
    LABEL_SIGNED_IN_AT: 'PRIHLÁSENÝ',
    BTN_SIGN_OUT: 'Odhlásiť sa',
    BTN_SIGNING_OUT: 'Odhlasovanie...',
    BTN_START_OVER: 'Začať odznova',
    LABEL_NOT_YOU: 'Nie ste to vy?',
    TOAST_SUCCESS: 'Boli ste úspešne odhlásení!',
    TOAST_ERROR_FALLBACK: 'Chyba pri odhlasovaní. Skúste to prosím znova.',
    CUSTOM_FIELDS_EYEBROW: 'Takmer hotovo',
    CUSTOM_FIELDS_TITLE: 'Dokončiť odhlásenie',
    CUSTOM_FIELDS_SUBTITLE: 'Prosím, vyplňte požadované údaje.',
    BTN_CONFIRM_SIGN_OUT: 'Potvrdiť odhlásenie',
    BTN_GO_BACK: 'Späť',
    SUCCESS_TITLE: 'Boli ste odhlásení',
    SUCCESS_SUBTITLE: 'Ďakujeme za návštevu',
    BTN_DONE: 'Hotovo',
    LABEL_VISIT_DURATION: 'Trvanie návštevy',
    GEOLOCATION_UNAVAILABLE: 'Na odhlásenie je potrebný prístup k polohe. Použite prosím mobilné zariadenie.',
    LOCATION_PERMISSION_REQUIRED: 'Na odhlásenie je potrebné povolenie polohy. Povoľte prístup k polohe a skúste to znova.',
    MUST_BE_ON_SITE: 'Na odhlásenie musíte byť na mieste.',
    GUEST_PLACEHOLDER: 'Hosť',
    SUCCESS_CLOSING: 'Príjemný deň!',
    STATUS_CHECKED_IN: 'Prihlásený',
    STATUS_CHECKED_OUT: 'Odhlásený',
    DETAIL_LABEL_HOST: 'Hostiteľ',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Všetko pripravené na vašu návštevu',
    ALL_SUB_TITLE: 'Vaša predbežná registrácia je dokončená. Podrobnosti návštevy si môžete skontrolovať nižšie.',
    BTN_CHECK_DETAILS: 'Skontrolovať moje údaje',
    BTN_VIEW_CHECKIN: 'Zobraziť bezkontaktné prihlásenie',
    QR_TITLE: 'Bezkontaktné prihlásenie',
    QR_SUBTITLE: 'Nasmerujte kameru na QR kód zobrazený na iPade alebo tlačenom oznámení a pokračujte',
    ERROR_TITLE: 'Niečo sa pokazilo!',
    ERROR_SUBTITLE: 'Ľutujeme, vyskytla sa chyba. Skúste to prosím neskôr.',
    DETAIL_LABEL_VISITOR: 'Návštevník',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Hostiteľ',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Odkaz predbežnej registrácie vypršal',
    SUBTITLE: 'Tento odkaz už nie je platný',
    REASONS_HEADING: 'Možné dôvody:',
    REASON_CANCELLED: 'Pozvánka zrušená',
    REASON_CHECKED_IN: 'Už prihlásený',
    REASON_PAST: 'Plánovaný čas uplynul',
    HELP_PROMPT: 'Potrebujete pomoc?',
    HELP_TEXT: 'Kontaktujte svojho hostiteľa alebo recepciu',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Neplatný odkaz na priepustku.',
    PASS_REVOKED_OR_EXPIRED: 'Táto priepustka bola odvolaná alebo vypršala.',
    UNABLE_TO_LOAD: 'Nepodarilo sa načítať stránku prihlásenia.',
    PASS_EXPIRED_TITLE: 'Priepustka vypršala',
    CONTACT_HOST_FOR_NEW_PASS: 'Kontaktujte prosím svojho hostiteľa pre novú SmartPass priepustku.',
    ERROR_TITLE: 'Niečo sa pokazilo',
    BTN_TRY_AGAIN: 'Skúsiť znova',
    STATUS_NOT_ON_SITE: 'Mimo miesta',
    STATUS_CHECKED_IN: 'Prihlásený',
    SELECT_LOCATION_LABEL: 'Vyberte svoju polohu:',
    SELECT_LOCATION_PLACEHOLDER: '-- Vybrať polohu --',
    BTN_PROCESSING: 'Spracovávanie...',
    BTN_CHECK_IN: 'Prihlásiť sa',
    BTN_CHECK_OUT: 'Odhlásiť sa',
    BTN_CLOSE_HINT: 'Túto stránku môžete zavrieť.',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Neplatný odkaz na priepustku.',
    GOOGLE_WALLET_COMING_SOON: 'Podpora Google Wallet čoskoro. Skúste to neskôr.',
    OPEN_ON_MOBILE: 'Otvorte prosím tento odkaz na svojom iPhone alebo Android telefóne a pridajte priepustku do peňaženky.',
    PREPARING_PASS: 'Pripravujem vašu SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Neplatný odkaz na priepustku.',
    LOCATION_REQUIRED: 'Prihlásenie cez SmartPass vyžaduje prístup k polohe. Otvorte prosím tento odkaz na mobilnom zariadení.',
    LOCATION_PERMISSION_REQUIRED: 'Na prihlásenie cez SmartPass je potrebné povolenie polohy. Povoľte prístup k polohe a skúste to znova.',
    LOCATION_UNAVAILABLE: 'Nepodarilo sa určiť vašu polohu. Uistite sa, že sú povolené služby polohy, a skúste to znova.',
    PASS_REVOKED_OR_EXPIRED: 'Táto priepustka bola odvolaná alebo vypršala.',
    OUTSIDE_GEOFENCE: 'Nachádzate sa mimo oblasti prihlásenia. Priblížte sa prosím k budove a skúste to znova.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Skúsiť znova',
    OPENING_CHECK_IN: 'Otváranie prihlásenia...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'V TOMTO MIESTE PREBIEHA EVAKUÁCIA',
    BTN_TAP_TO_SIGNIN: 'Ťuknutím sa prihlásite',
    BTN_SCAN_QR_CODE: 'Skenovať QR kód',
    TOAST_INVALID_QR: 'Neplatný alebo expirovaný QR kód. Skúste znova!',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: 'KROK ', OF: ' Z ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Ťuknutím spustíte' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Neplatný QR kód. Skúste to prosím znova!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Kameru používame iba na čítanie QR kódu v lobby a žiadne video neukladáme',
    PERMISSION_PROMPT: 'Ťuknite na „Povoliť kameru" na udelenie prístupu k fotoaparátu.',
    PERMISSION_DENIED: 'Prístup ku kamere je zablokovaný. Povoľte ho v nastaveniach prehliadača a znovu ťuknite na Povoliť kameru.',
    PERMISSION_UNSUPPORTED: 'Na tomto zariadení sa nenašla žiadna kamera.',
    CAMERA_NOT_AVAILABLE: 'Kameru sa nepodarilo spustiť. Ťuknite na Skúsiť znova alebo obnovte stránku.',
    ERROR_CAMERA_IN_USE: 'Kamera sa používa. Zatvorte druhú aplikáciu a skúste to znova.',
    ERROR_CAMERA_INIT_FAILED: 'Inicializácia kamery zlyhala. Skontrolujte prosím oprávnenia.',
    ERROR_CAMERA_BUSY_RETRY: 'Kamera sa stále používa. Zatvorte druhú aplikáciu a skúste to znova.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Na skenovanie QR kódov je potrebné povolenie kamery.',
    ACCESS_DENIED: 'Prístup ku kamere zamietnutý alebo nedostupný',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Naozaj chcete zrušiť? Stratíte všetok váš postup.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Vybrať hostiteľa',      prefillTitle: 'Vybrať hostiteľa',      prefillDesc: 'Vyberte, koho navštevujete' },
    DETAILS:    { headerTitle: 'Vaše údaje',            prefillTitle: 'Vaše údaje',            prefillDesc: 'Zadajte kontaktné informácie' },
    DOCUMENT:   { headerTitle: 'Podpísať dokumenty',   prefillTitle: 'Podpísať dokumenty',   prefillDesc: 'Skontrolujte a podpíšte požadované dokumenty' },
    ID_CAPTURE: { headerTitle: 'Overiť doklad',        prefillTitle: 'Overiť doklad',        prefillDesc: 'Naskenujte váš doklad' },
    PHOTO:      { headerTitle: 'Odfotiť sa',           prefillTitle: 'Odfotiť sa',           prefillDesc: 'Zachyťte svoju fotografiu' },
    VIDEO:      { headerTitle: 'Pozrieť video',        prefillTitle: 'Pozrieť video',        prefillDesc: 'Pozrite si bezpečnostné alebo orientačné video' },
  },
};

// ── Swedish ────────────────────────────────────────────────────────────────────
export const SV_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Beröringsfri incheckning | Visitly',
    PRE_ARRIVAL: 'Föranmälan | Visitly',
  },
  HOME: {
    TITLE: 'Beröringsfri incheckning',
    SUBTITLE: 'Rikta kameran mot QR-koden som visas på iPaden eller den utskrivna skylten för att fortsätta',
  },
  COMMON: {
    BACK: 'Tillbaka',
    CONTINUE: 'Fortsätt',
    AGREE_AND_CONTINUE: 'Godkänn och fortsätt',
    DECLINE: 'Avböj',
    CLEAR_SIGNATURE: 'Rensa underskrift',
    LOADING: 'Laddar...',
    SEARCHING: 'Söker...',
    NO: 'NEJ',
    YES: 'JA',
    ENABLE_CAMERA: 'Aktivera kamera',
    PAGE_NOT_FOUND: '404 - Sidan hittades inte',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: 'Ta om',
    CAPTURE: 'Fotografera',
    RETRY: 'Försök igen',
    POINT_OF_ENTRY: 'Ingång',
    PARKING_LOT: 'Parkering',
    DESTINATION: 'Byggnad',
    ERROR_PROCESSING_REQUEST: 'Fel vid behandling av begäran. Försök igen.',
    SESSION_EXPIRED: 'Sessionen har gått ut. Öppna länken från ditt e-postmeddelande igen.',
  },
  FULL_NAME: {
    TITLE: 'Vad är ditt fullständiga namn?',
    SUBTITLE: 'Så att vi vet vem som besöker oss idag',
    FORM_LABEL: 'Fullständigt namn',
    PLACEHOLDER: 't.ex. Anna Andersson',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Välj typ av besök.',
    FALLBACK_TITLE: 'Vad tar dig hit?',
  },
  SELECT_HOST: {
    TITLE: 'Vem besöker du?',
    SUBTITLE: 'Sök efter din värd för att meddela dem om din ankomst.',
    PLACEHOLDER: 'Välj en värd',
    SEARCH_PLACEHOLDER: 'Sök efter värd...',
    SEARCH_PLACEHOLDER_ALT: 'Sök värd efter namn...',
    NO_HOSTS_FOUND: 'Inga värdar hittades',
    MIN_CHARS_MESSAGE: 'Ange minst 3 tecken för att söka',
  },
  VISITOR_FORM: {
    TITLE: 'Besöksdetaljer',
    SUBTITLE: 'Fyll i den begärda informationen nedan.',
    SELECT_FIELD_PLACEHOLDER: 'Välj {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Ange ditt fullständiga namn',
    EMAIL_REQUIRED: 'Ange en giltig e-postadress',
    EMAIL_INVALID: 'Ange en giltig e-postadress',
    HOST_REQUIRED: 'Välj din värds namn',
    COMPANY_NAME_REQUIRED: 'Ange ditt företagsnamn',
    PHONE_NUMBER_REQUIRED: 'Ange ditt telefonnummer',
    CUSTOM_FIELD_REQUIRED: 'Ange {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} är obligatoriskt',
    FULL_NAME_INVALID: 'Ange ett giltigt namn.',
    TOAST_ERROR_PROCESSING: 'Fel vid bearbetning av begäran. Försök igen.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Granska och signera dokumentet nedan',
    ERROR_DOCUMENT_EXPIRY: 'Fel vid kontroll av dokumentets giltighetstid',
    ERROR_LOADING_CONTENT: 'Fel vid inläsning av dokumentinnehåll',
    ERROR_FETCHING_DOCUMENT: 'Fel vid hämtning av dokument',
    ERROR_RESTORE_SESSION: 'Det gick inte att återställa dokumentsigneringssessionen. Starta om från startsidan.',
    BTN_COLLAPSE: 'Minimera',
    BTN_EXPAND: 'Expandera',
  },
  PRIVACY_POLICY: {
    TITLE: 'Innan du fortsätter',
    SUBTITLE: 'Läs igenom och godkänn innan du fortsätter',
    IMPLICIT_CONSENT: 'Genom att klicka på fortsätt nedan godkänner du att {orgName} lagrar och behandlar den personliga information som lämnats ovan för att tillhandahålla det begärda innehållet.',
    EXPLICIT_CONSENT: 'Jag godkänner att {orgName} lagrar och behandlar mina personuppgifter.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Välj ID-handling',
    SUBTITLE: 'Välj en typ av ID-handling att ladda upp',
    ID_TYPES: [
      { label: 'Pass', value: 'PASSPORT' },
      { label: 'Körkort', value: 'DRIVING_LICENCE' },
      { label: 'Nationellt ID-kort', value: 'STATE_ISSUED_ID' },
      { label: 'Jag har en annan ID-handling', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Ta foto',
    SUBTITLE: 'Centrera ditt ansikte och klicka på fortsätt',
    INSTRUCTIONS: 'Använd din frontkamera, titta rakt fram och centrera ditt ansikte.',
    BTN_CAPTURE: 'Fotografera',
    BTN_RETAKE: 'Ta om',
    BTN_ENABLE_CAMERA: 'Aktivera kamera',
    PERMISSION_PROMPT: 'Tryck på "Aktivera kamera" för att bevilja kameråtkomst.',
    PERMISSION_DENIED: 'Kameråtkomst är blockerad. Aktivera den i webbläsarinställningarna och tryck sedan på Aktivera kamera igen.',
    PERMISSION_UNSUPPORTED: 'Ingen kamera hittades på den här enheten.',
    TOAST_CAMERA_ERROR: 'Fel vid kameraåtkomst. Kontrollera konsolen!',
    BTN_RETRY_CAMERA: 'Försök med kameran igen',
    CAMERA_NOT_AVAILABLE: 'Kameran kunde inte startas. Tryck på Försök igen eller ladda om sidan.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Aktivera kamera',
    BTN_START_CAMERA: 'Starta kamera',
    BTN_TRY_NOW: 'Prova nu',
    PERMISSION_PROMPT: 'Tryck på "Aktivera kamera" för att bevilja kameråtkomst.',
    PERMISSION_DENIED: 'Kameråtkomst är blockerad. Aktivera den i webbläsarinställningarna och tryck sedan på Aktivera kamera igen.',
    PERMISSION_UNSUPPORTED: 'Ingen kamera hittades på den här enheten.',
    ID_DRIVERS_LICENSE: 'Körkort',
    ID_STATE_ISSUED: 'Nationellt ID-kort',
    ID_PASSPORT: 'Pass',
    ID_OTHERS: 'Övrigt',
    ERROR_CAMERA_IN_USE: 'Kameran används. Stäng den andra appen och försök igen.',
    ERROR_CAMERA_INIT_FAILED: 'Kamerainitiering misslyckades. Kontrollera behörigheterna.',
    ERROR_CAMERA_BUSY_RETRY: 'Kameran används fortfarande. Stäng den andra appen och försök igen.',
    ERROR_PLEASE_SCAN_DOCS: 'Skanna dokument(en)',
    SUBTITLE: 'Skanna det valda dokumentet nedan.',
    LABEL_FRONT_SIDE: 'Framsida',
    LABEL_BACK_SIDE: 'Baksida',
    INSTRUCTION_POSITION_FRONT: 'Placera framsidan av ditt ID',
    INSTRUCTION_POSITION_BACK: 'Placera baksidan av ditt ID',
    MRZ_BAND_HINT: 'Placera de två nedersta raderna på fotosidan under den streckade linjen',
    MRZ_SCAN_FAILED: 'Vi kunde inte läsa ditt pass. Håll det stilla med de två nedersta raderna inom fältet och försök igen.',
    MRZ_SCAN_ATTEMPT: 'Skanning {current} av {total}',
    GLARE_WARNING: 'Reflex på dokumentet. Luta det lite eller flytta dig från ljuset.',
    BARCODE_BAND_HINT: 'Håll kortet stilla, cirka 20 cm bort, med streckkoden inom ramen',
    BARCODE_SCAN_FAILED: 'Vi kunde inte läsa streckkoden. Håll kortets baksida stilla och rakt inom ramen och försök igen.',
    TORCH_ON: 'Tänd lampan',
    TORCH_OFF: 'Släck lampan',
    BARCODE_READING_PHOTO: 'Läser streckkoden…',
    ID_CHECKING: 'Kontrollerar din legitimation…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Nästan klart!',
    SUBTITLE: 'Titta på videon nedan för att fortsätta.',
    BTN_CHECK_IN: 'Checka in',
    BTN_FINISH: 'Avsluta',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registrering hittad',
    SUBTITLE: 'Verifiera dina uppgifter för att slutföra incheckningen.',
    BTN_NOT_YOU: 'Inte du?',
    MEETING_WITH_LABEL: 'Möte med {hostName}',
    ONSITE_TITLE: 'Du är redan incheckad',
    ONSITE_SUBTITLE: 'Välj din session för att logga ut.',
    CHECKIN_AT_LABEL: 'Incheckad kl.:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Du är incheckad!',
    WALLET_HINT: 'Passet öppnas automatiskt på din enhet.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Föranmälan slutförd',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Anvisningar vid ankomst',
    ARRIVAL_INSTRUCTIONS_TEXT: 'En QR-kod har skickats till din registrerade e-postadress. Använd den för snabbare incheckning i kiosken när du anländer.',
    QR_CAPTION: 'Denna QR-kod kan skannas i lobbyn för att hämta din registrering.',
    BTN_SAVE_QR: 'Spara QR-kod',
    WALLET_LABEL: 'Plånbok',
    ACTION_ADD_PASS: 'Lägg till pass',
    CALENDAR_LABEL: 'Kalender',
    ACTION_SAVE_EVENT: 'Spara händelse',
    DEFAULT_WELCOME_MESSAGE: 'Din värd har meddelats och kommer snart till dig.',
    PREFILL_SCHEDULED_FOR: 'Allt är klart för {date}.',
    PREFILL_HOST_NOTIFIED: 'Din värd har meddelats och vi ser fram emot att välkomna dig.',
    TOAST_UPDATE_VISIT_ERROR: 'Fel vid uppdatering av besöket. Försök igen.',
    TOAST_GENERIC_ERROR: 'Ett fel uppstod. Försök igen!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Besöksdetaljer',
    PAGE_SUBTITLE: 'Slutför stegen nedan för att påskynda din incheckning.',
    SECTION_HEADING: 'Slutför din föranmälan',
    STEPS_COMPLETED_SUFFIX: 'steg slutförda',
    BTN_START: 'Nu kör vi!',
  },
  SIGN_OUT: {
    EYEBROW: 'Utcheckning',
    TITLE_WITH_NAME: 'Hej då,',
    TITLE_FALLBACK: 'Hej då!',
    SUBTITLE_SESSION: 'Bekräfta dina uppgifter och checka ut.',
    STRIP_SUBTITLE: 'Incheckad just nu',
    LABEL_ORG: 'ORGANISATION',
    LABEL_LOCATION: 'PLATS',
    LABEL_HOST: 'VÄRD',
    LABEL_SIGNED_IN_AT: 'INCHECKAD',
    BTN_SIGN_OUT: 'Checka ut',
    BTN_SIGNING_OUT: 'Checkar ut...',
    BTN_START_OVER: 'Börja om',
    LABEL_NOT_YOU: 'Inte du?',
    TOAST_SUCCESS: 'Du har checkats ut!',
    TOAST_ERROR_FALLBACK: 'Fel vid utcheckning. Försök igen.',
    CUSTOM_FIELDS_EYEBROW: 'Nästan klart',
    CUSTOM_FIELDS_TITLE: 'Slutför utcheckning',
    CUSTOM_FIELDS_SUBTITLE: 'Fyll i de obligatoriska uppgifterna.',
    BTN_CONFIRM_SIGN_OUT: 'Bekräfta utcheckning',
    BTN_GO_BACK: 'Gå tillbaka',
    SUCCESS_TITLE: 'Du är utcheckad',
    SUCCESS_SUBTITLE: 'Tack för ditt besök',
    BTN_DONE: 'Klar',
    LABEL_VISIT_DURATION: 'Besökets varaktighet',
    GEOLOCATION_UNAVAILABLE: 'Platsåtkomst krävs för utcheckning. Använd en mobil enhet.',
    LOCATION_PERMISSION_REQUIRED: 'Platstillstånd krävs för utcheckning. Tillåt platsåtkomst och försök igen.',
    MUST_BE_ON_SITE: 'Du måste vara på plats för att checka ut.',
    GUEST_PLACEHOLDER: 'Gäst',
    SUCCESS_CLOSING: 'Ha en bra dag!',
    STATUS_CHECKED_IN: 'Incheckad',
    STATUS_CHECKED_OUT: 'Utcheckad',
    DETAIL_LABEL_HOST: 'Värd',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Allt klart för ditt besök',
    ALL_SUB_TITLE: 'Din föranmälan är klar. Du kan granska besöksdetaljerna nedan.',
    BTN_CHECK_DETAILS: 'Kontrollera mina uppgifter',
    BTN_VIEW_CHECKIN: 'Visa beröringsfri incheckning',
    QR_TITLE: 'Beröringsfri incheckning',
    QR_SUBTITLE: 'Rikta kameran mot QR-koden som visas på iPaden eller den utskrivna skylten för att fortsätta',
    ERROR_TITLE: 'Något gick fel!',
    ERROR_SUBTITLE: 'Vi beklagar, ett fel uppstod. Försök igen senare.',
    DETAIL_LABEL_VISITOR: 'Besökare',
    DETAIL_LABEL_EMAIL: 'E-post',
    DETAIL_LABEL_HOST: 'Värd',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Länk för föranmälan har gått ut',
    SUBTITLE: 'Den här länken är inte längre giltig',
    REASONS_HEADING: 'Möjliga orsaker:',
    REASON_CANCELLED: 'Inbjudan avbokad',
    REASON_CHECKED_IN: 'Redan incheckad',
    REASON_PAST: 'Schemalagd tid har passerat',
    HELP_PROMPT: 'Behöver du hjälp?',
    HELP_TEXT: 'Kontakta din värd eller receptionen',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Ogiltig passlänk.',
    PASS_REVOKED_OR_EXPIRED: 'Det här passet har återkallats eller gått ut.',
    UNABLE_TO_LOAD: 'Det gick inte att läsa in incheckningssidan.',
    PASS_EXPIRED_TITLE: 'Passet har gått ut',
    CONTACT_HOST_FOR_NEW_PASS: 'Kontakta din värd för ett nytt SmartPass.',
    ERROR_TITLE: 'Något gick fel',
    BTN_TRY_AGAIN: 'Försök igen',
    STATUS_NOT_ON_SITE: 'Inte på plats',
    STATUS_CHECKED_IN: 'Incheckad',
    SELECT_LOCATION_LABEL: 'Välj din plats:',
    SELECT_LOCATION_PLACEHOLDER: '-- Välj plats --',
    BTN_PROCESSING: 'Bearbetar...',
    BTN_CHECK_IN: 'Checka in',
    BTN_CHECK_OUT: 'Checka ut',
    BTN_CLOSE_HINT: 'Du kan stänga den här sidan.',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Ogiltig passlänk.',
    GOOGLE_WALLET_COMING_SOON: 'Stöd för Google Wallet kommer snart. Återkom senare.',
    OPEN_ON_MOBILE: 'Öppna den här länken på din iPhone eller Android-telefon för att lägga till passet i din plånbok.',
    PREPARING_PASS: 'Förbereder ditt SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Ogiltig passlänk.',
    LOCATION_REQUIRED: 'SmartPass-incheckning kräver platsåtkomst. Öppna den här länken på din mobila enhet.',
    LOCATION_PERMISSION_REQUIRED: 'Platstillstånd krävs för SmartPass-incheckning. Tillåt platsåtkomst och försök igen.',
    LOCATION_UNAVAILABLE: 'Det gick inte att fastställa din plats. Kontrollera att platstjänsterna är aktiverade och försök igen.',
    PASS_REVOKED_OR_EXPIRED: 'Det här passet har återkallats eller gått ut.',
    OUTSIDE_GEOFENCE: 'Du befinner dig utanför incheckningsområdet. Gå närmare byggnaden och försök igen.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Försök igen',
    OPENING_CHECK_IN: 'Öppnar incheckning...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'EN EVAKUERING PÅGÅR PÅ DENNA PLATS',
    BTN_TAP_TO_SIGNIN: 'Tryck för att checka in',
    BTN_SCAN_QR_CODE: 'Skanna QR-kod',
    TOAST_INVALID_QR: 'Ogiltig eller utgången QR-kod. Försök igen!',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: 'STEG ', OF: ' AV ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Tryck för att starta' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Ogiltig QR-kod. Försök igen!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Vi använder bara din kamera för att läsa lobby-QR-koden och lagrar ingen video',
    PERMISSION_PROMPT: 'Tryck på "Aktivera kamera" för att bevilja kameråtkomst.',
    PERMISSION_DENIED: 'Kameråtkomst är blockerad. Aktivera den i webbläsarinställningarna och tryck sedan på Aktivera kamera igen.',
    PERMISSION_UNSUPPORTED: 'Ingen kamera hittades på den här enheten.',
    CAMERA_NOT_AVAILABLE: 'Kameran kunde inte startas. Tryck på Försök igen eller ladda om sidan.',
    ERROR_CAMERA_IN_USE: 'Kameran används. Stäng den andra appen och försök igen.',
    ERROR_CAMERA_INIT_FAILED: 'Kamerainitiering misslyckades. Kontrollera behörigheterna.',
    ERROR_CAMERA_BUSY_RETRY: 'Kameran används fortfarande. Stäng den andra appen och försök igen.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Kameratillstånd krävs för att skanna QR-koder.',
    ACCESS_DENIED: 'Kameråtkomst nekad eller inte tillgänglig',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'Är du säker på att du vill avbryta? Du förlorar all din framsteg.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Välj värd',              prefillTitle: 'Välj värd',              prefillDesc: 'Välj vem du besöker' },
    DETAILS:    { headerTitle: 'Dina uppgifter',         prefillTitle: 'Dina uppgifter',         prefillDesc: 'Ange din kontaktinformation' },
    DOCUMENT:   { headerTitle: 'Signera dokument',      prefillTitle: 'Signera dokument',      prefillDesc: 'Granska och signera nödvändiga dokument' },
    ID_CAPTURE: { headerTitle: 'Verifiera ID',          prefillTitle: 'Verifiera ID',          prefillDesc: 'Skanna ditt ID' },
    PHOTO:      { headerTitle: 'Ta en selfie',          prefillTitle: 'Ta en selfie',          prefillDesc: 'Ta ditt foto' },
    VIDEO:      { headerTitle: 'Titta på video',        prefillTitle: 'Titta på video',        prefillDesc: 'Se ett säkerhets- eller orienteringsvideo' },
  },
};

// ── Thai ───────────────────────────────────────────────────────────────────────
export const TH_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'เช็คอินแบบไม่สัมผัส | Visitly',
    PRE_ARRIVAL: 'ลงทะเบียนล่วงหน้า | Visitly',
  },
  HOME: {
    TITLE: 'เช็คอินแบบไม่สัมผัส',
    SUBTITLE: 'ชี้กล้องไปที่รหัส QR ที่แสดงบน iPad หรือป้ายที่พิมพ์ออกมาเพื่อดำเนินการต่อ',
  },
  COMMON: {
    BACK: 'ย้อนกลับ',
    CONTINUE: 'ดำเนินการต่อ',
    AGREE_AND_CONTINUE: 'ยอมรับและดำเนินการต่อ',
    DECLINE: 'ปฏิเสธ',
    CLEAR_SIGNATURE: 'ล้างลายเซ็น',
    LOADING: 'กำลังโหลด...',
    SEARCHING: 'กำลังค้นหา...',
    NO: 'ไม่',
    YES: 'ใช่',
    ENABLE_CAMERA: 'เปิดใช้งานกล้อง',
    PAGE_NOT_FOUND: '404 - ไม่พบหน้านี้',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: 'ถ่ายใหม่',
    CAPTURE: 'ถ่ายภาพ',
    RETRY: 'ลองใหม่',
    POINT_OF_ENTRY: 'จุดเข้า',
    PARKING_LOT: 'ที่จอดรถ',
    DESTINATION: 'อาคาร',
    ERROR_PROCESSING_REQUEST: 'เกิดข้อผิดพลาดในการประมวลผลคำขอ กรุณาลองใหม่อีกครั้ง',
    SESSION_EXPIRED: 'เซสชันหมดอายุ กรุณาเปิดลิงก์จากอีเมลของคุณอีกครั้ง',
  },
  FULL_NAME: {
    TITLE: 'ชื่อ-นามสกุลของคุณคืออะไร?',
    SUBTITLE: 'เพื่อให้เราทราบว่าใครมาเยี่ยมชมวันนี้',
    FORM_LABEL: 'ชื่อ-นามสกุล',
    PLACEHOLDER: 'เช่น สมชาย ใจดี',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'กรุณาเลือกประเภทการเยี่ยมชม',
    FALLBACK_TITLE: 'คุณมาเพื่ออะไร?',
  },
  SELECT_HOST: {
    TITLE: 'คุณมาพบใคร?',
    SUBTITLE: 'ค้นหาเจ้าบ้านของคุณเพื่อแจ้งการมาถึงของคุณ',
    PLACEHOLDER: 'เลือกเจ้าบ้าน',
    SEARCH_PLACEHOLDER: 'ค้นหาเจ้าบ้าน...',
    SEARCH_PLACEHOLDER_ALT: 'ค้นหาเจ้าบ้านตามชื่อ...',
    NO_HOSTS_FOUND: 'ไม่พบเจ้าบ้าน',
    MIN_CHARS_MESSAGE: 'พิมพ์อย่างน้อย 3 ตัวอักษรเพื่อค้นหา',
  },
  VISITOR_FORM: {
    TITLE: 'รายละเอียดการเยี่ยมชม',
    SUBTITLE: 'กรุณากรอกข้อมูลที่จำเป็นด้านล่าง',
    SELECT_FIELD_PLACEHOLDER: 'เลือก {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'กรุณากรอกชื่อ-นามสกุลของคุณ',
    EMAIL_REQUIRED: 'กรุณากรอกที่อยู่อีเมลที่ถูกต้อง',
    EMAIL_INVALID: 'กรุณากรอกที่อยู่อีเมลที่ถูกต้อง',
    HOST_REQUIRED: 'กรุณาเลือกชื่อเจ้าบ้านของคุณ',
    COMPANY_NAME_REQUIRED: 'กรุณากรอกชื่อบริษัทของคุณ',
    PHONE_NUMBER_REQUIRED: 'กรุณากรอกหมายเลขโทรศัพท์ของคุณ',
    CUSTOM_FIELD_REQUIRED: 'กรุณากรอก {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} เป็นข้อมูลที่จำเป็น',
    FULL_NAME_INVALID: 'กรุณากรอกชื่อที่ถูกต้อง',
    TOAST_ERROR_PROCESSING: 'เกิดข้อผิดพลาดในการประมวลผลคำขอ โปรดลองอีกครั้ง',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'กรุณาตรวจสอบและลงนามในเอกสารด้านล่าง',
    ERROR_DOCUMENT_EXPIRY: 'เกิดข้อผิดพลาดในการตรวจสอบวันหมดอายุของเอกสาร',
    ERROR_LOADING_CONTENT: 'เกิดข้อผิดพลาดในการโหลดเนื้อหาเอกสาร',
    ERROR_FETCHING_DOCUMENT: 'เกิดข้อผิดพลาดในการดึงเอกสาร',
    ERROR_RESTORE_SESSION: 'ไม่สามารถกู้คืนเซสชันการลงนามเอกสารได้ กรุณาเริ่มต้นใหม่จากหน้าหลัก',
    BTN_COLLAPSE: 'ยุบ',
    BTN_EXPAND: 'ขยาย',
  },
  PRIVACY_POLICY: {
    TITLE: 'ก่อนดำเนินการต่อ',
    SUBTITLE: 'กรุณาอ่านและยอมรับก่อนดำเนินการต่อ',
    IMPLICIT_CONSENT: 'การคลิกดำเนินการต่อด้านล่าง แสดงว่าคุณยินยอมให้ {orgName} จัดเก็บและประมวลผลข้อมูลส่วนบุคคลที่ส่งไว้ข้างต้นเพื่อให้บริการเนื้อหาที่คุณร้องขอ',
    EXPLICIT_CONSENT: 'ฉันยินยอมให้ {orgName} จัดเก็บและประมวลผลข้อมูลส่วนบุคคลของฉัน',
  },
  CAPTURE_PICTURE: {
    TITLE: 'เลือกเอกสารประจำตัว',
    SUBTITLE: 'กรุณาเลือกประเภทเอกสารประจำตัวที่ต้องการอัปโหลด',
    ID_TYPES: [
      { label: 'หนังสือเดินทาง', value: 'PASSPORT' },
      { label: 'ใบขับขี่', value: 'DRIVING_LICENCE' },
      { label: 'บัตรประจำตัวประชาชน', value: 'STATE_ISSUED_ID' },
      { label: 'ฉันมีเอกสารประจำตัวอื่น', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'ถ่ายรูป',
    SUBTITLE: 'กรุณาจัดให้ใบหน้าอยู่กึ่งกลางและคลิกดำเนินการต่อ',
    INSTRUCTIONS: 'ใช้กล้องด้านหน้า มองตรงไปข้างหน้า และจัดให้ใบหน้าอยู่กึ่งกลาง',
    BTN_CAPTURE: 'ถ่ายภาพ',
    BTN_RETAKE: 'ถ่ายใหม่',
    BTN_ENABLE_CAMERA: 'เปิดใช้งานกล้อง',
    PERMISSION_PROMPT: 'แตะ "เปิดใช้งานกล้อง" เพื่ออนุญาตการเข้าถึงกล้อง',
    PERMISSION_DENIED: 'การเข้าถึงกล้องถูกบล็อก เปิดใช้งานในการตั้งค่าเบราว์เซอร์ของคุณ แล้วแตะเปิดใช้งานกล้องอีกครั้ง',
    PERMISSION_UNSUPPORTED: 'ไม่พบกล้องในอุปกรณ์นี้',
    TOAST_CAMERA_ERROR: 'เกิดข้อผิดพลาดในการเข้าถึงกล้อง โปรดตรวจสอบคอนโซล!',
    BTN_RETRY_CAMERA: 'ลองใช้กล้องอีกครั้ง',
    CAMERA_NOT_AVAILABLE: 'ไม่สามารถเริ่มกล้องได้ แตะลองอีกครั้งหรือโหลดหน้านี้ใหม่',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'เปิดใช้งานกล้อง',
    BTN_START_CAMERA: 'เริ่มกล้อง',
    BTN_TRY_NOW: 'ลองเลย',
    PERMISSION_PROMPT: 'แตะ "เปิดใช้งานกล้อง" เพื่ออนุญาตการเข้าถึงกล้อง',
    PERMISSION_DENIED: 'การเข้าถึงกล้องถูกบล็อก เปิดใช้งานในการตั้งค่าเบราว์เซอร์ของคุณ แล้วแตะเปิดใช้งานกล้องอีกครั้ง',
    PERMISSION_UNSUPPORTED: 'ไม่พบกล้องในอุปกรณ์นี้',
    ID_DRIVERS_LICENSE: 'ใบขับขี่',
    ID_STATE_ISSUED: 'บัตรประจำตัวประชาชน',
    ID_PASSPORT: 'หนังสือเดินทาง',
    ID_OTHERS: 'อื่นๆ',
    ERROR_CAMERA_IN_USE: 'กล้องกำลังถูกใช้งาน ปิดแอปอื่นแล้วลองใหม่',
    ERROR_CAMERA_INIT_FAILED: 'การเริ่มต้นกล้องล้มเหลว กรุณาตรวจสอบสิทธิ์การใช้งาน',
    ERROR_CAMERA_BUSY_RETRY: 'กล้องยังคงถูกใช้งานอยู่ ปิดแอปอื่นแล้วลองใหม่',
    ERROR_PLEASE_SCAN_DOCS: 'กรุณาสแกนเอกสาร',
    SUBTITLE: 'สแกนเอกสารที่เลือกด้านล่าง',
    LABEL_FRONT_SIDE: 'ด้านหน้า',
    LABEL_BACK_SIDE: 'ด้านหลัง',
    INSTRUCTION_POSITION_FRONT: 'วางด้านหน้าของเอกสารประจำตัวของคุณ',
    INSTRUCTION_POSITION_BACK: 'วางด้านหลังของเอกสารประจำตัวของคุณ',
    MRZ_BAND_HINT: 'จัดสองบรรทัดด้านล่างของหน้ารูปถ่ายให้อยู่ใต้เส้นประ',
    MRZ_SCAN_FAILED: 'ไม่สามารถอ่านหนังสือเดินทางของคุณได้ ถือให้นิ่งโดยให้สองบรรทัดด้านล่างอยู่ในแถบ แล้วลองอีกครั้ง',
    MRZ_SCAN_ATTEMPT: 'สแกนครั้งที่ {current} จาก {total}',
    GLARE_WARNING: 'มีแสงสะท้อนบนเอกสาร เอียงเล็กน้อยหรือถอยห่างจากแสง',
    BARCODE_BAND_HINT: 'ถือบัตรให้นิ่ง ห่างประมาณ 20 ซม. โดยให้บาร์โค้ดอยู่ในกรอบ',
    BARCODE_SCAN_FAILED: 'ไม่สามารถอ่านบาร์โค้ดได้ ถือด้านหลังของบัตรให้นิ่งและตรงภายในกรอบ แล้วลองอีกครั้ง',
    TORCH_ON: 'เปิดไฟ',
    TORCH_OFF: 'ปิดไฟ',
    BARCODE_READING_PHOTO: 'กำลังอ่านบาร์โค้ด…',
    ID_CHECKING: 'กำลังตรวจสอบบัตรประจำตัวของคุณ…',
  },
  VISITOR_VIDEO: {
    TITLE: 'เกือบเสร็จแล้ว!',
    SUBTITLE: 'ดูวิดีโอด้านล่างเพื่อดำเนินการต่อ',
    BTN_CHECK_IN: 'เช็คอิน',
    BTN_FINISH: 'เสร็จสิ้น',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'พบการลงทะเบียน',
    SUBTITLE: 'กรุณายืนยันรายละเอียดของคุณเพื่อดำเนินการเช็คอินให้เสร็จสิ้น',
    BTN_NOT_YOU: 'ไม่ใช่คุณ?',
    MEETING_WITH_LABEL: 'ประชุมกับ {hostName}',
    ONSITE_TITLE: 'คุณเช็คอินอยู่แล้ว',
    ONSITE_SUBTITLE: 'เลือกเซสชันของคุณเพื่อออกจากระบบ',
    CHECKIN_AT_LABEL: 'เช็คอินเมื่อ:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'คุณเช็คอินแล้ว!',
    WALLET_HINT: 'บัตรผ่านจะเปิดโดยอัตโนมัติบนอุปกรณ์ของคุณ',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'การลงทะเบียนล่วงหน้าเสร็จสมบูรณ์',
    ARRIVAL_INSTRUCTIONS_TITLE: 'คำแนะนำเมื่อมาถึง',
    ARRIVAL_INSTRUCTIONS_TEXT: 'รหัส QR ถูกส่งไปยังอีเมลที่ลงทะเบียนของคุณแล้ว ใช้รหัสนั้นสำหรับการเช็คอินที่เร็วขึ้นที่คีออสก์เมื่อคุณมาถึง',
    QR_CAPTION: 'รหัส QR นี้สามารถสแกนได้ที่ล็อบบี้เพื่อดึงข้อมูลการลงทะเบียนของคุณ',
    BTN_SAVE_QR: 'บันทึกรหัส QR',
    WALLET_LABEL: 'กระเป๋าสตางค์',
    ACTION_ADD_PASS: 'เพิ่มบัตรผ่าน',
    CALENDAR_LABEL: 'ปฏิทิน',
    ACTION_SAVE_EVENT: 'บันทึกกิจกรรม',
    DEFAULT_WELCOME_MESSAGE: 'เจ้าของได้รับการแจ้งเตือนแล้ว และจะมาพบคุณในไม่ช้า',
    PREFILL_SCHEDULED_FOR: 'ทุกอย่างพร้อมแล้วสำหรับ {date}',
    PREFILL_HOST_NOTIFIED: 'เจ้าของได้รับการแจ้งเตือนแล้ว และเรารอต้อนรับคุณ',
    TOAST_UPDATE_VISIT_ERROR: 'เกิดข้อผิดพลาดในการอัปเดตการเยี่ยมชม โปรดลองอีกครั้ง',
    TOAST_GENERIC_ERROR: 'เกิดข้อผิดพลาด โปรดลองอีกครั้ง!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'รายละเอียดการเยี่ยมชม',
    PAGE_SUBTITLE: 'ดำเนินการตามขั้นตอนด้านล่างเพื่อเร่งกระบวนการเช็คอินของคุณ',
    SECTION_HEADING: 'ดำเนินการลงทะเบียนล่วงหน้าให้เสร็จสิ้น',
    STEPS_COMPLETED_SUFFIX: 'ขั้นตอนเสร็จสิ้น',
    BTN_START: 'เริ่มกันเลย!',
  },
  SIGN_OUT: {
    EYEBROW: 'เช็คเอาท์',
    TITLE_WITH_NAME: 'ลาก่อน,',
    TITLE_FALLBACK: 'ลาก่อน!',
    SUBTITLE_SESSION: 'ยืนยันรายละเอียดของคุณและเช็คเอาท์',
    STRIP_SUBTITLE: 'เช็คอินอยู่ในขณะนี้',
    LABEL_ORG: 'องค์กร',
    LABEL_LOCATION: 'สถานที่',
    LABEL_HOST: 'เจ้าบ้าน',
    LABEL_SIGNED_IN_AT: 'เวลาเช็คอิน',
    BTN_SIGN_OUT: 'เช็คเอาท์',
    BTN_SIGNING_OUT: 'กำลังเช็คเอาท์...',
    BTN_START_OVER: 'เริ่มใหม่',
    LABEL_NOT_YOU: 'ไม่ใช่คุณ?',
    TOAST_SUCCESS: 'คุณเช็คเอาท์เรียบร้อยแล้ว!',
    TOAST_ERROR_FALLBACK: 'เกิดข้อผิดพลาดในการเช็คเอาท์ กรุณาลองใหม่',
    CUSTOM_FIELDS_EYEBROW: 'เกือบเสร็จแล้ว',
    CUSTOM_FIELDS_TITLE: 'ดำเนินการเช็คเอาท์ให้เสร็จสิ้น',
    CUSTOM_FIELDS_SUBTITLE: 'กรุณากรอกรายละเอียดที่จำเป็น',
    BTN_CONFIRM_SIGN_OUT: 'ยืนยันการเช็คเอาท์',
    BTN_GO_BACK: 'ย้อนกลับ',
    SUCCESS_TITLE: 'คุณเช็คเอาท์แล้ว',
    SUCCESS_SUBTITLE: 'ขอบคุณที่มาเยี่ยมชม',
    BTN_DONE: 'เสร็จสิ้น',
    LABEL_VISIT_DURATION: 'ระยะเวลาการเยี่ยมชม',
    GEOLOCATION_UNAVAILABLE: 'ต้องการการเข้าถึงตำแหน่งเพื่อเช็คเอาท์ กรุณาใช้อุปกรณ์มือถือ',
    LOCATION_PERMISSION_REQUIRED: 'ต้องการสิทธิ์ตำแหน่งเพื่อเช็คเอาท์ กรุณาอนุญาตการเข้าถึงตำแหน่งแล้วลองใหม่',
    MUST_BE_ON_SITE: 'คุณต้องอยู่ในสถานที่เพื่อเช็คเอาท์',
    GUEST_PLACEHOLDER: 'ผู้เยี่ยมชม',
    SUCCESS_CLOSING: 'ขอให้มีวันที่ดี!',
    STATUS_CHECKED_IN: 'เช็คอินแล้ว',
    STATUS_CHECKED_OUT: 'เช็คเอาท์แล้ว',
    DETAIL_LABEL_HOST: 'เจ้าบ้าน',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'พร้อมสำหรับการเยี่ยมชมของคุณแล้ว',
    ALL_SUB_TITLE: 'การลงทะเบียนล่วงหน้าของคุณเสร็จสมบูรณ์แล้ว คุณสามารถตรวจสอบรายละเอียดการเยี่ยมชมด้านล่าง',
    BTN_CHECK_DETAILS: 'ตรวจสอบรายละเอียดของฉัน',
    BTN_VIEW_CHECKIN: 'ดูการเช็คอินแบบไม่สัมผัส',
    QR_TITLE: 'เช็คอินแบบไม่สัมผัส',
    QR_SUBTITLE: 'ชี้กล้องไปที่รหัส QR ที่แสดงบน iPad หรือป้ายที่พิมพ์ออกมาเพื่อดำเนินการต่อ',
    ERROR_TITLE: 'เกิดข้อผิดพลาด!',
    ERROR_SUBTITLE: 'ขออภัย เกิดข้อผิดพลาดขึ้น กรุณาลองใหม่ในภายหลัง',
    DETAIL_LABEL_VISITOR: 'ผู้เยี่ยมชม',
    DETAIL_LABEL_EMAIL: 'อีเมล',
    DETAIL_LABEL_HOST: 'เจ้าบ้าน',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'ลิงก์ลงทะเบียนล่วงหน้าหมดอายุ',
    SUBTITLE: 'ลิงก์นี้ไม่ถูกต้องอีกต่อไป',
    REASONS_HEADING: 'สาเหตุที่เป็นไปได้:',
    REASON_CANCELLED: 'คำเชิญถูกยกเลิก',
    REASON_CHECKED_IN: 'เช็คอินแล้ว',
    REASON_PAST: 'เวลาที่กำหนดไว้ผ่านไปแล้ว',
    HELP_PROMPT: 'ต้องการความช่วยเหลือ?',
    HELP_TEXT: 'ติดต่อเจ้าบ้านหรือแผนกต้อนรับของคุณ',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'ลิงก์บัตรผ่านไม่ถูกต้อง',
    PASS_REVOKED_OR_EXPIRED: 'บัตรผ่านนี้ถูกเพิกถอนหรือหมดอายุแล้ว',
    UNABLE_TO_LOAD: 'ไม่สามารถโหลดหน้าเช็คอินได้',
    PASS_EXPIRED_TITLE: 'บัตรผ่านหมดอายุ',
    CONTACT_HOST_FOR_NEW_PASS: 'กรุณาติดต่อเจ้าบ้านของคุณเพื่อขอ SmartPass ใหม่',
    ERROR_TITLE: 'เกิดข้อผิดพลาด',
    BTN_TRY_AGAIN: 'ลองใหม่',
    STATUS_NOT_ON_SITE: 'ไม่ได้อยู่ในสถานที่',
    STATUS_CHECKED_IN: 'เช็คอินแล้ว',
    SELECT_LOCATION_LABEL: 'เลือกสถานที่ของคุณ:',
    SELECT_LOCATION_PLACEHOLDER: '-- เลือกสถานที่ --',
    BTN_PROCESSING: 'กำลังดำเนินการ...',
    BTN_CHECK_IN: 'เช็คอิน',
    BTN_CHECK_OUT: 'เช็คเอาท์',
    BTN_CLOSE_HINT: 'คุณสามารถปิดหน้านี้ได้',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'ลิงก์บัตรผ่านไม่ถูกต้อง',
    GOOGLE_WALLET_COMING_SOON: 'รองรับ Google Wallet เร็วๆ นี้ กรุณากลับมาตรวจสอบในภายหลัง',
    OPEN_ON_MOBILE: 'กรุณาเปิดลิงก์นี้บน iPhone หรือโทรศัพท์ Android ของคุณเพื่อเพิ่มบัตรผ่านในกระเป๋าสตางค์',
    PREPARING_PASS: 'กำลังเตรียม SmartPass ของคุณ...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'ลิงก์บัตรผ่านไม่ถูกต้อง',
    LOCATION_REQUIRED: 'การเช็คอินด้วย SmartPass ต้องการการเข้าถึงตำแหน่ง กรุณาเปิดลิงก์นี้บนอุปกรณ์มือถือของคุณ',
    LOCATION_PERMISSION_REQUIRED: 'ต้องการสิทธิ์ตำแหน่งสำหรับการเช็คอินด้วย SmartPass กรุณาอนุญาตการเข้าถึงตำแหน่งแล้วลองใหม่',
    LOCATION_UNAVAILABLE: 'ไม่สามารถระบุตำแหน่งของคุณได้ กรุณาตรวจสอบว่าเปิดใช้งานบริการตำแหน่งแล้วลองใหม่',
    PASS_REVOKED_OR_EXPIRED: 'บัตรผ่านนี้ถูกเพิกถอนหรือหมดอายุแล้ว',
    OUTSIDE_GEOFENCE: 'คุณอยู่นอกพื้นที่เช็คอิน กรุณาเข้าใกล้อาคารมากขึ้นแล้วลองใหม่',
    TITLE: 'SmartPass',
    BTN_RETRY: 'ลองใหม่',
    OPENING_CHECK_IN: 'กำลังเปิดการเช็คอิน...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'มีการอพยพฉุกเฉินที่สถานที่นี้',
    BTN_TAP_TO_SIGNIN: 'แตะเพื่อลงชื่อเข้าใช้',
    BTN_SCAN_QR_CODE: 'สแกนรหัส QR',
    TOAST_INVALID_QR: 'รหัส QR ไม่ถูกต้องหรือหมดอายุ โปรดลองอีกครั้ง!',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: 'ขั้นตอน ', OF: ' จาก ' },
  SPLASH_SCREEN: { TAP_TO_START: 'แตะเพื่อเริ่ม' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'รหัส QR ไม่ถูกต้อง กรุณาลองใหม่!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'เราใช้กล้องของคุณเพื่ออ่านรหัส QR ของล็อบบี้เท่านั้น และไม่จัดเก็บวิดีโอใดๆ',
    PERMISSION_PROMPT: 'แตะ "เปิดใช้งานกล้อง" เพื่ออนุญาตการเข้าถึงกล้อง',
    PERMISSION_DENIED: 'การเข้าถึงกล้องถูกบล็อก เปิดใช้งานในการตั้งค่าเบราว์เซอร์ของคุณ แล้วแตะเปิดใช้งานกล้องอีกครั้ง',
    PERMISSION_UNSUPPORTED: 'ไม่พบกล้องในอุปกรณ์นี้',
    CAMERA_NOT_AVAILABLE: 'ไม่สามารถเริ่มกล้องได้ แตะลองอีกครั้งหรือโหลดหน้านี้ใหม่',
    ERROR_CAMERA_IN_USE: 'กล้องกำลังถูกใช้งาน ปิดแอปอื่นแล้วลองใหม่',
    ERROR_CAMERA_INIT_FAILED: 'การเริ่มต้นกล้องล้มเหลว กรุณาตรวจสอบสิทธิ์การใช้งาน',
    ERROR_CAMERA_BUSY_RETRY: 'กล้องยังคงถูกใช้งานอยู่ ปิดแอปอื่นแล้วลองใหม่',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'ต้องการสิทธิ์กล้องเพื่อสแกนรหัส QR',
    ACCESS_DENIED: 'การเข้าถึงกล้องถูกปฏิเสธหรือไม่พร้อมใช้งาน',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: 'คุณแน่ใจหรือไม่ว่าต้องการยกเลิก? คุณจะสูญเสียความคืบหน้าทั้งหมด' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'เลือกเจ้าบ้าน',              prefillTitle: 'เลือกเจ้าบ้าน',              prefillDesc: 'เลือกคนที่คุณต้องการเยี่ยมชม' },
    DETAILS:    { headerTitle: 'ข้อมูลของคุณ',               prefillTitle: 'ข้อมูลของคุณ',               prefillDesc: 'ให้ข้อมูลติดต่อของคุณ' },
    DOCUMENT:   { headerTitle: 'ลงนามในเอกสาร',             prefillTitle: 'ลงนามในเอกสาร',             prefillDesc: 'ตรวจสอบและลงนามในเอกสารที่จำเป็น' },
    ID_CAPTURE: { headerTitle: 'ยืนยันเอกสารประจำตัว',      prefillTitle: 'ยืนยันเอกสารประจำตัว',      prefillDesc: 'สแกนเอกสารประจำตัวของคุณ' },
    PHOTO:      { headerTitle: 'ถ่ายเซลฟี่',                prefillTitle: 'ถ่ายเซลฟี่',                prefillDesc: 'ถ่ายรูปของคุณ' },
    VIDEO:      { headerTitle: 'ดูวิดีโอ',                  prefillTitle: 'ดูวิดีโอ',                  prefillDesc: 'ดูวิดีโอเพื่อความปลอดภัยหรือการปฐมนิเทศ' },
  },
};

// ── Chinese (Simplified) ───────────────────────────────────────────────────────
export const ZH_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: '无接触签到 | Visitly',
    PRE_ARRIVAL: '预先登记 | Visitly',
  },
  HOME: {
    TITLE: '无接触签到',
    SUBTITLE: '将摄像头对准 iPad 或打印标牌上显示的二维码以继续',
  },
  COMMON: {
    BACK: '返回',
    CONTINUE: '继续',
    AGREE_AND_CONTINUE: '同意并继续',
    DECLINE: '拒绝',
    CLEAR_SIGNATURE: '清除签名',
    LOADING: '加载中...',
    SEARCHING: '搜索中...',
    NO: '否',
    YES: '是',
    ENABLE_CAMERA: '启用摄像头',
    PAGE_NOT_FOUND: '404 - 页面未找到',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: '重拍',
    CAPTURE: '拍摄',
    RETRY: '重试',
    POINT_OF_ENTRY: '入口',
    PARKING_LOT: '停车场',
    DESTINATION: '楼宇',
    ERROR_PROCESSING_REQUEST: '处理请求时出错，请重试。',
    SESSION_EXPIRED: '会话已过期，请重新打开邮件中的链接。',
  },
  FULL_NAME: {
    TITLE: '您的全名是什么？',
    SUBTITLE: '以便我们了解今天的访客身份',
    FORM_LABEL: '全名',
    PLACEHOLDER: '例如：张伟',
  },
  VISITOR_TYPES: {
    SUBTITLE: '请选择您的访问类型。',
    FALLBACK_TITLE: '您来访的目的是什么？',
  },
  SELECT_HOST: {
    TITLE: '您要拜访谁？',
    SUBTITLE: '搜索您的接待人，以便通知他们您的到来。',
    PLACEHOLDER: '选择接待人',
    SEARCH_PLACEHOLDER: '搜索接待人...',
    SEARCH_PLACEHOLDER_ALT: '按姓名搜索接待人...',
    NO_HOSTS_FOUND: '未找到接待人',
    MIN_CHARS_MESSAGE: '请输入至少 3 个字符进行搜索',
  },
  VISITOR_FORM: {
    TITLE: '访问详情',
    SUBTITLE: '请填写以下必填信息。',
    SELECT_FIELD_PLACEHOLDER: '请选择 {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: '请输入您的全名',
    EMAIL_REQUIRED: '请输入有效的电子邮件地址',
    EMAIL_INVALID: '请输入有效的电子邮件地址',
    HOST_REQUIRED: '请选择您的接待人姓名',
    COMPANY_NAME_REQUIRED: '请输入您的公司名称',
    PHONE_NUMBER_REQUIRED: '请输入您的电话号码',
    CUSTOM_FIELD_REQUIRED: '请输入 {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} 为必填项',
    FULL_NAME_INVALID: '请输入有效的姓名。',
    TOAST_ERROR_PROCESSING: '处理请求时出错。请重试。',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: '请查阅并签署以下文件',
    ERROR_DOCUMENT_EXPIRY: '检查文件有效期时出错',
    ERROR_LOADING_CONTENT: '加载文件内容时出错',
    ERROR_FETCHING_DOCUMENT: '获取文件时出错',
    ERROR_RESTORE_SESSION: '无法恢复文件签署会话，请从主页重新开始。',
    BTN_COLLAPSE: '收起',
    BTN_EXPAND: '展开',
  },
  PRIVACY_POLICY: {
    TITLE: '继续之前',
    SUBTITLE: '请在继续之前阅读并同意',
    IMPLICIT_CONSENT: '点击下方"继续"，即表示您同意 {orgName} 存储和处理上述个人信息，以提供您所请求的内容。',
    EXPLICIT_CONSENT: '我同意 {orgName} 存储和处理我的个人数据。',
  },
  CAPTURE_PICTURE: {
    TITLE: '选择证件类型',
    SUBTITLE: '请选择要上传的证件类型',
    ID_TYPES: [
      { label: '护照', value: 'PASSPORT' },
      { label: '驾驶证', value: 'DRIVING_LICENCE' },
      { label: '身份证', value: 'STATE_ISSUED_ID' },
      { label: '我有其他证件', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: '拍摄照片',
    SUBTITLE: '请将面部居中并点击继续',
    INSTRUCTIONS: '使用前置摄像头，目视正前方，使面部居中。',
    BTN_CAPTURE: '拍摄',
    BTN_RETAKE: '重拍',
    BTN_ENABLE_CAMERA: '启用摄像头',
    PERMISSION_PROMPT: '点击"启用摄像头"以授予摄像头访问权限。',
    PERMISSION_DENIED: '摄像头访问被阻止，请在浏览器设置中启用，然后再次点击启用摄像头。',
    PERMISSION_UNSUPPORTED: '此设备未找到摄像头。',
    TOAST_CAMERA_ERROR: '相机访问错误。请检查控制台！',
    BTN_RETRY_CAMERA: '重试相机',
    CAMERA_NOT_AVAILABLE: '相机无法启动。点击重试或重新加载页面。',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: '启用摄像头',
    BTN_START_CAMERA: '启动摄像头',
    BTN_TRY_NOW: '立即尝试',
    PERMISSION_PROMPT: '点击"启用摄像头"以授予摄像头访问权限。',
    PERMISSION_DENIED: '摄像头访问被阻止，请在浏览器设置中启用，然后再次点击启用摄像头。',
    PERMISSION_UNSUPPORTED: '此设备未找到摄像头。',
    ID_DRIVERS_LICENSE: '驾驶证',
    ID_STATE_ISSUED: '身份证',
    ID_PASSPORT: '护照',
    ID_OTHERS: '其他',
    ERROR_CAMERA_IN_USE: '摄像头正在使用中，请关闭其他应用后重试。',
    ERROR_CAMERA_INIT_FAILED: '摄像头初始化失败，请检查权限设置。',
    ERROR_CAMERA_BUSY_RETRY: '摄像头仍在使用中，请关闭其他应用后重试。',
    ERROR_PLEASE_SCAN_DOCS: '请扫描文件',
    SUBTITLE: '请扫描以下所选文件。',
    LABEL_FRONT_SIDE: '正面',
    LABEL_BACK_SIDE: '背面',
    INSTRUCTION_POSITION_FRONT: '将证件正面对准摄像头',
    INSTRUCTION_POSITION_BACK: '将证件背面对准摄像头',
    MRZ_BAND_HINT: '请将照片页底部的两行对准虚线下方',
    MRZ_SCAN_FAILED: '无法读取您的护照。请拿稳护照，使底部两行位于条带内，然后重试。',
    MRZ_SCAN_ATTEMPT: '扫描 {current}/{total}',
    GLARE_WARNING: '证件有反光。请稍微倾斜或远离光源。',
    BARCODE_BAND_HINT: '请将卡片保持稳定，距离约 20 厘米，并使条形码位于框内',
    BARCODE_SCAN_FAILED: '无法读取条形码。请将卡片背面平稳地放正在框内，然后重试。',
    TORCH_ON: '打开补光灯',
    TORCH_OFF: '关闭补光灯',
    BARCODE_READING_PHOTO: '正在读取条形码…',
    ID_CHECKING: '正在核验您的证件…',
  },
  VISITOR_VIDEO: {
    TITLE: '即将完成！',
    SUBTITLE: '请观看以下视频以继续。',
    BTN_CHECK_IN: '签到',
    BTN_FINISH: '完成',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: '找到登记信息',
    SUBTITLE: '请核实您的信息以完成签到。',
    BTN_NOT_YOU: '不是您？',
    MEETING_WITH_LABEL: '与 {hostName} 会面',
    ONSITE_TITLE: '您已签到',
    ONSITE_SUBTITLE: '请选择您的会话以签出。',
    CHECKIN_AT_LABEL: '签到时间：',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: '签到成功！',
    WALLET_HINT: '通行证将在您的设备上自动打开。',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: '预先登记已完成',
    ARRIVAL_INSTRUCTIONS_TITLE: '到达须知',
    ARRIVAL_INSTRUCTIONS_TEXT: '二维码已发送至您的注册邮箱，到达时在自助终端使用它可加快签到速度。',
    QR_CAPTION: '此二维码可在大厅扫描以调取您的登记信息。',
    BTN_SAVE_QR: '保存二维码',
    WALLET_LABEL: '钱包',
    ACTION_ADD_PASS: '添加通行证',
    CALENDAR_LABEL: '日历',
    ACTION_SAVE_EVENT: '保存活动',
    DEFAULT_WELCOME_MESSAGE: '您的主人已收到通知，将很快与您见面。',
    PREFILL_SCHEDULED_FOR: '{date} 的访问已准备就绪。',
    PREFILL_HOST_NOTIFIED: '您的主人已收到通知，我们期待欢迎您的光临。',
    TOAST_UPDATE_VISIT_ERROR: '更新访问时出错。请重试。',
    TOAST_GENERIC_ERROR: '发生错误。请重试！',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: '访问详情',
    PAGE_SUBTITLE: '完成以下步骤以加快签到速度。',
    SECTION_HEADING: '完成预先登记',
    STEPS_COMPLETED_SUFFIX: '步骤已完成',
    BTN_START: '开始吧！',
  },
  SIGN_OUT: {
    EYEBROW: '签出',
    TITLE_WITH_NAME: '再见，',
    TITLE_FALLBACK: '再见！',
    SUBTITLE_SESSION: '确认您的信息并签出。',
    STRIP_SUBTITLE: '当前已签到',
    LABEL_ORG: '组织',
    LABEL_LOCATION: '地点',
    LABEL_HOST: '接待人',
    LABEL_SIGNED_IN_AT: '签到时间',
    BTN_SIGN_OUT: '签出',
    BTN_SIGNING_OUT: '正在签出...',
    BTN_START_OVER: '重新开始',
    LABEL_NOT_YOU: '不是您？',
    TOAST_SUCCESS: '您已成功签出！',
    TOAST_ERROR_FALLBACK: '签出时出错，请重试。',
    CUSTOM_FIELDS_EYEBROW: '即将完成',
    CUSTOM_FIELDS_TITLE: '完成签出',
    CUSTOM_FIELDS_SUBTITLE: '请填写必填信息。',
    BTN_CONFIRM_SIGN_OUT: '确认签出',
    BTN_GO_BACK: '返回',
    SUCCESS_TITLE: '您已签出',
    SUCCESS_SUBTITLE: '感谢您的到访',
    BTN_DONE: '完成',
    LABEL_VISIT_DURATION: '访问时长',
    GEOLOCATION_UNAVAILABLE: '签出需要位置访问权限，请使用移动设备。',
    LOCATION_PERMISSION_REQUIRED: '签出需要位置权限，请允许位置访问后重试。',
    MUST_BE_ON_SITE: '您必须在现场才能签出。',
    GUEST_PLACEHOLDER: '访客',
    SUCCESS_CLOSING: '祝您度过愉快的一天！',
    STATUS_CHECKED_IN: '已签到',
    STATUS_CHECKED_OUT: '已签出',
    DETAIL_LABEL_HOST: '接待人',
  },
  PREREGISTER: {
    ALL_SET_TITLE: '您的访问已准备就绪',
    ALL_SUB_TITLE: '预先登记已完成，您可以在下方查看访问详情。',
    BTN_CHECK_DETAILS: '查看我的详情',
    BTN_VIEW_CHECKIN: '查看无接触签到',
    QR_TITLE: '无接触签到',
    QR_SUBTITLE: '将摄像头对准 iPad 或打印标牌上显示的二维码以继续',
    ERROR_TITLE: '出错了！',
    ERROR_SUBTITLE: '抱歉，发生了错误，请稍后重试。',
    DETAIL_LABEL_VISITOR: '访客',
    DETAIL_LABEL_EMAIL: '电子邮件',
    DETAIL_LABEL_HOST: '接待人',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: '预先登记链接已过期',
    SUBTITLE: '此链接已不再有效',
    REASONS_HEADING: '可能的原因：',
    REASON_CANCELLED: '邀请已取消',
    REASON_CHECKED_IN: '已完成签到',
    REASON_PAST: '预定时间已过',
    HELP_PROMPT: '需要帮助？',
    HELP_TEXT: '请联系您的接待人或前台',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: '无效的通行证链接。',
    PASS_REVOKED_OR_EXPIRED: '此通行证已被撤销或已过期。',
    UNABLE_TO_LOAD: '无法加载签到页面。',
    PASS_EXPIRED_TITLE: '通行证已过期',
    CONTACT_HOST_FOR_NEW_PASS: '请联系您的接待人获取新的 SmartPass。',
    ERROR_TITLE: '出现问题',
    BTN_TRY_AGAIN: '重试',
    STATUS_NOT_ON_SITE: '不在现场',
    STATUS_CHECKED_IN: '已签到',
    SELECT_LOCATION_LABEL: '选择您的位置：',
    SELECT_LOCATION_PLACEHOLDER: '-- 选择位置 --',
    BTN_PROCESSING: '处理中...',
    BTN_CHECK_IN: '签到',
    BTN_CHECK_OUT: '签出',
    BTN_CLOSE_HINT: '您可以关闭此页面。',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: '无效的通行证链接。',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet 支持即将推出，请稍后再查看。',
    OPEN_ON_MOBILE: '请在您的 iPhone 或 Android 手机上打开此链接，将通行证添加到您的钱包。',
    PREPARING_PASS: '正在准备您的 SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: '无效的通行证链接。',
    LOCATION_REQUIRED: 'SmartPass 签到需要位置访问权限，请在移动设备上打开此链接。',
    LOCATION_PERMISSION_REQUIRED: 'SmartPass 签到需要位置权限，请允许位置访问后重试。',
    LOCATION_UNAVAILABLE: '无法确定您的位置，请确保位置服务已启用后重试。',
    PASS_REVOKED_OR_EXPIRED: '此通行证已被撤销或已过期。',
    OUTSIDE_GEOFENCE: '您不在签到区域内，请靠近楼宇后重试。',
    TITLE: 'SmartPass',
    BTN_RETRY: '重试',
    OPENING_CHECK_IN: '正在打开签到...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: '此地点正在进行紧急疏散',
    BTN_TAP_TO_SIGNIN: '点击签到',
    BTN_SCAN_QR_CODE: '扫描二维码',
    TOAST_INVALID_QR: '无效或已过期的二维码。请重试！',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: '第 ', OF: ' 步，共 ' },
  SPLASH_SCREEN: { TAP_TO_START: '点击开始' },
  QR_SCANNER: { INVALID_CODE_ERROR: '无效的二维码，请重试！' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: '我们仅使用您的摄像头读取大厅二维码，不存储任何视频',
    PERMISSION_PROMPT: '点击"启用摄像头"以授予摄像头访问权限。',
    PERMISSION_DENIED: '摄像头访问被阻止，请在浏览器设置中启用，然后再次点击启用摄像头。',
    PERMISSION_UNSUPPORTED: '此设备未找到摄像头。',
    CAMERA_NOT_AVAILABLE: '相机无法启动。点击重试或重新加载页面。',
    ERROR_CAMERA_IN_USE: '摄像头正在使用中，请关闭其他应用后重试。',
    ERROR_CAMERA_INIT_FAILED: '摄像头初始化失败，请检查权限设置。',
    ERROR_CAMERA_BUSY_RETRY: '摄像头仍在使用中，请关闭其他应用后重试。',
  },
  CAMERA_PERMISSION: {
    REQUIRED: '扫描二维码需要摄像头权限。',
    ACCESS_DENIED: '摄像头访问被拒绝或不可用',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: '您确定要取消吗？您的所有进度将丢失。' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: '选择接待人',   prefillTitle: '选择接待人',   prefillDesc: '选择您要拜访的人' },
    DETAILS:    { headerTitle: '您的信息',     prefillTitle: '您的信息',     prefillDesc: '提供您的联系信息' },
    DOCUMENT:   { headerTitle: '签署文件',    prefillTitle: '签署文件',    prefillDesc: '查阅并签署所需文件' },
    ID_CAPTURE: { headerTitle: '验证证件',    prefillTitle: '验证证件',    prefillDesc: '扫描您的证件' },
    PHOTO:      { headerTitle: '拍摄自拍',    prefillTitle: '拍摄自拍',    prefillDesc: '拍摄您的照片' },
    VIDEO:      { headerTitle: '观看视频',    prefillTitle: '观看视频',    prefillDesc: '观看安全或入职视频' },
  },
};

// ── Chinese (Taiwan) ──────────────────────────────────────────────────────────
export const ZH_TW_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: '無接觸簽到 | Visitly',
    PRE_ARRIVAL: '預先登記 | Visitly',
  },
  HOME: {
    TITLE: '無接觸簽到',
    SUBTITLE: '將攝影機對準 iPad 或列印標牌上顯示的 QR 碼以繼續',
  },
  COMMON: {
    BACK: '返回',
    CONTINUE: '繼續',
    AGREE_AND_CONTINUE: '同意並繼續',
    DECLINE: '拒絕',
    CLEAR_SIGNATURE: '清除簽名',
    LOADING: '載入中...',
    SEARCHING: '搜尋中...',
    NO: '否',
    YES: '是',
    ENABLE_CAMERA: '啟用攝影機',
    PAGE_NOT_FOUND: '404 - 找不到頁面',
    POWERED_BY: 'Powered by Visitly',
    RETAKE: '重拍',
    CAPTURE: '拍攝',
    RETRY: '重試',
    POINT_OF_ENTRY: '入口',
    PARKING_LOT: '停車場',
    DESTINATION: '大樓',
    ERROR_PROCESSING_REQUEST: '處理請求時發生錯誤，請重試。',
    SESSION_EXPIRED: '工作階段已過期，請重新開啟電子郵件中的連結。',
  },
  FULL_NAME: {
    TITLE: '您的全名是什麼？',
    SUBTITLE: '以便我們了解今天的訪客身份',
    FORM_LABEL: '全名',
    PLACEHOLDER: '例如：張偉',
  },
  VISITOR_TYPES: {
    SUBTITLE: '請選擇您的訪問類型。',
    FALLBACK_TITLE: '您來訪的目的是什麼？',
  },
  SELECT_HOST: {
    TITLE: '您要拜訪誰？',
    SUBTITLE: '搜尋您的接待人，以便通知他們您的到來。',
    PLACEHOLDER: '選擇接待人',
    SEARCH_PLACEHOLDER: '搜尋接待人...',
    SEARCH_PLACEHOLDER_ALT: '按姓名搜尋接待人...',
    NO_HOSTS_FOUND: '未找到接待人',
    MIN_CHARS_MESSAGE: '請輸入至少 3 個字元進行搜尋',
  },
  VISITOR_FORM: {
    TITLE: '訪問詳情',
    SUBTITLE: '請填寫以下必填資訊。',
    SELECT_FIELD_PLACEHOLDER: '請選擇 {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: '請輸入您的全名',
    EMAIL_REQUIRED: '請輸入有效的電子郵件地址',
    EMAIL_INVALID: '請輸入有效的電子郵件地址',
    HOST_REQUIRED: '請選擇您的接待人姓名',
    COMPANY_NAME_REQUIRED: '請輸入您的公司名稱',
    PHONE_NUMBER_REQUIRED: '請輸入您的電話號碼',
    CUSTOM_FIELD_REQUIRED: '請輸入 {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} 為必填項',
    FULL_NAME_INVALID: '請輸入有效的姓名。',
    TOAST_ERROR_PROCESSING: '處理請求時發生錯誤。請重試。',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: '請查閱並簽署以下文件',
    ERROR_DOCUMENT_EXPIRY: '檢查文件有效期時發生錯誤',
    ERROR_LOADING_CONTENT: '載入文件內容時發生錯誤',
    ERROR_FETCHING_DOCUMENT: '取得文件時發生錯誤',
    ERROR_RESTORE_SESSION: '無法恢復文件簽署工作階段，請從首頁重新開始。',
    BTN_COLLAPSE: '收合',
    BTN_EXPAND: '展開',
  },
  PRIVACY_POLICY: {
    TITLE: '繼續之前',
    SUBTITLE: '請在繼續之前閱讀並同意',
    IMPLICIT_CONSENT: '點擊下方「繼續」，即表示您同意 {orgName} 儲存和處理上述個人資訊，以提供您所請求的內容。',
    EXPLICIT_CONSENT: '我同意 {orgName} 儲存和處理我的個人資料。',
  },
  CAPTURE_PICTURE: {
    TITLE: '選擇證件類型',
    SUBTITLE: '請選擇要上傳的證件類型',
    ID_TYPES: [
      { label: '護照', value: 'PASSPORT' },
      { label: '駕駛執照', value: 'DRIVING_LICENCE' },
      { label: '身分證', value: 'STATE_ISSUED_ID' },
      { label: '我有其他證件', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: '拍攝照片',
    SUBTITLE: '請將臉部置中並點擊繼續',
    INSTRUCTIONS: '使用前置攝影機，目視正前方，使臉部置中。',
    BTN_CAPTURE: '拍攝',
    BTN_RETAKE: '重拍',
    BTN_ENABLE_CAMERA: '啟用攝影機',
    PERMISSION_PROMPT: '點擊「啟用攝影機」以授予攝影機存取權限。',
    PERMISSION_DENIED: '攝影機存取被封鎖，請在瀏覽器設定中啟用，然後再次點擊啟用攝影機。',
    PERMISSION_UNSUPPORTED: '此裝置未找到攝影機。',
    TOAST_CAMERA_ERROR: '攝影機存取錯誤。請檢查主控台！',
    BTN_RETRY_CAMERA: '重試攝影機',
    CAMERA_NOT_AVAILABLE: '攝影機無法啟動。點擊重試或重新載入頁面。',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: '啟用攝影機',
    BTN_START_CAMERA: '啟動攝影機',
    BTN_TRY_NOW: '立即嘗試',
    PERMISSION_PROMPT: '點擊「啟用攝影機」以授予攝影機存取權限。',
    PERMISSION_DENIED: '攝影機存取被封鎖，請在瀏覽器設定中啟用，然後再次點擊啟用攝影機。',
    PERMISSION_UNSUPPORTED: '此裝置未找到攝影機。',
    ID_DRIVERS_LICENSE: '駕駛執照',
    ID_STATE_ISSUED: '身分證',
    ID_PASSPORT: '護照',
    ID_OTHERS: '其他',
    ERROR_CAMERA_IN_USE: '攝影機正在使用中，請關閉其他應用程式後重試。',
    ERROR_CAMERA_INIT_FAILED: '攝影機初始化失敗，請檢查權限設定。',
    ERROR_CAMERA_BUSY_RETRY: '攝影機仍在使用中，請關閉其他應用程式後重試。',
    ERROR_PLEASE_SCAN_DOCS: '請掃描文件',
    SUBTITLE: '請掃描以下所選文件。',
    LABEL_FRONT_SIDE: '正面',
    LABEL_BACK_SIDE: '背面',
    INSTRUCTION_POSITION_FRONT: '將證件正面對準攝影機',
    INSTRUCTION_POSITION_BACK: '將證件背面對準攝影機',
    MRZ_BAND_HINT: '請將照片頁底部的兩行對準虛線下方',
    MRZ_SCAN_FAILED: '無法讀取您的護照。請拿穩護照，使底部兩行位於條帶內，然後重試。',
    MRZ_SCAN_ATTEMPT: '掃描 {current}/{total}',
    GLARE_WARNING: '證件有反光。請稍微傾斜或遠離光源。',
    BARCODE_BAND_HINT: '請將卡片保持穩定，距離約 20 公分，並使條碼位於框內',
    BARCODE_SCAN_FAILED: '無法讀取條碼。請將卡片背面平穩地放正在框內，然後重試。',
    TORCH_ON: '開啟補光燈',
    TORCH_OFF: '關閉補光燈',
    BARCODE_READING_PHOTO: '正在讀取條碼…',
    ID_CHECKING: '正在核驗您的證件…',
  },
  VISITOR_VIDEO: {
    TITLE: '即將完成！',
    SUBTITLE: '請觀看以下影片以繼續。',
    BTN_CHECK_IN: '簽到',
    BTN_FINISH: '完成',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: '找到登記資訊',
    SUBTITLE: '請核實您的資訊以完成簽到。',
    BTN_NOT_YOU: '不是您？',
    MEETING_WITH_LABEL: '與 {hostName} 會面',
    ONSITE_TITLE: '您已簽到',
    ONSITE_SUBTITLE: '請選擇您的工作階段以簽出。',
    CHECKIN_AT_LABEL: '簽到時間：',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: '簽到成功！',
    WALLET_HINT: '通行證將在您的裝置上自動開啟。',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: '預先登記已完成',
    ARRIVAL_INSTRUCTIONS_TITLE: '抵達須知',
    ARRIVAL_INSTRUCTIONS_TEXT: 'QR 碼已傳送至您的註冊電子郵件，抵達時在自助終端使用它可加快簽到速度。',
    QR_CAPTION: '此 QR 碼可在大廳掃描以調取您的登記資訊。',
    BTN_SAVE_QR: '儲存 QR 碼',
    WALLET_LABEL: '錢包',
    ACTION_ADD_PASS: '新增通行證',
    CALENDAR_LABEL: '行事曆',
    ACTION_SAVE_EVENT: '儲存活動',
    DEFAULT_WELCOME_MESSAGE: '您的接待人已收到通知，將很快與您見面。',
    PREFILL_SCHEDULED_FOR: '{date} 的訪問已準備就緒。',
    PREFILL_HOST_NOTIFIED: '您的接待人已收到通知，我們期待歡迎您的光臨。',
    TOAST_UPDATE_VISIT_ERROR: '更新訪問時發生錯誤。請重試。',
    TOAST_GENERIC_ERROR: '發生錯誤。請重試！',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: '訪問詳情',
    PAGE_SUBTITLE: '完成以下步驟以加快簽到速度。',
    SECTION_HEADING: '完成預先登記',
    STEPS_COMPLETED_SUFFIX: '步驟已完成',
    BTN_START: '開始吧！',
  },
  SIGN_OUT: {
    EYEBROW: '簽出',
    TITLE_WITH_NAME: '再見，',
    TITLE_FALLBACK: '再見！',
    SUBTITLE_SESSION: '確認您的資訊並簽出。',
    STRIP_SUBTITLE: '目前已簽到',
    LABEL_ORG: '組織',
    LABEL_LOCATION: '地點',
    LABEL_HOST: '接待人',
    LABEL_SIGNED_IN_AT: '簽到時間',
    BTN_SIGN_OUT: '簽出',
    BTN_SIGNING_OUT: '正在簽出...',
    BTN_START_OVER: '重新開始',
    LABEL_NOT_YOU: '不是您？',
    TOAST_SUCCESS: '您已成功簽出！',
    TOAST_ERROR_FALLBACK: '簽出時發生錯誤，請重試。',
    CUSTOM_FIELDS_EYEBROW: '即將完成',
    CUSTOM_FIELDS_TITLE: '完成簽出',
    CUSTOM_FIELDS_SUBTITLE: '請填寫必填資訊。',
    BTN_CONFIRM_SIGN_OUT: '確認簽出',
    BTN_GO_BACK: '返回',
    SUCCESS_TITLE: '您已簽出',
    SUCCESS_SUBTITLE: '感謝您的到訪',
    BTN_DONE: '完成',
    LABEL_VISIT_DURATION: '訪問時長',
    GEOLOCATION_UNAVAILABLE: '簽出需要位置存取權限，請使用行動裝置。',
    LOCATION_PERMISSION_REQUIRED: '簽出需要位置權限，請允許位置存取後重試。',
    MUST_BE_ON_SITE: '您必須在現場才能簽出。',
    GUEST_PLACEHOLDER: '訪客',
    SUCCESS_CLOSING: '祝您度過愉快的一天！',
    STATUS_CHECKED_IN: '已簽到',
    STATUS_CHECKED_OUT: '已簽出',
    DETAIL_LABEL_HOST: '接待人',
  },
  PREREGISTER: {
    ALL_SET_TITLE: '您的訪問已準備就緒',
    ALL_SUB_TITLE: '預先登記已完成，您可以在下方查看訪問詳情。',
    BTN_CHECK_DETAILS: '查看我的詳情',
    BTN_VIEW_CHECKIN: '查看無接觸簽到',
    QR_TITLE: '無接觸簽到',
    QR_SUBTITLE: '將攝影機對準 iPad 或列印標牌上顯示的 QR 碼以繼續',
    ERROR_TITLE: '出錯了！',
    ERROR_SUBTITLE: '抱歉，發生了錯誤，請稍後重試。',
    DETAIL_LABEL_VISITOR: '訪客',
    DETAIL_LABEL_EMAIL: '電子郵件',
    DETAIL_LABEL_HOST: '接待人',
    DETAIL_HOST_PLACEHOLDER: 'TBD',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: '預先登記連結已過期',
    SUBTITLE: '此連結已不再有效',
    REASONS_HEADING: '可能的原因：',
    REASON_CANCELLED: '邀請已取消',
    REASON_CHECKED_IN: '已完成簽到',
    REASON_PAST: '預定時間已過',
    HELP_PROMPT: '需要協助？',
    HELP_TEXT: '請聯絡您的接待人或櫃台',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: '無效的通行證連結。',
    PASS_REVOKED_OR_EXPIRED: '此通行證已被撤銷或已過期。',
    UNABLE_TO_LOAD: '無法載入簽到頁面。',
    PASS_EXPIRED_TITLE: '通行證已過期',
    CONTACT_HOST_FOR_NEW_PASS: '請聯絡您的接待人取得新的 SmartPass。',
    ERROR_TITLE: '出現問題',
    BTN_TRY_AGAIN: '重試',
    STATUS_NOT_ON_SITE: '不在現場',
    STATUS_CHECKED_IN: '已簽到',
    SELECT_LOCATION_LABEL: '選擇您的位置：',
    SELECT_LOCATION_PLACEHOLDER: '-- 選擇位置 --',
    BTN_PROCESSING: '處理中...',
    BTN_CHECK_IN: '簽到',
    BTN_CHECK_OUT: '簽出',
    BTN_CLOSE_HINT: '您可以關閉此頁面。',
    POWERED_BY: 'Powered by Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: '無效的通行證連結。',
    GOOGLE_WALLET_COMING_SOON: 'Google Wallet 支援即將推出，請稍後再查看。',
    OPEN_ON_MOBILE: '請在您的 iPhone 或 Android 手機上開啟此連結，將通行證新增到您的錢包。',
    PREPARING_PASS: '正在準備您的 SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: '無效的通行證連結。',
    LOCATION_REQUIRED: 'SmartPass 簽到需要位置存取權限，請在行動裝置上開啟此連結。',
    LOCATION_PERMISSION_REQUIRED: 'SmartPass 簽到需要位置權限，請允許位置存取後重試。',
    LOCATION_UNAVAILABLE: '無法確定您的位置，請確保位置服務已啟用後重試。',
    PASS_REVOKED_OR_EXPIRED: '此通行證已被撤銷或已過期。',
    OUTSIDE_GEOFENCE: '您不在簽到區域內，請靠近大樓後重試。',
    TITLE: 'SmartPass',
    BTN_RETRY: '重試',
    OPENING_CHECK_IN: '正在開啟簽到...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: '此地點正在進行緊急疏散',
    BTN_TAP_TO_SIGNIN: '點擊簽到',
    BTN_SCAN_QR_CODE: '掃描 QR 碼',
    TOAST_INVALID_QR: '無效或已過期的 QR 碼。請重試！',
  },
  FOOTER: { POWERED_BY: 'POWERED BY ' },
  HEADER: { STEP: '第 ', OF: ' 步，共 ' },
  SPLASH_SCREEN: { TAP_TO_START: '點擊開始' },
  QR_SCANNER: { INVALID_CODE_ERROR: '無效的 QR 碼，請重試！' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: '我們僅使用您的攝影機讀取大廳 QR 碼，不儲存任何影片',
    PERMISSION_PROMPT: '點擊「啟用攝影機」以授予攝影機存取權限。',
    PERMISSION_DENIED: '攝影機存取被封鎖，請在瀏覽器設定中啟用，然後再次點擊啟用攝影機。',
    PERMISSION_UNSUPPORTED: '此裝置未找到攝影機。',
    CAMERA_NOT_AVAILABLE: '攝影機無法啟動。點擊重試或重新載入頁面。',
    ERROR_CAMERA_IN_USE: '攝影機正在使用中，請關閉其他應用程式後重試。',
    ERROR_CAMERA_INIT_FAILED: '攝影機初始化失敗，請檢查權限設定。',
    ERROR_CAMERA_BUSY_RETRY: '攝影機仍在使用中，請關閉其他應用程式後重試。',
  },
  CAMERA_PERMISSION: {
    REQUIRED: '掃描 QR 碼需要攝影機權限。',
    ACCESS_DENIED: '攝影機存取被拒絕或不可用',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: '您確定要取消嗎？您的所有進度將遺失。' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: '選擇接待人',   prefillTitle: '選擇接待人',   prefillDesc: '選擇您要拜訪的人' },
    DETAILS:    { headerTitle: '您的資訊',     prefillTitle: '您的資訊',     prefillDesc: '提供您的聯絡資訊' },
    DOCUMENT:   { headerTitle: '簽署文件',    prefillTitle: '簽署文件',    prefillDesc: '查閱並簽署所需文件' },
    ID_CAPTURE: { headerTitle: '驗證證件',    prefillTitle: '驗證證件',    prefillDesc: '掃描您的證件' },
    PHOTO:      { headerTitle: '拍攝自拍',    prefillTitle: '拍攝自拍',    prefillDesc: '拍攝您的照片' },
    VIDEO:      { headerTitle: '觀看影片',    prefillTitle: '觀看影片',    prefillDesc: '觀看安全或入職影片' },
  },
};

// ── Spanish (Mexico) ───────────────────────────────────────────────────────────
export const ES_MX_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Registro sin contacto | Visitly',
    PRE_ARRIVAL: 'Pre-llegada | Visitly',
  },
  HOME: {
    TITLE: 'Registro sin contacto',
    SUBTITLE: 'Apunta tu cámara al código QR que aparece en el iPad o en el letrero impreso para continuar',
  },
  COMMON: {
    BACK: 'Atrás',
    CONTINUE: 'Continuar',
    AGREE_AND_CONTINUE: 'Aceptar y continuar',
    DECLINE: 'Rechazar',
    CLEAR_SIGNATURE: 'Borrar firma',
    LOADING: 'Cargando...',
    SEARCHING: 'Buscando...',
    NO: 'NO',
    YES: 'SÍ',
    ENABLE_CAMERA: 'Activar cámara',
    PAGE_NOT_FOUND: '404 - Página no encontrada',
    POWERED_BY: 'Desarrollado por Visitly',
    RETAKE: 'Volver a tomar',
    CAPTURE: 'Capturar',
    RETRY: 'Reintentar',
    POINT_OF_ENTRY: 'Punto de entrada',
    PARKING_LOT: 'Estacionamiento',
    DESTINATION: 'Edificio',
    ERROR_PROCESSING_REQUEST: 'Hubo un error al procesar tu solicitud. Por favor, inténtalo de nuevo.',
    SESSION_EXPIRED: 'Tu sesión expiró. Por favor, vuelve a abrir el enlace desde tu correo electrónico.',
  },
  FULL_NAME: {
    TITLE: '¿Cuál es tu nombre completo?',
    SUBTITLE: 'Para saber quién nos visita hoy',
    FORM_LABEL: 'Nombre completo',
    PLACEHOLDER: 'p. ej. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Por favor, elige tu tipo de visita.',
    FALLBACK_TITLE: '¿Cuál es el motivo de tu visita?',
  },
  SELECT_HOST: {
    TITLE: '¿A quién vas a visitar?',
    SUBTITLE: 'Busca a tu anfitrión para avisarle que ya llegaste.',
    PLACEHOLDER: 'Seleccionar anfitrión',
    SEARCH_PLACEHOLDER: 'Buscar anfitrión...',
    SEARCH_PLACEHOLDER_ALT: 'Buscar anfitrión por nombre...',
    NO_HOSTS_FOUND: 'No se encontraron anfitriones',
    MIN_CHARS_MESSAGE: 'Escribe al menos 3 caracteres para buscar',
  },
  VISITOR_FORM: {
    TITLE: 'Detalles de la visita',
    SUBTITLE: 'Por favor, completa la información requerida a continuación.',
    SELECT_FIELD_PLACEHOLDER: 'Seleccionar {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Por favor, ingresa tu nombre completo',
    EMAIL_REQUIRED: 'Por favor, ingresa un correo electrónico válido',
    EMAIL_INVALID: 'Por favor, ingresa un correo electrónico válido',
    HOST_REQUIRED: 'Por favor, selecciona el nombre de tu anfitrión',
    COMPANY_NAME_REQUIRED: 'Por favor, ingresa el nombre de tu empresa',
    PHONE_NUMBER_REQUIRED: 'Por favor, ingresa tu número de teléfono',
    CUSTOM_FIELD_REQUIRED: 'Por favor, ingresa {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} es obligatorio',
    FULL_NAME_INVALID: 'Por favor, ingresa un nombre válido.',
    TOAST_ERROR_PROCESSING: 'Hubo un error al procesar tu solicitud. Por favor, inténtalo de nuevo.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Por favor, revisa y firma el documento a continuación',
    ERROR_DOCUMENT_EXPIRY: 'Error al verificar la vigencia del documento',
    ERROR_LOADING_CONTENT: 'Error al cargar el contenido del documento',
    ERROR_FETCHING_DOCUMENT: 'Error al obtener el documento',
    ERROR_RESTORE_SESSION: 'No se pudo restablecer tu sesión de firma del documento. Por favor, vuelve a empezar desde la página de inicio.',
    BTN_COLLAPSE: 'Contraer',
    BTN_EXPAND: 'Expandir',
  },
  PRIVACY_POLICY: {
    TITLE: 'Antes de continuar',
    SUBTITLE: 'Por favor, revisa y acepta antes de continuar',
    IMPLICIT_CONSENT: 'Al hacer clic en continuar, aceptas que {orgName} almacene y procese la información personal que enviaste para brindarte el contenido solicitado.',
    EXPLICIT_CONSENT: 'Acepto que {orgName} almacene y procese mis datos personales.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Seleccionar identificación',
    SUBTITLE: 'Por favor, elige un tipo de identificación para subir',
    ID_TYPES: [
      { label: 'Pasaporte', value: 'PASSPORT' },
      { label: 'Licencia de conducir', value: 'DRIVING_LICENCE' },
      { label: 'Identificación estatal', value: 'STATE_ISSUED_ID' },
      { label: 'Tengo otro tipo de identificación', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Tomar foto',
    SUBTITLE: 'Por favor, centra tu rostro y presiona continuar',
    INSTRUCTIONS: 'Usa tu cámara frontal, mira al frente y centra tu rostro.',
    BTN_CAPTURE: 'Capturar',
    BTN_RETAKE: 'Volver a tomar',
    BTN_ENABLE_CAMERA: 'Activar cámara',
    PERMISSION_PROMPT: 'Toca "Activar cámara" para dar acceso a la cámara.',
    PERMISSION_DENIED: 'El acceso a la cámara está bloqueado. Actívalo en la configuración de tu navegador y vuelve a tocar Activar cámara.',
    PERMISSION_UNSUPPORTED: 'No se encontró ninguna cámara en este dispositivo.',
    TOAST_CAMERA_ERROR: 'Error de acceso a la cámara. ¡Revisa la consola!',
    BTN_RETRY_CAMERA: 'Reintentar cámara',
    CAMERA_NOT_AVAILABLE: 'La cámara no pudo iniciar. Toca Reintentar o recarga la página.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Activar cámara',
    BTN_START_CAMERA: 'Iniciar cámara',
    BTN_TRY_NOW: 'Intentar ahora',
    PERMISSION_PROMPT: 'Toca "Activar cámara" para dar acceso a la cámara.',
    PERMISSION_DENIED: 'El acceso a la cámara está bloqueado. Actívalo en la configuración de tu navegador y vuelve a tocar Activar cámara.',
    PERMISSION_UNSUPPORTED: 'No se encontró ninguna cámara en este dispositivo.',
    ID_DRIVERS_LICENSE: 'Licencia de conducir',
    ID_STATE_ISSUED: 'Identificación estatal',
    ID_PASSPORT: 'Pasaporte',
    ID_OTHERS: 'Otros',
    ERROR_CAMERA_IN_USE: 'La cámara está en uso. Cierra la otra aplicación e inténtalo de nuevo.',
    ERROR_CAMERA_INIT_FAILED: 'No se pudo inicializar la cámara. Por favor, verifica los permisos.',
    ERROR_CAMERA_BUSY_RETRY: 'La cámara sigue en uso. Cierra la otra aplicación e inténtalo de nuevo.',
    ERROR_PLEASE_SCAN_DOCS: 'Por favor, escanea el/los documento(s)',
    SUBTITLE: 'Escanea el documento seleccionado a continuación.',
    LABEL_FRONT_SIDE: 'Frente',
    LABEL_BACK_SIDE: 'Reverso',
    INSTRUCTION_POSITION_FRONT: 'Coloca el frente de tu identificación',
    INSTRUCTION_POSITION_BACK: 'Coloca el reverso de tu identificación',
    MRZ_BAND_HINT: 'Coloca las dos líneas inferiores de la página de la foto debajo de la línea discontinua',
    MRZ_SCAN_FAILED: 'No pudimos leer tu pasaporte. Mantenlo quieto con las dos líneas inferiores dentro de la franja e inténtalo de nuevo.',
    MRZ_SCAN_ATTEMPT: 'Escaneo {current} de {total}',
    GLARE_WARNING: 'Hay reflejos en el documento. Inclínalo un poco o aléjate de la luz.',
    BARCODE_BAND_HINT: 'Mantén la tarjeta quieta, a unos 20 cm, con el código de barras dentro del marco',
    BARCODE_SCAN_FAILED: 'No pudimos leer el código de barras. Mantén el reverso de la tarjeta quieto y recto dentro del marco e inténtalo de nuevo.',
    TORCH_ON: 'Encender la luz',
    TORCH_OFF: 'Apagar la luz',
    BARCODE_READING_PHOTO: 'Leyendo el código de barras…',
    ID_CHECKING: 'Verificando tu identificación…',
  },
  VISITOR_VIDEO: {
    TITLE: '¡Ya casi!',
    SUBTITLE: 'Mira el video a continuación para continuar.',
    BTN_CHECK_IN: 'Registrarse',
    BTN_FINISH: 'Finalizar',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registro encontrado',
    SUBTITLE: 'Por favor, verifica tus datos para completar tu registro.',
    BTN_NOT_YOU: '¿No eres tú?',
    MEETING_WITH_LABEL: 'Reunión con {hostName}',
    ONSITE_TITLE: 'Ya estás registrado',
    ONSITE_SUBTITLE: 'Selecciona tu sesión para cerrar sesión.',
    CHECKIN_AT_LABEL: 'Registrado a las:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: '¡Ya estás registrado!',
    WALLET_HINT: 'El pase se abrirá automáticamente en tu dispositivo.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Registro previo completado',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Instrucciones de llegada',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Te enviamos un código QR a tu correo electrónico registrado. Úsalo para registrarte más rápido en el kiosco cuando llegues.',
    QR_CAPTION: 'Este código QR se puede escanear en el lobby para mostrar tu registro.',
    BTN_SAVE_QR: 'Guardar código QR',
    WALLET_LABEL: 'Cartera',
    ACTION_ADD_PASS: 'Agregar pase',
    CALENDAR_LABEL: 'Calendario',
    ACTION_SAVE_EVENT: 'Guardar evento',
    DEFAULT_WELCOME_MESSAGE: 'Tu anfitrión ya fue notificado y estará contigo en un momento.',
    PREFILL_SCHEDULED_FOR: 'Todo está listo para el {date}.',
    PREFILL_HOST_NOTIFIED: 'Tu anfitrión ya fue notificado y esperamos darte la bienvenida.',
    TOAST_UPDATE_VISIT_ERROR: 'Hubo un error al actualizar tu visita. Por favor, inténtalo de nuevo.',
    TOAST_GENERIC_ERROR: 'Ocurrió un error. ¡Por favor, inténtalo de nuevo!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Detalles de la visita',
    PAGE_SUBTITLE: 'Completa los siguientes pasos para agilizar tu registro.',
    SECTION_HEADING: 'Completa tu registro previo',
    STEPS_COMPLETED_SUFFIX: 'pasos completados',
    BTN_START: '¡Empecemos!',
  },
  SIGN_OUT: {
    EYEBROW: 'Cerrar sesión',
    TITLE_WITH_NAME: 'Adiós,',
    TITLE_FALLBACK: '¡Adiós!',
    SUBTITLE_SESSION: 'Confirma tus datos y cierra sesión.',
    STRIP_SUBTITLE: 'Sesión activa',
    LABEL_ORG: 'ORGANIZACIÓN',
    LABEL_LOCATION: 'UBICACIÓN',
    LABEL_HOST: 'ANFITRIÓN',
    LABEL_SIGNED_IN_AT: 'INGRESO',
    BTN_SIGN_OUT: 'Cerrar sesión',
    BTN_SIGNING_OUT: 'Cerrando sesión...',
    BTN_START_OVER: 'Empezar de nuevo',
    LABEL_NOT_YOU: '¿No eres tú?',
    TOAST_SUCCESS: '¡Cerraste sesión con éxito!',
    TOAST_ERROR_FALLBACK: 'Error al cerrar sesión. Por favor, inténtalo de nuevo.',
    CUSTOM_FIELDS_EYEBROW: 'Casi listo',
    CUSTOM_FIELDS_TITLE: 'Completa tu cierre de sesión',
    CUSTOM_FIELDS_SUBTITLE: 'Por favor, completa los datos requeridos.',
    BTN_CONFIRM_SIGN_OUT: 'Confirmar cierre de sesión',
    BTN_GO_BACK: 'Regresar',
    SUCCESS_TITLE: 'Ya cerraste sesión',
    SUCCESS_SUBTITLE: 'Gracias por tu visita',
    BTN_DONE: 'Listo',
    LABEL_VISIT_DURATION: 'Duración de la visita',
    GEOLOCATION_UNAVAILABLE: 'Se necesita acceso a tu ubicación para cerrar sesión. Por favor, usa un dispositivo móvil.',
    LOCATION_PERMISSION_REQUIRED: 'Se necesita permiso de ubicación para cerrar sesión. Por favor, permite el acceso a tu ubicación e inténtalo de nuevo.',
    MUST_BE_ON_SITE: 'Debes estar en el sitio para cerrar sesión.',
    GUEST_PLACEHOLDER: 'Invitado',
    SUCCESS_CLOSING: '¡Que tengas un buen día!',
    STATUS_CHECKED_IN: 'Registrado',
    STATUS_CHECKED_OUT: 'Salida registrada',
    DETAIL_LABEL_HOST: 'Anfitrión',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Todo listo para tu visita',
    ALL_SUB_TITLE: 'Tu registro previo está completo. Puedes revisar los detalles de tu visita a continuación.',
    BTN_CHECK_DETAILS: 'Ver mis datos',
    BTN_VIEW_CHECKIN: 'Ver registro sin contacto',
    QR_TITLE: 'Registro sin contacto',
    QR_SUBTITLE: 'Apunta tu cámara al código QR que aparece en el iPad o en el letrero impreso para continuar',
    ERROR_TITLE: '¡Algo salió mal!',
    ERROR_SUBTITLE: 'Lo sentimos, ocurrió un error. Por favor, inténtalo de nuevo más tarde.',
    DETAIL_LABEL_VISITOR: 'Visitante',
    DETAIL_LABEL_EMAIL: 'Correo electrónico',
    DETAIL_LABEL_HOST: 'Anfitrión',
    DETAIL_HOST_PLACEHOLDER: 'Por definir',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'El enlace de registro previo expiró',
    SUBTITLE: 'Este enlace ya no es válido',
    REASONS_HEADING: 'Posibles razones:',
    REASON_CANCELLED: 'Se canceló la invitación',
    REASON_CHECKED_IN: 'Ya te registraste',
    REASON_PAST: 'Ya pasó la hora programada',
    HELP_PROMPT: '¿Necesitas ayuda?',
    HELP_TEXT: 'Contacta a tu anfitrión o a recepción',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'El enlace del pase no es válido.',
    PASS_REVOKED_OR_EXPIRED: 'Este pase fue revocado o ya expiró.',
    UNABLE_TO_LOAD: 'No se pudo cargar la página de registro.',
    PASS_EXPIRED_TITLE: 'Pase vencido',
    CONTACT_HOST_FOR_NEW_PASS: 'Por favor, contacta a tu anfitrión para obtener un nuevo SmartPass.',
    ERROR_TITLE: 'Algo salió mal',
    BTN_TRY_AGAIN: 'Intentar de nuevo',
    STATUS_NOT_ON_SITE: 'Fuera del sitio',
    STATUS_CHECKED_IN: 'Registrado',
    SELECT_LOCATION_LABEL: 'Selecciona tu ubicación:',
    SELECT_LOCATION_PLACEHOLDER: '-- Selecciona ubicación --',
    BTN_PROCESSING: 'Procesando...',
    BTN_CHECK_IN: 'Registrarse',
    BTN_CHECK_OUT: 'Cerrar sesión',
    BTN_CLOSE_HINT: 'Puedes cerrar esta página.',
    POWERED_BY: 'Desarrollado por Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'El enlace del pase no es válido.',
    GOOGLE_WALLET_COMING_SOON: 'Muy pronto podrás usar Google Wallet. Por favor, vuelve más tarde.',
    OPEN_ON_MOBILE: 'Por favor, abre este enlace en tu iPhone o celular Android para agregar el pase a tu cartera.',
    PREPARING_PASS: 'Preparando tu SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'El enlace del pase no es válido.',
    LOCATION_REQUIRED: 'El registro con SmartPass necesita acceso a tu ubicación. Por favor, abre este enlace desde tu celular.',
    LOCATION_PERMISSION_REQUIRED: 'Se necesita permiso de ubicación para el registro con SmartPass. Por favor, permite el acceso a tu ubicación e inténtalo de nuevo.',
    LOCATION_UNAVAILABLE: 'No se pudo determinar tu ubicación. Por favor, asegúrate de tener activados los servicios de ubicación e inténtalo de nuevo.',
    PASS_REVOKED_OR_EXPIRED: 'Este pase fue revocado o ya expiró.',
    OUTSIDE_GEOFENCE: 'Estás fuera del área de registro. Por favor, acércate al edificio e inténtalo de nuevo.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Reintentar',
    OPENING_CHECK_IN: 'Abriendo registro...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'HAY UNA EVACUACIÓN EN PROGRESO EN ESTA UBICACIÓN',
    BTN_TAP_TO_SIGNIN: 'Toca para registrarte',
    BTN_SCAN_QR_CODE: 'Escanear código QR',
    TOAST_INVALID_QR: 'Código QR inválido o vencido. ¡Por favor, inténtalo de nuevo!',
  },
  FOOTER: { POWERED_BY: 'DESARROLLADO POR ' },
  HEADER: { STEP: 'PASO ', OF: ' DE ' },
  SPLASH_SCREEN: { TAP_TO_START: 'Toca para comenzar' },
  QR_SCANNER: { INVALID_CODE_ERROR: 'Código QR inválido. ¡Por favor, inténtalo de nuevo!' },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Solo usamos tu cámara para leer el código QR del lobby y no guardamos ningún video',
    PERMISSION_PROMPT: 'Toca "Activar cámara" para dar acceso a la cámara.',
    PERMISSION_DENIED: 'El acceso a la cámara está bloqueado. Actívalo en la configuración de tu navegador y vuelve a tocar Activar cámara.',
    PERMISSION_UNSUPPORTED: 'No se encontró ninguna cámara en este dispositivo.',
    CAMERA_NOT_AVAILABLE: 'La cámara no pudo iniciar. Toca Reintentar o recarga la página.',
    ERROR_CAMERA_IN_USE: 'La cámara está en uso. Cierra la otra aplicación e inténtalo de nuevo.',
    ERROR_CAMERA_INIT_FAILED: 'No se pudo inicializar la cámara. Por favor, verifica los permisos.',
    ERROR_CAMERA_BUSY_RETRY: 'La cámara sigue en uso. Cierra la otra aplicación e inténtalo de nuevo.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'Se necesita permiso de cámara para escanear códigos QR.',
    ACCESS_DENIED: 'El acceso a la cámara fue denegado o no está disponible',
  },
  CONFIRMATION_MODAL: { DEFAULT_MESSAGE: '¿Seguro que quieres cancelar? Perderás todo tu progreso.' },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Seleccionar anfitrión', prefillTitle: 'Seleccionar anfitrión', prefillDesc: 'Elige a quién vas a visitar' },
    DETAILS:    { headerTitle: 'Tus datos',             prefillTitle: 'Tus datos',             prefillDesc: 'Danos tu información de contacto' },
    DOCUMENT:   { headerTitle: 'Firmar documentos',    prefillTitle: 'Firmar documentos',    prefillDesc: 'Revisa y firma los documentos requeridos' },
    ID_CAPTURE: { headerTitle: 'Verificar identificación', prefillTitle: 'Verificar identificación', prefillDesc: 'Escanea tu identificación' },
    PHOTO:      { headerTitle: 'Tomar selfie',         prefillTitle: 'Tomar selfie',         prefillDesc: 'Captura tu foto' },
    VIDEO:      { headerTitle: 'Ver video',            prefillTitle: 'Ver video',            prefillDesc: 'Mira un video de seguridad o bienvenida' },
  },
};

// ── Portuguese (Portugal) ──────────────────────────────────────────────────────
export const PT_PT_STRINGS: StringsShape = {
  PAGE_TITLES: {
    DEFAULT: 'Entrada sem Contacto | Visitly',
    PRE_ARRIVAL: 'Pré-chegada | Visitly',
  },
  HOME: {
    TITLE: 'Entrada sem Contacto',
    SUBTITLE: 'Aponta a câmara para o código QR apresentado no iPad ou no cartaz impresso para continuar',
  },
  COMMON: {
    BACK: 'Voltar',
    CONTINUE: 'Continuar',
    AGREE_AND_CONTINUE: 'Concordar e Continuar',
    DECLINE: 'Recusar',
    CLEAR_SIGNATURE: 'Limpar Assinatura',
    LOADING: 'A carregar...',
    SEARCHING: 'A pesquisar...',
    NO: 'NÃO',
    YES: 'SIM',
    ENABLE_CAMERA: 'Ativar Câmara',
    PAGE_NOT_FOUND: '404 - Página Não Encontrada',
    POWERED_BY: 'Desenvolvido por Visitly',
    RETAKE: 'Tirar Novamente',
    CAPTURE: 'Capturar',
    RETRY: 'Tentar Novamente',
    POINT_OF_ENTRY: 'Ponto de Entrada',
    PARKING_LOT: 'Estacionamento',
    DESTINATION: 'Edifício',
    ERROR_PROCESSING_REQUEST: 'Erro ao processar o pedido. Por favor, tenta novamente.',
    SESSION_EXPIRED: 'Sessão expirada. Por favor, reabre o link a partir do teu e-mail.',
  },
  FULL_NAME: {
    TITLE: 'Qual é o teu nome completo?',
    SUBTITLE: 'Só para sabermos quem nos visita hoje',
    FORM_LABEL: 'Nome Completo',
    PLACEHOLDER: 'ex. Joseph Jose',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'Por favor, escolhe o tipo de visita.',
    FALLBACK_TITLE: 'O que te traz aqui?',
  },
  SELECT_HOST: {
    TITLE: 'Quem vais visitar?',
    SUBTITLE: 'Pesquisa o teu anfitrião para o notificar da tua chegada.',
    PLACEHOLDER: 'Selecionar anfitrião',
    SEARCH_PLACEHOLDER: 'Pesquisar anfitrião...',
    SEARCH_PLACEHOLDER_ALT: 'Pesquisar anfitrião pelo nome...',
    NO_HOSTS_FOUND: 'Nenhum anfitrião encontrado',
    MIN_CHARS_MESSAGE: 'Introduz pelo menos 3 caracteres para pesquisar',
  },
  VISITOR_FORM: {
    TITLE: 'Detalhes da Visita',
    SUBTITLE: 'Por favor, preenche as informações necessárias abaixo.',
    SELECT_FIELD_PLACEHOLDER: 'Selecionar {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'Por favor, introduz o teu nome completo',
    EMAIL_REQUIRED: 'Por favor, introduz um endereço de e-mail válido',
    EMAIL_INVALID: 'Por favor, introduz um endereço de e-mail válido',
    HOST_REQUIRED: 'Por favor, seleciona o nome do teu anfitrião',
    COMPANY_NAME_REQUIRED: 'Por favor, introduz o nome da tua empresa',
    PHONE_NUMBER_REQUIRED: 'Por favor, introduz o teu número de telefone',
    CUSTOM_FIELD_REQUIRED: 'Por favor, introduz {field}',
    FIELD_REQUIRED_TEMPLATE: '{field} é obrigatório',
    FULL_NAME_INVALID: 'Por favor, introduz um nome válido.',
    TOAST_ERROR_PROCESSING: 'Erro ao processar o pedido. Por favor, tenta novamente.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'Por favor, revê e assina o documento abaixo',
    ERROR_DOCUMENT_EXPIRY: 'Erro ao verificar a validade do documento',
    ERROR_LOADING_CONTENT: 'Erro ao carregar o conteúdo do documento',
    ERROR_FETCHING_DOCUMENT: 'Erro ao obter o documento',
    ERROR_RESTORE_SESSION: 'Não foi possível restaurar a sessão de assinatura do documento. Por favor, reinicia a partir da página inicial.',
    BTN_COLLAPSE: 'Recolher',
    BTN_EXPAND: 'Expandir',
  },
  PRIVACY_POLICY: {
    TITLE: 'Antes de continuar',
    SUBTITLE: 'Por favor, revê e concorda antes de continuar',
    IMPLICIT_CONSENT: 'Ao clicares em continuar abaixo, consentes que {orgName} armazene e processe os dados pessoais enviados acima para fornecer o conteúdo solicitado.',
    EXPLICIT_CONSENT: 'Concordo em permitir que {orgName} armazene e processe os meus dados pessoais.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'Selecionar Documento',
    SUBTITLE: 'Por favor, seleciona um tipo de documento para enviar',
    ID_TYPES: [
      { label: 'Passaporte', value: 'PASSPORT' },
      { label: 'Carta de Condução', value: 'DRIVING_LICENCE' },
      { label: 'Documento de Identificação', value: 'STATE_ISSUED_ID' },
      { label: 'Tenho um documento diferente', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'Tirar Foto',
    SUBTITLE: 'Por favor, centra o teu rosto e clica em continuar',
    INSTRUCTIONS: 'Usa a câmara frontal, olha em frente e centra o teu rosto.',
    BTN_CAPTURE: 'Capturar',
    BTN_RETAKE: 'Tirar Novamente',
    BTN_ENABLE_CAMERA: 'Ativar Câmara',
    PERMISSION_PROMPT: 'Toca em "Ativar Câmara" para conceder acesso à câmara.',
    PERMISSION_DENIED: 'O acesso à câmara está bloqueado. Ativa-o nas definições do teu navegador e toca novamente em Ativar Câmara.',
    PERMISSION_UNSUPPORTED: 'Nenhuma câmara encontrada neste dispositivo.',
    TOAST_CAMERA_ERROR: 'Erro de acesso à câmara. Verifica a consola!',
    BTN_RETRY_CAMERA: 'Tentar Câmara Novamente',
    CAMERA_NOT_AVAILABLE: 'Não foi possível iniciar a câmara. Toca em Tentar novamente ou recarrega a página.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'Ativar Câmara',
    BTN_START_CAMERA: 'Iniciar Câmara',
    BTN_TRY_NOW: 'Experimentar Agora',
    PERMISSION_PROMPT: 'Toca em "Ativar Câmara" para conceder acesso à câmara.',
    PERMISSION_DENIED: 'O acesso à câmara está bloqueado. Ativa-o nas definições do teu navegador e toca novamente em Ativar Câmara.',
    PERMISSION_UNSUPPORTED: 'Nenhuma câmara encontrada neste dispositivo.',
    ID_DRIVERS_LICENSE: 'Carta de Condução',
    ID_STATE_ISSUED: 'Documento de Identificação',
    ID_PASSPORT: 'Passaporte',
    ID_OTHERS: 'Outros',
    ERROR_CAMERA_IN_USE: 'A câmara está ocupada. Fecha a outra aplicação e tenta novamente.',
    ERROR_CAMERA_INIT_FAILED: 'Falha ao iniciar a câmara. Por favor, verifica as permissões.',
    ERROR_CAMERA_BUSY_RETRY: 'A câmara continua ocupada. Fecha a outra aplicação e tenta novamente.',
    ERROR_PLEASE_SCAN_DOCS: 'Por favor, digitaliza o(s) documento(s)',
    SUBTITLE: 'Digitaliza o documento selecionado abaixo.',
    LABEL_FRONT_SIDE: 'Frente',
    LABEL_BACK_SIDE: 'Verso',
    INSTRUCTION_POSITION_FRONT: 'Posiciona a frente do teu documento',
    INSTRUCTION_POSITION_BACK: 'Posiciona o verso do teu documento',
    MRZ_BAND_HINT: 'Alinhe as duas linhas inferiores da página da fotografia abaixo da linha tracejada',
    MRZ_SCAN_FAILED: 'Não foi possível ler o seu passaporte. Segure-o firme com as duas linhas inferiores dentro da faixa e tente novamente.',
    MRZ_SCAN_ATTEMPT: 'Leitura {current} de {total}',
    GLARE_WARNING: 'Reflexo no documento. Incline-o ligeiramente ou afaste-se da luz.',
    BARCODE_BAND_HINT: 'Segure o cartão firme, a cerca de 20 cm, com o código de barras dentro da moldura',
    BARCODE_SCAN_FAILED: 'Não foi possível ler o código de barras. Segure o verso do cartão firme e direito dentro da moldura e tente novamente.',
    TORCH_ON: 'Acender a luz',
    TORCH_OFF: 'Apagar a luz',
    BARCODE_READING_PHOTO: 'A ler o código de barras…',
    ID_CHECKING: 'A verificar o seu documento…',
  },
  VISITOR_VIDEO: {
    TITLE: 'Quase lá!',
    SUBTITLE: 'Vê o vídeo abaixo para continuar.',
    BTN_CHECK_IN: 'Fazer Check-in',
    BTN_FINISH: 'Concluir',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'Registo Encontrado',
    SUBTITLE: 'Por favor, confirma os teus dados para concluir o check-in.',
    BTN_NOT_YOU: 'Não és tu?',
    MEETING_WITH_LABEL: 'Reunião com {hostName}',
    ONSITE_TITLE: 'Já fizeste o check-in',
    ONSITE_SUBTITLE: 'Seleciona a tua sessão para saíres.',
    CHECKIN_AT_LABEL: 'Check-in realizado às:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'Check-in Realizado!',
    WALLET_HINT: 'O passe abre-se automaticamente no teu dispositivo.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'Pré-registo Concluído',
    ARRIVAL_INSTRUCTIONS_TITLE: 'Instruções de Chegada',
    ARRIVAL_INSTRUCTIONS_TEXT: 'Foi enviado um código QR para o teu e-mail registado. Utiliza-o para um check-in mais rápido no quiosque quando chegares.',
    QR_CAPTION: 'Este código QR pode ser lido na receção para aceder ao teu registo.',
    BTN_SAVE_QR: 'Guardar Código QR',
    WALLET_LABEL: 'Carteira',
    ACTION_ADD_PASS: 'Adicionar Passe',
    CALENDAR_LABEL: 'Calendário',
    ACTION_SAVE_EVENT: 'Guardar Evento',
    DEFAULT_WELCOME_MESSAGE: 'O teu anfitrião foi notificado e estará contigo em breve.',
    PREFILL_SCHEDULED_FOR: 'Está tudo pronto para {date}.',
    PREFILL_HOST_NOTIFIED: 'O teu anfitrião foi notificado e aguardamos com todo o gosto a tua chegada.',
    TOAST_UPDATE_VISIT_ERROR: 'Ocorreu um erro ao atualizar a visita. Por favor, tenta novamente.',
    TOAST_GENERIC_ERROR: 'Ocorreu um erro. Por favor, tenta novamente!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'Detalhes da Visita',
    PAGE_SUBTITLE: 'Conclui os passos abaixo para agilizar o teu check-in.',
    SECTION_HEADING: 'Conclui o teu Pré-registo',
    STEPS_COMPLETED_SUFFIX: 'passos concluídos',
    BTN_START: 'Vamos Começar!',
  },
  SIGN_OUT: {
    EYEBROW: 'Saída',
    TITLE_WITH_NAME: 'Até logo,',
    TITLE_FALLBACK: 'Até logo!',
    SUBTITLE_SESSION: 'Confirma os teus dados e realiza o check-out.',
    STRIP_SUBTITLE: 'Atualmente com check-in ativo',
    LABEL_ORG: 'ORGANIZAÇÃO',
    LABEL_LOCATION: 'LOCAL',
    LABEL_HOST: 'ANFITRIÃO',
    LABEL_SIGNED_IN_AT: 'ENTRADA',
    BTN_SIGN_OUT: 'Fazer Check-out',
    BTN_SIGNING_OUT: 'A fazer check-out...',
    BTN_START_OVER: 'Recomeçar',
    LABEL_NOT_YOU: 'Não és tu?',
    TOAST_SUCCESS: 'Check-out concluído com sucesso!',
    TOAST_ERROR_FALLBACK: 'Erro ao fazer check-out. Por favor, tenta novamente.',
    CUSTOM_FIELDS_EYEBROW: 'Quase Pronto',
    CUSTOM_FIELDS_TITLE: 'Concluir Check-out',
    CUSTOM_FIELDS_SUBTITLE: 'Por favor, preenche os dados necessários.',
    BTN_CONFIRM_SIGN_OUT: 'Confirmar Check-out',
    BTN_GO_BACK: 'Voltar',
    SUCCESS_TITLE: 'Check-out Concluído',
    SUCCESS_SUBTITLE: 'Obrigado pela visita',
    BTN_DONE: 'Concluir',
    LABEL_VISIT_DURATION: 'Duração da visita',
    GEOLOCATION_UNAVAILABLE: 'É necessário acesso à localização para fazeres o check-out. Por favor, utiliza um dispositivo móvel.',
    LOCATION_PERMISSION_REQUIRED: 'É necessária permissão de localização para fazeres o check-out. Por favor, permite o acesso à localização e tenta novamente.',
    MUST_BE_ON_SITE: 'Tens de estar no local para fazeres o check-out.',
    GUEST_PLACEHOLDER: 'Visitante',
    SUCCESS_CLOSING: 'Tem um ótimo dia!',
    STATUS_CHECKED_IN: 'Check-in Realizado',
    STATUS_CHECKED_OUT: 'Check-out Realizado',
    DETAIL_LABEL_HOST: 'Anfitrião',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'Está tudo pronto para a tua visita',
    ALL_SUB_TITLE: 'O teu pré-registo está concluído. Podes rever os detalhes da tua visita abaixo.',
    BTN_CHECK_DETAILS: 'Consultar os Meus Dados',
    BTN_VIEW_CHECKIN: 'Ver Check-in Sem Contacto',
    QR_TITLE: 'Entrada sem Contacto',
    QR_SUBTITLE: 'Aponta a câmara para o código QR apresentado no iPad ou no cartaz impresso para continuar',
    ERROR_TITLE: 'Algo correu mal!',
    ERROR_SUBTITLE: 'Lamentamos, mas ocorreu um erro. Por favor, tenta novamente mais tarde.',
    DETAIL_LABEL_VISITOR: 'Visitante',
    DETAIL_LABEL_EMAIL: 'E-mail',
    DETAIL_LABEL_HOST: 'Anfitrião',
    DETAIL_HOST_PLACEHOLDER: 'A definir',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'Link de Pré-registo Expirado',
    SUBTITLE: 'Este link já não é válido',
    REASONS_HEADING: 'Possíveis motivos:',
    REASON_CANCELLED: 'Convite cancelado',
    REASON_CHECKED_IN: 'Check-in já efetuado',
    REASON_PAST: 'A hora agendada já passou',
    HELP_PROMPT: 'Precisas de ajuda?',
    HELP_TEXT: 'Contacta o teu anfitrião ou a receção',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'Link do passe inválido.',
    PASS_REVOKED_OR_EXPIRED: 'Este passe foi revogado ou expirou.',
    UNABLE_TO_LOAD: 'Não foi possível carregar a página de check-in.',
    PASS_EXPIRED_TITLE: 'Passe Expirado',
    CONTACT_HOST_FOR_NEW_PASS: 'Por favor, contacta o teu anfitrião para obteres um novo SmartPass.',
    ERROR_TITLE: 'Algo correu mal',
    BTN_TRY_AGAIN: 'Tentar Novamente',
    STATUS_NOT_ON_SITE: 'Fora do Local',
    STATUS_CHECKED_IN: 'Check-in Realizado',
    SELECT_LOCATION_LABEL: 'Seleciona o teu local:',
    SELECT_LOCATION_PLACEHOLDER: '-- Selecionar local --',
    BTN_PROCESSING: 'A processar...',
    BTN_CHECK_IN: 'Fazer Check-in',
    BTN_CHECK_OUT: 'Fazer Check-out',
    BTN_CLOSE_HINT: 'Já podes fechar esta página.',
    POWERED_BY: 'Desenvolvido por Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'Link do passe inválido.',
    GOOGLE_WALLET_COMING_SOON: 'Suporte para Google Wallet brevemente. Por favor, volta mais tarde.',
    OPEN_ON_MOBILE: 'Por favor, abre este link no teu iPhone ou telemóvel Android para adicionares o passe à tua carteira.',
    PREPARING_PASS: 'A preparar o teu SmartPass...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'Link do passe inválido.',
    LOCATION_REQUIRED: 'O check-in com SmartPass requer acesso à localização. Por favor, abre este link no teu dispositivo móvel.',
    LOCATION_PERMISSION_REQUIRED: 'É necessária permissão de localização para o check-in com SmartPass. Por favor, permite o acesso à localização e tenta novamente.',
    LOCATION_UNAVAILABLE: 'Não foi possível determinar a tua localização. Por favor, verifica se os serviços de localização estão ativados e tenta novamente.',
    PASS_REVOKED_OR_EXPIRED: 'Este passe foi revogado ou expirou.',
    OUTSIDE_GEOFENCE: 'Estás fora da área de check-in. Por favor, aproxima-te do edifício e tenta novamente.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'Tentar Novamente',
    OPENING_CHECK_IN: 'A abrir o check-in...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'ESTÁ EM CURSO UMA EVACUAÇÃO NESTE LOCAL',
    BTN_TAP_TO_SIGNIN: 'Toca para Entrar',
    BTN_SCAN_QR_CODE: 'Ler Código QR',
    TOAST_INVALID_QR: 'Código QR inválido ou expirado. Por favor, tenta novamente!',
  },
  FOOTER: {
    POWERED_BY: 'DESENVOLVIDO POR ',
  },
  HEADER: {
    STEP: 'PASSO ',
    OF: ' DE ',
  },
  SPLASH_SCREEN: {
    TAP_TO_START: 'Toca para Começar',
  },
  QR_SCANNER: {
    INVALID_CODE_ERROR: 'Código QR inválido. Por favor, tenta novamente!',
  },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'Utilizamos a câmara apenas para ler o código QR da receção e não armazenamos qualquer vídeo',
    PERMISSION_PROMPT: 'Toca em "Ativar Câmara" para conceder acesso à câmara.',
    PERMISSION_DENIED: 'O acesso à câmara está bloqueado. Ativa-o nas definições do teu navegador e toca novamente em Ativar Câmara.',
    PERMISSION_UNSUPPORTED: 'Nenhuma câmara encontrada neste dispositivo.',
    CAMERA_NOT_AVAILABLE: 'Não foi possível iniciar a câmara. Toca em Tentar novamente ou recarrega a página.',
    ERROR_CAMERA_IN_USE: 'A câmara está ocupada. Fecha a outra aplicação e tenta novamente.',
    ERROR_CAMERA_INIT_FAILED: 'Falha ao iniciar a câmara. Por favor, verifica as permissões.',
    ERROR_CAMERA_BUSY_RETRY: 'A câmara continua ocupada. Fecha a outra aplicação e tenta novamente.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'É necessária permissão de câmara para ler códigos QR.',
    ACCESS_DENIED: 'Acesso à câmara negado ou indisponível',
  },
  CONFIRMATION_MODAL: {
    DEFAULT_MESSAGE: 'Tens a certeza de que queres cancelar? Vais perder todo o teu progresso.',
  },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'Selecionar Anfitrião',    prefillTitle: 'Selecionar Anfitrião',    prefillDesc: 'Escolhe quem vais visitar' },
    DETAILS:    { headerTitle: 'Os Teus Dados',           prefillTitle: 'Os Teus Dados',           prefillDesc: 'Indica os teus dados de contacto' },
    DOCUMENT:   { headerTitle: 'Assinar Documentos',      prefillTitle: 'Assinar Documentos',      prefillDesc: 'Revê e assina os documentos necessários' },
    ID_CAPTURE: { headerTitle: 'Verificar Documento',     prefillTitle: 'Verificar Documento',     prefillDesc: 'Digitaliza o teu documento' },
    PHOTO:      { headerTitle: 'Tirar Selfie',            prefillTitle: 'Tirar Selfie',            prefillDesc: 'Captura a tua foto' },
    VIDEO:      { headerTitle: 'Ver Vídeo',               prefillTitle: 'Ver Vídeo',               prefillDesc: 'Vê um vídeo de segurança ou orientação' },
  },
};

// ── Dari (Afghanistan) ─────────────────────────────────────────────────────────
export const FA_AF_STRINGS: StringsShape = {

  PAGE_TITLES: {
    DEFAULT: 'ورود بدون تماس | Visitly',
    PRE_ARRIVAL: 'پیش از رسیدن | Visitly',
  },
  HOME: {
    TITLE: 'ورود بدون تماس',
    SUBTITLE: 'برای ادامه، کمرۀ خود را به سوی کد QR که در آیپد یا تابلوی چاپ‌شده نشان داده شده، بگیرید',
  },
  COMMON: {
    BACK: 'بازگشت',
    CONTINUE: 'ادامه',
    AGREE_AND_CONTINUE: 'موافقم و ادامه',
    DECLINE: 'رد کردن',
    CLEAR_SIGNATURE: 'پاک کردن امضا',
    LOADING: 'در حال بارگذاری...',
    SEARCHING: 'در حال جستجو...',
    NO: 'نخیر',
    YES: 'بلی',
    ENABLE_CAMERA: 'فعال کردن کمره',
    PAGE_NOT_FOUND: '۴۰۴ - صفحه یافت نشد',
    POWERED_BY: 'قدرت گرفته از Visitly',
    RETAKE: 'دوباره گرفتن',
    CAPTURE: 'گرفتن عکس',
    RETRY: 'تلاش دوباره',
    POINT_OF_ENTRY: 'نقطه ورود',
    PARKING_LOT: 'پارکینگ',
    DESTINATION: 'ساختمان',
    ERROR_PROCESSING_REQUEST: 'خطا در پردازش درخواست. لطفاً دوباره تلاش کنید.',
    SESSION_EXPIRED: 'نشست شما به پایان رسیده است. لطفاً لینک را دوباره از ایمیل خود باز کنید.',
  },
  FULL_NAME: {
    TITLE: 'نام کامل تان چیست؟',
    SUBTITLE: 'فقط تا بدانیم امروز چه کسی به دیدن آمده است',
    FORM_LABEL: 'نام کامل',
    PLACEHOLDER: 'به طور مثال: جوزف جوز',
  },
  VISITOR_TYPES: {
    SUBTITLE: 'لطفاً نوع بازدید خود را انتخاب کنید.',
    FALLBACK_TITLE: 'هدف از آمدن شما چیست؟',
  },
  SELECT_HOST: {
    TITLE: 'به دیدن چه کسی آمده‌اید؟',
    SUBTITLE: 'میزبان خود را جستجو کنید تا از رسیدن شما آگاه شود.',
    PLACEHOLDER: 'یک میزبان انتخاب کنید',
    SEARCH_PLACEHOLDER: 'جستجوی میزبان...',
    SEARCH_PLACEHOLDER_ALT: 'جستجوی میزبان با نام...',
    NO_HOSTS_FOUND: 'هیچ میزبانی یافت نشد',
    MIN_CHARS_MESSAGE: 'برای جستجو حداقل ۳ حرف را وارد کنید',
  },
  VISITOR_FORM: {
    TITLE: 'جزئیات بازدید',
    SUBTITLE: 'لطفاً معلومات ضروری را در ذیل خانه‌پری کنید.',
    SELECT_FIELD_PLACEHOLDER: 'انتخاب {field}',
  },
  VALIDATION: {
    FULL_NAME_REQUIRED: 'لطفاً نام کامل خود را وارد کنید',
    EMAIL_REQUIRED: 'لطفاً یک آدرس ایمیل معتبر وارد کنید',
    EMAIL_INVALID: 'لطفاً یک آدرس ایمیل معتبر وارد کنید',
    HOST_REQUIRED: 'لطفاً نام میزبان خود را انتخاب کنید',
    COMPANY_NAME_REQUIRED: 'لطفاً نام شرکت خود را وارد کنید',
    PHONE_NUMBER_REQUIRED: 'لطفاً شماره تلفن خود را وارد کنید',
    CUSTOM_FIELD_REQUIRED: 'لطفاً {field} را وارد کنید',
    FIELD_REQUIRED_TEMPLATE: '{field} ضروری است',
    FULL_NAME_INVALID: 'لطفاً یک نام معتبر وارد کنید.',
    TOAST_ERROR_PROCESSING: 'خطا در پردازش درخواست. لطفاً دوباره تلاش کنید.',
  },
  SIGN_DOCUMENT: {
    TITLE_PREFIX: '',
    SUBTITLE: 'لطفاً سند ذیل را بررسی و امضا کنید',
    ERROR_DOCUMENT_EXPIRY: 'خطا در بررسی مدت اعتبار سند',
    ERROR_LOADING_CONTENT: 'خطا در بارگذاری محتوای سند',
    ERROR_FETCHING_DOCUMENT: 'خطا در دریافت سند',
    ERROR_RESTORE_SESSION: 'بازیابی نشست امضای سند ممکن نشد. لطفاً از صفحه اصلی دوباره آغاز کنید.',
    BTN_COLLAPSE: 'جمع کردن',
    BTN_EXPAND: 'باز کردن',
  },
  PRIVACY_POLICY: {
    TITLE: 'پیش از ادامه',
    SUBTITLE: 'لطفاً پیش از ادامه، بررسی و موافقت کنید',
    IMPLICIT_CONSENT: 'با کلیک روی «ادامه» در ذیل، شما موافقت می‌کنید که {orgName} معلومات شخصی ارائه‌شده در بالا را برای فراهم کردن محتوای درخواست‌شده ذخیره و پردازش کند.',
    EXPLICIT_CONSENT: 'موافقم که {orgName} معلومات شخصی مرا ذخیره و پردازش کند.',
  },
  CAPTURE_PICTURE: {
    TITLE: 'انتخاب سند هویتی',
    SUBTITLE: 'لطفاً نوع سند هویتی را برای بارگذاری انتخاب کنید',
    ID_TYPES: [
      { label: 'پاسپورت', value: 'PASSPORT' },
      { label: 'جواز رانندگی', value: 'DRIVING_LICENCE' },
      { label: 'کارت هویت دولتی', value: 'STATE_ISSUED_ID' },
      { label: 'من سند هویتی دیگری دارم', value: 'OTHERS' },
    ],
  },
  VISITOR_PICTURE: {
    TITLE: 'گرفتن عکس',
    SUBTITLE: 'لطفاً چهره خود را در وسط قرار داده و روی «ادامه» کلیک کنید',
    INSTRUCTIONS: 'از کمرۀ پیشرو استفاده کنید، مستقیم به پیش نگاه کنید و چهره خود را در وسط قرار دهید.',
    BTN_CAPTURE: 'گرفتن عکس',
    BTN_RETAKE: 'دوباره گرفتن',
    BTN_ENABLE_CAMERA: 'فعال کردن کمره',
    PERMISSION_PROMPT: 'برای اجازه دسترسی به کمره، روی «فعال کردن کمره» کلیک کنید.',
    PERMISSION_DENIED: 'دسترسی به کمره مسدود شده است. آن را در تنظیمات براوزر خود فعال کنید، سپس دوباره روی «فعال کردن کمره» کلیک کنید.',
    PERMISSION_UNSUPPORTED: 'در این دستگاه هیچ کمره‌ای یافت نشد.',
    TOAST_CAMERA_ERROR: 'خطا در دسترسی به کمره. کنسول را بررسی کنید!',
    BTN_RETRY_CAMERA: 'تلاش دوباره برای کمره',
    CAMERA_NOT_AVAILABLE: 'کمره آغاز نشد. روی «تلاش دوباره» کلیک کنید یا صفحه را دوباره بارگذاری کنید.',
  },
  FRONT_BACK_PHOTO: {
    BTN_ENABLE_CAMERA: 'فعال کردن کمره',
    BTN_START_CAMERA: 'آغاز کمره',
    BTN_TRY_NOW: 'اکنون تلاش کنید',
    PERMISSION_PROMPT: 'برای اجازه دسترسی به کمره، روی «فعال کردن کمره» کلیک کنید.',
    PERMISSION_DENIED: 'دسترسی به کمره مسدود شده است. آن را در تنظیمات براوزر خود فعال کنید، سپس دوباره روی «فعال کردن کمره» کلیک کنید.',
    PERMISSION_UNSUPPORTED: 'در این دستگاه هیچ کمره‌ای یافت نشد.',
    ID_DRIVERS_LICENSE: 'جواز رانندگی',
    ID_STATE_ISSUED: 'کارت هویت دولتی',
    ID_PASSPORT: 'پاسپورت',
    ID_OTHERS: 'سایر',
    ERROR_CAMERA_IN_USE: 'کمره در حال استفاده است. اپلیکیشن دیگر را بسته و دوباره تلاش کنید.',
    ERROR_CAMERA_INIT_FAILED: 'راه‌اندازی کمره ناکام شد. لطفاً اجازه‌ها را بررسی کنید.',
    ERROR_CAMERA_BUSY_RETRY: 'کمره هنوز در حال استفاده است. اپلیکیشن دیگر را بسته و دوباره تلاش کنید.',
    ERROR_PLEASE_SCAN_DOCS: 'لطفاً سند (اسناد) را اسکن کنید',
    SUBTITLE: 'سند انتخاب‌شده را در ذیل اسکن کنید.',
    LABEL_FRONT_SIDE: 'روی سند',
    LABEL_BACK_SIDE: 'پشت سند',
    INSTRUCTION_POSITION_FRONT: 'روی سند هویتی خود را در برابر کمره قرار دهید',
    INSTRUCTION_POSITION_BACK: 'پشت سند هویتی خود را در برابر کمره قرار دهید',
    MRZ_BAND_HINT: 'دو سطر پایین صفحهٔ عکس را زیر خط‌چین قرار دهید',
    MRZ_SCAN_FAILED: 'پاسپورت شما خوانده نشد. آن را ثابت نگه دارید تا دو سطر پایین داخل نوار باشد و دوباره تلاش کنید.',
    MRZ_SCAN_ATTEMPT: 'اسکن {current} از {total}',
    GLARE_WARNING: 'روی سند بازتاب نور وجود دارد. آن را کمی کج کنید یا از نور دور شوید.',
    BARCODE_BAND_HINT: 'کارت را ثابت و در فاصلهٔ حدود ۲۰ سانتی‌متری نگه دارید تا بارکد داخل کادر باشد',
    BARCODE_SCAN_FAILED: 'بارکد خوانده نشد. پشت کارت را ثابت و صاف داخل کادر نگه دارید و دوباره تلاش کنید.',
    TORCH_ON: 'روشن کردن چراغ',
    TORCH_OFF: 'خاموش کردن چراغ',
    BARCODE_READING_PHOTO: 'در حال خواندن بارکد…',
    ID_CHECKING: 'در حال بررسی مدرک شناسایی شما…',
  },
  VISITOR_VIDEO: {
    TITLE: 'تقریباً تمام شد!',
    SUBTITLE: 'برای ادامه، ویدیوی ذیل را تماشا کنید.',
    BTN_CHECK_IN: 'ثبت ورود',
    BTN_FINISH: 'خاتمه',
  },
  PRE_REG_CONFIRMATION: {
    TITLE: 'ثبت‌نام یافت شد',
    SUBTITLE: 'لطفاً جهت تکمیل ثبت ورود، جزئیات خود را تائید کنید.',
    BTN_NOT_YOU: 'شما نیستید؟',
    MEETING_WITH_LABEL: 'ملاقات با {hostName}',
    ONSITE_TITLE: 'شما در حال حاضر ثبت ورود شده‌اید',
    ONSITE_SUBTITLE: 'برای ثبت خروج، نشست خود را انتخاب کنید.',
    CHECKIN_AT_LABEL: 'زمان ثبت ورود:',
  },
  CONFIRMATION: {
    CHECKED_IN_TITLE: 'ثبت ورود شما انجام شد!',
    WALLET_HINT: 'پاس به صورت خودکار در دستگاه شما باز خواهد شد.',
    BTN_APPLE_WALLET: 'Apple Wallet',
    BTN_GOOGLE_WALLET: 'Google Wallet',
    REGISTRATION_COMPLETE_TITLE: 'ثبت‌نام پیش از رسیدن تکمیل شد',
    ARRIVAL_INSTRUCTIONS_TITLE: 'هدایات رسیدن',
    ARRIVAL_INSTRUCTIONS_TEXT: 'یک کد QR به ایمیل ثبت‌شده شما ارسال شده است. هنگام رسیدن، از آن برای ثبت ورود سریع‌تر در کیوسک استفاده کنید.',
    QR_CAPTION: 'این کد QR را می‌توان در لابی اسکن کرد تا ثبت‌نام شما نمایان شود.',
    BTN_SAVE_QR: 'ذخیره کد QR',
    WALLET_LABEL: 'ولت',
    ACTION_ADD_PASS: 'افزودن پاس',
    CALENDAR_LABEL: 'تقویم',
    ACTION_SAVE_EVENT: 'ذخیره رویداد',
    DEFAULT_WELCOME_MESSAGE: 'میزبان شما آگاه شده و به زودی نزد شما خواهد آمد.',
    PREFILL_SCHEDULED_FOR: 'همه چیز برای {date} آماده است.',
    PREFILL_HOST_NOTIFIED: 'میزبان شما آگاه شده است و ما مشتاقانه منتظر خوش‌آمدگویی به شما هستیم.',
    TOAST_UPDATE_VISIT_ERROR: 'هنگام به‌روزرسانی بازدید خطایی رخ داد. لطفاً دوباره تلاش کنید.',
    TOAST_GENERIC_ERROR: 'با یک خطا مواجه شدیم. لطفاً دوباره تلاش کنید!',
  },
  PREFILL_DETAILS: {
    VISIT_LABEL: 'جزئیات بازدید',
    PAGE_SUBTITLE: 'برای سریع‌تر شدن ثبت ورود، مراحل ذیل را تکمیل کنید.',
    SECTION_HEADING: 'ثبت‌نام پیش از رسیدن خود را تکمیل کنید',
    STEPS_COMPLETED_SUFFIX: 'مرحله تکمیل شده',
    BTN_START: 'بیایید آغاز کنیم!',
  },
  SIGN_OUT: {
    EYEBROW: 'ثبت خروج',
    TITLE_WITH_NAME: 'خدا حافظ،',
    TITLE_FALLBACK: 'خدا حافظ!',
    SUBTITLE_SESSION: 'جزئیات خود را تائید کرده و ثبت خروج کنید.',
    STRIP_SUBTITLE: 'در حال حاضر ثبت ورود شده',
    LABEL_ORG: 'سازمان',
    LABEL_LOCATION: 'موقعیت',
    LABEL_HOST: 'میزبان',
    LABEL_SIGNED_IN_AT: 'ثبت ورود',
    BTN_SIGN_OUT: 'ثبت خروج',
    BTN_SIGNING_OUT: 'در حال ثبت خروج...',
    BTN_START_OVER: 'از سر آغاز کنید',
    LABEL_NOT_YOU: 'شما نیستید؟',
    TOAST_SUCCESS: 'ثبت خروج شما با موفقیت انجام شد!',
    TOAST_ERROR_FALLBACK: 'خطا در ثبت خروج. لطفاً دوباره تلاش کنید.',
    CUSTOM_FIELDS_EYEBROW: 'تقریباً تمام شد',
    CUSTOM_FIELDS_TITLE: 'تکمیل ثبت خروج',
    CUSTOM_FIELDS_SUBTITLE: 'لطفاً جزئیات ضروری را خانه‌پری کنید.',
    BTN_CONFIRM_SIGN_OUT: 'تائید ثبت خروج',
    BTN_GO_BACK: 'بازگشت',
    SUCCESS_TITLE: 'ثبت خروج شما تکمیل شد',
    SUCCESS_SUBTITLE: 'از بازدید شما تشکر می‌کنیم',
    BTN_DONE: 'تکمیل',
    LABEL_VISIT_DURATION: 'مدت بازدید',
    GEOLOCATION_UNAVAILABLE: 'برای ثبت خروج، دسترسی به موقعیت مکانی ضروری است. لطفاً از یک دستگاه موبایل استفاده کنید.',
    LOCATION_PERMISSION_REQUIRED: 'برای ثبت خروج، اجازه دسترسی به موقعیت مکانی ضروری است. لطفاً دسترسی به موقعیت مکانی را اجازه داده و دوباره تلاش کنید.',
    MUST_BE_ON_SITE: 'برای ثبت خروج باید در محل حضور داشته باشید.',
    GUEST_PLACEHOLDER: 'مهمان',
    SUCCESS_CLOSING: 'روز خوبی داشته باشید!',
    STATUS_CHECKED_IN: 'ثبت ورود شده',
    STATUS_CHECKED_OUT: 'ثبت خروج شده',
    DETAIL_LABEL_HOST: 'میزبان',
  },
  PREREGISTER: {
    ALL_SET_TITLE: 'همه چیز برای بازدید شما آماده است',
    ALL_SUB_TITLE: 'ثبت‌نام پیش از رسیدن شما تکمیل شده است. می‌توانید جزئیات بازدید خود را در ذیل بررسی کنید.',
    BTN_CHECK_DETAILS: 'بررسی جزئیات من',
    BTN_VIEW_CHECKIN: 'مشاهده ثبت ورود بدون تماس',
    QR_TITLE: 'ورود بدون تماس',
    QR_SUBTITLE: 'برای ادامه، کمرۀ خود را به سوی کد QR که در آیپد یا تابلوی چاپ‌شده نشان داده شده، بگیرید',
    ERROR_TITLE: 'مشکلی پیش آمد!',
    ERROR_SUBTITLE: 'متأسفیم، خطایی رخ داد. لطفاً بعداً دوباره تلاش کنید.',
    DETAIL_LABEL_VISITOR: 'بازدیدکننده',
    DETAIL_LABEL_EMAIL: 'ایمیل',
    DETAIL_LABEL_HOST: 'میزبان',
    DETAIL_HOST_PLACEHOLDER: 'تعیین نشده',
  },
  REGISTRATION_LINK_EXPIRED: {
    TITLE: 'لینک ثبت‌نام پیش از رسیدن منقضی شده است',
    SUBTITLE: 'این لینک دیگر معتبر نیست',
    REASONS_HEADING: 'دلایل احتمالی:',
    REASON_CANCELLED: 'دعوت‌نامه لغو شده است',
    REASON_CHECKED_IN: 'قبلاً ثبت ورود شده است',
    REASON_PAST: 'زمان تعیین‌شده سپری شده است',
    HELP_PROMPT: 'به کمک نیاز دارید؟',
    HELP_TEXT: 'با میزبان خود یا میز پذیرش تماس بگیرید',
  },
  SMARTPASS_CHECKIN: {
    INVALID_PASS_LINK: 'لینک پاس نامعتبر است.',
    PASS_REVOKED_OR_EXPIRED: 'این پاس لغو شده یا منقضی شده است.',
    UNABLE_TO_LOAD: 'بارگذاری صفحه ثبت ورود ممکن نشد.',
    PASS_EXPIRED_TITLE: 'پاس منقضی شده',
    CONTACT_HOST_FOR_NEW_PASS: 'لطفاً برای دریافت یک SmartPass جدید با میزبان خود تماس بگیرید.',
    ERROR_TITLE: 'مشکلی پیش آمد',
    BTN_TRY_AGAIN: 'دوباره تلاش کنید',
    STATUS_NOT_ON_SITE: 'در محل حضور ندارد',
    STATUS_CHECKED_IN: 'ثبت ورود شده',
    SELECT_LOCATION_LABEL: 'موقعیت خود را انتخاب کنید:',
    SELECT_LOCATION_PLACEHOLDER: '-- انتخاب موقعیت --',
    BTN_PROCESSING: 'در حال پردازش...',
    BTN_CHECK_IN: 'ثبت ورود',
    BTN_CHECK_OUT: 'ثبت خروج',
    BTN_CLOSE_HINT: 'می‌توانید این صفحه را ببندید.',
    POWERED_BY: 'قدرت گرفته از Visitly',
  },
  SMARTPASS_DOWNLOAD: {
    INVALID_PASS_LINK: 'لینک پاس نامعتبر است.',
    GOOGLE_WALLET_COMING_SOON: 'پشتیبانی از Google Wallet به زودی فراهم می‌شود. لطفاً بعداً دوباره بررسی کنید.',
    OPEN_ON_MOBILE: 'لطفاً برای افزودن پاس به ولت خود، این لینک را در آیفون یا گوشی اندروید خود باز کنید.',
    PREPARING_PASS: 'در حال آماده کردن SmartPass شما...',
    TITLE: 'SmartPass',
  },
  SMARTPASS_REDIRECT: {
    INVALID_PASS_LINK: 'لینک پاس نامعتبر است.',
    LOCATION_REQUIRED: 'ثبت ورود با SmartPass نیاز به دسترسی موقعیت مکانی دارد. لطفاً این لینک را در دستگاه موبایل خود باز کنید.',
    LOCATION_PERMISSION_REQUIRED: 'برای ثبت ورود با SmartPass، اجازه دسترسی به موقعیت مکانی ضروری است. لطفاً دسترسی به موقعیت مکانی را اجازه داده و دوباره تلاش کنید.',
    LOCATION_UNAVAILABLE: 'موقعیت مکانی شما قابل تشخیص نیست. لطفاً مطمئن شوید که خدمات موقعیت مکانی فعال است و دوباره تلاش کنید.',
    PASS_REVOKED_OR_EXPIRED: 'این پاس لغو شده یا منقضی شده است.',
    OUTSIDE_GEOFENCE: 'شما بیرون از ساحه ثبت ورود قرار دارید. لطفاً نزدیک‌تر به ساختمان بروید و دوباره تلاش کنید.',
    TITLE: 'SmartPass',
    BTN_RETRY: 'تلاش دوباره',
    OPENING_CHECK_IN: 'در حال باز کردن ثبت ورود...',
  },
  TOUCHLESS: {
    EVACUATION_BANNER: 'تخلیه اضطراری در این محل در جریان است',
    BTN_TAP_TO_SIGNIN: 'برای ورود کلیک کنید',
    BTN_SCAN_QR_CODE: 'اسکن کردن کد QR',
    TOAST_INVALID_QR: 'کد QR نامعتبر یا منقضی شده است. لطفاً دوباره تلاش کنید!',
  },
  FOOTER: {
    POWERED_BY: 'قدرت گرفته از ',
  },
  HEADER: {
    STEP: 'مرحله ',
    OF: ' از ',
  },
  SPLASH_SCREEN: {
    TAP_TO_START: 'برای آغاز کلیک کنید',
  },
  QR_SCANNER: {
    INVALID_CODE_ERROR: 'کد QR نامعتبر است. لطفاً دوباره تلاش کنید!',
  },
  QR_SCANNER_CARD: {
    CAMERA_DISCLAIMER: 'ما فقط از کمرۀ شما برای خواندن کد QR لابی استفاده می‌کنیم و هیچ ویدیویی را ذخیره نمی‌کنیم',
    PERMISSION_PROMPT: 'برای اجازه دسترسی به کمره، روی «فعال کردن کمره» کلیک کنید.',
    PERMISSION_DENIED: 'دسترسی به کمره مسدود شده است. آن را در تنظیمات براوزر خود فعال کنید، سپس دوباره روی «فعال کردن کمره» کلیک کنید.',
    PERMISSION_UNSUPPORTED: 'در این دستگاه هیچ کمره‌ای یافت نشد.',
    CAMERA_NOT_AVAILABLE: 'کمره آغاز نشد. روی «تلاش دوباره» کلیک کنید یا صفحه را دوباره بارگذاری کنید.',
    ERROR_CAMERA_IN_USE: 'کمره در حال استفاده است. اپلیکیشن دیگر را بسته و دوباره تلاش کنید.',
    ERROR_CAMERA_INIT_FAILED: 'راه‌اندازی کمره ناکام شد. لطفاً اجازه‌ها را بررسی کنید.',
    ERROR_CAMERA_BUSY_RETRY: 'کمره هنوز در حال استفاده است. اپلیکیشن دیگر را بسته و دوباره تلاش کنید.',
  },
  CAMERA_PERMISSION: {
    REQUIRED: 'برای اسکن کردن کدهای QR، اجازه دسترسی به کمره ضروری است.',
    ACCESS_DENIED: 'دسترسی به کمره رد شده یا در دسترس نیست',
  },
  CONFIRMATION_MODAL: {
    DEFAULT_MESSAGE: 'آیا مطمئن هستید که می‌خواهید لغو کنید؟ تمام پیشرفت شما از بین خواهد رفت.',
  },
  PROGRESS_STEPS: {
    HOST:       { headerTitle: 'انتخاب میزبان',      prefillTitle: 'انتخاب میزبان',    prefillDesc: 'انتخاب کنید که به دیدن چه کسی می‌روید' },
    DETAILS:    { headerTitle: 'جزئیات شما',         prefillTitle: 'جزئیات شما',       prefillDesc: 'معلومات تماس خود را ارائه کنید' },
    DOCUMENT:   { headerTitle: 'امضای اسناد',        prefillTitle: 'امضای اسناد',      prefillDesc: 'اسناد ضروری را بررسی و امضا کنید' },
    ID_CAPTURE: { headerTitle: 'تائید سند هویتی',    prefillTitle: 'تائید سند هویتی',  prefillDesc: 'سند هویتی خود را اسکن کنید' },
    PHOTO:      { headerTitle: 'گرفتن سلفی',         prefillTitle: 'گرفتن سلفی',       prefillDesc: 'عکس خود را بگیرید' },
    VIDEO:      { headerTitle: 'تماشای ویدیو',       prefillTitle: 'تماشای ویدیو',     prefillDesc: 'مشاهده ویدیوی ایمنی یا آشنایی' },
  },
};
